import { isMicrositePlanningReview } from "../utils/micrositePlanningReview";
import { isYahooPlatform } from "../utils/platformPresentationOrder";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { resolveStrategyVisibility } from "../utils/campaignStrategyVisibility";
import { resolvePublicWithholdingCopy } from "../utils/publicWithholdingPresenter";
import "./CampaignStrategyMicrosite.css";

import CphereHeaderLogo from "../components/CphereHeaderLogo";
import CphereAnalysisWriteup from "../components/CphereAnalysisWriteup";
import StrategyFunnel from "../components/StrategyFunnel";
import CampaignMediaNetwork from "../components/CampaignMediaNetwork";
import {
  completeCampaignPlatformCards,
  formatAllocationPercentLabel,
  filterCampaignPlatformCards,
  expandCampaignPlatformScope,
  findMissingCampaignPlatformSelections,
  normalizeCampaignPlatformScope,
  selectCampaignRequestedAllocationRows,
  resolveCampaignPlatformIdentity,
  resolveCampaignMetaChildAllocationPercent,
} from "../utils/campaignMediaNetwork";
import { buildStrategyFunnel } from "../utils/strategyFunnel";
import { isCampaignFilmVertical, resolveCampaignJourneyIdentity, servedFilmName } from "../utils/campaignJourneySemantics";
import useResolvedImageCandidates from "../hooks/useResolvedImageCandidates";
import SherlocMediaPlanCharts, {
  ChartPopoverPortal,
  getChartInteractionProps,
  normalizeMediaPlanChartData,
  parseBudgetValue,
  useAnchoredChartPopover,
} from "./SherlocMediaPlanCharts";
import { usePageAwarenessState } from "../contexts/PageAwarenessContext";
import { absoluteAdvertiserLogoUrl, buildAdvertiserLogoCandidates } from "../utils/advertiserLogo";
import { formatCampaignStatusLabel } from "../utils/campaignStatusLabel";
import {
  CAMPAIGN_MICROSITE_GOVERNANCE_SCHEMA_VERSION,
  GOVERNANCE_INTERPRETATION_FAILURE_CODES,
  canRegisterCampaignMicrositePageAwareness,
  normalizeCampaignMicrositeGovernanceFindings,
  resolveCampaignMicrositePayloadForRequest,
  validateCampaignMicrositeCalendar,
  validateCampaignMicrositeGovernanceEvidence,
} from "../utils/campaignMicrositeGovernance";
import {
  resolveStrategyNextActionNotice,
  normalizeStrategyNextAction,
  STRATEGY_NEXT_ACTION_STATES,
} from "../utils/strategyNextAction";
import { formatFlightDate } from "../utils/flightDates";
import {
  EARNED_MEDIA_DIMENSION_STATUSES,
  resolveEarnedMediaDimensionStates,
} from "../utils/earnedMediaDimensionState";
import { resolveBenchmarkUnit } from "../utils/micrositeChannelProjection";
import {
  describeMetricCount,
  formatMetricCount,
  resolveCanonicalVolume,
} from "../utils/micrositeMetricFormat";
import CampaignProposalPlanningStory, {
  ProposalNarrativeNote,
  buildProposalAssistantContext,
  buildProposalImpressionOutlook,
  buildProposalMediaPlanChartProjection,
  buildProposalSpendRampCalendar,
  isSectionWithheldInProposalMode,
  resolveCampaignProposalPlanning,
} from "../components/CampaignProposalPlanning";
import CampaignRecommendedDataLayers, {
  resolveRecommendedDataLayers,
} from "../components/RecommendedDataLayers";
import {
  dropHeadingsEmptiedByRedaction,
  redactNarrativeLineBySentence,
} from "../utils/strategyNarrativeSentenceRedaction";
import {
  normalizePlatformAllocationKey,
  normalizePlatformAllocationItems,
  normalizeSherlocPlatformDisplayLabel,
  orderPlatformRowsByAllocation,
  normalizeSherlocBudgetAllocationText,
  parseSherlocBudgetAllocationItems,
  resolvePlatformAllocationPercent,
  resolvePlatformBudgetAmount,
  resolveCanonicalChartPlatformKey,
} from "../utils/mediaPlanAllocations";
import {
  isReadableCampaignStrategyMicrositePath,
  micrositeReturnStateMatches,
  readCampaignStrategyMicrositeAddress,
  rememberMicrositeReturnAddress,
} from "../utils/campaignStrategyMicrositeAddress";
import {
  PLATFORM_FALLBACK_BRANDING,
  resolvePlatformBranding as resolvePlatformBrandingBase,
  resolvePlatformBrandKey as resolvePlatformBrandKeyBase,
  resolveMicrositePlatformBranding as resolveMicrositePlatformBrandingBase,
} from "../utils/platformBranding";
import {
  formatAdvertiserVerticalImpactLabel,
  formatAdvertiserVerticalStrategyLabel,
  formatCampaignStrategyMicrositeTitle,
  getBrandAwarenessTaxonomy,
  getVerticalIntelligenceConfig,
  getVerticalPlatformRole,
  isEntertainmentReferenceIntelligenceVertical,
  normalizeAdvertiserVertical,
  sanitizeVerticalIntelligenceCopy,
  serializeVerticalIntelligence,
} from "../config/verticalIntelligenceConfig";
import imdbLogo from "../assets/imdb.png";
import rottenTomatoesLogo from "../assets/rottentomatoes.png";
import dashboardBrainFallbackImage from "../assets/back-icon.gif";
import CphereBrandFooter from "../components/CphereBrandFooter";
import StrategyReviewAccessPanel from "../components/StrategyReviewAccessPanel";
import {
  fetchStrategyMicrositeWithReviewGrant,
  normalizeStrategyReviewCampaignId,
  resolveStrategyReviewApiBase,
} from "../utils/strategyReviewAccess";
import {
  GENERAL_CAMPAIGN_WITHHELD_SECTIONS,
  campaignReceivesGeneralTemplate,
  omitTheatricalTemplate,
} from "../utils/micrositeGeneralTemplate";
import {
  platformStrategyCompletenessProps,
  resolvePlatformRolePillLabel,
  resolvePlatformStrategyValue,
} from "../utils/micrositePlatformStrategyCard";
import { formatMicrositePrimaryKpiDisplay } from "../utils/micrositePrimaryKpiDisplay";
import {
  AUDIENCE_PLANNING_WEIGHT_CHART_NOTE,
  AUDIENCE_PLANNING_WEIGHT_DISCLOSURE,
  AUDIENCE_PLANNING_WEIGHT_LABEL,
  PLANNING_WEIGHTS_AUDIENCES,
  PLANNING_WEIGHTS_CHART_META,
  PLANNING_WEIGHTS_DISCLOSURE,
  audiencePriorityCohorts,
  describeAudienceGroup,
  formatAudienceGroupChartLabel,
  formatPlanningWeight,
  frameAudienceSection,
  framePlanningWeightsSection,
  readPlanningWeights,
} from "../utils/micrositeAudienceGroups";
import { campaignOwnWordGuard, transformKeepingOwnWords } from "../utils/campaignOwnWords";

// Who reads this portal's strategy page: the client (and anyone the public
// link reaches). Planning-weight coverage is worded for them.
export const PLANNING_WEIGHTS_READER = PLANNING_WEIGHTS_AUDIENCES.CLIENT;

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL ?? "";
// The same-site API origin the review cookie belongs to (api.cphere.ai on a
// cphere.ai page; otherwise the default backend).
const STRATEGY_REVIEW_API_BASE = resolveStrategyReviewApiBase(API_BASE_URL);
const CAMPAIGN_STRATEGY_MICROSITE_ROUTE_CLASS = "campaign-strategy-microsite-route";
export const CAMPAIGN_STRATEGY_MICROSITE_CLIENT_DASHBOARD_ROUTE = "/dashboard";
/*
 * Strategies published on their link as read-only PUBLIC PROPOSALS (DJ,
 * 2026-10-05): Blood Behind Us (campaign 56; a production campaign id).
 * Search engines must not index them: public/_headers sends X-Robots-Tag for
 * the two known addresses, and the page also marks itself, at any address
 * that renders it, once its strategy has loaded. A visitor with no sign-in
 * stored gets no Media Planner control on them (at a legacy name link, only
 * once the strategy has loaded). Every other page is untouched.
 */
export const CAMPAIGN_STRATEGY_PUBLIC_PROPOSAL_CAMPAIGN_IDS = Object.freeze(["56"]);
export const CAMPAIGN_STRATEGY_NOINDEX_CAMPAIGN_IDS = CAMPAIGN_STRATEGY_PUBLIC_PROPOSAL_CAMPAIGN_IDS;
// Their readable addresses (the ones public/_headers lists), so the page knows
// the campaign before its path lookup answers.
export const CAMPAIGN_STRATEGY_PUBLIC_PROPOSAL_ADDRESSES = Object.freeze({
  "/campaign-strategy/blood-behind-us/blood-behind-us-2027-theatrical-release": "56",
});

/**
 * The campaign a strategy page is about, as early as it can be known: the
 * loaded strategy's own id; before that, a public proposal's readable address;
 * otherwise the key being read, a numeric one as the backend reads it ("056"
 * is campaign 56).
 */
export const resolveCampaignStrategyPageCampaignKey = ({ loadedCampaignId, readableAddress, requestKey }) => {
  const loaded = String(loadedCampaignId ?? "").trim();
  if (loaded) return loaded;
  const known = CAMPAIGN_STRATEGY_PUBLIC_PROPOSAL_ADDRESSES[String(readableAddress || "").toLowerCase()];
  if (known) return known;
  const key = String(requestKey ?? "").trim();
  return /^\d+$/.test(key) ? String(Number(key)) : key;
};

/**
 * Add a robots noindex marker while this campaign's strategy page is shown;
 * returns the cleanup. A campaign not listed gets nothing.
 */
export const markCampaignStrategyNoindex = (
  campaignId,
  doc = typeof document === "undefined" ? null : document
) => {
  const id = String(campaignId ?? "").trim();
  if (!doc?.head || !CAMPAIGN_STRATEGY_NOINDEX_CAMPAIGN_IDS.includes(id)) return () => {};
  const meta = doc.createElement("meta");
  meta.setAttribute("name", "robots");
  meta.setAttribute("content", "noindex");
  meta.setAttribute("data-campaign-strategy-noindex", id);
  doc.head.appendChild(meta);
  return () => meta.remove();
};
const CAMPAIGN_STRATEGY_MICROSITE_FALLBACK_ROUTE = "/sherloc-media-planner";
const CAMPAIGN_STRATEGY_MICROSITE_RETURN_STATE_KEY = "cphere:sherloc:microsite-return-state";
/*
 * microsite_pricing_evidence_v1. "Planning Benchmark" understated what these
 * numbers are: an unsourced Cphere constant moved onto the client-billed basis.
 * The fallback label now names the evidence tier itself, and a card that
 * carries a stronger tier (an approved or provider forecast, a contracted
 * rate) says so in its own words.
 */
const CLIENT_SAFE_BENCHMARK_DISPLAY_LABEL = "Directional Planning Benchmark";

/* The tier the backend publishes decides the words. No surface re-derives them. */
const MICROSITE_PRICING_EVIDENCE_TIER_LABEL = {
  APPROVED_GOVERNING_FORECAST: "Approved forecast",
  CERTIFIED_PROVIDER_FORECAST: "Provider forecast",
  CONFIGURED_CONTRACT_RATE: "Contract rate",
  GOVERNED_HISTORICAL_OBSERVATION: "Observed Cphere delivery",
  DIRECTIONAL_PLANNING_BENCHMARK: "Directional Planning Benchmark",
  UNAVAILABLE: "Not established",
};

const MICROSITE_DELIVERY_BASIS_LABEL = {
  APPROVED_GOVERNING_FORECAST: "Approved forecast",
  CERTIFIED_PROVIDER_FORECAST: "Provider forecast",
  CONFIGURED_CONTRACT_RATE: "Contract-rate implied",
  GOVERNED_HISTORICAL_OBSERVATION: "Observed-rate implied",
  DIRECTIONAL_PLANNING_BENCHMARK: "Benchmark-implied",
  UNAVAILABLE: "Not established",
};

const FORECAST_EVIDENCE_TIERS = new Set([
  "APPROVED_GOVERNING_FORECAST",
  "CERTIFIED_PROVIDER_FORECAST",
]);

export const resolveMicrositeEvidenceTier = (row = {}) =>
  row?.evidenceTier || row?.benchmarkCpmEvidenceTier || null;

export const resolveMicrositeEvidenceTierLabel = (row = {}) => {
  const tier = resolveMicrositeEvidenceTier(row);
  return (tier && MICROSITE_PRICING_EVIDENCE_TIER_LABEL[tier]) || null;
};

export const resolveMicrositeDeliveryBasisLabel = (row = {}) => {
  const explicit = String(row?.basisLabel || "").trim();
  if (explicit) return explicit;
  const tier = resolveMicrositeEvidenceTier(row);
  return (tier && MICROSITE_DELIVERY_BASIS_LABEL[tier]) || null;
};

export const isMicrositeForecastEvidence = (row = {}) =>
  row?.benchmarkCpmIsForecast === true ||
  row?.isForecastEvidence === true ||
  FORECAST_EVIDENCE_TIERS.has(resolveMicrositeEvidenceTier(row));
const MICROSITE_HERO_KPI_CHIP_KEYS = new Set(["status", "primary_kpi", "channel_mix"]);
export { CAMPAIGN_MICROSITE_GOVERNANCE_SCHEMA_VERSION };
export const CAMPAIGN_MICROSITE_CURRENT_STATUS = "CURRENT";
// Current + governed + integrity-valid, approval still outstanding. Renders
// the governed body for review; it is NOT an approved/publishable state.
export const CAMPAIGN_MICROSITE_AWAITING_APPROVAL_STATUS = "AWAITING_APPROVAL";

export const isMicrositePlatformGridCompanionChip = (chip = {}) => {
  const key = String(chip?.key || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return Boolean(key && !MICROSITE_HERO_KPI_CHIP_KEYS.has(key));
};

export const isFinalPlatformGridOrphan = (
  platformIndex,
  platformCount,
  precedingPlatformGridItemCount = 0
) =>
  Number.isInteger(platformIndex) &&
  Number.isInteger(platformCount) &&
  Number.isInteger(precedingPlatformGridItemCount) &&
  platformCount > 0 &&
  precedingPlatformGridItemCount >= 0 &&
  (platformCount + precedingPlatformGridItemCount) % 2 === 1 &&
  platformIndex === platformCount - 1;

export function CampaignStrategyMicrositeHeaderLogoLink() {
  return (
    <Link
      to={CAMPAIGN_STRATEGY_MICROSITE_CLIENT_DASHBOARD_ROUTE}
      className="campaign-strategy-microsite-logo-link"
    >
      <CphereHeaderLogo className="campaign-strategy-microsite-logo" />
    </Link>
  );
}

/**
 * The microsite's brand footer IS the shared Cphere brand footer.
 *
 * This markup used to live here inline. It moved to CphereBrandFooter so the
 * public Platform Analytics report could render the SAME approved treatment —
 * same brain asset, same typography, same divider, same spacing, same
 * responsive wrap — instead of a second look-alike footer. Nothing about what
 * this microsite renders changed in the move: the shared component's defaults
 * ARE this footer's copy and asset, and CphereBrandFooter.test.js pins its
 * default markup byte-for-byte against what stood here.
 *
 * The alias stays exported because callers (and their tests) name the footer
 * this way; it is not a wrapper and adds no behavior.
 */
export const CampaignStrategyMicrositeFooter = CphereBrandFooter;

const safeBase = (base) => String(base || "").replace(/\/+$/, "");

const sanitizeMicrositeReturnPath = (
  value,
  fallback = CAMPAIGN_STRATEGY_MICROSITE_FALLBACK_ROUTE
) => {
  const raw = String(value || "").trim();
  if (!raw || !raw.startsWith("/") || raw.startsWith("//")) return fallback;
  if (/^\/(?:login|logout|signup|reset-password|forgot-password)(?:\/|$)/i.test(raw)) {
    return fallback;
  }
  return raw;
};

const readMicrositeReturnState = (viewing = {}) => {
  try {
    const stored = JSON.parse(
      sessionStorage.getItem(CAMPAIGN_STRATEGY_MICROSITE_RETURN_STATE_KEY) || "{}"
    );
    if (!stored || typeof stored !== "object") return {};
    return micrositeReturnStateMatches(stored, viewing) ? stored : {};
  } catch {
    return {};
  }
};

const getAuthHeader = () => {
  const token =
    localStorage.getItem("clientAuthToken") ||
    localStorage.getItem("authToken") ||
    "";
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/*
 * How the AUTHENTICATED governance read went.
 *
 * A refusal used to be dropped: the page kept the public payload and its
 * public notice, so a signed-in reviewer whose session the backend did not
 * authorize for the campaign's advertiser was told the strategy was "withheld
 * until allocation approval" (campaign 56, 2026-10-04: the account held the
 * advertiser, but its sign-in predated that access, and the backend reads the
 * advertiser scope from the sign-in). What stood between that reader and the
 * strategy was the session, not the allocation. The outcome lets the page say
 * so (CAMPAIGN_STRATEGY_SESSION_ACCESS_NOTICE_COPY). It authorizes nothing:
 * the payload is merged exactly as before.
 */
export const STRATEGY_REVIEW_READ = Object.freeze({
  LOADED: "LOADED",
  NO_VERDICT: "NO_VERDICT",
  NOT_SIGNED_IN: "NOT_SIGNED_IN",
  // 401: the sign-in is missing, expired or revoked.
  SESSION_REFUSED: "SESSION_REFUSED",
  // 403: the signed-in role has no review audience.
  ROLE_REFUSED: "ROLE_REFUSED",
  // 404: the backend does not find this campaign in the session's advertiser scope.
  NOT_IN_SESSION_SCOPE: "NOT_IN_SESSION_SCOPE",
  FAILED: "FAILED",
});

const STRATEGY_REVIEW_READ_BY_REFUSAL = Object.freeze({
  401: STRATEGY_REVIEW_READ.SESSION_REFUSED,
  403: STRATEGY_REVIEW_READ.ROLE_REFUSED,
  404: STRATEGY_REVIEW_READ.NOT_IN_SESSION_SCOPE,
});

/**
 * Read internal governance from the AUTHENTICATED endpoint and merge it into
 * the public microsite payload, reporting how the read went.
 *
 * The payload comes back unchanged when the caller is anonymous, when the
 * endpoint refuses (401/403/404), or on any transport failure. It must never
 * be able to break a page that renders perfectly well without it. A cancelled
 * read reports no outcome.
 *
 * `authHeader` is the sign-in the caller already read for the strategy
 * itself. The read is made with that sign-in and only while it is still the
 * stored one: a sign-in that appeared, was replaced or was dropped since (an
 * ended session being signed out) belongs to the next load, so what the page
 * concludes about the reader cannot disagree with what was sent, and a
 * dropped sign-in is not sent again.
 */
export const readAuthenticatedStrategyGovernance = async (
  payload,
  campaignRequestKey,
  signal,
  authHeader = getAuthHeader()
) => {
  if (!payload || typeof payload !== "object") return { payload, reviewRead: null };
  const outcome = (state, httpStatus = null) => ({ state, httpStatus });
  // Anonymous viewers do not ask. There is nothing here for them, and asking
  // would only produce a 401 per page view. Nor does a load whose sign-in has
  // changed since its strategy read.
  if (!authHeader.Authorization || getAuthHeader().Authorization !== authHeader.Authorization) {
    return { payload, reviewRead: outcome(STRATEGY_REVIEW_READ.NOT_SIGNED_IN) };
  }
  if (!API_BASE_URL) return { payload, reviewRead: outcome(STRATEGY_REVIEW_READ.FAILED) };

  try {
    const response = await fetch(
      `${safeBase(API_BASE_URL)}/api/media-campaigns/${encodeURIComponent(
        campaignRequestKey
      )}/strategy-governance`,
      {
        method: "GET",
        headers: { "Content-Type": "application/json", ...authHeader },
        cache: "no-store",
        signal,
      }
    );
    const httpStatus = Number.isInteger(response?.status) ? response.status : null;
    if (!response.ok) {
      return {
        payload,
        reviewRead: outcome(STRATEGY_REVIEW_READ_BY_REFUSAL[httpStatus] || STRATEGY_REVIEW_READ.FAILED, httpStatus),
      };
    }

    const governancePayload = await response.json().catch(() => null);
    if (!governancePayload || typeof governancePayload !== "object") {
      return { payload, reviewRead: outcome(STRATEGY_REVIEW_READ.FAILED, httpStatus) };
    }

    /*
     * ONLY the approval binding crosses over.
     *
     * strategyVisibility IS the binding: the artifact, the actionability
     * verdicts and approvalRevisionKey. The backend has already scoped it to
     * the caller's role — a superadmin receives the artifact and its
     * provenance, a scoped client receives the verdict without them — so this
     * takes what the authorized endpoint chose to send and re-scopes nothing.
     *
     * governancePayload.governance is NOT merged. That is the internal
     * evidence block: current/strategy/publication identity, contentAuthority,
     * contentBinding, bindingDecision, blockers, findings, diagnostics. Its
     * whole reason for moving to an authenticated endpoint was to stop it
     * living on this payload object, and folding it back in here would put it
     * exactly where it was — one fetch further from the route that used to
     * serve it, but on the same object, reachable by the same serializer drift.
     *
     * The render gates on the public payload stay untouched and authoritative.
     */
    const merged = governancePayload.strategyVisibility
      ? { ...payload, strategyVisibility: governancePayload.strategyVisibility }
      : payload;
    const reviewRead = outcome(
      governancePayload.strategyVisibility ? STRATEGY_REVIEW_READ.LOADED : STRATEGY_REVIEW_READ.NO_VERDICT,
      httpStatus
    );
    /*
     * The campaign's readable address crosses over too: this authenticated
     * read registers it, so a page opened by a legacy link can replace the
     * link with it. It is an address, not evidence.
     */
    return {
      payload: isReadableCampaignStrategyMicrositePath(governancePayload.strategyMicrositePath)
        ? { ...merged, strategyMicrositeAddress: governancePayload.strategyMicrositePath }
        : merged,
      reviewRead,
    };
  } catch (error) {
    // A cancelled read simply contributes nothing, and reports nothing.
    return {
      payload,
      reviewRead: error?.name === "AbortError" ? null : outcome(STRATEGY_REVIEW_READ.FAILED),
    };
  }
};

/** The merged payload alone (readAuthenticatedStrategyGovernance). */
export const mergeAuthenticatedStrategyGovernance = async (payload, campaignRequestKey, signal) =>
  (await readAuthenticatedStrategyGovernance(payload, campaignRequestKey, signal)).payload;

// Actual dollars — never an abbreviated "$1.2M" (utils/micrositeMetricFormat).
const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "Not set";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return value || "Not set";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: Number.isInteger(numeric) ? 0 : 2,
      maximumFractionDigits: numeric >= 1000 && Number.isInteger(numeric) ? 0 : 2,
    }).format(numeric);
  } catch {
    return `$${numeric.toLocaleString("en-US")}`;
  }
};

export const formatMicrositePlatformDisplayLabel = (label) => {
  const key = normalizePlatformAllocationKey(label, { allowUnknown: true });
  if (key === "meta") return "Meta";
  if (key === "shopify") return "Shopify";
  if (key === "linkedin") return "LinkedIn";
  // ctv-family display normalization: any label resolving to the canonical
  // ctv identity (legacy CTV / OTT phrasing, Connected TV (CTV), ctv-ott, OTT)
  // renders exactly "CTV" via the shared Sherloc display normalization point.
  // Presentation-only — canonical keys and alias parsing are untouched.
  if (key === "ctv") return normalizeSherlocPlatformDisplayLabel("ctv", label);
  return formatMicrositeTextValue(label, "");
};

// Defensive display-layer guard for platform-identity labels arriving in
// payload data (benchmark cards, CPM chips, chart categories, execution
// modules): a ctv-family identity always renders the governed visible label
// "CTV"; every other label passes through untouched. Reuses the shared
// normalizeSherlocPlatformDisplayLabel single normalization point.
export const displayMicrositePlatformIdentityLabel = (label, platformKey = "") =>
  normalizeSherlocPlatformDisplayLabel(platformKey || label, label) || label || "";

const MICROSITE_PLATFORM_ORDER = Object.freeze([
  "facebook",
  "instagram",
  "meta",
  "youtube",
  "ctv",
  "tiktok",
  "x",
  "google_search",
  "chatgpt_search",
  "rumble",
  "truth_social",
  "reddit",
  "amazon",
  "yahoo",
  "shopify",
  "linkedin",
  "video",
  "display",
  "mobile",
  "audio",
  "ingame",
  "dooh",
  "email",
  "reserve_test",
]);

const MICROSITE_PLATFORM_ORDER_INDEX = new Map(
  MICROSITE_PLATFORM_ORDER.map((key, index) => [key, index])
);

const MICROSITE_PLATFORM_CONTEXT = Object.freeze({
  shopify: {
    key: "shopify",
    label: "Shopify",
    role: "Commerce intelligence, measurement, and optimization layer.",
    funnelPurpose: "Measurement / Optimization",
    primaryKpi: "Commerce signal quality",
    targetingLogic:
      "Use Shopify product, order, customer, cart, checkout, revenue, AOV, and repeat-purchase signals to inform audience, landing-page, and optimization decisions.",
    creativeApproach:
      "Read product and checkout behavior as planning context; paid creative still runs through selected media channels.",
    measurementLogic:
      "Shopify is used as a commerce intelligence and measurement layer, not a live media buying destination.",
    launchReadiness:
      "Shopify is used as a commerce intelligence and measurement layer, not a live media buying destination.",
    allocationLabel: "Measurement layer",
    budgetLabel: "$0 paid media",
    caveat:
      "Shopify is used as a commerce intelligence and measurement layer, not a live media buying destination.",
  },
  linkedin: {
    key: "linkedin",
    label: "LinkedIn",
    role: "Professional/B2B paid media planning channel.",
    funnelPurpose: "Professional / B2B",
    primaryKpi: "Qualified professional engagement",
    targetingLogic:
      "Use role, industry, seniority, company, professional interest, and retargeting signals when the objective supports B2B, executive, recruiting, SaaS, enterprise, finance, or thought-leadership use cases.",
    creativeApproach:
      "Prioritize thought-leadership, professional proof, lead-generation, and high-value audience messaging when the brief supports LinkedIn activation.",
    measurementLogic:
      "LinkedIn is included as a planning channel only; execution remains subject to Cphere governance and platform readiness.",
    launchReadiness:
      "LinkedIn is included as a planning channel only; execution remains subject to Cphere governance and platform readiness.",
    allocationLabel: "Planning channel",
    budgetLabel: "Planning channel",
    caveat:
      "LinkedIn is included as a planning channel only; execution remains subject to Cphere governance and platform readiness.",
  },
});

const getMicrositePlatformOrderIndex = (key) => {
  const displayKey = isYahooPlatform(key) ? "yahoo" : key;
  return MICROSITE_PLATFORM_ORDER_INDEX.has(displayKey)
    ? MICROSITE_PLATFORM_ORDER_INDEX.get(displayKey)
    : Number.MAX_SAFE_INTEGER;
};

const resolveMicrositePlatformKey = (platform) => {
  const raw =
    platform && typeof platform === "object"
      ? platform.key || platform.platform || platform.label || platform.name || platform.title
      : platform;
  return (
    normalizePlatformAllocationKey(raw, { allowUnknown: false }) ||
    resolvePlatformBrandKeyBase(raw) ||
    normalizePlatformAllocationKey(raw, { allowUnknown: true })
  );
};

export const orderMicrositePlatformRows = (rows = [], allocationRows = []) =>
  orderPlatformRowsByAllocation(rows, allocationRows, {
    fallbackComparator: (a, b) => {
    const aKey = resolveMicrositePlatformKey(a);
    const bKey = resolveMicrositePlatformKey(b);
    const orderDelta = getMicrositePlatformOrderIndex(aKey) - getMicrositePlatformOrderIndex(bKey);
    if (orderDelta !== 0) return orderDelta;
    return Number(a?.firstIndex || 0) - Number(b?.firstIndex || 0);
    },
  });

export const buildMicrositeSelectedPlatformCaveats = (platforms = [], allocationRows = []) => {
  const seen = new Set();
  return orderMicrositePlatformRows(platforms, allocationRows)
    .map((platform) => resolveMicrositePlatformKey(platform))
    .filter((key) => MICROSITE_PLATFORM_CONTEXT[key]?.caveat)
    .filter((key) => {
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map((key) => MICROSITE_PLATFORM_CONTEXT[key].caveat);
};

export const resolveBenchmarkDisplayLabel = (card = {}) => {
  /*
   * The published evidence tier wins over every legacy label field. A card that
   * says "Approved forecast" must not be downgraded to the generic benchmark
   * wording by a stale `benchmarkLabel`, and a directional one must not be
   * upgraded by anything.
   */
  const tierLabel = resolveMicrositeEvidenceTierLabel(card);
  if (tierLabel) return tierLabel;
  const candidate =
    card?.benchmarkDisplayLabel ||
    card?.displayLabel ||
    card?.benchmarkLabel ||
    card?.secondaryValue ||
    card?.meta;
  const text = String(candidate || "").trim();
  if (!text || /[+-]?\d+(?:\.\d+)?\s*%/.test(text)) {
    return CLIENT_SAFE_BENCHMARK_DISPLAY_LABEL;
  }
  if (/^planning\s+benchmark$/i.test(text)) return CLIENT_SAFE_BENCHMARK_DISPLAY_LABEL;
  return text;
};

export const buildEstimatedImpressionTotal = (rows = []) => {
  const impressionsByPlatform = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row, index) => {
    if (row?.metricBasis === "cpc") return;
    /*
     * A benchmark-implied volume is summed at the precision it was published
     * with. 311,102 and 311,111 are the same directional scenario — the
     * difference is a rounding decision about the CPM's second decimal — so the
     * total must not re-import a precision the parts never had.
     */
    const rawValue = resolveCanonicalVolume(row);
    if (!Number.isFinite(rawValue) || rawValue <= 0) return;
    const key =
      normalizePlatformAllocationKey(row?.key || row?.label || row?.platform, {
        allowUnknown: true,
      }) || `platform_${index}`;
    const current = impressionsByPlatform.get(key) || 0;
    impressionsByPlatform.set(key, Math.max(current, rawValue));
  });
  return Array.from(impressionsByPlatform.values()).reduce((sum, value) => sum + value, 0);
};

export const getImpressionOutlookPlatformKey = (row) =>
  resolveCanonicalChartPlatformKey(row);

export const buildImpressionOutlookEntries = (deliveryRows = [], chartPlatforms = []) => {
  const seen = new Set();
  const platformByKey = new Map(
    (Array.isArray(chartPlatforms) ? chartPlatforms : [])
      .map((platform) => [getImpressionOutlookPlatformKey(platform), platform])
      .filter(([key]) => Boolean(key))
  );

  return (Array.isArray(deliveryRows) ? deliveryRows : [])
    .map((row) => {
      /* Same rule on the per-platform tiles as on the total. */
      const rawValue = resolveCanonicalVolume(row);
      if (!Number.isFinite(rawValue) || rawValue <= 0) return null;
      const key = getImpressionOutlookPlatformKey(row);
      if (!key || seen.has(key)) return null;
      seen.add(key);
      const chartPlatform = platformByKey.get(key) || {};
      const rawLabel =
        row?.label ||
        row?.platform ||
        row?.name ||
        row?.title ||
        chartPlatform?.name ||
        chartPlatform?.label ||
        resolvePlatformBrandingBase(key)?.label ||
        key;
      const label = key === "meta"
        ? "Meta (Facebook + Instagram)"
        : formatMicrositePlatformDisplayLabel(rawLabel);
      return {
        key,
        label,
        value: formatMetricCount(rawValue),
        rawValue,
        tooltipDatum: {
          ...chartPlatform,
          key,
          name: label,
          label,
          estimatedImpressions: rawValue,
        },
      };
    })
    .filter(Boolean);
};

const resolveEstimatedDeliveryPlatformKey = (item) =>
  normalizePlatformAllocationKey(item?.key || item?.label || item?.platform || item?.title, {
    allowUnknown: true,
  });

// Parses a CPM/CPC benchmark rate string (e.g. "$21.43 CPM", "$5–$8 CPC") into a number.
// Critically, it strips the CP* metric token first so the "M" in "CPM" is never treated as
// a millions multiplier (the shared budget parser would otherwise turn $21.43 CPM into
// 21,430,000 and deflate projected impressions by 1,000,000x).
const parseMicrositeBenchmarkRate = (value) => {
  if (typeof value === "number") return Number.isFinite(value) && value > 0 ? value : null;
  const text = String(value ?? "").trim();
  if (!text) return null;
  const stripped = text.replace(/\bcp[mcp]\b/gi, " ").replace(/,/g, "");
  const matches = stripped.match(/\d+(?:\.\d+)?/g);
  if (!matches?.length) return null;
  const numbers = matches.map(Number).filter((numeric) => Number.isFinite(numeric) && numeric > 0);
  if (!numbers.length) return null;
  return numbers.reduce((sum, numeric) => sum + numeric, 0) / numbers.length;
};

const resolveEstimatedDeliveryRate = (item) => {
  const key = resolveEstimatedDeliveryPlatformKey(item);
  const cpmRateText = pickFirstValue(
    item?.benchmarkCpm,
    item?.benchmarkCPM,
    item?.cpmBenchmark,
    item?.cpm
  );
  const cpcRateText = pickFirstValue(
    item?.benchmarkCpc,
    item?.benchmarkCPC,
    item?.cpcBenchmark,
    item?.cpc
  );
  const rateText = pickFirstValue(cpmRateText, item?.primaryValue, cpcRateText);
  const value = parseMicrositeBenchmarkRate(rateText);
  if (!Number.isFinite(value) || value <= 0) return null;
  const descriptor = [
    item?.metricType,
    item?.metric,
    item?.unit,
    item?.pricingModel,
    item?.secondaryValue,
    item?.subtitle,
    item?.meta,
    item?.label,
    item?.title,
  ]
    .filter(Boolean)
    .join(" ");
  // The explicit unit attached to the rate value ("$23.40 CPM") wins over the
  // google_search/chatgpt_search "assume CPC" fallback so a CPM benchmark is
  // projected as impressions, never as clicks. resolveBenchmarkUnit only
  // returns the platform CPC default when no explicit CPM/CPC signal exists.
  const unit = resolveBenchmarkUnit({
    rateText,
    descriptor,
    // Truthiness, not `!= null`: pickFirstValue returns "" when a row carries no
    // such field, so a null-check reported EVERY row as having both a CPM and a
    // CPC field, and resolveBenchmarkUnit's unit-typed-field rules could never
    // fire. Harmless while every benchmark rate in the payload was a CPM;
    // actively dangerous now that a click-priced card publishes a CPC.
    cpmFieldPresent: Boolean(cpmRateText),
    cpcFieldPresent: Boolean(cpcRateText),
    platformKey: key,
  });
  return {
    value,
    basis: unit === "CPC" ? "cpc" : "cpm",
  };
};

/*
 * Presentation rounding for a benchmark-implied volume. Mirrors
 * micrositePricingEvidenceService.displayVolumeValue, and is applied only where
 * this portal recomputes a projection the backend already published a rounded
 * form of. Tiers backed by real evidence are never coarsened.
 */
const roundToDirectionalPrecision = (evidenceTier, value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  if (evidenceTier !== "DIRECTIONAL_PLANNING_BENCHMARK") return Math.round(numeric);
  const magnitude = Math.pow(10, Math.max(0, Math.floor(Math.log10(numeric)) - 2));
  return Math.round(numeric / magnitude) * magnitude;
};

export const buildEstimatedDeliveryRows = ({
  rawDelivery = [],
  channelSplit = [],
  benchmarkCards = [],
} = {}) => {
  const deliveryRows = Array.isArray(rawDelivery) ? rawDelivery : [];
  const splitRows = Array.isArray(channelSplit) ? channelSplit : [];
  if (!splitRows.length) return deliveryRows;

  const deliveryByKey = new Map();
  deliveryRows.forEach((entry) => {
    const key = resolveEstimatedDeliveryPlatformKey(entry);
    if (!key) return;
    const current = deliveryByKey.get(key) || {
      value: 0,
      displayValue: 0,
      benchmarkCpm: entry?.benchmarkCpm,
      benchmarkCpmSource: entry?.benchmarkCpmSource,
      evidenceTier: entry?.evidenceTier,
      basisLabel: entry?.basisLabel,
    };
    current.value += Number(entry?.value || 0);
    /* The published display figure aggregates alongside the exact one. */
    current.displayValue += Number(entry?.displayValue ?? entry?.value ?? 0);
    current.benchmarkCpm = current.benchmarkCpm || entry?.benchmarkCpm;
    current.benchmarkCpmSource = current.benchmarkCpmSource || entry?.benchmarkCpmSource;
    current.evidenceTier = current.evidenceTier || entry?.evidenceTier;
    current.basisLabel = current.basisLabel || entry?.basisLabel;
    deliveryByKey.set(key, current);
  });

  const rateByKey = new Map();
  [...deliveryRows, ...(Array.isArray(benchmarkCards) ? benchmarkCards : []), ...splitRows].forEach(
    (entry) => {
      const key = resolveEstimatedDeliveryPlatformKey(entry);
      const rate = resolveEstimatedDeliveryRate(entry);
      if (!key || !rate) return;
      if (!rateByKey.has(key)) rateByKey.set(key, rate);
    }
  );

  return splitRows.map((item) => {
    const key = resolveEstimatedDeliveryPlatformKey(item);
    const delivery = deliveryByKey.get(key);
    const rawValue = Number(delivery?.value || 0);
    const itemEstimatedImpressions = Number(item?.estimatedImpressions || 0);
    const budget = parseBudgetValue(item?.budgetLabel || item?.budget);
    const rate = rateByKey.get(key);
    const projectedImpressions =
      rate?.basis === "cpm" && Number.isFinite(budget) && budget > 0
        ? Math.round((budget / rate.value) * 1000)
        : 0;
    const projectedClicks =
      rate?.basis === "cpc" && Number.isFinite(budget) && budget > 0
        ? Math.round(budget / rate.value)
        : 0;
    const fallbackValue =
      rawValue > 0 ? rawValue : itemEstimatedImpressions > 0 ? itemEstimatedImpressions : 0;
    const value =
      projectedImpressions > 0 ? projectedImpressions : projectedClicks > 0 ? projectedClicks : fallbackValue;

    /*
     * A volume inherits the evidence class of the rate it was divided by:
     * impressions = budget / CPM x 1000 cannot be better evidence than the CPM.
     * `displayValue` is that volume at the precision the tier supports — the
     * backend rounds a benchmark-implied figure to three significant figures
     * because its last digits move with the rounding of the CPM's second
     * decimal.
     */
    const evidenceTier =
      delivery?.evidenceTier ||
      item?.estimatedImpressionsEvidenceTier ||
      item?.benchmarkCpmEvidenceTier ||
      null;
    /*
     * When this portal recomputes the projection from budget / rate, the
     * backend's published rounding no longer applies to the number — so the
     * same rule is applied here. Three significant figures at the directional
     * tier: 311,102 and 311,111 are the same benchmark-implied scenario, and
     * the difference between them is a rounding decision about the CPM's
     * second decimal.
     */
    const recomputed = projectedImpressions > 0 ? projectedImpressions : projectedClicks;
    const displayValue =
      recomputed > 0
        ? roundToDirectionalPrecision(evidenceTier, recomputed)
        : delivery?.displayValue ?? item?.estimatedImpressionsDisplayValue ?? null;

    return {
      key: `${item.key || item.label}_delivery`,
      label: item.label,
      value,
      displayValue,
      evidenceTier,
      basisLabel:
        delivery?.basisLabel || item?.estimatedImpressionsBasisLabel || null,
      metricBasis: rate?.basis || "impressions",
      benchmarkCpm: delivery?.benchmarkCpm || item?.benchmarkCpm || (rate?.basis === "cpm" ? rate.value : undefined),
      benchmarkCpmSource: delivery?.benchmarkCpmSource || item?.benchmarkCpmSource,
      allocationLabel: item.allocationLabel,
      budgetLabel: item.budgetLabel,
      /* campaign_financial_basis_v1: server-declared basis of the budget figure. */
      budgetBasisLabel: item.budgetBasisLabel || null,
      allocationSource: item.allocationSource,
    };
  });
};

const getEstimatedDeliverySortValue = (entry) => {
  const value = Number(
    entry?.value ??
      entry?.rawValue ??
      entry?.estimatedImpressions ??
      entry?.estimatedClicks ??
      0
  );
  return Number.isFinite(value) ? value : 0;
};

export const sortEstimatedDeliveryRowsByValue = (rows = []) =>
  [...(Array.isArray(rows) ? rows : [])].sort((a, b) => {
    const delta = getEstimatedDeliverySortValue(b) - getEstimatedDeliverySortValue(a);
    if (delta !== 0) return delta;
    return String(a?.label || "").localeCompare(String(b?.label || ""));
  });

const parseAllocationPercent = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const match = String(value ?? "").match(/-?\d+(?:\.\d+)?/);
  if (!match) return null;
  const numeric = Number(match[0]);
  return Number.isFinite(numeric) ? numeric : null;
};

const firstPresentValue = (...values) => {
  for (const value of values) {
    if (value === undefined || value === null) continue;
    if (typeof value === "string" && !value.trim()) continue;
    return value;
  }
  return null;
};

export const normalizeRequestedChannelAllocationRows = (
  campaign = {},
  totalBudgetNumeric = null
) => {
  const rows = selectCampaignRequestedAllocationRows(campaign);

  return rows
    .map((row, index) => {
      if (!row || typeof row !== "object") return null;
      const rawLabel = firstPresentValue(
        row.channel,
        row.platform,
        row.label,
        row.name,
        row.title,
        row.key
      );
      const label = formatMicrositeTextValue(rawLabel, "");
      const key = normalizePlatformAllocationKey(label || row?.key, { allowUnknown: false });
      const percent = parseAllocationPercent(
        firstPresentValue(
          row.percent,
          row.percentage,
          row.allocationPercent,
          row.allocation_percent,
          row.budgetPercent,
          row.budget_percent,
          row.share,
          row.allocationValue,
          row.allocation_value,
          row.weight,
          row.value,
          row.allocation,
          row.allocationLabel,
          row.shareLabel
        )
      );
      // Resolve dollars from an explicit dollar field or the percent basis — never from a
      // bare allocation/share/weight that an AI revision may have left in `budget`.
      const grossAmount = resolvePlatformBudgetAmount(row, {
        budgetBasis: totalBudgetNumeric,
        totalBudget: totalBudgetNumeric,
        percent,
      });

      if (!label || !key || !Number.isFinite(percent) || percent <= 0) return null;

      return {
        ...row,
        key,
        label,
        platform: label,
        channel: label,
        value: percent,
        share: percent,
        percent,
        allocation: percent,
        grossAmount,
        budget: grossAmount ?? null,
        budgetLabel: grossAmount ? formatStandardCurrency(grossAmount) : row.budgetLabel || "",
        allocationLabel:
          row.allocationLabel || row.shareLabel || formatAllocationPercentLabel(percent),
        shareLabel: row.shareLabel || row.allocationLabel || formatAllocationPercentLabel(percent),
        allocationSource: row.allocationSource || "requested_channel_allocation",
        firstIndex: index,
      };
    })
    .filter(Boolean);
};

export const buildCanonicalFunnelSplit = (chartStages = [], fallbackSplit = []) => {
  const normalizeRows = (rows = [], source = "fallback") =>
    (Array.isArray(rows) ? rows : [])
      .map((item, index) => {
        const label = formatMicrositeTextValue(
          item?.name || item?.label || item?.title || item?.stage,
          ""
        );
        const percent = parseAllocationPercent(
          item?.percent ??
            item?.value ??
            item?.share ??
            item?.allocation ??
            item?.allocationLabel ??
            item?.shareLabel
        );
        if (!label || !Number.isFinite(percent) || percent <= 0) return null;
        return {
          ...item,
          key: item?.key || label.toLowerCase().replace(/[^a-z0-9]+/g, "_") || `funnel_${index}`,
          label,
          value: percent,
          share: percent,
          percent,
          allocation: percent,
          allocationLabel: item?.allocationLabel || item?.shareLabel || formatAllocationPercentLabel(percent),
          allocationSource: source,
        };
      })
      .filter(Boolean);

  const chartRows = normalizeRows(chartStages, "media_plan_chart");
  const rows = chartRows.length ? chartRows : normalizeRows(fallbackSplit, "structured_budget");
  return rows.sort((a, b) => Number(b?.percent || b?.value || 0) - Number(a?.percent || a?.value || 0));
};

const resolveCardPlatformKey = (card) =>
  resolvePlatformBrandKey(card?.title || card?.label || card?.platform || card?.name || card?.key);

export const enrichPlatformStrategyCardsWithAllocations = (cards = [], allocations = []) => {
  const cardList = Array.isArray(cards) ? cards : [];
  const allocationByKey = new Map();

  (Array.isArray(allocations) ? allocations : []).forEach((allocation) => {
    const key = resolvePlatformBrandKey(
      allocation?.label || allocation?.platform || allocation?.name || allocation?.title || allocation?.key
    );
    if (!key) return;
    const budgetValue = parseBudgetValue(allocation?.budgetLabel || allocation?.budget);
    allocationByKey.set(key, {
      budget: budgetValue,
      budgetLabel:
        allocation?.budgetLabel || (budgetValue ? formatStandardCurrency(budgetValue) : ""),
      percent: Number(
        allocation?.percent ?? allocation?.share ?? allocation?.value ?? allocation?.allocation
      ),
      allocationLabel:
        allocation?.allocationLabel ||
        allocation?.shareLabel ||
        formatAllocationPercentLabel(
          allocation?.percent ?? allocation?.share ?? allocation?.value ?? allocation?.allocation
        ),
    });
  });

  const metaChildCardCount = cardList.filter((card) =>
    ["facebook", "instagram"].includes(resolveCardPlatformKey(card))
  ).length;
  const explicitFacebookOrInstagram =
    allocationByKey.has("facebook") || allocationByKey.has("instagram");
  const groupedMetaAllocation = allocationByKey.get("meta");

  return orderMicrositePlatformRows(cardList.map((card) => {
    const key = resolveCardPlatformKey(card);
    let allocation = allocationByKey.get(key);
    const platformContext = MICROSITE_PLATFORM_CONTEXT[key] || {};

    if (
      !allocation &&
      !explicitFacebookOrInstagram &&
      ["facebook", "instagram"].includes(key) &&
      groupedMetaAllocation
    ) {
      const divisor = metaChildCardCount || 2;
      const splitBudget =
        Number.isFinite(groupedMetaAllocation.budget) && groupedMetaAllocation.budget > 0
          ? groupedMetaAllocation.budget / divisor
          : null;
      const splitPercent =
        Number.isFinite(groupedMetaAllocation.percent) && groupedMetaAllocation.percent > 0
          ? groupedMetaAllocation.percent / divisor
          : null;
      allocation = {
        budget: splitBudget,
        budgetLabel: splitBudget ? formatStandardCurrency(splitBudget) : "",
        percent: splitPercent,
        allocationLabel: splitPercent ? formatAllocationPercentLabel(splitPercent) : "",
      };
    }

    if (!allocation?.budgetLabel && !allocation?.allocationLabel) return card;

    return {
      ...card,
      title: platformContext.label || card.title,
      role: card.role || platformContext.role,
      funnelPurpose: card.funnelPurpose || platformContext.funnelPurpose,
      primaryKpi: card.primaryKpi || platformContext.primaryKpi,
      targetingLogic: card.targetingLogic || platformContext.targetingLogic,
      creativeApproach: card.creativeApproach || platformContext.creativeApproach,
      measurementLogic: card.measurementLogic || platformContext.measurementLogic,
      launchReadiness: card.launchReadiness || platformContext.launchReadiness,
      caveat: card.caveat || platformContext.caveat,
      canonicalAllocationLabel: allocation.allocationLabel || card.canonicalAllocationLabel,
      shareLabel: allocation.allocationLabel || card.shareLabel,
      budgetLabel: allocation.budgetLabel || card.budgetLabel,
    };
  }), allocations);
};

export const ensureMicrositeSelectedPlatformCards = ({
  cards = [],
  selectedPlatforms = [],
  allocations = [],
  advertiserVertical = null,
} = {}) => {
  const existingCards = Array.isArray(cards) ? cards : [];
  const allocationByKey = new Map(
    (Array.isArray(allocations) ? allocations : [])
      .map((allocation) => [resolveMicrositePlatformKey(allocation), allocation])
      .filter(([key]) => key)
  );
  const addedCards = [];

  findMissingCampaignPlatformSelections(existingCards, Array.isArray(selectedPlatforms) ? selectedPlatforms : []).forEach((platform) => {
    const identity = resolveCampaignPlatformIdentity(platform);
    const key = identity?.known ? identity.key : identity?.raw;
    if (!key) return;
    const context = Object.prototype.hasOwnProperty.call(MICROSITE_PLATFORM_CONTEXT, key)
      ? MICROSITE_PLATFORM_CONTEXT[key]
      : null;
    if (!context) {
      // Known platforms retain the existing page's fallback behavior.
      if (identity.known) return;
      // A newly supported destination can precede its detailed strategy card.
      // Keep its saved identity in both presentation surfaces without supplying
      // a role, allocation, or other strategy the artifact does not contain.
      const savedLabel = getMicrositePlatformDisplayLabel(platform);
      if (typeof savedLabel !== "string" || !savedLabel.trim()) return;
      addedCards.push({ key, title: savedLabel.trim() });
      return;
    }
    const allocation = allocationByKey.get(key) || {};
    addedCards.push({
      key,
      title: context.label,
      role: context.role || getVerticalPlatformRole(advertiserVertical, key),
      funnelPurpose: context.funnelPurpose,
      primaryKpi: context.primaryKpi,
      targetingLogic: context.targetingLogic,
      creativeApproach: context.creativeApproach,
      measurementLogic: context.measurementLogic,
      budgetLabel: allocation.budgetLabel || context.budgetLabel,
      canonicalAllocationLabel: allocation.allocationLabel || context.allocationLabel,
      shareLabel: allocation.shareLabel || allocation.allocationLabel || context.allocationLabel,
      launchReadiness: context.launchReadiness,
      caveat: context.caveat,
    });
  });

  return orderMicrositePlatformRows([...existingCards, ...addedCards], allocations);
};

const formatStandardCurrency = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(numeric);
};

const pickFirstValue = (...values) =>
  values.find((value) => String(value ?? "").trim()) || "";

export const formatCampaignObjectiveLabel = (value, fallback = "Not set") => {
  const raw = String(value ?? "").trim();
  if (!raw) return fallback;
  const normalized = raw
    .replace(/[_-]+/g, " ")
    .replace(/\s*\/\s*/g, " / ")
    .replace(/\s+/g, " ")
    .toLowerCase();
  if (
    normalized === "awareness" ||
    normalized === "reach awareness" ||
    normalized === "reach / awareness"
  ) {
    return "Reach / Awareness";
  }
  return normalized
    .replace(/[_-]+/g, " ")
    .replace(/\b[a-z]/g, (match) => match.toUpperCase());
};

export const resolveMicrositeCampaignBudget = (campaign = {}, budgetSection = null) => {
  const structuredBudget =
    budgetSection ||
    campaign?.sections?.budgetArchitecture ||
    campaign?.budgetArchitecture ||
    {};
  return (
    parseBudgetValue(
      pickFirstValue(
        campaign?.totalBudget,
        campaign?.total_budget,
        campaign?.campaignBudget,
        campaign?.campaign_budget,
        campaign?.budget
      )
    ) ??
    parseBudgetValue(
      pickFirstValue(
        structuredBudget?.totalBudget,
        structuredBudget?.total_budget,
        structuredBudget?.totalBudgetLabel,
        structuredBudget?.total_budget_label
      )
    )
  );
};

export const resolveMicrositeNetMediaBudget = (campaign = {}) => {
  const financialSummary = campaign?.financialSummary || campaign?.financial_summary || {};
  const strategyPayload = campaign?.strategyPayload || campaign?.strategy_payload || {};
  const financials = strategyPayload?.financials || {};
  const candidate = parseBudgetValue(
    pickFirstValue(
      campaign?.netMediaBudget,
      campaign?.net_media_budget,
      campaign?.netBudget,
      campaign?.mediaNetBudget,
      campaign?.approvedNetMediaBudget,
      financialSummary?.netMediaBudget,
      financialSummary?.net_media_budget,
      financialSummary?.netBudget,
      financialSummary?.mediaNetBudget,
      financialSummary?.approvedNetMediaBudget,
      financials?.netMediaBudget,
      financials?.net_media_budget,
      strategyPayload?.netMediaBudget,
      strategyPayload?.net_media_budget
    )
  );
  return Number.isFinite(candidate) && candidate > 0 ? candidate : null;
};

/* The planner's own phase percentages applied to the stored working-media
   amount: whole cents that sum to it, keyed by phase. It states no dates and
   no weekly schedule, and resolves nothing unless every phase is the planner's
   proposal and the percentages total 100. */
export const resolvePlannerPhaseWorkingMedia = (phases = [], netMediaBudget = null) => {
  const amounts = new Map();
  const rows = Array.isArray(phases) ? phases : [];
  const netCents = Math.round(Number(netMediaBudget) * 100);
  if (netMediaBudget == null || !Number.isFinite(netCents) || netCents <= 0 || !rows.length) return amounts;
  const shares = rows.map((row) => (row?.planningStatus === "PROPOSED" && row?.key ? Number(row.value) : NaN));
  if (shares.some((share) => !Number.isFinite(share) || share <= 0)) return amounts;
  if (Math.abs(shares.reduce((sum, share) => sum + share, 0) - 100) > 0.001) return amounts;
  let assignedCents = 0;
  rows.forEach((row, index) => {
    const cents =
      index === rows.length - 1 ? netCents - assignedCents : Math.round((netCents * shares[index]) / 100);
    assignedCents += cents;
    amounts.set(row.key, cents / 100);
  });
  return amounts;
};

const formatMicrositeBudgetDisplay = (campaign = {}, budgetSection = null) => {
  const resolvedBudget = resolveMicrositeCampaignBudget(campaign, budgetSection);
  const structuredBudget =
    budgetSection ||
    campaign?.sections?.budgetArchitecture ||
    campaign?.budgetArchitecture ||
    {};
  const structuredBudgetLabel = structuredBudget?.totalBudgetLabel || structuredBudget?.total_budget_label;
  const structuredBudgetValue = parseBudgetValue(
    pickFirstValue(structuredBudget?.totalBudget, structuredBudget?.total_budget, structuredBudgetLabel)
  );
  if (Number.isFinite(resolvedBudget)) {
    return Number.isFinite(structuredBudgetValue) && structuredBudgetValue === resolvedBudget && structuredBudgetLabel
      ? structuredBudgetLabel
      : formatCurrency(resolvedBudget);
  }
  return structuredBudgetLabel || formatCurrency(campaign?.totalBudget);
};

const extractFlightDatePair = (value) => {
  if (!value) return { start: "", end: "" };
  if (typeof value === "object" && !(value instanceof Date)) {
    return {
      start: pickFirstValue(
        value.startDate,
        value.dateStart,
        value.date_start,
        value.start,
        value.from,
        value.since,
        value.flightStart,
        value.flightStartDate,
        value.campaignStartDate
      ),
      end: pickFirstValue(
        value.endDate,
        value.dateEnd,
        value.date_end,
        value.end,
        value.to,
        value.until,
        value.flightEnd,
        value.flightEndDate,
        value.campaignEndDate
      ),
    };
  }

  const raw = String(value || "").trim();
  const parts = raw.split(/\s*(?:->|→|\bto\b)\s*/i).filter(Boolean);
  return {
    start: parts[0] || raw,
    end: parts.length > 1 ? parts[1] : "",
  };
};

const formatDateLabel = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "TBD";
  const formatted = formatFlightDate(raw);
  return formatted === "—" ? raw : formatted;
};

const resolveFlightWindowDates = (campaign) => {
  const directStart = pickFirstValue(
    campaign?.startDate,
    campaign?.dateStart,
    campaign?.date_start,
    campaign?.flightStartDate,
    campaign?.flight_start_date,
    campaign?.flightStart,
    campaign?.campaignStartDate
  );
  const directEnd = pickFirstValue(
    campaign?.endDate,
    campaign?.dateEnd,
    campaign?.date_end,
    campaign?.flightEndDate,
    campaign?.flight_end_date,
    campaign?.flightEnd,
    campaign?.campaignEndDate
  );
  if (directStart || directEnd) return { start: directStart, end: directEnd };

  const rangeSources = [
    campaign?.flightWindow,
    campaign?.flight_window,
    campaign?.timeframe,
    campaign?.timeFrame,
    campaign?.dateRange,
    campaign?.date_range,
  ];

  for (const source of rangeSources) {
    const range = extractFlightDatePair(source);
    if (range.start || range.end) return range;
  }

  return { start: "", end: "" };
};

const formatFlightWindow = (campaign) => {
  const { start, end } = resolveFlightWindowDates(campaign);
  if (!start && !end) return "Flight window pending";
  if (!start) return `Through ${formatDateLabel(end)}`;
  if (!end) return `Starting ${formatDateLabel(start)}`;
  return `${formatDateLabel(start)} → ${formatDateLabel(end)}`;
};

// Entertainment-only Release Date facts row. The release date is an
// independent campaign attribute — never inferred from flight dates, titles,
// or campaign years. Only strict calendar-date values (a valid YYYY-MM-DD
// prefix) are accepted, so the same stored value renders as the same calendar
// date in every viewer timezone and malformed input renders nothing.
export const extractReleaseDateOnly = (value) => {
  if (value == null) return "";
  const match = String(value)
    .trim()
    .match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s]|$)/);
  if (!match) return "";
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const roundTrip = new Date(Date.UTC(year, month - 1, day, 12));
  if (
    roundTrip.getUTCFullYear() !== year ||
    roundTrip.getUTCMonth() !== month - 1 ||
    roundTrip.getUTCDate() !== day
  ) {
    return "";
  }
  return `${match[1]}-${match[2]}-${match[3]}`;
};

export const resolveCampaignReleaseDate = (campaign) => {
  const strategyPayload = campaign?.strategyPayload || campaign?.strategy_payload || {};
  const candidates = [
    campaign?.releaseDate,
    campaign?.release_date,
    strategyPayload?.releaseDate,
    strategyPayload?.release_date,
    strategyPayload?.formData?.releaseDate,
    strategyPayload?.formData?.release_date,
    strategyPayload?.plannerDraft?.formData?.releaseDate,
    strategyPayload?.plannerDraft?.formData?.release_date,
    strategyPayload?.plannerFormData?.releaseDate,
    strategyPayload?.plannerFormData?.release_date,
    campaign?.releaseMilestoneSchedule?.releaseDate,
    campaign?.filmIntelligence?.hero?.releaseDate,
  ];
  for (const candidate of candidates) {
    const dateOnly = extractReleaseDateOnly(candidate);
    if (dateOnly) return dateOnly;
  }
  return "";
};

export const formatCampaignReleaseDateLabel = (campaign) => {
  const releaseDate = resolveCampaignReleaseDate(campaign);
  if (!releaseDate) return "";
  const formatted = formatFlightDate(releaseDate);
  return formatted === "—" ? "" : formatted;
};

const resolveMicrositeFlightDays = (campaign) => {
  const { start, end } = resolveFlightWindowDates(campaign);
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (!Number.isFinite(startDate.getTime()) || !Number.isFinite(endDate.getTime())) return null;
  const days = Math.floor((endDate.getTime() - startDate.getTime()) / 86400000) + 1;
  return days > 0 ? days : null;
};

export const buildMicrositeFinancialContext = (campaign = {}, budgetSection = null) => {
  const grossBudget = resolveMicrositeCampaignBudget(campaign, budgetSection);
  const flightDays = resolveMicrositeFlightDays(campaign);
  const pacingBudget = grossBudget || null;
  const targetDailySpend =
    Number.isFinite(pacingBudget) && flightDays ? pacingBudget / flightDays : null;

  return {
    authorizedBudget: Number.isFinite(grossBudget) ? formatCurrency(grossBudget) : "Not set",
    grossAuthorizedBudget: Number.isFinite(grossBudget) ? formatCurrency(grossBudget) : "Not set",
    pacingBasis: "Gross Plan Budget",
    financialBasis: "Gross Authorized Budget Only",
    targetDailySpend: Number.isFinite(targetDailySpend) ? formatCurrency(targetDailySpend) : "Not set",
    flightDays,
  };
};

const sentenceCase = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  return text
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .replace(/\b[a-z]/g, (match) => match.toUpperCase());
};

const MICROSITE_TEXT_VALUE_KEYS = [
  "headline",
  "body",
  "text",
  "primaryText",
  "message",
  "label",
  "title",
  "name",
  "summary",
  "description",
  "content",
  "value",
  "copy",
  "hook",
  "angle",
  "audienceAngle",
  "rationale",
  "recommendation",
  "strategy",
  "approach",
  "direction",
  "cta",
  "callToAction",
  "supportingCopy",
  "notes",
];

const isInvalidDisplayText = (value) =>
  /^(?:\[object (?:object|promise)\]|undefined|null|nan)$/i.test(String(value || "").trim());

const INVALID_DISPLAY_OBJECT_TOKEN_PATTERN = /\[object\s+(?:object|promise)\]/gi;

const sanitizeDisplayObjectTokens = (value) =>
  String(value || "")
    .replace(INVALID_DISPLAY_OBJECT_TOKEN_PATTERN, "the relevant signal")
    .replace(/\s+([,.!?;:])/g, "$1")
    .replace(/\s{2,}/g, " ")
    .trim();

export const formatMicrositeTextValue = (value, fallback = "Not set") => {
  if (value === undefined || value === null) return fallback;
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : fallback;
  if (typeof value === "string") {
    const raw = value.trim();
    if (!raw || isInvalidDisplayText(raw)) return fallback;
    const text = sanitizeDisplayObjectTokens(raw);
    return text || fallback;
  }
  if (Array.isArray(value)) {
    const seen = new Set();
    const parts = value
      .map((item) => formatMicrositeTextValue(item, ""))
      .filter(Boolean)
      .filter((item) => {
        const key = normalizeMicrositeKey(item);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    return parts.length ? parts.join(" • ") : fallback;
  }
  if (typeof value === "object") {
    const keyedValues = MICROSITE_TEXT_VALUE_KEYS
      .map((key) => formatMicrositeTextValue(value[key], ""))
      .filter(Boolean);
    const seen = new Set();
    const uniqueValues = keyedValues.filter((item) => {
      const key = normalizeMicrositeKey(item);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    if (uniqueValues.length >= 2 && (value.approach || value.rationale)) {
      return uniqueValues.slice(0, 2).join(" - ");
    }
    if (uniqueValues.length) return uniqueValues.slice(0, 3).join(" • ");

    const fallbackValues = Object.entries(value)
      .filter(([key]) => !MICROSITE_TEXT_VALUE_KEYS.includes(key))
      .map(([, nestedValue]) => formatMicrositeTextValue(nestedValue, ""))
      .filter(Boolean);
    const uniqueFallbackValues = fallbackValues.filter((item) => {
      const key = normalizeMicrositeKey(item);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    if (uniqueFallbackValues.length) return uniqueFallbackValues.slice(0, 3).join(" • ");
  }
  return fallback;
};

const parseMicrositePercentValue = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const match = String(value || "")
    .replace(/,/g, "")
    .match(/-?\d+(?:\.\d+)?/);
  if (!match) return null;
  const parsed = Number(match[0]);
  return Number.isFinite(parsed) ? parsed : null;
};

export const normalizeFlightRhythmChartItems = (items = []) => {
  const normalizedItems = (Array.isArray(items) ? items : [])
    .map((item, index) => {
      const value = [
        item?.value,
        item?.share,
        item?.allocation,
        item?.percent,
        item?.allocationLabel,
        item?.primaryValue,
      ].reduce((resolved, candidate) => {
        if (resolved !== null) return resolved;
        return parseMicrositePercentValue(candidate);
      }, null);

      if (!Number.isFinite(value)) return null;

      const label = formatMicrositeTextValue(
        item?.label || item?.title || item?.phase,
        `Flight Phase ${index + 1}`
      );

      return {
        key: item?.key || normalizeMicrositeKey(label) || `flight-rhythm-${index}`,
        label,
        shortLabel: label.replace(/\s+window$/i, ""),
        value: Math.max(0, value),
        allocationLabel: item?.allocationLabel || `${Math.round(value)}%`,
        rangeLabel: formatMicrositeTextValue(item?.rangeLabel || item?.range, ""),
      };
    })
    .filter(Boolean);

  const maxValue = Math.max(...normalizedItems.map((item) => item.value), 1);

  return normalizedItems.map((item) => ({
    ...item,
    height: `${Math.max((item.value / maxValue) * 100, 12)}%`,
  }));
};

export const pickMicrositeTextValue = (fallback = "Not set", ...values) => {
  for (const value of values) {
    const text = formatMicrositeTextValue(value, "");
    if (text) return text;
  }
  return fallback;
};

export const normalizeAudienceLabel = (value = "") =>
  String(value)
    .trim()
    .replace(/\s+/g, " ")
    .replace(/[.,:;]+$/u, "")
    .trim();

// A section badge counts what the section shows, in the client's words.
// The saved primary KPI may be a whole sentence ("In-market unique reach,
// supported by CPM and frequency. Secondary KPIs: …"). Chips and card footers
// show its lead clause; the Measurement section still shows it in full.
export const formatMicrositeCountLabel = (count, singular, plural = `${singular}s`) => {
  const numeric = Number(count);
  if (!Number.isFinite(numeric)) return "";
  return `${numeric} ${numeric === 1 ? singular : plural}`;
};

// The weight chart's values are the cards' shares, shown the same way.
const formatAudienceWeightChartValue = (item) => formatPlanningWeight(item?.value ?? item?.share);

export const normalizeMicrositeDisplayTerminalPunctuation = (value) => {
  const text = String(value ?? "")
    .trim()
    .replace(/\s+([.!?])\s*$/u, "$1");
  if (!text) return "";

  return text.replace(/(?:\s*(?:[.!?]|\u2026))+$/u, (ending) => {
    const compact = ending.replace(/\s+/g, "");
    const emphaticMarks = compact.match(/[!?]/g);
    if (emphaticMarks?.length) return emphaticMarks[emphaticMarks.length - 1];
    if (compact.includes("\u2026") || /\.{3,}/u.test(compact)) return "...";
    return ".";
  });
};

export const appendMicrositeDisplayTerminalPunctuation = (value, terminal = ".") => {
  const text = normalizeMicrositeDisplayTerminalPunctuation(value);
  if (!text) return "";
  return /(?:[.!?\u2026]|\.\.\.)$/u.test(text) ? text : `${text}${terminal}`;
};

const CONNECTED_SOURCE_SUFFIX_PATTERN =
  /\s*(?:[-\u2013\u2014|])\s*(?:wikipedia|rotten\s+tomatoes|imdb|official\s+(?:website|site))\b/gi;
const MICROSITE_RELEASE_YEAR_SUFFIX_PATTERN = /\s*\(\s*(?:19|20)\d{2}\s*\)\s*$/u;

export const cleanMicrositeNarrativeTitle = (value = "") =>
  String(value || "").replace(CONNECTED_SOURCE_SUFFIX_PATTERN, "").replace(/\s+/g, " ").trim();

export const formatMicrositeNarrativeCopy = (value, fallback = "") => {
  const text = formatMicrositeTextValue(value, fallback);
  return cleanMicrositeNarrativeTitle(text);
};

export const cleanMicrositeAdvertiserName = (value = "") =>
  cleanMicrositeNarrativeTitle(value)
    .replace(MICROSITE_RELEASE_YEAR_SUFFIX_PATTERN, "")
    .replace(/\s+/g, " ")
    .trim();

const escapeMicrositeRegExp = (value = "") =>
  String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const resolveMicrositeNarrativeAdvertiserName = (campaign = {}) => {
  const safeCampaign = campaign && typeof campaign === "object" ? campaign : {};
  return cleanMicrositeAdvertiserName(
    pickMicrositeTextValue(
      "",
      safeCampaign.advertiserName,
      safeCampaign.advertiser?.name,
      safeCampaign.advertiser?.advertiser,
      safeCampaign.advertiser,
      safeCampaign.advertiserDisplayName,
      safeCampaign.campaignAdvertiser?.name,
      safeCampaign.campaignAdvertiser?.advertiser,
      safeCampaign.name
    )
  );
};

export const formatMicrositeAdvertiserNarrativeCopy = (
  value,
  advertiserName = "",
  rawTitleValues = [],
  fallback = ""
) => {
  const cleanAdvertiserName = cleanMicrositeAdvertiserName(advertiserName);
  let text = formatMicrositeNarrativeCopy(value, fallback);

  if (!text) return "";

  if (cleanAdvertiserName) {
    const candidates = [rawTitleValues]
      .flat(Infinity)
      .map((candidate) => formatMicrositeTextValue(candidate, ""))
      .map((candidate) => candidate.trim())
      .filter(Boolean)
      .flatMap((candidate) => [
        candidate,
        cleanMicrositeNarrativeTitle(candidate),
        cleanMicrositeAdvertiserName(candidate),
      ])
      .filter(Boolean);

    const uniqueCandidates = Array.from(new Set(candidates)).sort(
      (a, b) => b.length - a.length
    );

    uniqueCandidates.forEach((candidate) => {
      const candidateKey = normalizeMicrositeKey(cleanMicrositeAdvertiserName(candidate));
      if (
        candidateKey &&
        candidateKey === normalizeMicrositeKey(cleanAdvertiserName) &&
        candidate !== cleanAdvertiserName
      ) {
        text = text.replace(new RegExp(escapeMicrositeRegExp(candidate), "gi"), cleanAdvertiserName);
      }
    });

    text = text.replace(
      /([A-Za-z0-9][A-Za-z0-9'’&.-]*(?:\s+[A-Za-z0-9][A-Za-z0-9'’&.-]*){0,8})\s*\(\s*(?:19|20)\d{2}\s*\)/g,
      (match, title) =>
        normalizeMicrositeKey(title) === normalizeMicrositeKey(cleanAdvertiserName)
          ? cleanAdvertiserName
          : match
    );
  }

  return text.replace(/\s*\|\s*/g, " — ").replace(/\s+/g, " ").trim();
};

const pickDeclaredCampaignStrategyAdvertiserVertical = (campaign = {}) =>
  pickFirstValue(
    campaign?.advertiserVertical,
    campaign?.advertiser_vertical,
    campaign?.vertical,
    campaign?.advertiser?.vertical,
    campaign?.advertiserProfile?.vertical,
    campaign?.campaignAdvertiser?.vertical,
    campaign?.account?.vertical,
    campaign?.strategyPayload?.advertiserVertical,
    campaign?.strategyPayload?.vertical,
    campaign?.strategy_payload?.advertiserVertical,
    campaign?.strategy_payload?.vertical,
    campaign?.microsite?.advertiserVertical,
    campaign?.microsite?.vertical
  );

export const resolveCampaignStrategyAdvertiserVertical = (campaign = {}) =>
  normalizeAdvertiserVertical(pickDeclaredCampaignStrategyAdvertiserVertical(campaign));

/**
 * Does this campaign declare a category at all?
 *
 * Diagnostic only. The resolver already fails closed — an undeclared category
 * resolves to "general", which is the NEUTRAL taxonomy and is not entertainment
 * eligible — so nothing about authority or output depends on this. It exists so
 * that a categorized render path silently missing its vertical is visible
 * instead of quietly presenting as General.
 */
export const campaignStrategyAdvertiserVerticalIsDeclared = (campaign = {}) =>
  pickDeclaredCampaignStrategyAdvertiserVertical(campaign) != null &&
  String(pickDeclaredCampaignStrategyAdvertiserVertical(campaign)).trim() !== "";


const CAMPAIGN_STRATEGY_PUBLIC_INTERNAL_ECONOMICS_PATTERN =
  /\b(?:internal\s+)?(?:net\s+media(?:\s+(?:budget|after\s+margin|investment|allocation))?|target\s+margin|margin\s*%|margin\s+(?:applied|rate|percent|percentage)|net\s+figures?|net\s+values?|actual\s+media\s+investment)\b/i;

const isCampaignStrategyInternalEconomicsText = (value) =>
  CAMPAIGN_STRATEGY_PUBLIC_INTERNAL_ECONOMICS_PATTERN.test(String(value || ""));

/*
 * Prose is redacted by SENTENCE, structured pipe rows by segment. Removing the
 * whole line deleted campaign 53's entire "Assumptions / Watchouts" body for
 * one internal sentence and left its heading rendering alone — the "clipped"
 * strategy panel. See utils/strategyNarrativeSentenceRedaction.
 */
export const sanitizeCampaignStrategyPublicText = (value) =>
  dropHeadingsEmptiedByRedaction(
    String(value || "")
      .replace(/\r\n/g, "\n")
      .replace(/\r/g, "\n")
      .split("\n")
      .map((line) => {
        const text = String(line || "").trim();
        if (!text) return { text: "", removed: false };
        if (text.includes("|")) {
          const kept = text
            .split(/\s*\|\s*/)
            .filter((segment) => !isCampaignStrategyInternalEconomicsText(segment))
            .join(" | ")
            .trim();
          return { text: kept, removed: !kept };
        }
        const redacted = redactNarrativeLineBySentence(text, isCampaignStrategyInternalEconomicsText);
        return { text: redacted.text, removed: redacted.changed && !redacted.text };
      })
  )
    .map((entry) => entry.text)
    .filter(Boolean)
    .join("\n")
    .trim();

// Current governed narrative is already reconciled by the backend. The
// legacy allocator rescales recognized percentages and adds template roles;
// it must never reinterpret that authoritative display copy. Unstamped
// historical artifacts keep their existing presentation behavior.
export const resolveMicrositeNarrativeDisplayText = (strategy = {}, { generalTemplate = false } = {}) => {
  const text = sanitizeCampaignStrategyPublicText(strategy?.text || "");
  return strategy?.narrativePresentation === "GOVERNED_SERVE_TIME" || generalTemplate
    ? text
    : normalizeSherlocBudgetAllocationText(text);
};

export const MicrositeStrategyNarrative = ({ strategy, generalTemplate = false }) => (
  <CphereAnalysisWriteup
    text={resolveMicrositeNarrativeDisplayText(strategy, { generalTemplate })}
    prefix="smp-analysis"
    emptyMessage="No strategy has been published for this campaign yet."
  />
);

// ownWordGuard (utils/campaignOwnWords) keeps the campaign's own saved values
// out of the vertical wording; the public-text pass still reads everything.
const sanitizeCampaignStrategyCopy = (value, advertiserVertical, ownWordGuard = null) =>
  sanitizeCampaignStrategyPublicText(
    transformKeepingOwnWords(value, ownWordGuard, (text) =>
      sanitizeVerticalIntelligenceCopy(text, advertiserVertical)
    )
  );

/* ---------------------------------------------------------------------------
 * The artifact presentation gate: client-facing copy and content features.
 *
 * DJ's decision (2026-10-01): Campaign 53's page stays exactly as Kevin saw
 * it. The client-facing copy of the 2026-10-01 pass and the brand
 * presentation's copy refinements therefore apply only to a
 * STAMPED artifact — a payload whose campaign carries the backend's
 * presentation contract (campaign_microsite_presentation_v2 or later). A
 * legacy payload carries no contract: every copy site reads its legacy string
 * while renderer styles are selected independently below (CampaignStrategyMicrositePresentationContract.test.js
 * pins the controls; CampaignStrategyMicrositeBrandPresentation.test.js pins
 * the stamped page). Sentence typography is a separate capability, below.
 * ------------------------------------------------------------------------- */
export const MICROSITE_BRAND_PRESENTATION_CLASS = "campaign-strategy-microsite-page--brand-hierarchy";

export const isMicrositeBrandPresentation = (campaign) =>
  typeof campaign?.presentationContract?.version === "string" &&
  campaign.presentationContract.version.trim().length > 0;

export const MicrositeBrandPresentationContext = React.createContext(false);
const useMicrositeBrandPresentation = () => useContext(MicrositeBrandPresentationContext);

/* ---------------------------------------------------------------------------
 * Presentation capability: semantic typography (semanticTypographyV1).
 *
 * Every sentence in the body face (Oxanium), and a sentence-length KPI value
 * set as a sentence beside its UI-face label. Every pill in the hero row is a
 * label in the UI face, whatever its length. Every structured Primary KPI slot
 * shows the KPI through formatMicrositePrimaryKpiDisplay
 * (utils/micrositePrimaryKpiDisplay.js), which changes letter case only, and
 * so do the KPIs the hero sentence names; the hero pill reads
 * campaign.primaryKpi, never the title-cased copy saved on the chip. It never
 * changes figures, sections or behaviour, and it is a capability of its own,
 * not a feature of the presentation contract. Every stamped artifact has it. A
 * legacy artifact gets these content/casing rules only through an explicit
 * grant below. The page-root MODIFIERS are a separate, explicit presentation
 * profile (resolveMicrositePresentationProfile, below): the capability decides
 * the markup and casing, the profile decides which stylesheet rules dress it,
 * and neither is inferred from the other.
 *
 * The page-root modifier .campaign-strategy-microsite-page--semantic-typography
 * scopes the sentence and hero-pill rules of CampaignStrategyMicrosite.css. The
 * label and title refinements stay under the brand presentation's own
 * modifier.
 * CampaignStrategyMicrositeSemanticTypography.test.js pins the capability and
 * its grants; CampaignStrategyMicrositePresentationContract.test.js pins that
 * a granted legacy page differs from its frozen control in typography and in
 * the enumerated KPI letter case only.
 * ------------------------------------------------------------------------- */
export const MICROSITE_SEMANTIC_TYPOGRAPHY = "semanticTypographyV1";
export const MICROSITE_SEMANTIC_TYPOGRAPHY_CLASS = "campaign-strategy-microsite-page--semantic-typography";

// One entry per legacy artifact, named by campaign, advertiser and public slug.
export const MICROSITE_PRESENTATION_CAPABILITY_GRANTS = Object.freeze([
  // The Action Bible · Christmas CTV 2026 (Kevin's page), requested 2026-10-01:
  // the sentences move to the body face; its figures and sections stay exactly
  // as frozen. The capability's hero-pill and Primary KPI rules (requested
  // 2026-10-02) set its hero pills in the UI face and its KPI as
  // "Incremental household reach; CPM (Awareness)" in every structured slot,
  // and as "… and CPM (Awareness)" in the hero sentence.
  Object.freeze({
    capability: MICROSITE_SEMANTIC_TYPOGRAPHY,
    campaignId: 53,
    advertiserId: 29,
    publicSlug: "the-action-bible-christmas-ctv-2026",
  }),
]);

export const micrositeCapabilityGranted = (capability, campaign) =>
  MICROSITE_PRESENTATION_CAPABILITY_GRANTS.some(
    (grant) =>
      grant.capability === capability &&
      Number(campaign?.id) === grant.campaignId &&
      Number(campaign?.advertiserId) === grant.advertiserId &&
      String(campaign?.publicSlug || "") === grant.publicSlug
  );

export const resolveMicrositeSemanticTypography = (campaign) =>
  isMicrositeBrandPresentation(campaign) || micrositeCapabilityGranted(MICROSITE_SEMANTIC_TYPOGRAPHY, campaign);

/* ---------------------------------------------------------------------------
 * The presentation profile: which page-root modifiers select the stylesheet's
 * three-face hierarchy (CampaignStrategyMicrosite.css, "Brand type
 * hierarchy"). CSS only: the brand copy, the KPI casing, the sentence slots,
 * the content features, governance and access read the two contexts above,
 * which keep the artifact's own flags (isMicrositeBrandPresentation,
 * resolveMicrositeSemanticTypography). Never feed a profile into either
 * presentation context, and never read approval or access state here.
 *
 * A profile is selected explicitly, never inferred (Task 3 review,
 * 2026-10-12):
 *   - a STAMPED artifact (a presentation contract) keeps its current approved
 *     presentation, both modifiers;
 *   - a legacy artifact takes the profile listed below for its POSITIVELY
 *     ESTABLISHED identity: campaign id, advertiser id and public slug, all
 *     three present and matching one entry;
 *   - everything else keeps the legacy presentation, no modifier: a page that
 *     is still loading, a body whose identity is incomplete or unlisted, any
 *     other legacy artifact. Neither a capability grant's presence or absence,
 *     nor a loading state, nor approval authority selects a profile.
 * Dark Horse (campaign 13; DJ, 2026-10-08) is listed for the shared
 * three-face design; Campaign 53 is listed for the sentence layer alone, the
 * profile it was reviewed and frozen with (its whole page is pinned in
 * CampaignStrategyMicrositePresentationContract.test.js).
 * CampaignStrategyMicrositePresentationProfile.test.js pins this selector.
 * ------------------------------------------------------------------------- */
export const MICROSITE_PRESENTATION_PROFILE = Object.freeze({
  // neither modifier: the 2026-09 design
  LEGACY: "legacy",
  // --semantic-typography alone
  SENTENCE: "sentence",
  // both modifiers: the shared three-face design
  BRAND: "brand",
});

// One entry per legacy artifact, by its full identity.
export const MICROSITE_PRESENTATION_PROFILES = Object.freeze([
  // The Action Bible · Christmas CTV 2026 (Kevin's page): reviewed with the
  // sentence layer alone (2026-10-01/02) and frozen with it.
  Object.freeze({
    profile: MICROSITE_PRESENTATION_PROFILE.SENTENCE,
    campaignId: 53,
    advertiserId: 29,
    publicSlug: "the-action-bible-christmas-ctv-2026",
  }),
  // Dark Horse · 2026 Theatrical Release: the shared three-face design
  // (DJ, 2026-10-08).
  Object.freeze({
    profile: MICROSITE_PRESENTATION_PROFILE.BRAND,
    campaignId: 13,
    advertiserId: 7,
    publicSlug: "dark-horse-2026-theatrical-release",
  }),
]);

// An identity field is read only from the two representations the served
// artifact uses: a positive integer number, or the same integer as a plain
// decimal string (digits only, no sign, no leading zero, surrounding
// whitespace allowed). Anything else — an array, an object, a boolean, a
// fraction, an empty or non-decimal string — reads as absent. The primitive
// type is checked BEFORE any conversion: Number([13]) would be 13.
const MICROSITE_IDENTITY_INTEGER_STRING = /^[1-9]\d*$/;
export const readMicrositeIdentityInteger = (value) => {
  if (typeof value === "number") return Number.isInteger(value) && value > 0 ? value : null;
  if (typeof value === "string") {
    const digits = value.trim();
    return MICROSITE_IDENTITY_INTEGER_STRING.test(digits) && Number.isSafeInteger(Number(digits)) ? Number(digits) : null;
  }
  return null;
};

// The artifact's identity, or null unless its campaign id, advertiser id and
// public slug are all present and well-formed: nothing is inferred from a
// partial or malformed identity.
export const readMicrositeArtifactIdentity = (campaign) => {
  if (!campaign || typeof campaign !== "object" || Array.isArray(campaign)) return null;
  const campaignId = readMicrositeIdentityInteger(campaign.id);
  const advertiserId = readMicrositeIdentityInteger(campaign.advertiserId);
  const publicSlug = typeof campaign.publicSlug === "string" ? campaign.publicSlug.trim() : "";
  if (campaignId === null || advertiserId === null || !publicSlug) return null;
  return { campaignId, advertiserId, publicSlug };
};

export const resolveMicrositePresentationProfile = (campaign) => {
  if (isMicrositeBrandPresentation(campaign)) return MICROSITE_PRESENTATION_PROFILE.BRAND;
  const identity = readMicrositeArtifactIdentity(campaign);
  if (!identity) return MICROSITE_PRESENTATION_PROFILE.LEGACY;
  const listed = MICROSITE_PRESENTATION_PROFILES.find(
    (entry) =>
      entry.campaignId === identity.campaignId &&
      entry.advertiserId === identity.advertiserId &&
      entry.publicSlug === identity.publicSlug
  );
  return listed ? listed.profile : MICROSITE_PRESENTATION_PROFILE.LEGACY;
};

// The page root's modifiers for the profile; its only consumer is the root
// className in the page component.
export const resolveMicrositeRendererPresentation = (campaign) => {
  const profile = resolveMicrositePresentationProfile(campaign);
  return {
    semanticTypography: profile !== MICROSITE_PRESENTATION_PROFILE.LEGACY,
    brandPresentation: profile === MICROSITE_PRESENTATION_PROFILE.BRAND,
  };
};

export const MicrositeSemanticTypographyContext = React.createContext(false);
const useMicrositeSemanticTypography = () => useContext(MicrositeSemanticTypographyContext);

export const micrositePageClassName = ({ brandPresentation = false, semanticTypography = false } = {}) =>
  ["campaign-strategy-microsite-page", semanticTypography && MICROSITE_SEMANTIC_TYPOGRAPHY_CLASS, brandPresentation && MICROSITE_BRAND_PRESENTATION_CLASS]
    .filter(Boolean)
    .join(" ");

export const MICROSITE_PRESENTATION_COPY = Object.freeze({
  legacy: Object.freeze({
    headerDescription: "A current governed Cphere strategy presentation with campaign intelligence, platform architecture, creative systems, and chart-ready execution layers.",
    strategyLogicFallback: "Sherloc sequences the campaign across awareness, engagement, and conversion roles. High-attention video channels build discovery, social platforms create audience response and retargeting pools, and efficient display/CTV layers sustain reach while preserving conversion pressure through the flight window.",
    measurementSummaryGeneral: null,
    organicLiftMeta: "Organic lift system",
    chartEmpty: "Structured chart data has not been published for this section yet.",
    flightRhythmMeta: "Mini Chart",
    flowStagesEmpty: "Retargeting flow stages will appear once the strategy contract is present.",
    retargetingChartEmpty: "Retargeting performance chart data will appear once audience windows are published.",
    retargetingChartTitleFallback: "Retargeting Performance — CTR by Time Window After First Interaction",
    earnedOpportunityPlaceholder: "Add cast list, director, official site, IMDb/TMDb metadata, or an uploaded media brief to generate earned-media opportunity rows.",
    earnedSourceFallback: "Discovery Source",
    executionEmpty: "Execution blueprint cards will appear once platform campaign objects are published.",
    adSetEmpty: "Activation-ready ad set objects will appear once setup details are available.",
    adSetIntro: "These setup objects are display-only today. They are structured for future Generate Setup, Review Label, Approve, Push to Platform, Sync Status, and View Evidence actions without triggering live platform writes.",
    objectivesMeta: "Web-native strategy layer",
    audienceAwaitingMeta: "Audience strategy layer",
    platformStrategyIntro: "Each media platform is assigned a distinct job inside the campaign system so reach, persuasion, and conversion pressure are sequenced deliberately rather than duplicated.",
    platformRoleFallback: "Platform role will populate once the platform contract is enriched.",
    activationNotesFallback: "Platform-ready once creative, trafficking, and tracking align with the approved plan.",
    activationReadinessMeta: "MCP Campaign Actions readiness",
    reportingEmpty: "Reporting logic is waiting on a richer campaign contract.",
    optimizationEmpty: "Optimization cadence will appear once the strategy contract is enriched.",
    benchmarksMeta: "Deck intelligence",
    narrativeTitle: "Sherloc Narrative Output",
    vertical: Object.freeze({
      film: Object.freeze({ whyIntro: "This hosted strategy page translates Sherloc planner output into a theatrical marketing system with platform roles, audience priority, pacing logic, and launch-readiness framing.", emptyStrategy: "Strategic framing will populate once the campaign contract is enriched with objective, audience, and film-reference context." }),
      automotive: Object.freeze({ whyIntro: "This hosted strategy page translates Sherloc planner output into a vehicle consideration system with platform roles, audience priority, pacing logic, and dealer-market readiness framing.", emptyStrategy: "Strategic framing will populate once the campaign contract is enriched with objective, audience, and automotive planning context." }),
      retail: Object.freeze({ whyIntro: "This hosted strategy page translates Sherloc planner output into a retail and e-commerce strategy with platform roles, audience priority, pacing logic, and offer-readiness framing.", emptyStrategy: "Strategic framing will populate once the campaign contract is enriched with objective, audience, and retail planning context." }),
      travel_hospitality: Object.freeze({ whyIntro: "This hosted strategy page translates Sherloc planner output into a travel and hospitality strategy with platform roles, audience priority, pacing logic, and destination-readiness framing.", emptyStrategy: "Strategic framing will populate once the campaign contract is enriched with objective, audience, and travel planning context." }),
      general: Object.freeze({ whyIntro: "This hosted strategy page translates Sherloc planner output into a clear cross-platform strategy with platform roles, audience priority, pacing logic, and launch-readiness framing.", emptyStrategy: "Strategic framing will populate once the campaign contract is enriched with objective, audience, and planning context." }),
    }),
    // The stage count reads as a count ("3 stages"; Dark Horse, 2026-10-12, read
    // "3 STAGE(S)"). The other legacy badges keep their frozen wording:
    // Campaign 53's page shows several of them and must not change.
    countBadge: (count, noun) =>
      noun === "stage"
        ? formatMicrositeCountLabel(count, noun)
        : `${count} ${{"platform benchmark":"benchmark line(s)","platform plan":"platform plan(s)","platform setup":"platform object(s)","message pillar":"message pillar(s)","launch plan":"module(s)","KPI":"KPI anchor(s)","strategy section":"structured block(s)"}[noun] || `${noun}(s)`}`,
  }),
  brand: Object.freeze({
    headerDescription: "The current Cphere strategy presentation for this campaign: campaign intelligence, platform architecture, creative direction, and execution planning.",
    strategyLogicFallback: "The plan sequences the campaign across awareness, engagement, and conversion roles. High-attention video channels build discovery, social platforms create audience response and retargeting pools, and efficient display and CTV layers sustain reach and support conversion through the flight window.",
    measurementSummaryGeneral: "Measurement should stay cross-platform and outcome aware, reading audience response, platform contribution, efficiency, and qualified traffic together.",
    organicLiftMeta: "Organic lift",
    chartEmpty: "Chart data will appear once this section has been prepared.",
    flightRhythmMeta: "Weekly rhythm",
    flowStagesEmpty: "Retargeting stages will appear once the strategy is saved.",
    retargetingChartEmpty: "The retargeting response pattern will appear once audience windows are defined.",
    retargetingChartTitleFallback: "Retargeting Response Pattern — Click-Through by Time Window",
    earnedOpportunityPlaceholder: "Add cast list, director, official site, IMDb/TMDb metadata, or an uploaded media brief to build earned-media opportunities.",
    earnedSourceFallback: "Pending",
    executionEmpty: "Execution details will appear once the platform campaigns are set up.",
    adSetEmpty: "Ad set details will appear once campaign setup is available.",
    adSetIntro: "These setup objects are a preview: they show how the approved plan would be built on each platform. Nothing on this page writes to a platform; setup, review, and activation stay separate steps.",
    objectivesMeta: "Campaign objectives",
    audienceAwaitingMeta: "Awaiting planner inputs",
    platformStrategyIntro: "Each media platform is assigned a distinct job so reach, persuasion, and conversion are sequenced deliberately rather than duplicated.",
    platformRoleFallback: "The platform's role will appear once its plan is saved.",
    activationNotesFallback: "Ready to launch once creative, trafficking, and tracking are aligned with the plan.",
    activationReadinessMeta: "Activation readiness",
    reportingEmpty: "Reporting details will appear once the campaign plan is complete.",
    optimizationEmpty: "The optimization cadence will appear once the strategy is complete.",
    benchmarksMeta: "Cphere program benchmarks",
    narrativeTitle: "Strategy Narrative",
    vertical: Object.freeze({
      film: Object.freeze({ whyIntro: "This strategy page turns the campaign plan into a theatrical marketing system: platform roles, audience priority, pacing logic, and launch readiness.", emptyStrategy: "Strategic framing will appear once the campaign's objective, audience, and film reference are saved." }),
      automotive: Object.freeze({ whyIntro: "This strategy page turns the campaign plan into a vehicle consideration system: platform roles, audience priority, pacing logic, and dealer-market readiness.", emptyStrategy: "Strategic framing will appear once the campaign's objective, audience, and automotive planning context are saved." }),
      retail: Object.freeze({ whyIntro: "This strategy page turns the campaign plan into a retail and e-commerce strategy: platform roles, audience priority, pacing logic, and offer readiness.", emptyStrategy: "Strategic framing will appear once the campaign's objective, audience, and retail planning context are saved." }),
      travel_hospitality: Object.freeze({ whyIntro: "This strategy page turns the campaign plan into a travel and hospitality strategy: platform roles, audience priority, pacing logic, and destination readiness.", emptyStrategy: "Strategic framing will appear once the campaign's objective, audience, and travel planning context are saved." }),
      general: Object.freeze({ whyIntro: "This strategy page turns the campaign plan into a clear cross-platform strategy: platform roles, audience priority, pacing logic, and launch readiness.", emptyStrategy: "Strategic framing will appear once the campaign's objective, audience, and planning context are saved." }),
    }),
    countBadge: (count, noun) => formatMicrositeCountLabel(count, noun),
  }),
});

export const selectMicrositeCopy = (brandPresentation) =>
  brandPresentation ? MICROSITE_PRESENTATION_COPY.brand : MICROSITE_PRESENTATION_COPY.legacy;

// A saved KPI that is a sentence or a multi-clause explanation ("In-market
// unique reach, supported by CPM and frequency. Secondary KPIs: …") reads in
// the body face, in full and in its own case, in the page's KPI value slots; a
// short KPI ("Reach") stays a UI-face value. It never chooses a face in the
// hero: a hero pill is a label whatever its length, and the summary cards
// decide their own face.
export const isSentenceLikeKpi = (value) => {
  const text = formatMicrositeTextValue(value, "");
  if (!text) return false;
  return /[.;!?]\s/.test(text) || /\bsecondary kpis?\b/i.test(text) || text.split(/\s+/).length > 6;
};

// Fit, never face: a hero summary value longer than this takes the compact
// size, because it would wrap at the summary-value size in the narrowest card.
// Measured 2026-10-02 in Oxanium at the summary-value size: every value of up to
// 27 characters (a date range such as "Oct 15, 2026 → Nov 30, 2026", an objective,
// a status in capitals) fits on one line in the narrowest card of every layout,
// 239 px wide against 288 px at a 721 px viewport and 290 px at 360 px; a
// 44-character KPI does not. The summary card's face comes from the stylesheet,
// never from this length.
export const MICROSITE_SUMMARY_VALUE_COMPACT_LENGTH = 28;
export const isCompactSummaryValue = (value) =>
  formatMicrositeTextValue(value, "").length > MICROSITE_SUMMARY_VALUE_COMPACT_LENGTH;

// A KPI value slot. Where semantic typography applies it shows the KPI in its
// display case (formatMicrositePrimaryKpiDisplay) and a sentence-length KPI
// carries the body-face class; elsewhere, the saved words exactly as served.
function MicrositeKpiValue({ value, as: Tag = "span" }) {
  const semanticTypography = useMicrositeSemanticTypography();
  const sentence = semanticTypography && isSentenceLikeKpi(value);
  return (
    <Tag className={sentence ? "campaign-strategy-microsite-kpi-sentence" : undefined}>
      {semanticTypography ? formatMicrositePrimaryKpiDisplay(value) : value}
    </Tag>
  );
}

export const buildVerticalMicrositeFallbacks = (advertiserVertical, { brandPresentation = false, allocationApprovalStatus = null } = {}) => {
  const allocationIntro = (totalBudgetDisplay) =>
    String(allocationApprovalStatus || "").toUpperCase() === "PENDING_APPROVAL"
      ? `Cphere proposes allocating ${totalBudgetDisplay} across the proposed media mix, awaiting allocation approval`
      : `Cphere is allocating ${totalBudgetDisplay} across the approved media system`;
  const vertical = normalizeAdvertiserVertical(advertiserVertical);
  const config = getVerticalIntelligenceConfig(vertical);
  const copy = selectMicrositeCopy(brandPresentation);
  if (vertical === "film") {
    return {
      whyTitle: "Cross-Platform Release Logic",
      whyIntro:
        copy.vertical.film.whyIntro,
      objectiveTitle: "Awareness, Recall, and Local Watch Intent",
      platformIntro: (totalBudgetDisplay) =>
        `${allocationIntro(totalBudgetDisplay)} so each platform supports a distinct theatrical marketing job while the flight phases escalate intelligently.`,
      emptyStrategy:
        copy.vertical.film.emptyStrategy,
      measurementSummary:
        "Measurement should stay cross-platform and outcome aware, reading attention, traffic, and supported local-intent signals together.",
    };
  }
  if (vertical === "automotive") {
    return {
      whyTitle: "Cross-Platform Shopper Logic",
      whyIntro:
        copy.vertical.automotive.whyIntro,
      objectiveTitle: "Awareness, Consideration, and Shopper Response",
      platformIntro: (totalBudgetDisplay) =>
        `${allocationIntro(totalBudgetDisplay)} so each platform supports vehicle consideration, in-market shopper reach, and local-market response without inventing lead or showroom outcomes.`,
      emptyStrategy:
        copy.vertical.automotive.emptyStrategy,
      measurementSummary:
        "Measurement should stay cross-platform and outcome aware, reading traffic efficiency, engagement, and supported lead or site-action signals together.",
    };
  }
  if (vertical === "retail") {
    return {
      whyTitle: "Cross-Platform Commerce Logic",
      whyIntro:
        copy.vertical.retail.whyIntro,
      objectiveTitle: "Awareness, Product Interest, and Conversion Response",
      platformIntro: (totalBudgetDisplay) =>
        `${allocationIntro(totalBudgetDisplay)} so each platform supports product discovery, retargeting depth, and conversion response without inventing purchase or cart outcomes.`,
      emptyStrategy:
        copy.vertical.retail.emptyStrategy,
      measurementSummary:
        "Measurement should stay cross-platform and outcome aware, reading media efficiency, traffic quality, and supported conversion or product signals together.",
    };
  }
  if (vertical === "travel_hospitality") {
    return {
      whyTitle: "Cross-Platform Travel Logic",
      whyIntro:
        copy.vertical.travel_hospitality.whyIntro,
      objectiveTitle: "Awareness, Traveler Intent, and Booking Consideration",
      platformIntro: (totalBudgetDisplay) =>
        `${allocationIntro(totalBudgetDisplay)} so each platform supports destination demand, traveler intent, and booking consideration without inventing bookings or occupancy outcomes.`,
      emptyStrategy:
        copy.vertical.travel_hospitality.emptyStrategy,
      measurementSummary:
        "Measurement should stay cross-platform and outcome aware, reading media efficiency, traffic quality, and supported booking-consideration or destination-demand signals together.",
    };
  }
  return {
    whyTitle: "Cross-Platform Strategy Logic",
    whyIntro:
      copy.vertical.general.whyIntro,
    objectiveTitle: "Awareness, Engagement, and Response",
    platformIntro: (totalBudgetDisplay) =>
      `${allocationIntro(totalBudgetDisplay)} so each platform supports a distinct campaign role while the flight phases stay adaptable.`,
    emptyStrategy:
      copy.vertical.general.emptyStrategy,
    // Legacy pages print the vertical config's narrativeGuidance here (an
    // instruction to the AI writer); the brand presentation prints a sentence
    // written for the client.
    measurementSummary: brandPresentation ? copy.measurementSummaryGeneral : config.narrativeGuidance,
  };
};

// Distinct cross-platform strategy rationale used when no strategy-specific field exists
// (or the only available copy duplicates the hero positioning summary).
const MICROSITE_STRATEGY_LOGIC_FALLBACK_COPY =
  "Sherloc sequences the campaign across awareness, engagement, and conversion roles. High-attention video channels build discovery, social platforms create audience response and retargeting pools, and efficient display/CTV layers sustain reach while preserving conversion pressure through the flight window.";

const normalizeMicrositeIntroComparable = (value) =>
  formatMicrositeTextValue(value, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

// Two section intros are near-duplicates when they are identical after normalization or
// share the same long opening run (the same generated positioning paragraph).
export const micrositeIntrosAreNearDuplicate = (a, b) => {
  const left = normalizeMicrositeIntroComparable(a);
  const right = normalizeMicrositeIntroComparable(b);
  if (!left || !right) return false;
  if (left === right) return true;
  const shorter = left.length <= right.length ? left : right;
  const longer = left.length <= right.length ? right : left;
  const prefixLength = Math.min(shorter.length, 80);
  return prefixLength >= 40 && longer.startsWith(shorter.slice(0, prefixLength));
};

// Resolves the Cross-Platform Strategy Logic intro so it never repeats the hero summary:
// prefers strategy-specific copy, skips any candidate that duplicates the hero, and falls
// back to distinct strategy-logic copy.
export const resolveMicrositeStrategyLogicIntro = ({
  heroSummary = "",
  candidates = [],
  fallback = MICROSITE_STRATEGY_LOGIC_FALLBACK_COPY,
} = {}) => {
  const candidateList = Array.isArray(candidates) ? candidates : [];
  for (const candidate of candidateList) {
    const text = formatMicrositeTextValue(candidate, "").trim();
    if (!text) continue;
    if (micrositeIntrosAreNearDuplicate(text, heroSummary)) continue;
    return text;
  }
  return fallback;
};

// Internal editing-state placeholder for creative pillars. Treated as "missing content"
// for public microsite rendering so it never repeats across cards.
const MICROSITE_CREATIVE_THEME_PLACEHOLDER_COPY =
  "Creative message details will appear once the planner recommendation is saved.";
// Single scoped guidance card shown when no real creative messaging content exists.
const MICROSITE_CREATIVE_THEME_GUIDANCE_COPY =
  "Creative messaging details have not been generated for this plan yet. Save or refresh the planner recommendation to populate narrative hooks, audience persuasion, and conversion messaging.";

const normalizeMicrositeCreativeThemeComparable = (value) =>
  formatMicrositeTextValue(value, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const isMicrositePlaceholderCreativeCopy = (value) => {
  const normalized = normalizeMicrositeCreativeThemeComparable(value);
  if (!normalized) return true;
  return (
    normalized ===
    normalizeMicrositeCreativeThemeComparable(MICROSITE_CREATIVE_THEME_PLACEHOLDER_COPY)
  );
};

// Resolves the creative message pillars for the microsite. Pillars whose body copy is
// missing or matches the internal placeholder are treated as empty; identical bodies are
// collapsed. When no pillar has distinct real content, a single guidance card is returned
// instead of repeating the placeholder across multiple cards.
export const resolveMicrositeCreativeThemeCards = (themes = []) => {
  const list = Array.isArray(themes) ? themes : [];
  const withResolvedCopy = list.map((theme, index) => {
    const copy = pickMicrositeTextValue(
      "",
      theme?.copy,
      theme?.content,
      theme?.description,
      theme?.body,
      theme?.text
    );
    return {
      key: theme?.key || formatMicrositeTextValue(theme?.title, `theme-${index + 1}`),
      title: formatMicrositeTextValue(theme?.title, "Message Pillar"),
      priorityLabel: formatMicrositeTextValue(theme?.priorityLabel, "Theme"),
      copy,
      isGuidance: false,
      hasRealCopy: !isMicrositePlaceholderCreativeCopy(copy),
    };
  });

  const seen = new Set();
  const realCards = withResolvedCopy
    .filter((card) => card.hasRealCopy)
    .filter((card) => {
      const signature = normalizeMicrositeCreativeThemeComparable(card.copy);
      if (seen.has(signature)) return false;
      seen.add(signature);
      return true;
    });

  if (!realCards.length) {
    return [
      {
        key: "creative-messaging-guidance",
        title: "Creative Messaging",
        priorityLabel: "Pending",
        copy: MICROSITE_CREATIVE_THEME_GUIDANCE_COPY,
        isGuidance: true,
        hasRealCopy: false,
      },
    ];
  }

  return realCards;
};

// Titles/priority labels mirror the backend buildCreativeMessagingSection so a
// list of generated creative lines (creativeRecommendations / creativeVariations)
// renders as message pillars identically whether the backend pre-derived them or
// the client derives them from the raw list on an older payload.
const MICROSITE_DERIVED_CREATIVE_THEME_TITLES = [
  "Narrative Hook",
  "Audience Persuasion",
  "Conversion Push",
];
const MICROSITE_CREATIVE_VARIATION_MAX_CARDS = 4;

export const normalizeMicrositeCreativeRecommendationText = (entry) => {
  if (typeof entry === "string") return entry.trim();
  if (entry === undefined || entry === null) return "";
  if (typeof entry !== "object") return formatMicrositeTextValue(entry, "").trim();

  // Client-facing flatten: real ad copy only (headline + primary text + CTA).
  // Internal strategy fields (uniqueDescription / designDirection /
  // audienceAngle / rationale) are intentionally excluded.
  const parts = [
    pickMicrositeTextValue("", entry.headline, entry.title, entry.label),
    pickMicrositeTextValue(
      "",
      entry.primaryText,
      entry.primary_text,
      entry.description,
      entry.body,
      entry.copy,
      entry.text,
      entry.message,
      entry.hook
    ),
    pickMicrositeTextValue("", entry.cta, entry.callToAction, entry.call_to_action),
  ];
  const seen = new Set();
  const uniqueParts = parts.filter((part) => {
    const key = normalizeMicrositeKey(part);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  return uniqueParts.length ? uniqueParts.join(" ") : formatMicrositeTextValue(entry, "").trim();
};

// Builds creative theme objects from raw generated creative strings or objects. Used
// as a fallback source so the Creative Messaging panel reflects real generated
// content even when no pre-built creativeMessaging.themes are present.
export const deriveMicrositeCreativeThemesFromList = (list = []) => {
  const items = (Array.isArray(list) ? list : [])
    .map(normalizeMicrositeCreativeRecommendationText)
    .filter(Boolean);

  return items.slice(0, 3).map((copy, index) => ({
    key: `derived_theme_${index + 1}`,
    title: MICROSITE_DERIVED_CREATIVE_THEME_TITLES[index] || "Message Pillar",
    priorityLabel: index === 0 ? "Primary" : index === 1 ? "Support" : "Conversion",
    copy,
  }));
};

const pickCreativeVariationTextValue = (...values) => {
  for (const value of values) {
    const text = Array.isArray(value)
      ? value.map((item) => formatMicrositeTextValue(item, "")).filter(Boolean).join(", ")
      : formatMicrositeTextValue(value, "");
    if (String(text || "").trim()) return String(text).trim();
  }
  return "";
};

const resolveMicrositeCreativeVariationSourceList = (campaignOrList = {}) => {
  if (Array.isArray(campaignOrList)) return campaignOrList;
  const strategyPayload = campaignOrList?.strategyPayload || campaignOrList?.strategy_payload || {};
  const sources = [
    campaignOrList?.creativeVariations,
    campaignOrList?.creative_variations,
    campaignOrList?.creativeRecommendations,
    campaignOrList?.creative_recommendations,
    strategyPayload?.creativeVariations,
    strategyPayload?.creative_variations,
    strategyPayload?.creativeRecommendations,
    strategyPayload?.creative_recommendations,
  ];
  return sources.find((source) => Array.isArray(source) && source.length) || [];
};

export const normalizeMicrositeCreativeVariationCards = (campaignOrList = {}) => {
  const seen = new Set();
  return resolveMicrositeCreativeVariationSourceList(campaignOrList)
    .map((entry, index) => {
      if (typeof entry !== "object" || entry === null) {
        const headline = formatMicrositeTextValue(entry, "");
        return headline
          ? {
              key: `creative-variation-${index + 1}`,
              title: `Variant ${index + 1}`,
              platform: "",
              headline,
              body: "",
              cta: "",
              visualNotes: "",
            }
          : null;
      }

      const title =
        pickCreativeVariationTextValue(
          entry.variantLabel,
          entry.variant_label,
          entry.variationTitle,
          entry.variation_title,
          entry.variationName,
          entry.variation_name,
          entry.name,
          entry.title,
          entry.label
        ) || `Variant ${index + 1}`;
      // Concise, client-friendly placement note only.
      const platformFit = pickCreativeVariationTextValue(
        entry.platformFit,
        entry.platform_fit
      );
      const platform = pickCreativeVariationTextValue(
        entry.platform,
        entry.channel,
        entry.channels,
        entry.placement,
        entry.platformChannel,
        entry.platform_channel
      );
      const headline = pickCreativeVariationTextValue(
        entry.headline,
        entry.hook,
        entry.primaryHook,
        entry.primary_hook,
        entry.title,
        entry.label
      );
      const body = pickCreativeVariationTextValue(
        entry.primaryText,
        entry.primary_text,
        entry.primaryMessage,
        entry.primary_message,
        entry.body,
        entry.copy,
        entry.description,
        entry.message
      );
      // Short description, only when it is not a duplicate of the primary text.
      let description = pickCreativeVariationTextValue(entry.description, entry.shortDescription);
      if (description && normalizeMicrositeKey(description) === normalizeMicrositeKey(body)) {
        description = "";
      }
      const card = {
        key: entry.key || entry.id || `creative-variation-${index + 1}`,
        title,
        platform,
        platformFit,
        headline,
        body,
        description,
        cta: pickCreativeVariationTextValue(
          entry.cta,
          entry.callToAction,
          entry.call_to_action,
          entry.actionLine,
          entry.action_line,
          entry.action
        ),
      };
      return card.headline || card.body || card.cta ? card : null;
    })
    .filter(Boolean)
    .filter((card) => {
      const signature = normalizeMicrositeKey(
        [card.platform, card.headline, card.body, card.description, card.cta, card.platformFit]
          .filter(Boolean)
          .join(" ")
      );
      if (!signature || seen.has(signature)) return false;
      seen.add(signature);
      return true;
    })
    .slice(0, MICROSITE_CREATIVE_VARIATION_MAX_CARDS);
};

export function CreativeVariationExamples({ cards = [], renderCopy = (value) => value }) {
  const items = Array.isArray(cards) ? cards.filter(Boolean) : [];
  if (!items.length) return null;

  return (
    <div
      className="campaign-strategy-microsite-creative-variation-subsection"
      data-testid="creative-variation-examples"
    >
      <div className="campaign-strategy-microsite-subsection-header">
        <span className="campaign-strategy-microsite-subsection-title">
          Generated Creative Variations
        </span>
        <span className="campaign-strategy-microsite-card-meta">
          {items.length} example{items.length === 1 ? "" : "s"}
        </span>
      </div>
      <div className="campaign-strategy-microsite-creative-variation-grid">
        {items.map((card, index) => (
          <article
            key={card.key || `${card.title}-${index}`}
            className="campaign-strategy-microsite-creative-variation-card"
          >
            <div className="campaign-strategy-microsite-card-header">
              <span className="campaign-strategy-microsite-card-title">
                {card.title || `Variant ${index + 1}`}
              </span>
              {card.platform ? (
                <span className="campaign-strategy-microsite-card-meta">{card.platform}</span>
              ) : null}
            </div>
            <div className="campaign-strategy-microsite-creative-variation-body">
              {[
                ["Headline", card.headline],
                ["Primary Text", card.body],
                ["Description", card.description],
                ["CTA", card.cta],
                ["Platform Fit", card.platformFit],
              ]
                .filter(([, value]) => value)
                .map(([label, value]) => (
                  <p
                    key={`${card.key || card.title}-${label}`}
                    className="campaign-strategy-microsite-creative-variation-row"
                  >
                    <span className="campaign-strategy-microsite-creative-variation-label">
                      {label}
                    </span>
                    <span>{renderCopy(value)}</span>
                  </p>
                ))}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

const buildVerticalPlatformStrategyCards = (
  platformChips = [],
  advertiserVertical,
  allocationRows = []
) => {
  const chips = Array.isArray(platformChips) ? platformChips : [];
  return orderMicrositePlatformRows(chips
    .map((platform) => {
      const identity = resolveCampaignPlatformIdentity(platform);
      if (!identity) return null;
      if (!identity.known) return { key: identity.raw, title: identity.label };
      // Detail-card labels retain selected Meta children; the shared display
      // formatter intentionally groups them on family-level summary surfaces.
      const label = ["facebook", "instagram"].includes(identity.key)
        ? identity.label
        : formatMicrositePlatformDisplayLabel(platform?.label || platform?.name || platform?.key || platform);
      if (!label) return null;
      const key = resolvePlatformBrandKey(platform?.key || platform?.label || platform);
      const platformContext = MICROSITE_PLATFORM_CONTEXT[key] || {};
      const role = getVerticalPlatformRole(advertiserVertical, key || label);
      return {
        key: key || label,
        title: platformContext.label || label,
        role: platformContext.role || role,
        funnelPurpose: platformContext.funnelPurpose || "Campaign role",
        primaryKpi: platformContext.primaryKpi || "Media efficiency",
        targetingLogic:
          platformContext.targetingLogic ||
          "Use approved audience and market inputs from the saved media plan.",
        creativeApproach:
          platformContext.creativeApproach ||
          "Align creative to the advertiser objective and supported performance signals.",
        measurementLogic:
          platformContext.measurementLogic ||
          "Read platform contribution with the same cross-platform KPI basis.",
        budgetLabel: platformContext.budgetLabel || "Approved allocation",
        canonicalAllocationLabel: platformContext.allocationLabel || "",
        launchReadiness:
          platformContext.launchReadiness ||
          "Adjust weighting only when supported delivery and response data are available.",
        caveat: platformContext.caveat || "",
      };
    })
    .filter(Boolean), allocationRows);
};

const ADVERTISER_LOGO_NESTED_KEYS = [
  "advertiser",
  "advertiserData",
  "advertiserRecord",
  "advertiserProfile",
  "advertiserMeta",
  "brand",
  "branding",
  "campaignAdvertiser",
  "mediaCampaign",
];

const pickRecordValue = (record, keys = []) => {
  if (!record || typeof record !== "object") return "";
  for (const key of keys) {
    const value = record[key];
    if (
      value !== undefined &&
      value !== null &&
      typeof value !== "object" &&
      String(value).trim()
    ) {
      return value;
    }
  }
  return "";
};

export const resolveMicrositeAdvertiserLogoMeta = (campaign = {}) => {
  const safeCampaign = campaign && typeof campaign === "object" ? campaign : {};
  const nestedRecords = ADVERTISER_LOGO_NESTED_KEYS.map((key) => safeCampaign[key]).filter(
    (record) => record && typeof record === "object"
  );
  const heroSection = safeCampaign?.sections?.hero;
  const sectionAdvertiser = safeCampaign?.sections?.advertiser;
  const records = [
    safeCampaign,
    ...nestedRecords,
    heroSection && typeof heroSection === "object" ? heroSection : null,
    sectionAdvertiser && typeof sectionAdvertiser === "object" ? sectionAdvertiser : null,
  ].filter(Boolean);

  const advertiserName = pickMicrositeTextValue(
    "",
    safeCampaign.advertiserName,
    safeCampaign.advertiser,
    safeCampaign.name,
    ...records.map((record) =>
      pickRecordValue(record, [
        "advertiserName",
        "advertiser",
        "advertiser_name",
        "name",
        "company",
        "company_name",
      ])
    )
  );
  const advertiserId = pickFirstValue(
    safeCampaign.advertiserId,
    safeCampaign.advertiser_id,
    ...records.map((record) => pickRecordValue(record, ["advertiserId", "advertiser_id", "id", "_id"]))
  );
  const logoAltText =
    pickMicrositeTextValue(
      "",
      ...records.map((record) => pickRecordValue(record, ["logoAltText", "logo_alt_text"]))
    ) || (advertiserName ? `${cleanMicrositeNarrativeTitle(advertiserName)} logo` : "Advertiser logo");

  const candidates = records.map((record) => ({
    ...record,
    id: pickRecordValue(record, ["id", "_id"]) || advertiserId,
    advertiserId: pickRecordValue(record, ["advertiserId", "advertiser_id", "id", "_id"]) || advertiserId,
    advertiserName:
      pickRecordValue(record, ["advertiserName", "advertiser_name", "advertiser", "name"]) ||
      advertiserName,
    logoUrl: pickRecordValue(record, [
      "logoUrl",
      "logo_url",
      "logo",
      "advertiserLogo",
      "advertiser_logo",
      "advertiserLogoUrl",
      "advertiser_logo_url",
      "companyLogo",
      "company_logo",
      "brandLogo",
      "brand_logo",
      "imageUrl",
      "image_url",
    ]),
    logoDriveFileId: pickRecordValue(record, [
      "logoDriveFileId",
      "logo_drive_file_id",
      "advertiserLogoDriveFileId",
      "advertiser_logo_drive_file_id",
    ]),
    logoUpdatedAt: pickRecordValue(record, ["logoUpdatedAt", "logo_updated_at"]),
    logoAltText,
    raw: record,
  }));

  return {
    advertiserId,
    advertiserName,
    logoAltText,
    candidates,
  };
};

export const buildSummaryCards = (campaign) => {
  const semanticTypography = resolveMicrositeSemanticTypography(campaign);
  const rawCards = Array.isArray(campaign?.sections?.hero?.summaryCards)
    ? campaign.sections.hero.summaryCards
    : Array.isArray(campaign?.chartReady?.summaryCards)
    ? campaign.chartReady.summaryCards
    : [];

  const normalizePlatformCopy = (value) =>
    String(value ?? "")
      .replace(/\bchannels\b/g, "platforms")
      .replace(/\bChannels\b/g, "Platforms")
      .replace(/\bCHANNELS\b/g, "PLATFORMS")
      .replace(/\bchannel mix\b/g, "platform mix")
      .replace(/\bChannel Mix\b/g, "Platform Mix")
      .replace(/\bCHANNEL MIX\b/g, "PLATFORM MIX");

  return rawCards.map((card) => {
    const displayLabel = normalizePlatformCopy(card?.label);
    if (card?.key === "budget_total") {
      const campaignBudget = resolveMicrositeCampaignBudget(campaign);
      return {
        ...card,
        label: displayLabel,
        displayValue: formatCurrency(campaignBudget ?? card.value),
      };
    }
    if (card?.key === "objective") {
      return {
        ...card,
        label: displayLabel,
        displayValue: formatCampaignObjectiveLabel(card?.value),
      };
    }
    if (card?.key === "flight_window") {
      return {
        ...card,
        label: displayLabel,
        // The campaign record is the canonical flight authority. A generated
        // hero card is presentation copy and must never override a later
        // governed flight revision.
        displayValue: formatFlightWindow(campaign),
      };
    }
    if (card?.key === "status") {
      return {
        ...card,
        label: displayLabel,
        displayValue: formatCampaignStatusLabel(card?.value) || "Not set",
      };
    }
    if (card?.key === "primary_kpi" && semanticTypography) {
      // The campaign's own KPI in its display case, as the hero pill shows it;
      // the card's saved value is a copy and never outranks it.
      return {
        ...card,
        label: displayLabel,
        displayValue: normalizePlatformCopy(
          formatMicrositePrimaryKpiDisplay(campaign?.primaryKpi) ||
            formatMicrositePrimaryKpiDisplay(card?.value) ||
            "Not set"
        ),
      };
    }
    return {
      ...card,
      label: displayLabel,
      displayValue: normalizePlatformCopy(String(card?.value ?? "").trim() || "Not set"),
    };
  });
};

const FILM_SOURCE_BRANDING = {
  imdb: { key: "imdb", label: "IMDb", image: imdbLogo },
  rottenTomatoes: {
    key: "rottenTomatoes",
    label: "Rotten Tomatoes",
    image: rottenTomatoesLogo,
  },
};

const SCORE_PENDING_LABEL = "Score pending";

const normalizeMicrositeKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

const resolveFilmSourceKey = (value) => {
  const normalized = normalizeMicrositeKey(value);
  if (!normalized) return "";
  if (normalized.includes("officialwebsite")) return "officialWebsite";
  if (normalized.includes("rottentomatoes")) return "rottenTomatoes";
  if (normalized.includes("imdb")) return "imdb";
  return normalized;
};

const resolveFilmSourceBranding = (value) => {
  const key = resolveFilmSourceKey(value);
  return FILM_SOURCE_BRANDING[key] || null;
};

const FILM_SOURCE_ARTWORK_CLASS_BY_KEY = {
  imdb: "imdb",
  rottenTomatoes: "rotten-tomatoes",
};

const getFilmSourceArtworkClassName = (value) => {
  const sourceClass = FILM_SOURCE_ARTWORK_CLASS_BY_KEY[resolveFilmSourceKey(value)];
  return sourceClass ? `campaign-strategy-microsite-source-artwork--${sourceClass}` : "";
};

const MICROSITE_ROTTEN_TOMATOES_PLACEHOLDER_IMAGE_PATTERNS = Object.freeze([
  /poster[-_]?default/i,
  /default[-_]?poster/i,
  /poster[-_]?placeholder/i,
  /placeholder[-_]?poster/i,
  /missing[-_]?poster/i,
  /missing[-_]?image/i,
  /no[-_]?poster/i,
  /no[-_]?image/i,
  /image[-_]?not[-_]?available/i,
  /not[-_]?available/i,
  /empty[-_]?poster/i,
  /blank[-_]?poster/i,
  /gray[-_]?tomato/i,
  /grey[-_]?tomato/i,
  /rt[-_]?logo/i,
  /rotten[-_]?tomatoes[-_]?logo/i,
  /tomatometer[-_]?icon/i,
  /scorecard[-_]?tomatometer/i,
  /audience[-_]?score[-_]?icon/i,
  /critic[-_]?score[-_]?icon/i,
]);

const FILM_SOURCE_LABEL_BY_KEY = {
  officialWebsite: "Official Website",
  imdb: "IMDb",
  rottenTomatoes: "Rotten Tomatoes",
};

const FILM_SOURCE_TYPE_BY_KEY = {
  officialWebsite: "official_website",
  imdb: "imdb",
  rottenTomatoes: "rotten_tomatoes",
};

const isRottenTomatoesPlaceholderPosterUrl = (url) => {
  const normalized = String(url || "").trim();
  if (!normalized) return true;

  try {
    const parsed = new URL(normalized);
    const candidate = decodeURIComponent(
      `${parsed.hostname}${parsed.pathname}${parsed.search}`
    ).toLowerCase();
    return MICROSITE_ROTTEN_TOMATOES_PLACEHOLDER_IMAGE_PATTERNS.some((pattern) =>
      pattern.test(candidate)
    );
  } catch {
    return true;
  }
};

export const isValidMicrositePosterImage = (url, source = "") => {
  const normalized = String(url || "").trim();
  if (!normalized) return false;

  try {
    const parsed = new URL(normalized);
    if (!/^https?:$/i.test(parsed.protocol)) return false;
  } catch {
    return false;
  }

  if (isRottenTomatoesPlaceholderPosterUrl(normalized)) return false;
  return resolveFilmSourceKey(source) !== "rottenTomatoes" ||
    !isRottenTomatoesPlaceholderPosterUrl(normalized);
};

const getFilmRecordPosterImage = (record = {}, includeGenericImage = true) =>
  pickMicrositeTextValue(
    "",
    record?.posterUrl,
    record?.poster_url,
    record?.posterImage,
    record?.poster_image,
    record?.posterImageUrl,
    record?.poster_image_url,
    record?.titlePosterUrl,
    record?.title_poster_url,
    record?.titlePosterImage,
    record?.title_poster_image,
    record?.titleImage,
    record?.title_image,
    record?.titleArtwork,
    record?.title_artwork,
    record?.creativeImage,
    record?.creative_image,
    record?.creativeImageUrl,
    record?.creative_image_url,
    record?.creativePosterUrl,
    record?.creative_poster_url,
    record?.heroImage,
    record?.hero_image,
    record?.hero?.image,
    includeGenericImage ? record?.image : ""
  );

const getExplicitCampaignPosterImage = (campaign = {}) =>
  pickMicrositeTextValue(
    "",
    campaign?.posterUrl,
    campaign?.poster_url,
    campaign?.posterImage,
    campaign?.poster_image,
    campaign?.posterImageUrl,
    campaign?.poster_image_url,
    campaign?.titlePosterUrl,
    campaign?.title_poster_url,
    campaign?.titlePosterImage,
    campaign?.title_poster_image,
    campaign?.titleImage,
    campaign?.title_image,
    campaign?.titleArtwork,
    campaign?.title_artwork,
    campaign?.creativeImage,
    campaign?.creative_image,
    campaign?.creativeImageUrl,
    campaign?.creative_image_url,
    campaign?.creativePosterUrl,
    campaign?.creative_poster_url,
    campaign?.heroPosterUrl,
    campaign?.hero_poster_url
  );

const getFilmSourceRecords = (filmSection = {}) =>
  []
    .concat(Array.isArray(filmSection?.cards) ? filmSection.cards : [])
    .concat(Array.isArray(filmSection?.sources) ? filmSection.sources : []);

const findFilmSourceRecord = (filmSection, sourceKey) =>
  getFilmSourceRecords(filmSection).find(
    (record) => resolveFilmSourceKey(record?.key || record?.sourceType || record?.title) === sourceKey
  );

const buildResolvedPosterHero = (hero = {}, candidate = null) => {
  if (!candidate?.image) return { ...hero, image: "" };
  const sourceKey = candidate.sourceKey || "";
  return {
    ...hero,
    image: candidate.image,
    imageSourceLabel:
      candidate.sourceLabel ||
      FILM_SOURCE_LABEL_BY_KEY[sourceKey] ||
      hero?.imageSourceLabel ||
      hero?.sourceLabel ||
      "",
    imageSourceKey: sourceKey || hero?.imageSourceKey || hero?.sourceKey || "",
    imageSourceType:
      candidate.sourceType ||
      FILM_SOURCE_TYPE_BY_KEY[sourceKey] ||
      hero?.imageSourceType ||
      hero?.sourceType ||
      "",
  };
};

/* The backend's presentation contract (campaign_microsite_presentation_v2)
   rides the payload of a stamped artifact only; a legacy payload carries none,
   so every feature reads false and the page renders exactly as before. */
const presentationFeature = (campaign, feature) =>
  campaign?.presentationContract?.features?.[feature] === true;

/* Presentation contract v2 (P5): the backend publishes the advertiser's logo
   as the hero image, keyed "advertiser_logo", as the backend-relative path the
   header logo also uses. Under the advertiserHeroIdentity feature it is
   resolved against the backend origin here, the way the header resolves it;
   a legacy hero (a scraped absolute URL) is left exactly as served. */
const isAdvertiserLogoRecord = (record) =>
  normalizeMicrositeKey(record?.imageSourceKey || record?.imageSourceType || "") === "advertiserlogo";
const resolveMicrositeHeroImage = (record, image, { advertiserHeroIdentity = false } = {}) =>
  advertiserHeroIdentity && isAdvertiserLogoRecord(record)
    ? absoluteAdvertiserLogoUrl(image, API_BASE_URL)
    : image;

// What a film section with neither a hero title nor a served name is called:
// "The film" where the backend served that the film has no governed name (as
// its narratives say "The film is positioned as …"); otherwise, for an older
// backend or a page outside the film vertical, the campaign name as before.
const unnamedFilmTitle = (filmSection, campaign) =>
  filmSection?.identitySource === "none" && isCampaignFilmVertical(campaign) ? "The film" : campaign?.campaignName;

export const resolveMicrositeFilmPosterHero = (filmSection = {}, campaign = {}) => {
  const hero = filmSection?.hero && typeof filmSection.hero === "object" ? filmSection.hero : {};
  const heroIdentity = { advertiserHeroIdentity: presentationFeature(campaign, "advertiserHeroIdentity") };
  const heroImage = resolveMicrositeHeroImage(hero, getFilmRecordPosterImage(hero, true), heroIdentity);
  const heroSourceInput =
    hero?.imageSourceKey ||
    hero?.imageSourceType ||
    hero?.imageSourceLabel ||
    hero?.sourceKey ||
    hero?.sourceType ||
    hero?.sourceLabel;
  const heroSourceKey = resolveFilmSourceKey(heroSourceInput);
  const officialRecord = findFilmSourceRecord(filmSection, "officialWebsite");
  const rottenTomatoesRecord = findFilmSourceRecord(filmSection, "rottenTomatoes");
  const explicitCampaignImage = getExplicitCampaignPosterImage(campaign);
  const candidates = [
    explicitCampaignImage
      ? { image: explicitCampaignImage, sourceKey: "", sourceLabel: "Campaign Artwork" }
      : null,
    heroImage && heroSourceKey !== "rottenTomatoes"
      ? {
          image: heroImage,
          sourceKey: heroSourceKey,
          sourceLabel: hero?.imageSourceLabel || hero?.sourceLabel || "",
          sourceType: hero?.imageSourceType || hero?.sourceType || "",
        }
      : null,
    officialRecord
      ? {
          image: resolveMicrositeHeroImage(officialRecord, getFilmRecordPosterImage(officialRecord, true), heroIdentity),
          sourceKey: "officialWebsite",
          sourceLabel: "Official Website",
          sourceType: "official_website",
        }
      : null,
    heroImage && heroSourceKey === "rottenTomatoes"
      ? {
          image: heroImage,
          sourceKey: "rottenTomatoes",
          sourceLabel: hero?.imageSourceLabel || hero?.sourceLabel || "Rotten Tomatoes",
          sourceType: hero?.imageSourceType || hero?.sourceType || "rotten_tomatoes",
        }
      : null,
    rottenTomatoesRecord
      ? {
          image: getFilmRecordPosterImage(rottenTomatoesRecord, true),
          sourceKey: "rottenTomatoes",
          sourceLabel: "Rotten Tomatoes",
          sourceType: "rotten_tomatoes",
        }
      : null,
  ];

  const validCandidate = candidates.find(
    (candidate) =>
      candidate?.image &&
      isValidMicrositePosterImage(
        candidate.image,
        candidate.sourceKey || candidate.sourceType || candidate.sourceLabel
      )
  );
  return buildResolvedPosterHero(hero, validCandidate);
};

export const resolvePlatformBrandKey = resolvePlatformBrandKeyBase;

export const resolvePlatformBranding = resolvePlatformBrandingBase;
export const resolveMicrositePlatformBranding = resolveMicrositePlatformBrandingBase;

export const normalizeMicrositePlatformList = (campaign = {}) => {
  const candidates = [
    campaign?.platforms,
    campaign?.targetPlatforms,
    campaign?.target_platforms,
    campaign?.strategyPayload?.platforms,
    campaign?.strategy_payload?.platforms,
    campaign?.strategyPayload?.targetPlatforms,
    campaign?.strategy_payload?.target_platforms,
    campaign?.aiPlan?.platforms,
    campaign?.aiPlan?.targetPlatforms,
    campaign?.aiPlan?.target_platforms,
    campaign?.aiPlan?.plannerDraft?.formData?.platforms,
    campaign?.ai_plan?.planner_draft?.formData?.platforms,
    campaign?.plannerDraft?.formData?.platforms,
    campaign?.planner_draft?.formData?.platforms,
  ];
  const seen = new Set();
  const platforms = candidates
    .flatMap((value) => {
      if (Array.isArray(value)) return value;
      if (typeof value === "string") return value.split(/\s*,\s*/);
      return [];
    })
    .map((platform) => {
      const brand = resolvePlatformBranding(platform);
      return brand || platform;
    })
    .filter((platform) => {
      const key = resolvePlatformBrandKey(
        platform && typeof platform === "object" ? platform.key || platform.label : platform
      );
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  return orderMicrositePlatformRows(platforms);
};

const getMicrositePlatformDisplayLabel = (platform) =>
  platform && typeof platform === "object"
    ? platform.label ||
      platform.title ||
      platform.platform ||
      platform.platformName ||
      platform.name ||
      platform.channel ||
      platform.key
    : platform;

const resolveMicrositePlatformDisplayChip = (platform) => {
  const identity = resolveCampaignPlatformIdentity(platform);
  if (!identity) return null;
  if (["facebook", "instagram", "meta"].includes(identity.key)) {
    return resolvePlatformBranding("Meta") || { key: "meta", label: "Meta" };
  }
  return identity.known ? resolvePlatformBranding(identity.key) || platform : platform;
};

export const dedupeMicrositePlatformChips = (chips = []) => {
  const seen = new Set();
  return orderMicrositePlatformRows(Array.isArray(chips) ? chips : [])
    .map((platform) => resolveMicrositePlatformDisplayChip(platform))
    .filter(Boolean)
    .filter((platform) => {
      const raw = getMicrositePlatformDisplayLabel(platform);
      const displayLabel = formatMicrositePlatformDisplayLabel(raw);
      const key = normalizeMicrositeKey(displayLabel) || normalizeMicrositeKey(raw);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const MICROSITE_NARRATIVE_PLATFORM_CATEGORIES = [
  ["social", new Set(["meta", "facebook", "instagram", "tiktok", "x", "rumble", "truth_social", "reddit", "linkedin"])],
  ["search", new Set(["google_search", "chatgpt_search"])],
  ["CTV", new Set(["ctv"])],
  ["digital video", new Set(["youtube", "video"])],
  ["retail media", new Set(["amazon"])],
  ["display", new Set(["display"])],
  ["mobile", new Set(["mobile"])],
  ["audio", new Set(["audio"])],
  ["DOOH", new Set(["dooh"])],
];

const joinMicrositeNarrativeItems = (items = []) => {
  const values = (Array.isArray(items) ? items : []).filter(Boolean);
  if (values.length < 2) return values[0] || "";
  if (values.length === 2) return `${values[0]} and ${values[1]}`;
  return `${values.slice(0, -1).join(", ")}, and ${values[values.length - 1]}`;
};

export const buildMicrositePlatformNarrativeSummary = (platforms = []) => {
  const normalizedPlatforms = dedupeMicrositePlatformChips(platforms);
  if (!normalizedPlatforms.length) return "";
  if (normalizedPlatforms.length < 8) {
    return joinMicrositeNarrativeItems(
      normalizedPlatforms.map((platform) =>
        formatMicrositePlatformDisplayLabel(getMicrositePlatformDisplayLabel(platform))
      )
    );
  }

  const keys = new Set(normalizedPlatforms.map(resolveMicrositePlatformKey));
  const categories = MICROSITE_NARRATIVE_PLATFORM_CATEGORIES
    .filter(([, categoryKeys]) => Array.from(categoryKeys).some((key) => keys.has(key)))
    .map(([label]) => label);
  const categorizedKeys = new Set(
    MICROSITE_NARRATIVE_PLATFORM_CATEGORIES.flatMap(([, categoryKeys]) =>
      Array.from(categoryKeys)
    )
  );
  const uncategorizedLabels = normalizedPlatforms
    .filter((platform) => !categorizedKeys.has(resolveMicrositePlatformKey(platform)))
    .map((platform) =>
      formatMicrositePlatformDisplayLabel(getMicrositePlatformDisplayLabel(platform))
    );
  const mix = joinMicrositeNarrativeItems([...categories, ...uncategorizedLabels]);
  return `${normalizedPlatforms.length}-channel media mix${mix ? ` across ${mix}` : ""}`;
};

// The positioning phrase a rebuilt hero narrative asserts. Film keeps its
// established wording; every other vertical is named from the canonical
// category, and an absent category stays category-neutral.
export const buildMicrositePositioningPhrase = (advertiserVertical = null) => {
  const vertical = normalizeAdvertiserVertical(advertiserVertical);
  if (vertical === "film") return "a premium theatrical release";
  if (vertical === "general") return "a premium campaign";
  return `a premium ${formatAdvertiserVerticalImpactLabel(vertical).toLowerCase()} campaign`;
};

export const resolvePlatformAwareHeroNarrative = ({
  narrative,
  title,
  platforms = [],
  advertiserVertical = null,
} = {}) => {
  const cleanedNarrative = formatMicrositeNarrativeCopy(narrative, "");
  const normalizedPlatforms = dedupeMicrositePlatformChips(platforms);
  if (!cleanedNarrative || !normalizedPlatforms.length) return cleanedNarrative;

  const isGeneratedPlatformTemplate =
    /\bis (?:being )?positioned as a premium theatrical (?:title|release),\s*using\b[\s\S]*?\bto translate\b/i.test(
      cleanedNarrative
    );
  if (!isGeneratedPlatformTemplate) return cleanedNarrative;

  const declaredCount = cleanedNarrative.match(/\b(\d+)\s*[- ]channel\b/i);
  const platformLabels = normalizedPlatforms.map((platform) =>
    formatMicrositePlatformDisplayLabel(getMicrositePlatformDisplayLabel(platform))
  );
  const listsEveryPlatform = platformLabels.every((label) =>
    new RegExp(`\\b${escapeMicrositeRegExp(label).replace(/\s+/g, "\\s*")}\\b`, "i").test(
      cleanedNarrative
    )
  );
  if (
    (declaredCount && Number(declaredCount[1]) === normalizedPlatforms.length) ||
    listsEveryPlatform
  ) {
    return cleanedNarrative;
  }

  const channelSummary = buildMicrositePlatformNarrativeSummary(normalizedPlatforms);
  const cleanTitle = cleanMicrositeNarrativeTitle(title) || "The campaign";
  const positioning = buildMicrositePositioningPhrase(advertiserVertical);
  return `${cleanTitle} is positioned as ${positioning}, using ${channelSummary} to translate awareness into qualified audience action.`;
};

// The hero sentence's KPI clause: "Primary KPIs: A and B." Each KPI it names is
// a fragment of the saved value, split at commas, semicolons and "and". With
// { displayCase: true } (where semantic typography applies) each fragment takes
// its display case (formatMicrositePrimaryKpiDisplay) before the clause is
// joined, so the sentence names the KPI as the structured slots show it:
// "CPM (awareness)" reads "CPM (Awareness)". The sentence itself, and any prose
// around the KPI, is never re-cased.
export const formatMicrositePrimaryKpiNarrative = (value, { displayCase = false } = {}) => {
  const rawValues = Array.isArray(value) ? value : [value];
  const values = rawValues
    .flatMap((entry) => String(entry || "").split(/\s*(?:,|;|\band\b)\s*/i))
    .map((entry) => normalizeMicrositeDisplayTerminalPunctuation(entry).trim())
    .map((entry) => (displayCase ? formatMicrositePrimaryKpiDisplay(entry) : entry))
    .filter(Boolean);
  if (!values.length) return "Primary KPI will appear here once assigned.";
  const uniqueValues = values.filter(
    (entry, index) =>
      values.findIndex((candidate) => normalizeMicrositeKey(candidate) === normalizeMicrositeKey(entry)) === index
  );
  return `Primary KPI${uniqueValues.length === 1 ? "" : "s"}: ${joinMicrositeNarrativeItems(uniqueValues)}.`;
};

// Count agreement for every label on this page that states a number.
// "1 channel planned", "2 channels planned" — never "1 channels planned".
export const micrositeCountNoun = (count, singular, plural = `${singular}s`) =>
  Number(count) === 1 ? singular : plural;

export const formatMicrositeHeroChipLabel = (chip = {}, platformDisplayCount = 0) => {
  const label = String(chip?.label || chip || "");
  const plannedNounMatch = label.match(/\b(channels?|platforms?)\s+planned\b/i);
  if (!plannedNounMatch) return label;
  // The count the chip should state: the displayed platform count when the
  // caller knows it, otherwise the count the saved label already declares.
  const declaredCountMatch = label.match(/\b(\d+)\s+(?:channels?|platforms?)\s+planned\b/i);
  const declaredCount = declaredCountMatch ? Number(declaredCountMatch[1]) : null;
  const count = platformDisplayCount || declaredCount;
  if (!Number.isFinite(count) || count <= 0) return label;
  const singular = plannedNounMatch[1].toLowerCase().replace(/s$/, "");
  return `${count} ${micrositeCountNoun(count, singular)} planned`;
};

export const filterMicrositePlatformChipsForAllocatedPlatforms = (platforms = [], allocations = []) => {
  const rows = Array.isArray(allocations) ? allocations : [];
  const allocatedKeys = new Set(
    rows
      .filter((row) => {
        const percent = Number(row?.percent ?? row?.share ?? row?.value ?? row?.allocation);
        const budget = parseBudgetValue(row?.budgetLabel || row?.budget);
        const key = normalizePlatformAllocationKey(row?.key || row?.label || row?.platform || row?.title, {
          allowUnknown: true,
        });
        return percent > 0 || budget > 0 || Boolean(MICROSITE_PLATFORM_CONTEXT[key]);
      })
      .map((row) =>
        normalizePlatformAllocationKey(row?.key || row?.label || row?.platform || row?.title, {
          allowUnknown: true,
        })
      )
      .filter(Boolean)
  );
  if (!allocatedKeys.size) return Array.isArray(platforms) ? platforms : [];
  return orderMicrositePlatformRows(platforms, rows).filter((platform) => {
    const key = normalizePlatformAllocationKey(
      platform && typeof platform === "object" ? platform.key || platform.label : platform,
      { allowUnknown: true }
    );
    return key && allocatedKeys.has(key);
  });
};

const applyMicrositePlatformContextToAllocation = (row = {}) => {
  const key = resolveMicrositePlatformKey(row);
  const context = MICROSITE_PLATFORM_CONTEXT[key];
  if (!context) return row;

  if (key === "shopify") {
    return {
      ...row,
      key,
      label: context.label,
      platform: context.label,
      value: 0,
      share: 0,
      percent: 0,
      allocation: 0,
      allocationLabel: context.allocationLabel,
      shareLabel: context.allocationLabel,
      budget: 0,
      budgetLabel: context.budgetLabel,
      metricBasis: "measurement",
      isNonPaidMeasurementLayer: true,
      micrositeCaveat: context.caveat,
    };
  }

  return {
    ...row,
    key,
    label: context.label,
    platform: context.label,
    role: row.role || context.role,
    primaryObjective: row.primaryObjective || context.role,
    allocationLabel: row.allocationLabel || row.shareLabel || context.allocationLabel,
    shareLabel: row.shareLabel || row.allocationLabel || context.allocationLabel,
    budgetLabel: row.budgetLabel || context.budgetLabel,
    budgetBasisLabel: row.budgetBasisLabel || context.budgetBasisLabel || null,
    micrositeCaveat: context.caveat,
  };
};

export const ensureMicrositeSelectedPlatformAllocations = (
  allocations = [],
  selectedPlatforms = []
) => {
  const rows = (Array.isArray(allocations) ? allocations : []).map(
    applyMicrositePlatformContextToAllocation
  );
  const rowKeys = new Set(rows.map(resolveMicrositePlatformKey).filter(Boolean));
  const selectedKeys = new Set(
    (Array.isArray(selectedPlatforms) ? selectedPlatforms : [])
      .map(resolveMicrositePlatformKey)
      .filter(Boolean)
  );

  ["shopify", "linkedin"].forEach((key) => {
    if (!selectedKeys.has(key) || rowKeys.has(key)) return;
    const context = MICROSITE_PLATFORM_CONTEXT[key];
    rows.push(
      applyMicrositePlatformContextToAllocation({
        key,
        label: context.label,
        platform: context.label,
        value: 0,
        share: 0,
        percent: 0,
        allocation: 0,
        allocationLabel: context.allocationLabel,
        shareLabel: context.allocationLabel,
        budget: key === "shopify" ? 0 : null,
        budgetLabel: context.budgetLabel,
        allocationSource: "selected_platform_context",
      })
    );
    rowKeys.add(key);
  });

  return orderMicrositePlatformRows(rows, rows);
};

const isMicrositeEstimatedEqualSplitFallbackRows = (rows = []) => {
  const list = Array.isArray(rows) ? rows : [];
  if (list.length < 2) return false;
  const allEstimated = list.every((row) => row?.estimated || row?.isEstimatedFallback);
  if (!allEstimated) return false;
  const values = list
    .map((row) => {
      const percent = parseAllocationPercent(
        firstPresentValue(row?.percent, row?.share, row?.shareLabel, row?.allocation)
      );
      if (Number.isFinite(percent)) return percent;
      return parseBudgetValue(firstPresentValue(row?.budget, row?.budgetLabel, row?.value));
    })
    .filter((value) => Number.isFinite(value));
  return values.length === list.length && new Set(values.map((value) => Math.round(value * 100))).size === 1;
};

// Resolves the single normalized allocation source the entire budget area (chart,
// list, table, percentages, dollars, impressions) derives from. Explicit allocation
// data always wins; the parsed AI strategy body is only a last resort so it can never
// override an approved Sherloc plan with stale equal-split values.
export const resolveMicrositeChannelSplit = ({
  requestedAllocationRows = [],
  structuredChannelSplit = [],
  strategyAllocationRows = [],
  totalBudgetNumeric = null,
  selectedPlatformChips = [],
  /*
   * The backend's canonical allocation identity (microsite_allocation_identity_v1).
   * When it says a structured allocation EXISTS, the equal-split heuristic below
   * may not hand the page over to parsed strategy prose — a governed 50/50 split
   * is not an equal-split fallback, it is a decision somebody made.
   */
  allocationIdentity = null,
} = {}) => {
  const requested = Array.isArray(requestedAllocationRows) ? requestedAllocationRows : [];
  const structured = Array.isArray(structuredChannelSplit) ? structuredChannelSplit : [];
  const strategyText = Array.isArray(strategyAllocationRows) ? strategyAllocationRows : [];
  const structuredIsEqualSplitFallback =
    allocationIdentity?.structured === true
      ? false
      : isMicrositeEstimatedEqualSplitFallbackRows(structured);

  const hasRequestedAllocation = requested.length > 0;
  const hasStructuredAllocation =
    !hasRequestedAllocation &&
    structured.length > 0 &&
    !(strategyText.length > 0 && structuredIsEqualSplitFallback);
  const hasParsedStrategyAllocation =
    !hasRequestedAllocation && !hasStructuredAllocation && strategyText.length > 0;

  const sourceRows = hasRequestedAllocation
    ? requested
    : hasStructuredAllocation
      ? structured
      : hasParsedStrategyAllocation
        ? strategyText
        : [];

  const resolvedSource = hasRequestedAllocation
    ? "requested_channel_allocation"
    : hasStructuredAllocation
      ? "structured_budget"
      : hasParsedStrategyAllocation
        ? "strategy_text"
        : "structured_budget";

  const normalizedRows = sourceRows.map((item) => {
    const percent = resolvePlatformAllocationPercent(item, {
      budgetBasis: totalBudgetNumeric,
      totalBudget: totalBudgetNumeric,
    });
    // Dollars come from an explicit dollar field or the percent basis — never a bare
    // allocation/share/weight left in `budget` by an AI revision.
    const computedBudget = resolvePlatformBudgetAmount(item, {
      budgetBasis: totalBudgetNumeric,
      totalBudget: totalBudgetNumeric,
      percent: Number.isFinite(percent) ? percent : null,
    });
    return {
      ...item,
      value: Number.isFinite(percent) ? percent : Number(item?.value || 0),
      share: Number.isFinite(percent) ? percent : Number(item?.share || 0),
      percent: Number.isFinite(percent) ? percent : Number(item?.percent || 0),
      allocation: Number.isFinite(percent) ? percent : Number(item?.allocation || 0),
      budget: computedBudget ?? null,
      budgetLabel:
        (computedBudget ? formatStandardCurrency(computedBudget) : "") || item?.budgetLabel || "",
      allocationSource: item?.allocationSource || resolvedSource,
    };
  });

  return ensureMicrositeSelectedPlatformAllocations(normalizedRows, selectedPlatformChips);
};

const buildSourceCardArtwork = (card, { advertiserHeroIdentity = false } = {}) => {
  const sourceKeyInput = card?.key || card?.sourceType || card?.title;
  const branding = resolveFilmSourceBranding(sourceKeyInput);
  return {
    // A card the backend keyed to the advertiser's logo (presentation contract
    // v2) resolves against the backend origin, as the hero does.
    image: branding?.image || resolveMicrositeHeroImage(card, card?.image, { advertiserHeroIdentity }) || "",
    isBrandAsset: Boolean(branding),
    sourceClassName: getFilmSourceArtworkClassName(sourceKeyInput),
  };
};

const formatSourceUrlLabel = (value) => {
  try {
    const parsed = new URL(String(value || "").trim());
    return parsed.hostname.replace(/^www\./i, "");
  } catch {
    return String(value || "").trim();
  }
};

const escapeCssUrlValue = (value) =>
  String(value || "")
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"');

const buildMediaBackdropStyle = (image) =>
  image
    ? {
        "--campaign-strategy-microsite-media-image": `url("${escapeCssUrlValue(image)}")`,
      }
    : undefined;

const coerceScorePercent = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") {
    return Number.isFinite(value) && value >= 0 && value <= 100 ? value : null;
  }

  const normalized = String(value || "").trim();
  if (!normalized) return null;

  const plainPercentMatch = normalized.match(/^(\d+(?:\.\d+)?)\s*%?$/);
  if (!plainPercentMatch) return null;

  const parsed = Number(plainPercentMatch[1]);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 100 ? parsed : null;
};

export const formatRottenTomatoesScoreDisplay = (value) => {
  const score = coerceScorePercent(value);
  return score === null ? SCORE_PENDING_LABEL : `${Math.round(score)}%`;
};

const hasRottenTomatoesSource = (filmSection) => {
  const cards = Array.isArray(filmSection?.cards) ? filmSection.cards : [];
  const sourceValues = [
    filmSection?.hero?.sourceKey,
    filmSection?.hero?.sourceType,
    filmSection?.hero?.contentSourceKey,
    filmSection?.hero?.contentSourceType,
    filmSection?.hero?.imageSourceKey,
    filmSection?.hero?.imageSourceType,
    ...cards.flatMap((card) => [card?.key, card?.sourceType, card?.title]),
  ];

  return sourceValues.some(
    (value) => resolveFilmSourceKey(value) === "rottenTomatoes"
  );
};

/*
 * An IMDb rating is a real number or it is absent. `Number()` was the wrong
 * coercion here: Number(null) is 0 and Number.isFinite(0) is true, so a campaign
 * with NO IMDb data at all rendered a fabricated "IMDb 0.0/10". Absence must
 * read as absence.
 */
const coerceImdbRating = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

/*
 * Entertainment ratings are schema for ONE governed vertical. The gate is here,
 * on the render path, as well as on the payload, so a score that reaches the
 * portal from a stale record or an older backend still cannot surface on a
 * campaign whose category does not permit it.
 */
export const buildFilmScoreItems = (filmSection, advertiserVertical) => {
  if (!isEntertainmentReferenceIntelligenceVertical(advertiserVertical)) return [];

  const scores = filmSection?.scores || {};
  const items = [];
  const rtAudienceScore = coerceScorePercent(scores.rottenTomatoesAudienceScore);
  const rtCriticScore = coerceScorePercent(scores.rottenTomatoesCriticScore);
  const imdbRating = coerceImdbRating(scores.imdbRating);

  if (rtAudienceScore !== null) {
    items.push({
      key: "rtAudience",
      label: "RT Audience",
      value: `${Math.round(rtAudienceScore)}%`,
    });
  }

  if (imdbRating !== null) {
    items.push({
      key: "imdb",
      label: "IMDb",
      value: `${imdbRating.toFixed(1)}/10`,
    });
  }

  if (rtCriticScore !== null || hasRottenTomatoesSource(filmSection)) {
    items.push({
      key: "rtCritic",
      label: "RT Critic",
      value: formatRottenTomatoesScoreDisplay(rtCriticScore),
    });
  }

  return items;
};

const formatRottenTomatoesSourceValue = (filmSection) => {
  const scores = filmSection?.scores || {};
  const rtAudienceScore = coerceScorePercent(scores.rottenTomatoesAudienceScore);
  const rtCriticScore = coerceScorePercent(scores.rottenTomatoesCriticScore);

  if (rtCriticScore !== null && rtAudienceScore !== null) {
    return `${Math.round(rtCriticScore)}% Critic • ${Math.round(rtAudienceScore)}% Audience`;
  }
  if (rtCriticScore !== null) return `${Math.round(rtCriticScore)}% Critic`;
  if (rtAudienceScore !== null) return `${Math.round(rtAudienceScore)}% Audience`;
  return SCORE_PENDING_LABEL;
};

const formatFilmSourceCardValue = (card, filmSection) => {
  if (resolveFilmSourceKey(card?.key || card?.sourceType || card?.title) === "rottenTomatoes") {
    return formatRottenTomatoesSourceValue(filmSection);
  }

  return formatMicrositeTextValue(card?.value, card?.badgeLabel || "Source Connected");
};

const compactMicrositeContextText = (value, fallback = "") => {
  const text = formatMicrositeTextValue(value, fallback).trim();
  if (!text || text === fallback) return text;
  return text.length > 640 ? `${text.slice(0, 637).trim()}...` : text;
};

const buildMicrositeContextList = (items = [], mapper, limit = 8) =>
  (Array.isArray(items) ? items : [])
    .map((item, index) => mapper(item, index))
    .filter(Boolean)
    .slice(0, limit);

export const buildCampaignStrategyMicrositeContext = ({
  campaign = null,
  advertiserVertical = null,
  advertiserVerticalContext = null,
  filmSection = {},
  budgetSection = {},
  strategyOverviewSection = {},
  audienceSection = {},
  benchmarkSection = {},
  retargetingSection = {},
  earnedMediaSection = {},
  releaseMilestoneSchedule = [],
  campaignDetailSection = {},
  creativeMessagingSection = {},
  optimizationSection = {},
  executionModulesSection = {},
  summaryCards = [],
  platformChips = [],
  channelSplit = [],
  objectiveSplit = [],
  timingPhases = [],
  estimatedDelivery = [],
  benchmarkCards = [],
  platformCards = [],
  trackingRequirements = [],
  reportingLogic = [],
  cadence = [],
  kpis = [],
  creativeThemes = [],
  executionModules = [],
  filmCards = [],
  filmScoreItems = [],
  proposalPlanning = null,
} = {}) => {
  if (!campaign) return null;
  // Eden reads what the page shows: in proposal mode every section the page
  // withholds is withheld here too, and the proposal carries the forecast.
  const shown = (sectionKey) => !isSectionWithheldInProposalMode(proposalPlanning, sectionKey);

  const normalizedAdvertiserVertical = normalizeAdvertiserVertical(
    advertiserVertical || resolveCampaignStrategyAdvertiserVertical(campaign)
  );
  const verticalContext =
    advertiserVerticalContext || serializeVerticalIntelligence(normalizedAdvertiserVertical);
  const flightDates = resolveFlightWindowDates(campaign);
  const financialContext = buildMicrositeFinancialContext(campaign, budgetSection);
  const connectedSources = buildMicrositeContextList(
    filmCards,
    (card) => ({
      key: card?.key || card?.sourceType || card?.title || "",
      title: compactMicrositeContextText(card?.title, "Source"),
      role: compactMicrositeContextText(card?.badgeLabel || card?.sourceType, "Source connected"),
      value: compactMicrositeContextText(formatFilmSourceCardValue(card, filmSection), ""),
      contribution: compactMicrositeContextText(card?.copy, ""),
      url: card?.url || "",
    }),
    6
  );
  const normalizedPlatformChips = orderMicrositePlatformRows(platformChips, channelSplit);
  const normalizedChannelSplit = ensureMicrositeSelectedPlatformAllocations(
    channelSplit,
    normalizedPlatformChips
  );
  const normalizedPlatformCards = ensureMicrositeSelectedPlatformCards({
    cards: platformCards,
    selectedPlatforms: normalizedPlatformChips,
    allocations: normalizedChannelSplit,
    advertiserVertical: normalizedAdvertiserVertical,
  });

  return {
    contextType: "campaign_strategy_microsite",
    source: "campaign_strategy_microsite",
    advertiser: {
      id: campaign?.advertiserId || campaign?.advertiser_id || null,
      name: campaign?.advertiserName || campaign?.advertiser || "",
      vertical: normalizedAdvertiserVertical,
      verticalContext,
    },
    advertiserVertical: normalizedAdvertiserVertical,
    advertiserVerticalContext: verticalContext,
    campaign: {
      id: campaign?.id || campaign?.campaignId || campaign?.mediaCampaignId || null,
      title: campaign?.campaignName || campaign?.name || "",
      objective: compactMicrositeContextText(formatCampaignObjectiveLabel(campaign?.objective, ""), ""),
      primaryKpi: compactMicrositeContextText(campaign?.primaryKpi, ""),
    },
    micrositeTitle: formatCampaignStrategyMicrositeTitle(normalizedAdvertiserVertical),
    strategySummary: compactMicrositeContextText(
      strategyOverviewSection?.summary ||
        filmSection?.combinedTakeaway ||
        formatCampaignObjectiveLabel(campaign?.objective, ""),
      ""
    ),
    positioningContent: {
      title: compactMicrositeContextText(
        filmSection?.hero?.title || servedFilmName(filmSection) || unnamedFilmTitle(filmSection, campaign),
        ""
      ),
      summary: compactMicrositeContextText(
        filmSection?.combinedTakeaway || filmSection?.hero?.synopsis,
        ""
      ),
      sourceLabel: compactMicrositeContextText(filmSection?.hero?.sourceLabel, ""),
      genre: compactMicrositeContextText(filmSection?.hero?.genreLabel, ""),
      release: compactMicrositeContextText(filmSection?.hero?.releaseLabel, ""),
      scores: buildMicrositeContextList(
        filmScoreItems,
        (item) => ({
          label: compactMicrositeContextText(item?.label, ""),
          value: compactMicrositeContextText(item?.value, ""),
        }),
        4
      ),
    },
    flightWindow: {
      start: flightDates.start || "",
      end: flightDates.end || "",
      label: formatFlightWindow(campaign),
    },
    totalBudget: formatMicrositeBudgetDisplay(campaign, budgetSection),
    /* campaign_financial_basis_v1: server-declared basis of the total budget. */
    budgetBasisLabel: budgetSection?.totalBudgetBasisLabel || null,
    financials: financialContext,
    summaryCards: buildMicrositeContextList(
      summaryCards,
      (card) => ({
        label: compactMicrositeContextText(card?.label, ""),
        value: compactMicrositeContextText(card?.displayValue || card?.value, ""),
      }),
      8
    ),
    platforms: buildMicrositeContextList(
      normalizedPlatformChips,
      (platform) => ({
        key: resolvePlatformBrandKey(platform?.key || platform?.label || platform),
        label: compactMicrositeContextText(
          formatMicrositePlatformDisplayLabel(platform?.label || platform?.name || platform),
          ""
        ),
      }),
      12
    ),
    platformAllocation: buildMicrositeContextList(
      normalizedChannelSplit,
      (item) => ({
        platform: compactMicrositeContextText(item?.label || item?.platform, ""),
        allocation:
          item?.allocationLabel ||
          item?.shareLabel ||
          `${item?.value ?? item?.share ?? item?.percent ?? item?.allocation ?? 0}%`,
        budget: compactMicrositeContextText(item?.budgetLabel || item?.primaryValue, ""),
      }),
      12
    ),
    platformCaveats: buildMicrositeContextList(
      buildMicrositeSelectedPlatformCaveats(normalizedPlatformChips),
      (item) => compactMicrositeContextText(item, ""),
      6
    ),
    platformObjectives: buildMicrositeContextList(
      normalizedPlatformCards,
      (card) => ({
        platform: compactMicrositeContextText(card?.title || card?.label, ""),
        objective: compactMicrositeContextText(card?.role || card?.objective, ""),
        primaryKpi: compactMicrositeContextText(card?.primaryKpi, ""),
        rationale: compactMicrositeContextText(card?.rationale || card?.copy, ""),
        caveat: compactMicrositeContextText(card?.caveat, ""),
        allocation: compactMicrositeContextText(card?.allocationLabel || card?.allocation, ""),
      }),
      12
    ),
    /* In proposal mode the proposal context carries the forecast figures. */
    estimatedDelivery: !proposalPlanning
      ? buildMicrositeContextList(
          estimatedDelivery,
          (entry) => ({
            platform: compactMicrositeContextText(entry?.label || entry?.platform, ""),
            value: formatMetricCount(resolveCanonicalVolume(entry)),
            rawValue: Number(entry?.value || 0),
          }),
          12
        )
      : [],
    proposal: buildProposalAssistantContext(proposalPlanning),
    funnelStageBreakdown: buildMicrositeContextList(
      objectiveSplit,
      (item) => ({
        stage: compactMicrositeContextText(item?.label, ""),
        allocation: item?.allocationLabel || `${item?.value || 0}%`,
      }),
      8
    ),
    timingPhases: buildMicrositeContextList(
      timingPhases,
      (item) => ({
        phase: compactMicrositeContextText(item?.label, ""),
        window: compactMicrositeContextText(item?.rangeLabel, ""),
        allocation: item?.allocationLabel || `${item?.value || 0}%`,
      }),
      8
    ),
    connectedSources,
    sourceLinks: connectedSources
      .filter((source) => source.url)
      .map((source) => ({ title: source.title, url: source.url, role: source.role })),
    kpiAssumptions: {
      primaryKpi: compactMicrositeContextText(campaign?.primaryKpi, ""),
      kpis: buildMicrositeContextList(
        shown("kpiMeasurement") ? kpis : [],
        (item) => compactMicrositeContextText(item),
        8
      ),
      trackingRequirements: buildMicrositeContextList(
        shown("kpiMeasurement") ? trackingRequirements : [],
        (item) => compactMicrositeContextText(item),
        8
      ),
      reportingLogic: buildMicrositeContextList(
        shown("kpiMeasurement") ? reportingLogic : [],
        (item) => compactMicrositeContextText(item),
        8
      ),
      cadence: buildMicrositeContextList(
        shown("kpiMeasurement") ? cadence : [],
        (item) => compactMicrositeContextText(item),
        8
      ),
      benchmarks: buildMicrositeContextList(
        !shown("benchmarkJustification")
          ? []
          : benchmarkCards.length
            ? benchmarkCards
            : benchmarkSection?.cards,
        (card) => ({
          title: compactMicrositeContextText(card?.title || card?.label, ""),
          value: compactMicrositeContextText(card?.value || card?.summary, ""),
          note: compactMicrositeContextText(card?.copy || card?.note, ""),
        }),
        8
      ),
    },
    strategicNarrative: {
      whyThisWorks: buildMicrositeContextList(
        shown("whyThisStrategyWorks") ? strategyOverviewSection?.cards : [],
        (card) => ({
          title: compactMicrositeContextText(card?.title, ""),
          text: compactMicrositeContextText(card?.copy || card?.summary, ""),
        }),
        6
      ),
      audiencePriorities: buildMicrositeContextList(
        shown("audienceCohorts") ? audiencePriorityCohorts(audienceSection) : [],
        (cohort) => ({
          label: compactMicrositeContextText(cohort?.label || cohort?.title, ""),
          share: compactMicrositeContextText(cohort?.shareLabel || cohort?.value, ""),
          rationale: compactMicrositeContextText(cohort?.rationale || cohort?.copy, ""),
        }),
        6
      ),
      creativeThemes: buildMicrositeContextList(
        creativeThemes,
        (theme) => ({
          title: compactMicrositeContextText(theme?.title, ""),
          description: compactMicrositeContextText(theme?.description || theme?.copy, ""),
          platformHints: compactMicrositeContextText(theme?.platformHints, ""),
        }),
        6
      ),
      retargetingFlow: buildMicrositeContextList(
        shown("retargeting") ? retargetingSection?.stages : [],
        (stage) => ({
          label: compactMicrositeContextText(stage?.label, ""),
          audience: compactMicrositeContextText(stage?.audience, ""),
          messaging: compactMicrositeContextText(stage?.messaging, ""),
          trigger: compactMicrositeContextText(stage?.trigger, ""),
        }),
        6
      ),
      earnedMediaIntelligence: !shown("earnedMedia") ? null : {
        summary: compactMicrositeContextText(earnedMediaSection?.summary, ""),
        primarySuccessMetric: compactMicrositeContextText(
          earnedMediaSection?.primarySuccessMetric,
          ""
        ),
        strategicThesis: compactMicrositeContextText(earnedMediaSection?.strategicThesis, ""),
        dataStatus: compactMicrositeContextText(earnedMediaSection?.dataStatus, ""),
        castDirectorDiscovery: {
          sourceStatus: compactMicrositeContextText(
            earnedMediaSection?.castDirectorDiscovery?.sourceStatus,
            ""
          ),
          primarySource: compactMicrositeContextText(
            earnedMediaSection?.castDirectorDiscovery?.primarySource,
            ""
          ),
          people: buildMicrositeContextList(
            earnedMediaSection?.castDirectorDiscovery?.people,
            (person) => ({
              name: compactMicrositeContextText(person?.name, ""),
              role: compactMicrositeContextText(person?.role, ""),
              characterName: compactMicrositeContextText(person?.characterName, ""),
              source: compactMicrositeContextText(person?.source, ""),
              socialDataStatus: compactMicrositeContextText(person?.socialDataStatus, ""),
            }),
            8
          ),
        },
        castTalentAnalysis: buildMicrositeContextList(
          earnedMediaSection?.castTalentAnalysis,
          (talent) => ({
            name: compactMicrositeContextText(talent?.name || talent?.title, ""),
            role: compactMicrositeContextText(talent?.role, ""),
            characterName: compactMicrositeContextText(talent?.characterName, ""),
            source: compactMicrositeContextText(talent?.source, ""),
            socialDataStatus: compactMicrositeContextText(talent?.socialDataStatus, ""),
            reach: compactMicrositeContextText(talent?.totalEstimatedReach, ""),
            audienceFit: compactMicrositeContextText(talent?.audienceFit, ""),
            engagementQuality: compactMicrositeContextText(talent?.engagementQuality, ""),
            conversionPotential: compactMicrositeContextText(talent?.conversionPotential, ""),
            amplificationPriority: compactMicrositeContextText(talent?.amplificationPriority, ""),
            recommendedUse: compactMicrositeContextText(talent?.recommendedUse, ""),
            buyTicketsRole: compactMicrositeContextText(talent?.buyTicketsRole, ""),
          }),
          8
        ),
        talentRanking: buildMicrositeContextList(
          earnedMediaSection?.talentRanking,
          (row) => ({
            rank: compactMicrositeContextText(row?.rank, ""),
            name: compactMicrositeContextText(row?.name, ""),
            score: compactMicrositeContextText(row?.score, ""),
            rationale: compactMicrositeContextText(row?.rationale, ""),
          }),
          8
        ),
        talentSignals: buildMicrositeContextList(
          earnedMediaSection?.talentSignals,
          (signal) => ({
            label: compactMicrositeContextText(signal?.label || signal?.title, ""),
            metric: compactMicrositeContextText(signal?.metricLabel, ""),
            insight: compactMicrositeContextText(signal?.insight || signal?.copy, ""),
          }),
          6
        ),
        amplificationRecommendations: buildMicrositeContextList(
          earnedMediaSection?.amplificationRecommendations,
          (item) => compactMicrositeContextText(item?.copy || item?.title || item),
          6
        ),
        buyTicketsTrafficPlan: {
          role: compactMicrositeContextText(earnedMediaSection?.buyTicketsTrafficPlan?.role, ""),
          signalsToWatch: buildMicrositeContextList(
            earnedMediaSection?.buyTicketsTrafficPlan?.signalsToWatch,
            (item) => compactMicrositeContextText(item),
            8
          ),
          optimizationActions: buildMicrositeContextList(
            earnedMediaSection?.buyTicketsTrafficPlan?.optimizationActions,
            (item) => compactMicrositeContextText(item),
            8
          ),
        },
      },
      releaseMilestoneSchedule: buildMicrositeContextList(
        shown("releaseMilestones") ? releaseMilestoneSchedule : [],
        (milestone) => ({
          phase: compactMicrositeContextText(milestone?.phase || milestone?.title, ""),
          timing: compactMicrositeContextText(milestone?.timing, ""),
          objective: compactMicrositeContextText(milestone?.objective || milestone?.copy, ""),
          successSignal: compactMicrositeContextText(milestone?.successSignal, ""),
        }),
        8
      ),
      executionModules: buildMicrositeContextList(
        executionModules,
        (module) => ({
          title: compactMicrositeContextText(module?.title, ""),
          summary: compactMicrositeContextText(module?.summary || module?.copy, ""),
        }),
        6
      ),
      campaignDetails: compactMicrositeContextText(campaignDetailSection?.summary || campaignDetailSection?.intro, ""),
      creativeObjective: compactMicrositeContextText(
        creativeMessagingSection?.objectiveContext || creativeMessagingSection?.callToAction,
        ""
      ),
      optimizationSummary: compactMicrositeContextText(
        optimizationSection?.summary || optimizationSection?.measurementLogic,
        ""
      ),
    },
  };
};

const mergeUniqueTextItems = (...groups) => {
  const seen = new Set();
  return groups
    .flat()
    .map((item) => String(item || "").trim())
    .filter(Boolean)
    .filter((item) => {
      const normalized = normalizeMicrositeKey(item);
      if (!normalized || seen.has(normalized)) return false;
      seen.add(normalized);
      return true;
    });
};

const brandAwarenessText = (fallback = "", ...values) =>
  appendMicrositeDisplayTerminalPunctuation(
    pickMicrositeTextValue("", ...values) || fallback
  );

const firstBrandAwarenessListText = (list = [], keys = []) => {
  const rows = Array.isArray(list) ? list : list ? [list] : [];
  for (const row of rows) {
    if (row && typeof row === "object" && !Array.isArray(row)) {
      const text = pickMicrositeTextValue(
        "",
        ...keys.map((key) => row?.[key]),
        row.copy,
        row.body,
        row.summary,
        row.title,
        row.label
      );
      if (text) return text;
      continue;
    }
    const text = formatMicrositeTextValue(row, "");
    if (text) return text;
  }
  return "";
};

export const buildBrandAwarenessStrategy = ({
  campaign = {},
  strategyOverviewSection = {},
  audienceSection = {},
  earnedMediaSection = {},
  releaseMilestoneSchedule = [],
  creativeMessagingSection = {},
  platformCards = [],
  platformChips = [],
  filmSection = {},
  advertiserVertical = "",
} = {}) => {
  // The campaign is named as the Campaign Journey names it
  // (resolveCampaignJourneyIdentity): a film by its saved title, else its
  // advertiser, else the title its campaign name carries; any other campaign
  // by its advertiser, unless the campaign name extends it with a product. A
  // flight label never names it: Dark Horse read "Making 2026 Theatrical
  // Release the story audiences keep hearing about", OutKick "Making Fall 2026
  // Audience Growth the story …" (2026-10-12).
  const identity = resolveCampaignJourneyIdentity(campaign);
  const titleSubject = identity.name || (identity.film ? "this film" : "this campaign");
  const objectiveLabel = formatCampaignObjectiveLabel(campaign?.objective, "awareness");
  const platformLabels = [
    ...(Array.isArray(platformChips) ? platformChips : []),
    ...(Array.isArray(platformCards) ? platformCards : []),
  ]
    .map((platform) =>
      formatMicrositePlatformDisplayLabel(
        platform?.label || platform?.title || platform?.platform || platform?.key || platform
      )
    )
    .filter(Boolean)
    .filter((label, index, list) => list.findIndex((item) => normalizeMicrositeKey(item) === normalizeMicrositeKey(label)) === index);
  /*
   * Presentation contract v2 (P2): a plan that activates no retargeting is not
   * described as building retargetable momentum or as handing off to paid
   * retargeting. The portal-composed copy below says what the approved lanes
   * do without it; a legacy payload carries no verdict and keeps every word.
   */
  const retargetingSuppressed =
    campaign?.presentationContract?.features?.retargetingSuppressedWithoutAuthority === true &&
    campaign?.audienceActivation?.retargetingActive === false;
  const withoutRetargeting = (value) =>
    retargetingSuppressed
      ? String(value || "")
          .replace(/ before paid retargeting asks for (?:action|a purchase)\./g, " before paid media asks for action.")
          .replace(/ through paid creative and retargeting\./g, " through paid creative.")
          .replace(/ through paid retargeting\./g, " through paid media.")
          .replace(/\bretargetable /g, "")
      : value;
  const platformSummary = withoutRetargeting(
    platformLabels.length
      ? `${platformLabels.slice(0, 4).join(", ")} can convert organic attention into retargetable paid-media momentum.`
      : "Paid media can convert organic attention into retargetable momentum once a trusted signal appears."
  );
  const milestone = Array.isArray(releaseMilestoneSchedule) ? releaseMilestoneSchedule[0] : null;
  const releaseSignal = milestone
    ? brandAwarenessText(
        "",
        milestone.objective,
        milestone.copy,
        milestone.description,
        firstBrandAwarenessListText(milestone.actions)
      )
    : "";

  /*
   * CATEGORY BOUNDARY. This module is composed here, in the portal, so the
   * backend's vertical language overlay never touched it — which is why a
   * retail advertiser was served "event or screening strategy" verbatim.
   *
   * The governed vertical now selects the card set AND the default copy. The
   * campaign-derived sources below stay shared because they are category
   * neutral (they are the campaign's own audience, earned-media, messaging and
   * flight content); it is the taxonomy that decides which of them a category
   * is entitled to and how each one is framed.
   */
  const taxonomy = getBrandAwarenessTaxonomy(advertiserVertical);

  const brandAwarenessSignalSources = {
    narrative: [
      creativeMessagingSection?.objectiveContext,
      creativeMessagingSection?.summary,
      strategyOverviewSection?.positioningStatement,
      firstBrandAwarenessListText(creativeMessagingSection?.themes, ["copy", "body", "rationale"]),
    ],
    audience: [
      audienceSection?.summary,
      firstBrandAwarenessListText(audienceSection?.insightCards, ["copy", "insight", "rationale"]),
    ],
    moment: [releaseSignal, formatFlightWindow(campaign)],
    earned: [
      earnedMediaSection?.strategicThesis,
      earnedMediaSection?.summary,
      firstBrandAwarenessListText(earnedMediaSection?.amplificationRecommendations, [
        "copy",
        "recommendation",
        "rationale",
      ]),
    ],
    socialProof: [
      earnedMediaSection?.primarySuccessMetric,
      earnedMediaSection?.dataStatus,
      firstBrandAwarenessListText(earnedMediaSection?.talentSignals, ["insight", "copy", "metricLabel"]),
    ],
    context: [
      strategyOverviewSection?.strategyLogic,
      strategyOverviewSection?.crossPlatformStrategy,
      strategyOverviewSection?.positioningStatement,
      filmSection?.hero?.synopsis,
      advertiserVertical ? `${sentenceCase(advertiserVertical)} context shapes the organic angle.` : "",
    ],
    // Describes the path the campaign already has; invents no retailer,
    // storefront, or commerce data that the campaign did not supply.
    purchasePath: [strategyOverviewSection?.crossPlatformStrategy, platformSummary],
  };

  const resolveBrandAwarenessSignal = (name) => {
    const fallback = taxonomy.signalDefaults?.[name];
    const fallbackText = typeof fallback === "function" ? fallback(titleSubject) : fallback || "";
    return brandAwarenessText(withoutRetargeting(fallbackText), ...(brandAwarenessSignalSources[name] || []));
  };

  // Key order matches the card literals this replaced, so a film campaign's
  // serialized module is byte-identical to before.
  const cards = taxonomy.cards.map((card) => ({
    key: card.key,
    title: card.title,
    meta: card.meta,
    copy: normalizeMicrositeDisplayTerminalPunctuation(
      `${card.lead(titleSubject)} ${resolveBrandAwarenessSignal(card.signal)}`
    ),
    accent: card.accent,
  }));

  // Directional, observed brand-awareness benchmarks — generalized and
  // entertainment-safe. Headline metrics stay generalized (never fabricated per
  // campaign), while the supporting copy leans on available context (objective,
  // active platforms). Framed as observed patterns, not guaranteed outcomes.
  const stats = [
    {
      key: "organic-discovery",
      label: "Organic Discovery",
      value: "77%",
      copy: normalizeMicrositeDisplayTerminalPunctuation(
        `Most audience discovery is observed to happen through word-of-mouth and social proof rather than paid ads, so PR and organic social act as force multipliers for ${objectiveLabel}.`
      ),
      accent: "rgba(76, 141, 255, 0.96)",
    },
    {
      key: "pr-amplification",
      label: "PR Amplification",
      value: "3.2×",
      copy: normalizeMicrositeDisplayTerminalPunctuation(
        `Coordinated PR and paid media tend to reinforce one another — press and creator coverage timed to a live paid flight is directionally observed to lift response versus running either alone. ${platformSummary}`
      ),
      accent: "rgba(63, 224, 160, 0.92)",
    },
    {
      key: "response-window",
      label: "Response Window",
      value: "48 hrs",
      copy: normalizeMicrositeDisplayTerminalPunctuation(
        retargetingSuppressed
          ? "Attention typically converts fastest in the short window after it peaks, so rapid follow-up in the first couple of days is where awareness momentum is usually won."
          : "Attention typically converts fastest in the short window after it peaks, so rapid retargeting and follow-up in the first couple of days is where awareness momentum is usually won."
      ),
      accent: "rgba(255, 107, 137, 0.9)",
    },
  ];

  return {
    key: "brand-awareness-strategy",
    title: `Making ${titleSubject} the story audiences keep hearing about`,
    intro: `Cphere treats brand awareness as an organic lift system for ${objectiveLabel}: create the conversation, capture proof, then let paid media amplify the strongest signal.`,
    cards,
    stats,
  };
};

const resolveFilmSourceRoleLabel = (card, hero = {}) => {
  const cardKey = resolveFilmSourceKey(card?.key || card?.sourceType || card?.title);
  const contentSourceKey = resolveFilmSourceKey(
    hero?.contentSourceKey || hero?.contentSourceLabel
  );
  const imageSourceKey = resolveFilmSourceKey(
    hero?.imageSourceKey || hero?.imageSourceLabel || hero?.sourceKey || hero?.sourceLabel
  );
  const roles = [];

  if (cardKey && cardKey === contentSourceKey) roles.push("Positioning");
  if (cardKey && cardKey === imageSourceKey) roles.push("Hero Art");

  return roles.join(" + ");
};

function MicrositeSourceButton({ card, hero = null, compact = false }) {
  const roleLabel = resolveFilmSourceRoleLabel(card, hero || {});
  const urlLabel = formatSourceUrlLabel(card?.url);

  return (
    <a
      className={`campaign-strategy-microsite-source-button${
        roleLabel ? " is-active" : ""
      }${compact ? " campaign-strategy-microsite-source-button--compact" : ""}`}
      href={card.url}
      target="_blank"
      rel="noreferrer"
    >
      <span className="campaign-strategy-microsite-source-button-copy">
        <span className="campaign-strategy-microsite-source-button-label">
          Open {card.title}
        </span>
        <span className="campaign-strategy-microsite-source-button-meta">
          {roleLabel || urlLabel}
        </span>
      </span>
    </a>
  );
}

export const buildMicrositeMediaInitialState = (preferPosterFrame = false) => ({
  failed: false,
  orientation: preferPosterFrame ? "portrait" : "standard",
  aspectRatio: "",
});

function MicrositeMediaArtwork({
  image,
  alt,
  className,
  fallbackLabel,
  fallbackImage = "",
  fallbackImageAlt = "Cphere strategy fallback artwork",
  hero = null,
  variant = "poster",
  preferPosterFrame = false,
}) {
  const imageRef = useRef(null);
  const [imageState, setImageState] = useState(() =>
    buildMicrositeMediaInitialState(preferPosterFrame)
  );
  const sourceBranding = resolveFilmSourceBranding(
    hero?.imageSourceKey || hero?.imageSourceLabel || hero?.sourceKey || hero?.sourceLabel
  );
  const sourceArtworkClassName = getFilmSourceArtworkClassName(
    hero?.imageSourceKey || hero?.imageSourceLabel || hero?.sourceKey || hero?.sourceLabel
  );
  const imageAlt = alt || hero?.title || fallbackLabel || "Campaign creative preview";
  const shouldRenderImage = image && !imageState.failed;
  useEffect(() => {
    setImageState(buildMicrositeMediaInitialState(preferPosterFrame));
  }, [image, preferPosterFrame]);

  useEffect(() => {
    const loadedImage = imageRef.current;
    if (!loadedImage?.complete || !loadedImage.naturalWidth || !loadedImage.naturalHeight) {
      return;
    }

    const orientation =
      loadedImage.naturalHeight > loadedImage.naturalWidth ? "portrait" : "landscape";
    const aspectRatio = `${loadedImage.naturalWidth} / ${loadedImage.naturalHeight}`;
    setImageState((current) =>
      current.orientation === orientation && current.aspectRatio === aspectRatio && !current.failed
        ? current
        : { failed: false, orientation, aspectRatio }
    );
  }, [image, preferPosterFrame]);

  const handleImageLoad = (event) => {
    const { naturalWidth, naturalHeight } = event.currentTarget;
    if (!naturalWidth || !naturalHeight) return;
    const orientation = naturalHeight > naturalWidth ? "portrait" : "landscape";
    const aspectRatio = `${naturalWidth} / ${naturalHeight}`;
    setImageState((current) =>
      current.orientation === orientation && current.aspectRatio === aspectRatio && !current.failed
        ? current
        : { failed: false, orientation, aspectRatio }
    );
  };
  const frameStyle = {
    ...buildMediaBackdropStyle(image),
    ...(imageState.aspectRatio
      ? { "--campaign-strategy-microsite-creative-aspect-ratio": imageState.aspectRatio }
      : {}),
  };
  const frameOrientationClass =
    imageState.orientation === "portrait"
      ? "hero-frame-portrait"
      : imageState.orientation === "landscape"
      ? "hero-frame-landscape"
      : "";
  const isPosterFrame =
    variant === "poster" &&
    (imageState.orientation === "portrait" ||
      (preferPosterFrame && imageState.orientation !== "landscape"));
  const frameBaseClass = isPosterFrame
    ? "campaign-strategy-microsite-poster-frame"
    : className;

  return (
    <div
      className={`${frameBaseClass} campaign-strategy-microsite-media-frame--${imageState.orientation}${
        frameOrientationClass ? ` ${frameOrientationClass}` : ""
      }${isPosterFrame ? " campaign-strategy-microsite-media-frame--poster-card" : ""}${
        sourceArtworkClassName ? ` ${sourceArtworkClassName}` : ""
      }`}
      style={frameStyle}
    >
      {shouldRenderImage && isPosterFrame ? (
        <div className="campaign-strategy-microsite-poster-frame-inner">
          {sourceBranding?.image ? (
            <div
              className={`campaign-strategy-microsite-media-brand-badge${
                sourceArtworkClassName ? ` ${sourceArtworkClassName}` : ""
              }`}
            >
              <img src={sourceBranding.image} alt={sourceBranding.label} />
            </div>
          ) : null}
          <img
            ref={imageRef}
            className={`campaign-strategy-microsite-poster-frame-image hero-image${
              sourceArtworkClassName ? ` ${sourceArtworkClassName}` : ""
            }`}
            src={image}
            alt={imageAlt}
            onLoad={handleImageLoad}
            onError={() => setImageState((current) => ({ ...current, failed: true }))}
          />
        </div>
      ) : shouldRenderImage ? (
        <div
          className={`campaign-strategy-microsite-media-stage campaign-strategy-microsite-media-stage--${variant} campaign-strategy-microsite-media-stage--${imageState.orientation}`}
        >
          <img
            className="campaign-strategy-microsite-media-backdrop"
            src={image}
            alt=""
            aria-hidden="true"
          />
          {sourceBranding?.image ? (
            <div
              className={`campaign-strategy-microsite-media-brand-badge${
                sourceArtworkClassName ? ` ${sourceArtworkClassName}` : ""
              }`}
            >
              <img src={sourceBranding.image} alt={sourceBranding.label} />
            </div>
          ) : null}
          <img
            ref={imageRef}
            className={`campaign-strategy-microsite-media-image campaign-strategy-microsite-media-image--contain hero-image${
              sourceArtworkClassName ? ` ${sourceArtworkClassName}` : ""
            }`}
            src={image}
            alt={imageAlt}
            onLoad={handleImageLoad}
            onError={() => setImageState((current) => ({ ...current, failed: true }))}
          />
        </div>
      ) : fallbackImage ? (
        <div className="campaign-strategy-microsite-hero-art-fallback">
          <img
            className="campaign-strategy-microsite-hero-art-fallback-image"
            src={fallbackImage}
            alt={fallbackImageAlt}
          />
        </div>
      ) : (
        <div className="campaign-strategy-microsite-hero-art-fallback">
          {fallbackLabel}
        </div>
      )}
    </div>
  );
}

function MicrositeSection({
  eyebrow,
  title,
  subtitle = "",
  meta = "",
  className = "",
  children,
}) {
  return (
    <section
      className={`campaign-strategy-microsite-section-card${className ? ` ${className}` : ""}`}
    >
      <div className="campaign-strategy-microsite-section-head">
        <div>
          {eyebrow ? (
            <span className="campaign-strategy-microsite-section-kicker">{eyebrow}</span>
          ) : null}
          <h3 className="campaign-strategy-microsite-section-title">{title}</h3>
          {subtitle ? (
            <p className="campaign-strategy-microsite-section-subtitle">{subtitle}</p>
          ) : null}
        </div>
        {meta ? <span className="campaign-strategy-microsite-section-meta">{meta}</span> : null}
      </div>
      {children}
    </section>
  );
}

function EmptyPanel({ children }) {
  return <div className="campaign-strategy-microsite-empty-panel">{children}</div>;
}

export function BrandAwarenessStrategySection({
  strategy,
  renderCopy = (value) => value,
  defaultFiguresApply = false,
}) {
  const copy = selectMicrositeCopy(useMicrositeBrandPresentation());
  const cards = Array.isArray(strategy?.cards)
    ? strategy.cards.filter((card) => card && card.title && card.copy)
    : [];
  // Its figures (77%, 3.2×, 48 hrs) are default figures
  // (micrositeShowsDefaultFigures): no saved strategy substantiates them.
  const stats =
    defaultFiguresApply && Array.isArray(strategy?.stats)
      ? strategy.stats.filter((stat) => stat && stat.value && stat.label)
      : [];

  if (!strategy || (!cards.length && !stats.length)) return null;

  return (
    <MicrositeSection
      eyebrow="Brand Awareness Strategy"
      title={strategy.title || "Brand Awareness Strategy"}
      meta={copy.organicLiftMeta}
    >
      {strategy.intro ? (
        <p className="campaign-strategy-microsite-section-intro">
          {renderCopy(strategy.intro)}
        </p>
      ) : null}

      {cards.length ? (
        <div className="campaign-strategy-microsite-brand-awareness-grid campaign-strategy-microsite-centered-card-grid campaign-strategy-microsite-centered-card-grid--2">
          {cards.map((card) => (
            <article
              key={card.key || card.title}
              className="campaign-strategy-microsite-brand-awareness-card"
              style={card.accent ? { "--brand-awareness-accent": card.accent } : undefined}
            >
              <div className="campaign-strategy-microsite-card-header">
                <span className="campaign-strategy-microsite-card-title campaign-strategy-microsite-brand-awareness-card-title">
                  {card.title}
                </span>
                <span className="campaign-strategy-microsite-card-meta">
                  {card.meta || "Awareness"}
                </span>
              </div>
              <p className="campaign-strategy-microsite-brand-awareness-copy">
                {renderCopy(card.copy)}
              </p>
            </article>
          ))}
        </div>
      ) : null}

      {stats.length ? (
        <div className="campaign-strategy-microsite-brand-awareness-stat-grid campaign-strategy-microsite-centered-card-grid campaign-strategy-microsite-centered-card-grid--3">
          {stats.map((stat) => (
            <article
              key={stat.key || stat.label}
              className="campaign-strategy-microsite-brand-awareness-stat-card"
              style={stat.accent ? { "--brand-awareness-accent": stat.accent } : undefined}
            >
              <span className="campaign-strategy-microsite-brand-awareness-stat-label">
                {stat.label}
              </span>
              <strong className="campaign-strategy-microsite-brand-awareness-stat-value">
                {stat.value}
              </strong>
              {stat.copy ? (
                <p className="campaign-strategy-microsite-brand-awareness-stat-copy">
                  {renderCopy(stat.copy)}
                </p>
              ) : null}
            </article>
          ))}
        </div>
      ) : null}
    </MicrositeSection>
  );
}

function PlatformLogoFrame({ platform, className = "" }) {
  const brand = resolveMicrositePlatformBranding(platform) || PLATFORM_FALLBACK_BRANDING;

  return (
    <span
      className={`campaign-strategy-microsite-platform-logo-frame${className ? ` ${className}` : ""}`}
      aria-hidden="true"
    >
      <img src={brand.image} alt="" />
    </span>
  );
}

// Presentation contract v2: one bar per planning priority, grouped by what
// the priority is (utils/micrositeAudienceGroups readPlanningWeights), every
// bar on one scale, a market's or a concept's own signals beside its name.
function PlanningWeightBars({ planningWeights }) {
  const maxWeight = planningWeights?.maxWeight || 0;
  return (
    <div className="campaign-strategy-microsite-planning-weight-groups">
      {(planningWeights?.groups || []).map((group) => (
        <div
          key={group.category}
          className="campaign-strategy-microsite-planning-weight-group"
          data-planning-category={group.category}
        >
          {group.showLabel ? (
            <span className="campaign-strategy-microsite-planning-weight-group-label">{group.label}</span>
          ) : null}
          <div className="campaign-strategy-microsite-bar-list">
            {group.rows.map((row) => (
              <div key={row.key} className="campaign-strategy-microsite-bar-row">
                <div className="campaign-strategy-microsite-bar-row-head">
                  <span className="campaign-strategy-microsite-bar-label">
                    <span className="campaign-strategy-microsite-bar-label-text">{row.displayLabel}</span>
                    {row.chartDetail ? (
                      <span className="campaign-strategy-microsite-planning-weight-members">{row.chartDetail}</span>
                    ) : null}
                  </span>
                  <span className="campaign-strategy-microsite-bar-value">{formatPlanningWeight(row.weight)}</span>
                </div>
                <div className="campaign-strategy-microsite-bar-track">
                  <span
                    className="campaign-strategy-microsite-bar-fill"
                    style={{ width: `${maxWeight > 0 ? Math.max(8, (row.weight / maxWeight) * 100) : 8}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function MetricBarList({
  items = [],
  valueFormatter = null,
  showPercent = true,
  showPlatformLogos = false,
  labelFormatter = null,
  emptyCopy = "",
}) {
  const copy = selectMicrositeCopy(useMicrositeBrandPresentation());
  if (!items.length) {
    /*
     * This placeholder is CORRECT when there is genuinely no structured data.
     * It was wrong on Campaign 53, where a canonical allocation existed and
     * three other surfaces were already rendering it. Callers that know an
     * allocation exists pass their own copy rather than letting this claim
     * absence on their behalf.
     */
    return (
      <EmptyPanel>
        {emptyCopy || copy.chartEmpty}
      </EmptyPanel>
    );
  }

  const maxValue = items.reduce(
    (highest, item) => Math.max(highest, Number(item?.value || item?.share || 0)),
    0
  );

  return (
    <div className="campaign-strategy-microsite-bar-list">
      {items.map((item) => {
        const rawValue = Number(item?.value ?? item?.share ?? 0);
        const width = maxValue > 0 ? Math.max(8, (rawValue / maxValue) * 100) : 8;
        const label =
          (typeof valueFormatter === "function" && valueFormatter(item)) ||
          item?.shareLabel ||
          item?.budgetLabel ||
          item?.allocationLabel ||
          item?.primaryValue ||
          (showPercent ? `${Number(rawValue || 0).toFixed(0)}%` : formatMetricCount(rawValue, rawValue || "0"));
        const displayLabel = formatMicrositeTextValue(item?.label, "Audience group");
        const renderedLabel = displayMicrositePlatformIdentityLabel(
          typeof labelFormatter === "function" ? labelFormatter(displayLabel) : displayLabel,
          item?.key
        );

        return (
          <div key={item?.key || item?.label} className="campaign-strategy-microsite-bar-row">
            <div className="campaign-strategy-microsite-bar-row-head">
              <span
                className={`campaign-strategy-microsite-bar-label${
                  showPlatformLogos ? " campaign-strategy-microsite-bar-label-with-logo" : ""
                }`}
              >
                {showPlatformLogos ? (
                  <PlatformLogoFrame
                    platform={item}
                    className="campaign-strategy-microsite-platform-logo-frame--bar"
                  />
                ) : null}
                <span className="campaign-strategy-microsite-bar-label-text">
                  {renderedLabel}
                </span>
              </span>
              <span className="campaign-strategy-microsite-bar-value">{label}</span>
            </div>
            <div className="campaign-strategy-microsite-bar-track">
              <span
                className="campaign-strategy-microsite-bar-fill"
                style={{ width: `${width}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function FlightRhythmMiniChart({ items = [] }) {
  const copy = selectMicrositeCopy(useMicrositeBrandPresentation());
  const chartItems = normalizeFlightRhythmChartItems(items);
  if (!chartItems.length) return null;
  // The planner's proposed phases carry percentages and no dates: not a weekly rhythm.
  const proposedPhases = (Array.isArray(items) ? items : []).some(
    (item) => item?.planningStatus === "PROPOSED"
  );

  return (
    <div
      className="campaign-strategy-microsite-chart-card campaign-strategy-microsite-flight-rhythm-chart"
      data-testid="campaign-strategy-flight-rhythm-chart"
    >
      <div className="campaign-strategy-microsite-card-header">
        <span className="campaign-strategy-microsite-card-title">Flight Rhythm</span>
        <span className="campaign-strategy-microsite-card-meta">
          {proposedPhases ? "Proposed phases" : copy.flightRhythmMeta}
        </span>
      </div>
      <div className="campaign-strategy-microsite-flight-rhythm-bars">
        {chartItems.map((item) => (
          <div
            key={item.key}
            className="campaign-strategy-microsite-flight-rhythm-bar"
            data-phase-height={item.height}
            style={{ "--phase-height": item.height }}
          >
            <div
              className="campaign-strategy-microsite-flight-rhythm-bar-shell"
              aria-label={`${item.label} ${item.allocationLabel}`}
            >
              <span className="campaign-strategy-microsite-flight-rhythm-bar-fill" />
            </div>
            <span className="campaign-strategy-microsite-flight-rhythm-label">
              {item.shortLabel}
            </span>
            <strong className="campaign-strategy-microsite-flight-rhythm-value">
              {item.allocationLabel}
            </strong>
          </div>
        ))}
      </div>
    </div>
  );
}

const formatImpressionOutlookAriaValue = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "estimated impressions unavailable";
  // The same digits the tile shows — never "311.1 thousand" beside "311,000".
  return `estimated ${describeMetricCount(numeric, "impression")}`;
};

export function ImpressionOutlookBars({ entries = [] }) {
  const { activePopover, showPopover, hidePopover } = useAnchoredChartPopover();
  const maxValue = Math.max(...entries.map((entry) => Number(entry.rawValue) || 0), 1);
  const tooltipId = "impression-outlook-platform-tooltip";

  return (
    <>
      <ChartPopoverPortal popover={activePopover} />
      <div className="campaign-strategy-microsite-trend-strip" data-testid="impression-outlook-bars">
        {entries.map((entry) => (
          <span
            key={entry.key || entry.label}
            className="campaign-strategy-microsite-trend-bar"
            data-platform-key={entry.key}
            {...getChartInteractionProps(
              entry.tooltipDatum,
              "name",
              showPopover,
              hidePopover,
              undefined,
              {
                tooltipId,
                showPlatformLogo: true,
                ariaLabel:
                  entry.ariaLabel || `${entry.label}, ${formatImpressionOutlookAriaValue(entry.rawValue)}`,
              }
            )}
            style={{
              height: `${Math.max(28, Math.min(100, (entry.rawValue / maxValue) * 100))}%`,
            }}
          />
        ))}
      </div>
    </>
  );
}

/*
 * The Impression Outlook in proposal mode (CampaignProposalPlanning
 * buildProposalImpressionOutlook): the same two cards, re-sourced. The headline
 * is the current scenario's broad forecast — never a total — and the qualified
 * planning estimate sits beside it as an alternative case.
 */
export function ProposalImpressionOutlook({ outlook }) {
  if (!outlook) return null;
  return (
    <div
      className="campaign-strategy-microsite-visual-grid campaign-strategy-microsite-proposal-outlook"
      data-testid="proposal-impression-outlook"
    >
      <article className="campaign-strategy-microsite-visual-card campaign-strategy-microsite-visual-card--impression-outlook">
        <div className="campaign-strategy-microsite-card-header">
          <span className="campaign-strategy-microsite-card-title">Impression Outlook</span>
          <span className="campaign-strategy-microsite-card-meta">{outlook.meta}</span>
        </div>
        <div className="campaign-strategy-microsite-impression-total-tile">
          <span>{outlook.headline.label}</span>
          <strong>{outlook.headline.value}</strong>
        </div>
        <p className="campaign-strategy-microsite-proposal-outlook-scenario">{outlook.scenarioLabel}</p>
        <ImpressionOutlookBars entries={outlook.bars} />
        <div className="campaign-strategy-microsite-mini-stat-grid campaign-strategy-microsite-centered-card-grid campaign-strategy-microsite-centered-card-grid--3">
          {outlook.cases.map((entry) => (
            <div
              key={entry.key}
              className="campaign-strategy-microsite-mini-stat-card campaign-strategy-microsite-proposal-outlook-case"
              data-planning-case={entry.key}
            >
              <div className="campaign-strategy-microsite-mini-stat-platform-head">
                <PlatformLogoFrame
                  platform={{ key: entry.channelKey }}
                  className="campaign-strategy-microsite-platform-logo-frame--mini"
                />
                <span>{entry.label}</span>
              </div>
              <strong>{entry.value}</strong>
              <small>{entry.note}</small>
              <dl>
                {entry.figures.map(([label, value]) => (
                  <div key={label}>
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
        {outlook.alternates.map((alternate) => (
          <div key={alternate.key} className="campaign-strategy-microsite-proposal-outlook-alternate">
            <span>{alternate.label}</span>
            <dl>
              {alternate.figures.map(([label, value]) => (
                <div key={label}>
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
            {alternate.note ? <small>{alternate.note}</small> : null}
          </div>
        ))}
      </article>

      <article className="campaign-strategy-microsite-visual-card campaign-strategy-microsite-visual-card--cpm-envelope">
        <div className="campaign-strategy-microsite-card-header">
          <span className="campaign-strategy-microsite-card-title">{outlook.cpm.title}</span>
          <span className="campaign-strategy-microsite-card-meta">{outlook.cpm.meta}</span>
        </div>
        <div className="campaign-strategy-microsite-cpm-chip-grid campaign-strategy-microsite-centered-card-grid campaign-strategy-microsite-centered-card-grid--3">
          {outlook.cpm.cards.map((entry) => (
            <article
              key={entry.key}
              className="campaign-strategy-microsite-cpm-chip-card"
              data-planning-case={entry.key}
            >
              <div className="campaign-strategy-microsite-cpm-platform-head">
                <PlatformLogoFrame
                  platform={{ key: entry.channelKey }}
                  className="campaign-strategy-microsite-platform-logo-frame--mini"
                />
                <span>{entry.label}</span>
              </div>
              <strong>{entry.value}</strong>
              <small>{entry.meta}</small>
            </article>
          ))}
        </div>
      </article>
    </div>
  );
}

/* Funnel Architecture estimate rows in proposal mode: labelled cases, no total. */
export function ProposalFunnelEstimateRows({ outlook }) {
  if (!outlook) return null;
  return (
    <div
      className="campaign-strategy-microsite-estimate-list campaign-strategy-microsite-proposal-estimate-list"
      data-testid="proposal-funnel-estimates"
    >
      {outlook.funnelRows.map((row) => (
        <div key={row.key} className="campaign-strategy-microsite-estimate-row" data-planning-case={row.key}>
          <span className="campaign-strategy-microsite-estimate-platform" title={row.label}>
            <PlatformLogoFrame
              platform={{ key: row.channelKey }}
              className="campaign-strategy-microsite-platform-logo-frame--mini"
            />
            <span className="campaign-strategy-microsite-estimate-platform-label">{row.label}</span>
          </span>
          <strong>{row.value}</strong>
        </div>
      ))}
      <p className="campaign-strategy-microsite-proposal-outlook-scenario">
        Alternative planning cases for the same working media, not a total.
      </p>
    </div>
  );
}

function FlowStages({ items = [], defaultFiguresApply = false }) {
  const copy = selectMicrositeCopy(useMicrositeBrandPresentation());
  if (!items.length) {
    return <EmptyPanel>{copy.flowStagesEmpty}</EmptyPanel>;
  }

  return (
    <div className="campaign-strategy-microsite-flow-grid campaign-strategy-microsite-centered-card-grid campaign-strategy-microsite-centered-card-grid--3">
      {items.map((item) => (
        <article
          key={item?.key || item?.label}
          className="campaign-strategy-microsite-flow-card"
        >
          <div className="campaign-strategy-microsite-flow-topline">
            <span className="campaign-strategy-microsite-flow-title">{item?.label}</span>
            {defaultFiguresApply ? (
              <span className="campaign-strategy-microsite-flow-share">
                {item?.allocationLabel || `${item?.allocation || 0}%`}
              </span>
            ) : null}
          </div>
          <div className="campaign-strategy-microsite-flow-chip">{item?.audience}</div>
          <p>{item?.messaging}</p>
          <span className="campaign-strategy-microsite-flow-note">{item?.trigger}</span>
        </article>
      ))}
    </div>
  );
}

export const normalizeRetargetingPerformanceChart = (chart = {}) => {
  const windows = Array.isArray(chart?.windows) ? chart.windows.filter(Boolean) : [];
  const series = (Array.isArray(chart?.series) ? chart.series : [])
    .map((entry, index) => ({
      key: entry?.key || entry?.label || `series-${index + 1}`,
      label: formatMicrositeTextValue(entry?.label, `Series ${index + 1}`),
      values: Array.isArray(entry?.values)
        ? entry.values.map((value) => Number(value)).filter((value) => Number.isFinite(value))
        : [],
    }))
    .filter((entry) => entry.values.length);
  return { windows, series };
};

export function RetargetingPerformanceChart({ chart = {} }) {
  const brandPresentation = useMicrositeBrandPresentation();
  const copy = selectMicrositeCopy(brandPresentation);
  const normalized = normalizeRetargetingPerformanceChart(chart);
  if (!normalized.windows.length || !normalized.series.length) {
    return <EmptyPanel>{copy.retargetingChartEmpty}</EmptyPanel>;
  }

  const maxValue = Math.max(
    ...normalized.series.flatMap((entry) => entry.values),
    1
  );
  const chartGridStyle = {
    "--retargeting-window-count": normalized.windows.length,
  };

  return (
    <>
    <div
      className="campaign-strategy-microsite-retargeting-chart"
      data-testid="retargeting-performance-chart"
      style={chartGridStyle}
    >
      <div className="campaign-strategy-microsite-card-header">
        <span className="campaign-strategy-microsite-card-title">
          {brandPresentation
            ? formatMicrositeTextValue(chart?.title, "").replace(/^Retargeting Performance\b/, "Retargeting Response Pattern") ||
              copy.retargetingChartTitleFallback
            : chart?.title || copy.retargetingChartTitleFallback}
        </span>
        <span className="campaign-strategy-microsite-card-meta">
          {brandPresentation ? (chart?.metricLabel ? `${chart.metricLabel} · illustrative` : "Illustrative") : chart?.metricLabel || "CTR"}
        </span>
      </div>
      <div className="campaign-strategy-microsite-retargeting-chart-grid">
        {normalized.series.map((entry) => (
          <div key={entry.key} className="campaign-strategy-microsite-retargeting-chart-row">
            <span className="campaign-strategy-microsite-retargeting-chart-label">
              {entry.label}
            </span>
            <div className="campaign-strategy-microsite-retargeting-chart-bars">
              {normalized.windows.map((windowLabel, index) => {
                const value = entry.values[index] || 0;
                return (
                  <span
                    key={`${entry.key}-${windowLabel}`}
                    className="campaign-strategy-microsite-retargeting-chart-bar"
                    style={{ height: `${Math.max(12, (value / maxValue) * 100)}%` }}
                    title={`${entry.label} ${windowLabel}: ${value}%`}
                  >
                    <span className="campaign-strategy-microsite-retargeting-chart-value">
                      {value}%
                    </span>
                  </span>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <div className="campaign-strategy-microsite-retargeting-chart-axis">
        {normalized.windows.map((windowLabel) => (
          <span key={windowLabel}>{windowLabel}</span>
        ))}
      </div>
    </div>
    {brandPresentation ? (
      <p className="campaign-strategy-microsite-chart-note">
        Illustrative response pattern used for planning — a Cphere planning assumption, not measured performance for this campaign.
      </p>
    ) : null}
    </>
  );
}

// Canonical benchmark metric identity. Every benchmark surface — legend swatch,
// plotted bar, value chip, and platform card — resolves a metric's identity
// through this one map, so CPM, CPC, CTR and VCR each own a distinct treatment
// instead of collapsing into a shared "cost" / "rate" colour family. The short
// code is printed beside the value so the metric survives without colour for
// readers who cannot separate the four accents.
export const BENCHMARK_METRIC_SHORT_LABELS = Object.freeze({
  cpm: "CPM",
  cpc: "CPC",
  ctr: "CTR",
  vcr: "VCR",
});

export const resolveBenchmarkMetricShortLabel = (metric) => {
  const key = String(metric?.key || metric || "").trim().toLowerCase();
  const supplied = String(metric?.shortLabel || "").trim();
  if (supplied) return supplied;
  return BENCHMARK_METRIC_SHORT_LABELS[key] || (key ? key.toUpperCase() : "");
};

// Phase 2 — Benchmark grouped-bar chart. Reuses the microsite's CSS-bar chart
// language (same as the retargeting performance chart). Each metric is scaled to
// its own max so different-scale planning benchmarks (CPM $, CTR %) read as a
// comparable relative-efficiency view. Renders only when the backend supplies it.
// Resolves the benchmark bars a row should render. Prefers the per-platform
// `row.metrics` model (each platform reports only its appropriate metrics — e.g.
// CTV reports a completion rate, never a CTR), and falls back to the legacy flat
// cpm/ctr shape keyed off the chart-level legend metrics for older payloads.
const resolveBenchmarkRowBars = (row, legendMetrics, substantiatedOnly = false) => {
  if (Array.isArray(row?.metrics) && row.metrics.length) {
    return row.metrics
      .map((metric) => {
        const value = Number(metric?.value);
        if (!Number.isFinite(value) || value <= 0) return null;
        // A bar the backend does not mark substantiated (a directional rate with
        // no source, sample or window) is a default figure.
        if (substantiatedOnly && metric?.substantiated !== true) return null;
        return {
          key: metric.key,
          label: metric.label || metric.key,
          shortLabel: resolveBenchmarkMetricShortLabel(metric),
          value,
          valueLabel: metric.valueLabel || String(value),
        };
      })
      .filter(Boolean);
  }
  // The flat form carries no substantiation: nothing in it is known to be sourced.
  if (substantiatedOnly) return [];
  return (Array.isArray(legendMetrics) ? legendMetrics : [])
    .map((metric) => {
      const value = Number(row?.[metric.key]);
      if (!Number.isFinite(value) || value <= 0) return null;
      return {
        key: metric.key,
        label: metric.label || metric.key,
        shortLabel: resolveBenchmarkMetricShortLabel(metric),
        value,
        valueLabel: row?.[`${metric.key}Label`] || String(value),
      };
    })
    .filter(Boolean);
};

// Guard/normalize the backend Cphere Intelligence block. Fails closed (null) so
// the proprietary panel never renders empty or fabricated when data is absent.
export const resolveCphereIntelligence = (section = {}) => {
  const intelligence =
    section && typeof section === "object" && section.intelligence && typeof section.intelligence === "object"
      ? section.intelligence
      : null;
  if (!intelligence) return null;
  const cards = Array.isArray(intelligence.cards)
    ? intelligence.cards.filter((card) => card && card.learning)
    : [];
  if (!cards.length) return null;
  return {
    label: intelligence.label || "Cphere Intelligence",
    disclaimer: intelligence.disclaimer || "",
    cards,
  };
};

// Proprietary, per-platform "Cphere Learning" panel — observed entertainment
// patterns framed as directional context, not guarantees. Fail-closed.
export function CphereIntelligencePanel({ intelligence, allocationRows = [] }) {
  const resolved =
    intelligence && Array.isArray(intelligence.cards)
      ? intelligence
      : resolveCphereIntelligence({ intelligence });
  if (!resolved) return null;
  const cards = Array.isArray(resolved.cards)
    ? orderMicrositePlatformRows(
        resolved.cards.filter((card) => card && card.learning),
        allocationRows
      )
    : [];
  if (!cards.length) return null;
  const desktopFinalRowCount = cards.length % 2 || 2;

  return (
    <div className="campaign-strategy-microsite-intelligence-panel" data-testid="cphere-intelligence-panel">
      <div className="campaign-strategy-microsite-intelligence-head">
        <span className="campaign-strategy-microsite-section-kicker">
          {resolved.label || "Cphere Intelligence"}
        </span>
        {resolved.disclaimer ? (
          <span className="campaign-strategy-microsite-intelligence-disclaimer">
            {resolved.disclaimer}
          </span>
        ) : null}
      </div>
      <div
        className={`campaign-strategy-microsite-intelligence-grid campaign-strategy-microsite-intelligence-grid--desktop-final-row-${desktopFinalRowCount} campaign-strategy-microsite-centered-card-grid campaign-strategy-microsite-centered-card-grid--4-to-3`}
        data-desktop-final-row-count={desktopFinalRowCount}
      >
        {cards.map((card, index) => (
          <article
            key={card.key || `intel-${index}`}
            className="campaign-strategy-microsite-intelligence-card"
          >
            <div className="campaign-strategy-microsite-intelligence-card-head">
              <span className="campaign-strategy-microsite-intelligence-platform-wrap">
                <PlatformLogoFrame
                  platform={card.key || card.label}
                  className="campaign-strategy-microsite-platform-logo-frame--intelligence"
                />
                <span className="campaign-strategy-microsite-intelligence-platform">
                  {displayMicrositePlatformIdentityLabel(card.label, card.key)}
                </span>
              </span>
              <span className="campaign-strategy-microsite-intelligence-tag">Learning</span>
            </div>
            <p className="campaign-strategy-microsite-intelligence-learning">{card.learning}</p>
            {card.whyItMatters ? (
              <p className="campaign-strategy-microsite-intelligence-why">
                <span className="campaign-strategy-microsite-kv-label">Why It Matters</span>
                {card.whyItMatters}
              </p>
            ) : null}
          </article>
        ))}
      </div>
    </div>
  );
}

// Deterministic platform-count-aware plot sizing. The plotting region no
// longer stretches to the card width: its maximum width is the sum of fixed
// category slots (plotWidth = clamp(count × minSlot, count × slot, available)),
// so one or two platforms render as a compact centered chart while large
// platform sets fill the card at a readable minimum slot width and fall back
// to contained horizontal scrolling on narrow screens.
export const BENCHMARK_CHART_SLOT_WIDTH = 148;
export const BENCHMARK_CHART_MIN_SLOT_WIDTH = 96;
export const BENCHMARK_CHART_GROUP_GAP = 24;

export const resolveBenchmarkChartLayout = (platformCount) => {
  const count = Math.max(0, Math.floor(Number(platformCount) || 0));
  const gapTotal = count > 0 ? (count - 1) * BENCHMARK_CHART_GROUP_GAP : 0;
  return {
    slotWidth: BENCHMARK_CHART_SLOT_WIDTH,
    minSlotWidth: BENCHMARK_CHART_MIN_SLOT_WIDTH,
    groupGap: BENCHMARK_CHART_GROUP_GAP,
    plotMaxWidth: count > 0 ? count * BENCHMARK_CHART_SLOT_WIDTH + gapTotal : 0,
    minPlotWidth: count > 0 ? count * BENCHMARK_CHART_MIN_SLOT_WIDTH + gapTotal : 0,
  };
};

export function BenchmarkEfficiencyChart({ chart, allocationRows = [], defaultFiguresApply = false }) {
  if (!chart || typeof chart !== "object") return null;
  const rows = orderMicrositePlatformRows(
    Array.isArray(chart.rows) ? chart.rows : [],
    allocationRows
  );
  if (!rows.length) return null;

  const rowBars = rows.map((row) =>
    resolveBenchmarkRowBars(row, Array.isArray(chart.metrics) ? chart.metrics : [], !defaultFiguresApply)
  );
  if (!rowBars.some((bars) => bars.length)) return null;
  // The legend names only the metrics that are drawn.
  const legendMetrics = (Array.isArray(chart.metrics) ? chart.metrics : []).filter(
    (metric) => defaultFiguresApply || rowBars.some((bars) => bars.some((bar) => bar.key === metric.key))
  );

  const layout = resolveBenchmarkChartLayout(rows.length);

  // Normalize each bar within its own metric so unlike benchmarks (e.g. a ~1%
  // CTR and a ~94% completion rate) scale independently and don't distort one
  // another on the shared bar axis.
  const maxByMetric = {};
  rowBars.forEach((bars) => {
    bars.forEach((bar) => {
      maxByMetric[bar.key] = Math.max(maxByMetric[bar.key] || 0, bar.value);
    });
  });

  return (
    <div
      className="campaign-strategy-microsite-chart-card campaign-strategy-microsite-benchmark-chart"
      data-testid="benchmark-efficiency-chart"
    >
      <div className="campaign-strategy-microsite-card-header">
        <span className="campaign-strategy-microsite-card-title">
          {chart.label || "Platform Performance Benchmarks"}
        </span>
        <span className="campaign-strategy-microsite-card-meta">Directional</span>
      </div>
      <div className="campaign-strategy-microsite-benchmark-chart-legend">
        {legendMetrics.map((metric) => (
          <span
            key={metric.key}
            className={`campaign-strategy-microsite-benchmark-chart-legend-item campaign-strategy-microsite-benchmark-chart-legend-item--${metric.key}`}
          >
            {metric.label}
          </span>
        ))}
      </div>
      <div
        className="campaign-strategy-microsite-benchmark-chart-groups"
        style={{ "--benchmark-chart-plot-max-width": `${layout.plotMaxWidth}px` }}
      >
        {rows.map((row, rowIndex) => {
          const rowLabel = displayMicrositePlatformIdentityLabel(row.label, row.key);
          return (
            <div key={row.key} className="campaign-strategy-microsite-benchmark-chart-group">
              <div className="campaign-strategy-microsite-benchmark-chart-group-values">
                {rowBars[rowIndex].map((bar) => (
                  <span
                    key={bar.key}
                    className={`campaign-strategy-microsite-benchmark-chart-group-value campaign-strategy-microsite-benchmark-chart-group-value--${bar.key}`}
                    aria-label={`${rowLabel} ${bar.label}: ${bar.valueLabel}`}
                  >
                    <span
                      className="campaign-strategy-microsite-benchmark-chart-group-value-metric"
                      aria-hidden="true"
                    >
                      {bar.shortLabel}
                    </span>
                    <span className="campaign-strategy-microsite-benchmark-chart-group-value-amount">
                      {bar.valueLabel}
                    </span>
                  </span>
                ))}
              </div>
              <div className="campaign-strategy-microsite-benchmark-chart-bars" aria-hidden="true">
                {rowBars[rowIndex].map((bar) => {
                  const max = maxByMetric[bar.key] || 0;
                  const hasValue = bar.value > 0 && max > 0;
                  const isMaxValue = hasValue && bar.value === max;
                  return (
                    <span
                      key={bar.key}
                      className={`campaign-strategy-microsite-benchmark-chart-bar campaign-strategy-microsite-benchmark-chart-bar--${bar.key}${
                        isMaxValue ? " campaign-strategy-microsite-benchmark-chart-bar--max" : ""
                      }`}
                      data-max-value={isMaxValue ? "true" : undefined}
                      style={{ height: hasValue ? `${Math.max(10, (bar.value / max) * 100)}%` : "10%" }}
                      title={`${rowLabel} · ${bar.label}: ${bar.valueLabel}`}
                    />
                  );
                })}
              </div>
              <span className="campaign-strategy-microsite-benchmark-chart-group-label">
                {rowLabel}
              </span>
            </div>
          );
        })}
      </div>
      {chart.disclaimer ? (
        <span className="campaign-strategy-microsite-benchmark-chart-disclaimer">
          {chart.disclaimer}
        </span>
      ) : null}
    </div>
  );
}

const EARNED_MEDIA_DATA_NEEDED = "Data Needed";
const EARNED_MEDIA_RESEARCH_REQUIRED = "Research Required";
const EARNED_MEDIA_PENDING_VERIFICATION = "Pending Verification";
const EARNED_MEDIA_SOCIAL_FOOTPRINT_PENDING = "Social Footprint Pending";
const EARNED_MEDIA_HANDLE_NEEDED = "Handle Needed";
const EARNED_MEDIA_ENGAGEMENT_PENDING = "Benchmark Pending";
const EARNED_MEDIA_ENGAGEMENT_QUALITY_PENDING = "Engagement Benchmark Pending";
const EARNED_MEDIA_OPPORTUNITY_PENDING = "Opportunity Pending";
const EARNED_MEDIA_DISPLAY_REACH_OPPORTUNITY = "Earned Reach Opportunity";
const EARNED_MEDIA_DISPLAY_OPPORTUNITY_MAPPING = "Opportunity Mapping";
const EARNED_MEDIA_DISPLAY_RESEARCH_PRIORITY = "Research Priority";
const EARNED_MEDIA_DATA_NEEDED_CALLOUT =
  "Main cast/director discovery is active. Verified social handles, follower counts, and recent post performance can further strengthen reach charts, engagement benchmarks, and amplification priorities.";
const EARNED_MEDIA_SOCIAL_NEEDED_CALLOUT =
  EARNED_MEDIA_DATA_NEEDED_CALLOUT;
const EARNED_MEDIA_VALUE_FALLBACK_COPY =
  "Earned-media value scoring requires verified cast, director, talent, or partner social data. Add official handles, audience fit, engagement quality, recent post performance, and conversion signals to generate ranked value opportunities.";
const EARNED_MEDIA_SOCIAL_FOOTPRINT_FALLBACK_COPY =
  "Social footprint data was not provided. Add official Instagram, TikTok, X, YouTube, follower counts, recent engagement, and content history to generate platform-level social cards.";
// Shown instead of the "not provided" copy once analyzed submitted social
// profiles exist: acknowledges the social footprint is in use while still
// (honestly) requesting cast/director metadata for talent-level scoring.
const EARNED_MEDIA_SOCIAL_FOOTPRINT_ACTIVE_COPY =
  "Submitted social footprint metrics are active in Submitted Social Footprint Intelligence above. Cast/director social metadata is still needed to score talent-level footprint.";
const EARNED_MEDIA_VALUE_SOCIAL_ACTIVE_COPY =
  "Submitted social footprint metrics are available above for directional value scoring. Add verified cast, director, or talent data to score talent-level earned-media value.";

// Strategic field labels (Rationale, Primary Role, Core Audience, Opportunity Mapping, …)
// are sometimes parsed out of media-brief text and mistaken for earned-media entities.
// They are not cast/talent/social records and must never become their own cards.
const EARNED_MEDIA_STRATEGIC_LABELS = new Set([
  "rationale",
  "primary role",
  "core audience",
  "opportunity mapping",
  "primary kpi",
  "objective",
  "summary",
  "strategy",
  "strategic thesis",
  "positioning",
  "positioning statement",
  "audience",
  "recommended use",
  "earned reach opportunity",
  "research priority",
  "source",
]);

const normalizeEarnedMediaLabelKey = (value) =>
  formatMicrositeTextValue(value, "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/[^a-z0-9 ]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const isEarnedMediaStrategicLabel = (value) =>
  EARNED_MEDIA_STRATEGIC_LABELS.has(normalizeEarnedMediaLabelKey(value));

// A stable signature for collapsing duplicated fallback rows (same name + body + source).
const buildEarnedMediaRowSignature = (row = {}) =>
  [
    normalizeEarnedMediaLabelKey(row.name || row.title || row.person || row.label),
    normalizeEarnedMediaLabelKey(
      row.recommendedUse || row.recommended_use || row.copy || row.rationale || row.description
    ),
    normalizeEarnedMediaLabelKey(row.source || row.type || row.credit),
  ].join("|");

const dropEarnedMediaStrategicLabelRows = (rows = []) =>
  (Array.isArray(rows) ? rows : []).filter(
    (row) => !isEarnedMediaStrategicLabel(row?.name || row?.title || row?.person || row?.label)
  );

const dedupeEarnedMediaRows = (rows = []) => {
  const seen = new Set();
  return (Array.isArray(rows) ? rows : []).filter((row) => {
    const signature = buildEarnedMediaRowSignature(row);
    if (seen.has(signature)) return false;
    seen.add(signature);
    return true;
  });
};

const isEarnedMediaPendingValue = (value) => {
  const text = formatMicrositeTextValue(value, "");
  if (!text) return true;
  return [
    EARNED_MEDIA_DATA_NEEDED,
    EARNED_MEDIA_RESEARCH_REQUIRED,
    EARNED_MEDIA_PENDING_VERIFICATION,
    EARNED_MEDIA_SOCIAL_FOOTPRINT_PENDING,
    EARNED_MEDIA_HANDLE_NEEDED,
    EARNED_MEDIA_ENGAGEMENT_PENDING,
    EARNED_MEDIA_ENGAGEMENT_QUALITY_PENDING,
    EARNED_MEDIA_OPPORTUNITY_PENDING,
    EARNED_MEDIA_DISPLAY_REACH_OPPORTUNITY,
    EARNED_MEDIA_DISPLAY_OPPORTUNITY_MAPPING,
    EARNED_MEDIA_DISPLAY_RESEARCH_PRIORITY,
    "Follower Count Pending",
    "Research Pending",
  ].some((label) => text.toLowerCase() === label.toLowerCase());
};

const parseEarnedMediaCount = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const text = formatMicrositeTextValue(value, "").replace(/,/g, "").trim();
  if (!text) return null;
  const compactMatch = text.match(/^(\d+(?:\.\d+)?)\s*([kmb])$/i);
  if (compactMatch) {
    const multiplier = { k: 1000, m: 1000000, b: 1000000000 }[
      compactMatch[2].toLowerCase()
    ];
    return Number(compactMatch[1]) * multiplier;
  }
  if (!/^\d+(?:\.\d+)?$/.test(text)) return null;
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatEarnedMediaCount = (value, fallback = EARNED_MEDIA_DATA_NEEDED) => {
  const numeric = parseEarnedMediaCount(value);
  if (Number.isFinite(numeric)) return formatMetricCount(numeric);
  return formatMicrositeTextValue(value, fallback);
};

const normalizeEarnedMediaScore = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  if (numeric <= 1) return Math.max(0, Math.min(100, Math.round(numeric * 100)));
  return Math.max(0, Math.min(100, Math.round(numeric)));
};

const hasEarnedMediaFollowerSignal = (platform = {}) => {
  const followerSource = formatMicrositeTextValue(platform.followerSource, "");
  return (
    (followerSource && followerSource !== "needed" && !isEarnedMediaPendingValue(platform.followers)) ||
    Number.isFinite(parseEarnedMediaCount(platform.followers)) ||
    /^estimated tier:/i.test(formatMicrositeTextValue(platform.followers, ""))
  );
};

const hasEarnedMediaPlatformSignal = (platform = {}) =>
  Boolean(
    platform &&
      typeof platform === "object" &&
      ((!isEarnedMediaPendingValue(platform.handle) && platform.handle) ||
        hasEarnedMediaFollowerSignal(platform) ||
        (!isEarnedMediaPendingValue(platform.engagementRate) && platform.engagementRate) ||
        (!isEarnedMediaPendingValue(platform.engagementQuality) && platform.engagementQuality))
  );

const getEarnedMediaRoleType = (row = {}) => {
  const role = formatMicrositeTextValue(row.role || row.credit || row.type, "");
  const roleContext = [
    role,
    formatMicrositeTextValue(row.knownFor || row.known_for, ""),
    formatMicrositeTextValue(row.audienceFit || row.audience_fit, ""),
  ]
    .filter(Boolean)
    .join(" ");
  const billingOrder = Number.parseInt(row.billingOrder ?? row.billing_order ?? row.order, 10);
  if (/documentary|subject[-\s]?driven|real[-\s]?world|subject matter/i.test(roleContext)) {
    return "documentary-subject";
  }
  if (/director/i.test(role)) return "director";
  if (/writer|screenplay|producer/i.test(role)) return "producer-writer";
  if (/lead|star|top-billed/i.test(role) || (/actor|cast/i.test(role) && (!billingOrder || billingOrder <= 2))) {
    return "lead-actor";
  }
  if (/supporting|actor|cast/i.test(role)) return "supporting-actor";
  return "partner-voice";
};

const getEarnedMediaResearchPriority = (row = {}) => {
  if (row.isDataNeeded) return EARNED_MEDIA_DISPLAY_RESEARCH_PRIORITY;
  switch (getEarnedMediaRoleType(row)) {
    case "director":
    case "lead-actor":
    case "documentary-subject":
      return "High Research Priority";
    case "supporting-actor":
    case "producer-writer":
      return "Medium Research Priority";
    default:
      return EARNED_MEDIA_DISPLAY_RESEARCH_PRIORITY;
  }
};

const buildRoleAwareEarnedMediaRecommendation = (row = {}) => {
  switch (getEarnedMediaRoleType(row)) {
    case "director":
      return "Use for director commentary, behind-the-scenes context, earned press interviews, festival/critic quote amplification, and credibility-building creative clips.";
    case "lead-actor":
      return "Use for character-focused trailer clips, cast reactions, fan Q&A, interview cutdowns, and opening-weekend reminder posts.";
    case "supporting-actor":
      return "Use for ensemble clips, quote graphics, niche audience outreach, podcast/interview angles, and social proof moments.";
    case "documentary-subject":
      return "Use the subject matter, real-world controversy, topical press moments, author/interview clips, and cultural conversation as earned-media fuel.";
    case "producer-writer":
      return "Credibility pieces, origin story, industry press, podcasts, and advocacy/community angles where relevant.";
    default:
      return "Map verified social footprint to earned reach opportunities, then prioritize organic moments with strong engagement quality for paid amplification.";
  }
};

const buildRoleAwareEarnedMediaContentFormats = (row = {}) => {
  switch (getEarnedMediaRoleType(row)) {
    case "director":
      return ["Director commentary", "Behind-the-scenes clips", "Press quote cards"];
    case "lead-actor":
      return ["Trailer reaction clips", "Character featurettes", "Fan Q&A"];
    case "supporting-actor":
      return ["Ensemble clips", "Quote graphics", "Community interview clips"];
    case "producer-writer":
      return ["Origin story clips", "Podcast excerpts", "Credibility quote cards"];
    default:
      return ["Social posts", "Interview clips", "Short-form earned-media moments"];
  }
};

const buildRoleAwareEarnedMediaOutreachTargets = (row = {}) => {
  switch (getEarnedMediaRoleType(row)) {
    case "director":
      return ["Film interviews", "Behind-the-scenes features", "Critic and festival quote amplification"];
    case "lead-actor":
      return ["Entertainment interviews", "Fan communities", "Short-form social channels"];
    case "supporting-actor":
      return ["Genre/community outlets", "Ensemble interviews", "Niche fan groups"];
    case "producer-writer":
      return ["Industry press", "Podcasts", "Advocacy/community outlets where relevant"];
    default:
      return ["Press", "Podcasts", "Creator and community partners"];
  }
};

const normalizeEarnedMediaPersonName = (value) => {
  const text = formatMicrositeTextValue(value, "");
  if (!text) return "";
  if (/^(?:role|credit|type|name|person|talent|cast|actor)$/i.test(text)) return "";
  if (/^role\s*[:=-]/i.test(text)) return "";
  return text;
};

const buildEarnedMediaFallbackName = (row = {}) =>
  row.isDataNeeded ? "Earned Media Opportunity Inputs" : "Primary Cast / Director Opportunity";

const getEarnedMediaResearchPriorityLevel = (row = {}) => {
  const priority = getEarnedMediaResearchPriority(row);
  if (/high/i.test(priority)) return "High";
  if (/medium/i.test(priority)) return "Medium";
  if (/low/i.test(priority)) return "Low";
  // Unknown level: "Pending", never the label itself ("Research Priority:
  // Research Priority").
  return "Pending";
};

const hasEarnedMediaScoreData = (row = {}) =>
  [
    row.audienceFitScore,
    row.engagementQualityScore,
    row.conversionPotentialScore,
    row.amplificationPriorityScore,
  ].some((value) => Number.isFinite(normalizeEarnedMediaScore(value)));

const buildEarnedMediaDisplayState = (row = {}) => {
  const hasPlatformSignal = (Array.isArray(row.platforms) ? row.platforms : []).some(
    hasEarnedMediaPlatformSignal
  );
  const hasScoreData = hasEarnedMediaScoreData(row);
  const researchPriorityLabel = getEarnedMediaResearchPriorityLevel(row);
  return {
    hasPlatformSignal,
    hasScoreData,
    socialStatusLabel: hasPlatformSignal ? "Social Metrics Available" : EARNED_MEDIA_DISPLAY_REACH_OPPORTUNITY,
    reachLabel: hasPlatformSignal
      ? formatMicrositeTextValue(row.totalEstimatedReach, "Organic Distribution Potential")
      : "Cast/Director-led awareness",
    researchPriorityLabel,
    rankingScoreLabel: hasPlatformSignal
      ? formatMicrositeTextValue(row.amplificationPriorityScore, "Amplification Priority")
      : `Research Priority: ${researchPriorityLabel}`,
    valueChartTitle: hasScoreData ? "Earned Media Value" : EARNED_MEDIA_DISPLAY_OPPORTUNITY_MAPPING,
    valueChartCopy: hasScoreData
      ? ""
      : row.isDataNeeded
      ? EARNED_MEDIA_VALUE_FALLBACK_COPY
      : "Audience fit, engagement quality, conversion potential, and amplification priority will be scored after social footprint verification. Current recommendation: prioritize main cast/director research first.",
    socialChecklist: row.isDataNeeded
      ? EARNED_MEDIA_SOCIAL_FOOTPRINT_FALLBACK_COPY
      : "Social research checklist: verify official Instagram, TikTok, X, YouTube, follower counts, recent post engagement, and content history.",
    collapsePlatformRows: !hasPlatformSignal,
  };
};

// A served display state may still carry the label as its own value
// ("Research Priority: Research Priority", from a backend before the
// 2026-10-01 copy pass); the page shows "Pending" for it.
const normalizeEarnedMediaResearchPriorityValue = (value) =>
  typeof value === "string" && value.trim() === EARNED_MEDIA_DISPLAY_RESEARCH_PRIORITY ? "Pending" : value;

const normalizeEarnedMediaDisplayState = (value, row = {}, { brandPresentation = false } = {}) => {
  const source = value && typeof value === "object" && !Array.isArray(value) ? value : {};
  const merged = {
    ...buildEarnedMediaDisplayState(row),
    ...source,
  };
  if (!brandPresentation) return merged;
  if (typeof merged.researchPriorityLabel === "string") {
    merged.researchPriorityLabel = normalizeEarnedMediaResearchPriorityValue(merged.researchPriorityLabel);
  }
  if (typeof merged.rankingScoreLabel === "string") {
    merged.rankingScoreLabel = merged.rankingScoreLabel.replace(
      new RegExp(`^Research Priority:\\s*${EARNED_MEDIA_DISPLAY_RESEARCH_PRIORITY}$`),
      "Research Priority: Pending"
    );
  }
  return merged;
};

const normalizeEarnedMediaList = (value, fallback = []) => {
  if (Array.isArray(value)) {
    const list = value.map((item) => formatMicrositeTextValue(item, "")).filter(Boolean);
    return list.length ? list : fallback;
  }
  const text = formatMicrositeTextValue(value, "");
  if (!text) return fallback;
  const list = text
    .split(/\r?\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);
  return list.length ? list : fallback;
};

const normalizeEarnedMediaPlatformRows = (value = []) => {
  const rows = Array.isArray(value)
    ? value
    : value && typeof value === "object"
    ? Object.entries(value).map(([platform, details]) =>
        details && typeof details === "object" && !Array.isArray(details)
          ? { platform, ...details }
          : { platform, handle: details }
      )
    : [];

  return rows
    .map((row, index) => {
      if (!row || typeof row !== "object" || Array.isArray(row)) return null;
      const platform = formatMicrositeTextValue(
        row.platform || row.name || row.label || row.key,
        ""
      );
      if (!platform) return null;
      const estimatedTier = formatMicrositeTextValue(
        row.estimatedTier || row.estimated_tier || row.followerTier || row.follower_tier,
        ""
      );
      const followers = estimatedTier
        ? `Estimated Tier: ${estimatedTier}`
        : formatEarnedMediaCount(
            row.followers || row.followerCount || row.follower_count || row.audienceSize || row.reach,
            EARNED_MEDIA_PENDING_VERIFICATION
          );
      const followerSource = row.followers || row.followerCount || row.follower_count || row.audienceSize || row.reach
        ? "provided"
        : estimatedTier
        ? "estimated_tier"
        : "needed";
      return {
        key: row.key || `${normalizeMicrositeKey(platform) || "platform"}-${index + 1}`,
        platform,
        handle: formatMicrositeTextValue(row.handle, EARNED_MEDIA_HANDLE_NEEDED),
        followers,
        followerSource,
        engagementRate: formatMicrositeTextValue(
          row.engagementRate || row.engagement_rate || row.rate,
          EARNED_MEDIA_ENGAGEMENT_PENDING
        ),
        engagementQuality: formatMicrositeTextValue(
          row.engagementQuality || row.engagement_quality,
          EARNED_MEDIA_ENGAGEMENT_QUALITY_PENDING
        ),
        notes: formatMicrositeTextValue(row.notes || row.copy || row.rationale, ""),
      };
    })
    .filter(Boolean);
};

const buildEarnedMediaDefaultPlatforms = () =>
  ["Instagram", "TikTok", "X", "YouTube"].map((platform) => ({
    key: normalizeMicrositeKey(platform),
    platform,
    handle: EARNED_MEDIA_HANDLE_NEEDED,
    followers: EARNED_MEDIA_PENDING_VERIFICATION,
    followerSource: "needed",
    engagementRate: EARNED_MEDIA_ENGAGEMENT_PENDING,
    engagementQuality: EARNED_MEDIA_ENGAGEMENT_QUALITY_PENDING,
    notes: "Add verified handle, follower count source, and recent post performance.",
  }));

const buildEarnedMediaDataNeededRow = () => ({
  key: "talent-data-needed",
  name: "Earned Media Opportunity Inputs",
  role: "Cast / Director Discovery",
  isDataNeeded: true,
});

const normalizeEarnedMediaTalentRows = (rows = [], options = {}) => {
  // Drop strategic field labels (Rationale, Primary Role, Core Audience, …) before
  // normalizing so they never get expanded into fallback talent/social cards.
  const providedRows = dropEarnedMediaStrategicLabelRows(rows);
  const sourceRows = providedRows.length
    ? providedRows
    : [buildEarnedMediaDataNeededRow()];

  const normalizedRows = sourceRows
    .map((row, index) => {
      if (!row || typeof row !== "object" || Array.isArray(row)) {
        const name = normalizeEarnedMediaPersonName(row);
        if (!name) return null;
        row = { name, role: "Talent / Partner Voice" };
      }
      const platforms = normalizeEarnedMediaPlatformRows(
        row.platforms || row.socialPlatforms || row.social_platforms || row.socialFootprint
      );
      const platformReachTotal = platforms
        .map((platform) => parseEarnedMediaCount(platform.followers))
        .filter(Number.isFinite)
        .reduce((sum, count) => sum + count, 0);
      const name =
        normalizeEarnedMediaPersonName(row.name || row.title || row.person || row.label) ||
        buildEarnedMediaFallbackName(row);
      const normalizedRowForRole = {
        ...row,
        name,
        billingOrder: Number.parseInt(row.billingOrder ?? row.billing_order ?? row.order, 10) || index + 1,
      };
      const hasPlatformSignal = platforms.some(hasEarnedMediaPlatformSignal);
      const researchPriority = getEarnedMediaResearchPriority(normalizedRowForRole);
      const normalizedRow = {
        key: row.key || row.name || row.title || `cast-talent-${index + 1}`,
        isDataNeeded: Boolean(row.isDataNeeded),
        name,
        role: formatMicrositeTextValue(row.role || row.credit || row.type, "Talent / Partner Voice"),
        knownFor: formatMicrositeTextValue(row.knownFor || row.known_for, ""),
        characterName: formatMicrositeTextValue(row.characterName || row.character_name || row.character, ""),
        billingOrder: Number.parseInt(row.billingOrder ?? row.billing_order ?? row.order, 10) || index + 1,
        source: formatMicrositeTextValue(row.source, ""),
        sourceConfidence: formatMicrositeTextValue(row.sourceConfidence || row.source_confidence, ""),
        socialDataStatus: formatMicrositeTextValue(
          row.socialDataStatus || row.social_data_status,
          hasPlatformSignal ? "Partial / Verified" : EARNED_MEDIA_SOCIAL_FOOTPRINT_PENDING
        ),
        dataNeeded: normalizeEarnedMediaList(row.dataNeeded || row.data_needed, [
          "Verified Instagram/TikTok/X/YouTube handles",
          "Follower count sources",
          "Recent post performance and engagement benchmarks",
        ]),
        platforms: platforms.length ? platforms : buildEarnedMediaDefaultPlatforms(),
        totalEstimatedReach: formatEarnedMediaCount(
          row.totalEstimatedReach ?? row.total_estimated_reach ?? row.reach,
          platformReachTotal ? formatMetricCount(platformReachTotal) : EARNED_MEDIA_SOCIAL_FOOTPRINT_PENDING
        ),
        audienceFit: formatMicrositeTextValue(
          row.audienceFit || row.audience_fit,
          row.isDataNeeded ? EARNED_MEDIA_OPPORTUNITY_PENDING : EARNED_MEDIA_RESEARCH_REQUIRED
        ),
        engagementQuality: formatMicrositeTextValue(
          row.engagementQuality || row.engagement_quality,
          EARNED_MEDIA_ENGAGEMENT_QUALITY_PENDING
        ),
        conversionPotential: formatMicrositeTextValue(
          row.conversionPotential || row.conversion_potential,
          row.isDataNeeded ? EARNED_MEDIA_OPPORTUNITY_PENDING : "Traffic Signal Pending"
        ),
        amplificationPriority: formatMicrositeTextValue(
          row.amplificationPriority || row.amplification_priority,
          researchPriority
        ),
        audienceFitScore: normalizeEarnedMediaScore(row.audienceFitScore ?? row.audience_fit_score),
        engagementQualityScore: normalizeEarnedMediaScore(
          row.engagementQualityScore ?? row.engagement_quality_score
        ),
        conversionPotentialScore: normalizeEarnedMediaScore(
          row.conversionPotentialScore ?? row.conversion_potential_score
        ),
        amplificationPriorityScore: normalizeEarnedMediaScore(
          row.amplificationPriorityScore ?? row.amplification_priority_score
        ),
        recommendedUse: formatMicrositeTextValue(
          row.recommendedUse || row.recommended_use,
          row.isDataNeeded
            ? selectMicrositeCopy(options.brandPresentation).earnedOpportunityPlaceholder
            : buildRoleAwareEarnedMediaRecommendation(normalizedRowForRole)
        ),
        contentFormats: normalizeEarnedMediaList(
          row.contentFormats || row.content_formats,
          row.isDataNeeded ? [EARNED_MEDIA_OPPORTUNITY_PENDING] : buildRoleAwareEarnedMediaContentFormats(normalizedRowForRole)
        ),
        outreachTargets: normalizeEarnedMediaList(
          row.outreachTargets || row.outreach_targets,
          row.isDataNeeded ? [EARNED_MEDIA_OPPORTUNITY_PENDING] : buildRoleAwareEarnedMediaOutreachTargets(normalizedRowForRole)
        ),
        paidAmplificationPlan: formatMicrositeTextValue(
          row.paidAmplificationPlan || row.paid_amplification_plan,
          row.isDataNeeded
            ? "Add earned-media opportunity inputs before assigning paid amplification priorities."
            : "Verify social footprint, then amplify organic moments with strong engagement quality, audience fit, and traffic signals."
        ),
        buyTicketsRole: formatMicrositeTextValue(
          row.buyTicketsRole || row.buy_tickets_role,
          row.isDataNeeded
            ? "Add cast/director opportunity inputs before assigning Buy Tickets traffic roles."
            : "Use tracked links and retarget engaged viewers when verified earned moments create measurable Buy Tickets page traffic."
        ),
        risksOrWatchouts: formatMicrositeTextValue(
          row.risksOrWatchouts || row.risks_or_watchouts,
          "Follower count indicates potential earned reach. Engagement quality and traffic signals help determine which opportunities should receive paid amplification."
        ),
      };
      return {
        ...normalizedRow,
        displayState: normalizeEarnedMediaDisplayState(row.displayState || row.display_state, normalizedRow, options),
      };
    })
    .filter(Boolean);

  // Collapse duplicated fallback rows (same name + body + source) so a single piece of
  // fallback guidance never renders as multiple repeated cards. If nothing distinct
  // remains, fall back to exactly one data-needed guidance row per section.
  const dedupedRows = dedupeEarnedMediaRows(normalizedRows);
  if (!dedupedRows.length) {
    return [
      {
        ...normalizeEarnedMediaTalentRows([buildEarnedMediaDataNeededRow()], options)[0],
      },
    ];
  }
  return dedupedRows;
};

const normalizeEarnedMediaRankingRows = (rows = [], talentRows = []) => {
  // Strategic labels are not rankable entities; drop them and collapse duplicated
  // fallback rows before falling back to the (already cleaned) talent records.
  const providedRows = dedupeEarnedMediaRows(dropEarnedMediaStrategicLabelRows(rows));
  const sourceRows = providedRows.length ? providedRows : talentRows;
  return sourceRows.map((row, index) => {
    const providedScore = formatMicrositeTextValue(row.score || row.amplificationPriorityScore, "");
    const score = providedScore || getEarnedMediaResearchPriority(row);
    return {
      key: row.key || row.name || `talent-rank-${index + 1}`,
      rank: Number.parseInt(row.rank, 10) || index + 1,
      name:
        normalizeEarnedMediaPersonName(row.name || row.title || row.person) ||
        buildEarnedMediaFallbackName(row),
      score,
      rationale: formatMicrositeTextValue(
        row.rationale || row.copy || row.recommendedUse,
        providedScore
          ? "Ranked by earned-media value, audience relevance, engagement quality, and amplification priority."
          : `${score} based on role and billing order. Verify social footprint before treating this as a confirmed performance score.`
      ),
    };
  });
};

const normalizeBuyTicketsTrafficPlan = (value = {}) => {
  const source = value && typeof value === "object" && !Array.isArray(value) ? value : {};
  return {
    role: formatMicrositeTextValue(
      source.role,
      "Once social links are verified, Cphere can benchmark follower reach, engagement quality, post velocity, referral traffic, and Buy Tickets page visits."
    ),
    signalsToWatch: normalizeEarnedMediaList(source.signalsToWatch || source.signals_to_watch, [
      "Talent post volume",
      "Engagement rate by person",
      "Saves, shares, and comment quality",
      "Creator clip velocity",
      "Traffic from talent links",
      "Buy Tickets page visits",
    ]),
    optimizationActions: normalizeEarnedMediaList(
      source.optimizationActions || source.optimization_actions,
      [
        "Amplify talent posts only after organic engagement clears benchmark.",
        "Retarget talent-engaged viewers into theater geos and Buy Tickets page paths.",
        "Shift budget toward people and clips proving downstream traffic quality.",
      ]
    ),
  };
};

const hasEarnedMediaTalentSourceSignal = (talent = {}) =>
  !talent.isDataNeeded &&
  (Array.isArray(talent.platforms) ? talent.platforms : []).some(hasEarnedMediaPlatformSignal);

const normalizeCastDirectorDiscovery = (value = {}, options = {}) => {
  const source = value && typeof value === "object" && !Array.isArray(value) ? value : {};
  const people = Array.isArray(source.people) && source.people.length
    ? normalizeEarnedMediaTalentRows(source.people, options)
    : [];
  return {
    sourceStatus: formatMicrositeTextValue(
      source.sourceStatus || source.source_status,
      people.length && people[0].name !== "Earned Media Opportunity Inputs"
        ? "Discovered"
        : "Opportunity Inputs Needed"
    ),
    primarySource: formatMicrositeTextValue(source.primarySource || source.primary_source, ""),
    people,
    dataNeeded: normalizeEarnedMediaList(source.dataNeeded || source.data_needed, []),
  };
};

// Entertainment-only submitted social profile URLs. Raw, truthful, pending —
// this never carries follower/engagement/audience metrics.
// Builds display metric rows for an analyzed profile. Only fields that are
// actually present are included — never an empty row and never a fabricated
// value. Non-analyzed profiles produce no metric rows (they stay "pending").
const buildEarnedMediaProfileAnalysisRows = (row = {}) => {
  const rows = [];
  const pushText = (label, value) => {
    const text = formatMicrositeTextValue(value, "");
    if (text) rows.push({ key: label, label, value: text });
  };
  const followerCount = Number(row?.followerCount);
  if (Number.isFinite(followerCount) && followerCount >= 0) {
    rows.push({ key: "Followers", label: "Followers", value: followerCount.toLocaleString("en-US") });
  }
  const recentPostCount = Number(row?.recentPostCount);
  if (Number.isFinite(recentPostCount) && recentPostCount >= 0) {
    rows.push({ key: "Recent posts", label: "Recent posts", value: String(recentPostCount) });
  }
  pushText("Avg engagement", row?.averageEngagementRateLabel);
  pushText("Handle", row?.handle);
  pushText("Recent engagement", row?.recentEngagementSignals);
  pushText("Audience & reach", row?.audienceReachNotes);
  pushText("Amplification", row?.amplificationOpportunity);
  pushText("Confidence", row?.confidence);
  pushText("Source", row?.source);
  return rows;
};

// Presentation-only grouping of an analyzed profile's flat metric rows into the
// three regions of a readable analysis card: compact numeric tiles, long-form
// analysis paragraphs, and a subtle source/meta footer. This never mutates,
// filters out, or fabricates data — it only routes the rows that
// buildEarnedMediaProfileAnalysisRows already produced into display regions, so
// the earned-media data shape is unchanged.
const EARNED_MEDIA_METRIC_TILE_KEYS = ["Followers", "Recent posts", "Avg engagement", "Handle"];
const EARNED_MEDIA_METRIC_SOURCE_KEYS = ["Source"];
export const partitionEarnedMediaProfileMetrics = (metrics = []) => {
  const tiles = [];
  const notes = [];
  let source = null;
  for (const metric of Array.isArray(metrics) ? metrics : []) {
    if (EARNED_MEDIA_METRIC_TILE_KEYS.includes(metric.key)) tiles.push(metric);
    else if (EARNED_MEDIA_METRIC_SOURCE_KEYS.includes(metric.key)) source = metric;
    else notes.push(metric);
  }
  return { tiles, notes, source };
};

export const normalizeEarnedMediaSubmittedProfiles = (source = {}) => {
  const block =
    source && typeof source.submittedProfiles === "object" && !Array.isArray(source.submittedProfiles)
      ? source.submittedProfiles
      : null;
  if (!block) return null;
  const rows = (Array.isArray(block.profiles) ? block.profiles : [])
    .map((row, index) => {
      const url = formatMicrositeTextValue(row?.url || row?.href || row, "");
      if (!url) return null;
      const analysisStatus = formatMicrositeTextValue(
        row?.analysisStatus || row?.analysis_status,
        "pending"
      );
      const isAnalyzed = analysisStatus === "analyzed";
      return {
        key: row?.key || `earned-media-profile-${index + 1}`,
        url,
        platform: formatMicrositeTextValue(row?.platform, "Website"),
        analysisStatus,
        isAnalyzed,
        analysisStatusLabel: formatMicrositeTextValue(
          row?.analysisStatusLabel || row?.analysis_status_label,
          "Analysis pending"
        ),
        metrics: isAnalyzed ? buildEarnedMediaProfileAnalysisRows(row) : [],
      };
    })
    .filter(Boolean);
  if (!rows.length) return null;
  return {
    statusLabel: formatMicrositeTextValue(
      block.statusLabel || block.status_label,
      "Profiles submitted for earned media analysis"
    ),
    note: formatMicrositeTextValue(block.note, ""),
    profiles: rows,
  };
};

// ---- Earned-media social footprint summary (data-aware intelligence) --------
// Aggregates the ANALYZED submitted social profiles (manual or verified) into a
// single directional summary the downstream intelligence modules can speak to.
// Pending profiles are excluded. Nothing is invented — every figure comes from a
// metric a human actually entered/verified for that profile.
const EARNED_MEDIA_ANALYZED_STATUSES = new Set(["analyzed", "verified", "available"]);

const parseEarnedMediaEngagementPercent = (row = {}) => {
  const raw = Number(row?.averageEngagementRate ?? row?.average_engagement_rate);
  if (Number.isFinite(raw) && raw >= 0) {
    // Stored as a fraction (0.041) → surface as a percent (4.1); a value already
    // in percent form (4.1) is left as-is.
    const pct = raw <= 1 ? raw * 100 : raw;
    return Math.round(pct * 10) / 10;
  }
  const label = formatMicrositeTextValue(
    row?.averageEngagementRateLabel || row?.average_engagement_rate_label,
    ""
  );
  const match = label.match(/([\d.]+)/);
  if (!match) return null;
  const pct = Number(match[1]);
  return Number.isFinite(pct) ? Math.round(pct * 10) / 10 : null;
};

export const buildEarnedMediaSocialSummary = (source = {}) => {
  const empty = {
    hasSocialData: false,
    analyzedCount: 0,
    totalFollowers: null,
    totalFollowersLabel: null,
    platforms: [],
    handles: [],
    sources: [],
    isManual: false,
    strongestReach: null,
    strongestEngagement: null,
    highestVelocity: null,
    audienceNotes: [],
  };
  const block =
    source && typeof source.submittedProfiles === "object" && !Array.isArray(source.submittedProfiles)
      ? source.submittedProfiles
      : null;
  const rawProfiles = block && Array.isArray(block.profiles) ? block.profiles : [];
  const analyzed = rawProfiles.filter((row) => {
    const status = formatMicrositeTextValue(
      row?.analysisStatus || row?.analysis_status,
      "pending"
    ).toLowerCase();
    return EARNED_MEDIA_ANALYZED_STATUSES.has(status);
  });
  if (!analyzed.length) return empty;

  const platforms = [];
  const handles = [];
  const sources = [];
  const audienceNotes = [];
  let totalFollowers = 0;
  let hasFollowerData = false;
  let strongestReach = null;
  let strongestEngagement = null;
  let highestVelocity = null;

  for (const row of analyzed) {
    const platform = formatMicrositeTextValue(row?.platform, "Profile");
    if (platform && !platforms.includes(platform)) platforms.push(platform);
    const handle = formatMicrositeTextValue(row?.handle, "");
    if (handle && !handles.includes(handle)) handles.push(handle);
    const src = formatMicrositeTextValue(row?.source, "").toLowerCase();
    if (src && !sources.includes(src)) sources.push(src);

    const followers = Number(row?.followerCount ?? row?.follower_count);
    if (Number.isFinite(followers) && followers >= 0) {
      totalFollowers += followers;
      hasFollowerData = true;
      if (!strongestReach || followers > strongestReach.followers) {
        strongestReach = { platform, followers };
      }
    }
    const engagement = parseEarnedMediaEngagementPercent(row);
    if (engagement !== null && (!strongestEngagement || engagement > strongestEngagement.rate)) {
      strongestEngagement = { platform, rate: engagement };
    }
    const posts = Number(row?.recentPostCount ?? row?.recent_post_count);
    if (Number.isFinite(posts) && posts >= 0 && (!highestVelocity || posts > highestVelocity.count)) {
      highestVelocity = { platform, count: posts };
    }
    const note = formatMicrositeTextValue(row?.audienceReachNotes || row?.audience_reach_notes, "");
    if (note) audienceNotes.push({ platform, note });
  }

  return {
    hasSocialData: true,
    analyzedCount: analyzed.length,
    totalFollowers: hasFollowerData ? totalFollowers : null,
    totalFollowersLabel: hasFollowerData ? totalFollowers.toLocaleString("en-US") : null,
    platforms,
    handles,
    sources,
    // Default to manual when a human entered metrics with no explicit source tag.
    isManual: sources.length === 0 || sources.includes("manual"),
    strongestReach: strongestReach
      ? {
          platform: strongestReach.platform,
          followersLabel: strongestReach.followers.toLocaleString("en-US"),
        }
      : null,
    strongestEngagement: strongestEngagement
      ? { platform: strongestEngagement.platform, rateLabel: `${strongestEngagement.rate}%` }
      : null,
    highestVelocity: highestVelocity
      ? { platform: highestVelocity.platform, count: highestVelocity.count }
      : null,
    audienceNotes,
  };
};

// Turns the aggregate summary into honest, on-brand copy the downstream modules
// reuse. Returns null when there is no analyzed social data (callers fall back
// to the existing cast/director-oriented copy).
export const buildEarnedMediaSocialNarrative = (summary = {}) => {
  if (!summary || !summary.hasSocialData) return null;
  const {
    analyzedCount,
    totalFollowersLabel,
    platforms,
    handles,
    strongestReach,
    strongestEngagement,
    highestVelocity,
    isManual,
  } = summary;
  const profileWord = analyzedCount === 1 ? "profile" : "profiles";
  const platformList = platforms.length ? platforms.join(", ") : "the submitted platforms";
  const sourceQualifier = isManual
    ? "Manual metrics are available for directional benchmarking, not API-verified precision."
    : "Verified social metrics are available for benchmarking.";

  const combinedAudience = totalFollowersLabel
    ? `${analyzedCount} analyzed social ${profileWord} create a combined audience base of ${totalFollowersLabel} across ${platformList}.`
    : `${analyzedCount} analyzed social ${profileWord} across ${platformList} are available as earned-media input.`;

  const platformRoles = strongestReach
    ? `${strongestReach.platform} leads reach at ${strongestReach.followersLabel} followers${
        strongestEngagement
          ? `; ${strongestEngagement.platform} shows the strongest engagement at ${strongestEngagement.rateLabel}`
          : ""
      }${
        highestVelocity
          ? `; ${highestVelocity.platform} has the highest recent content velocity (${highestVelocity.count} recent ${micrositeCountNoun(
              highestVelocity.count,
              "post"
            )})`
          : ""
      }.`
    : `Reach, engagement, and content velocity vary across ${platformList}.`;

  const amplificationGuidance =
    strongestEngagement && strongestReach && strongestEngagement.platform !== strongestReach.platform
      ? `Prioritize paid amplification behind the strongest engagement signal (${strongestEngagement.platform} at ${strongestEngagement.rateLabel}), not just the largest audience (${strongestReach.platform}).`
      : strongestEngagement
      ? `Prioritize paid amplification behind the strongest engagement signal (${strongestEngagement.platform} at ${strongestEngagement.rateLabel}) rather than raw audience size alone.`
      : "Prioritize paid amplification behind the profile with the strongest engagement signal, not just the largest audience.";

  const buyTicketsValidator =
    "Use Buy Tickets page traffic as the conversion validator after social engagement clears benchmark.";

  const advocateVoices = handles.length
    ? `Analyzed handles (${handles.join(", ")}) are first-party advocate voices; seed organic conversation there before paid amplification expands reach.`
    : "Analyzed profiles are first-party advocate voices; seed organic conversation there before paid amplification expands reach.";

  const organicTracking = `Track engagement quality and content velocity across ${platformList}${
    highestVelocity
      ? ` (${highestVelocity.platform} is most active at ${highestVelocity.count} recent ${micrositeCountNoun(
          highestVelocity.count,
          "post"
        )})`
      : ""
  }. Seed authentic conversation first, then amplify what clears benchmark.`;

  const note = `Using submitted social footprint: ${analyzedCount} analyzed ${profileWord}${
    totalFollowersLabel ? `, ${totalFollowersLabel} combined reach` : ""
  } across ${platformList}. ${isManual ? "Manual / directional." : "Verified metrics."}`;

  const callout = `Submitted social profile metrics are active and in use below — ${combinedAudience} ${sourceQualifier} Add cast, director, or IMDb/TMDb metadata to extend earned-media scoring beyond the submitted social footprint.`;

  return {
    combinedAudience,
    platformRoles,
    amplificationGuidance,
    buyTicketsValidator,
    advocateVoices,
    organicTracking,
    sourceQualifier,
    note,
    callout,
  };
};

// Social-derived fallback rows for the five array-driven earned-media cards.
// Used ONLY when the upstream section supplied none of its own rows but analyzed
// social data exists — so the modules speak to real metrics instead of reading
// empty. Shapes match what the renderers consume (title/copy/metricLabel).
const buildEarnedMediaSocialFallbackRows = (summary, narrative) => {
  const empty = {
    talentSignals: [],
    influencerOpportunities: [],
    organicConversationSignals: [],
    amplificationRecommendations: [],
    measurementPlan: [],
  };
  if (!summary?.hasSocialData || !narrative) return empty;
  return {
    talentSignals: [
      { key: "social-audience-relevance", metricLabel: "Audience Relevance", title: "Audience Relevance", copy: narrative.combinedAudience },
      { key: "social-engagement-quality", metricLabel: "Engagement Quality", title: "Engagement Quality", copy: `${narrative.platformRoles} ${narrative.sourceQualifier}` },
      { key: "social-conversion-potential", metricLabel: "Conversion Potential", title: "Conversion Potential", copy: narrative.buyTicketsValidator },
      { key: "social-amplification-priority", metricLabel: "Amplification Priority", title: "Amplification Priority", copy: narrative.amplificationGuidance },
    ],
    influencerOpportunities: [
      { key: "social-advocate-voices", title: "Advocate Voices", copy: narrative.advocateVoices },
    ],
    organicConversationSignals: [
      { key: "social-organic-tracking", title: "Organic Conversation", copy: narrative.organicTracking },
    ],
    amplificationRecommendations: [
      { key: "social-earned-to-paid", title: "Earned-to-Paid", copy: `${narrative.amplificationGuidance} Scale only demand the social footprint has already proven.` },
    ],
    measurementPlan: [
      { key: "social-ticket-intent", title: "Ticket Intent", copy: narrative.buyTicketsValidator },
    ],
  };
};

export const normalizeEarnedMediaIntelligence = (section = {}, options = {}) => {
  const source = section && typeof section === "object" && !Array.isArray(section) ? section : {};
  const normalizeRows = (rows = [], fallbackLabel = "Signal") =>
    (Array.isArray(rows) ? rows : [])
      .map((row, index) => {
        if (row && typeof row === "object" && !Array.isArray(row)) {
          return {
            key: row.key || row.title || row.label || `${fallbackLabel}-${index + 1}`,
            ...row,
            title: formatMicrositeTextValue(row.title || row.label || row.phase, fallbackLabel),
            copy: formatMicrositeTextValue(
              row.copy || row.description || row.insight || row.rationale || row.trackingLogic || row.value,
              ""
            ),
          };
        }
        const text = formatMicrositeTextValue(row, "");
        if (!text) return null;
        return {
          key: `${fallbackLabel}-${index + 1}`,
          title: text,
          copy: text,
        };
      })
      .filter(Boolean);
  const castTalentAnalysis = normalizeEarnedMediaTalentRows(
    source.castTalentAnalysis || source.cast_talent_analysis,
    options
  );
  const talentRanking = normalizeEarnedMediaRankingRows(
    source.talentRanking || source.talent_ranking,
    castTalentAnalysis
  );
  const hasSourceSignals = castTalentAnalysis.some(hasEarnedMediaTalentSourceSignal);
  const hasNamedPeople = castTalentAnalysis.some(
    (talent) => !talent.isDataNeeded && talent.name !== "Earned Media Opportunity Inputs"
  );
  const castDirectorDiscovery = normalizeCastDirectorDiscovery(
    source.castDirectorDiscovery || source.cast_director_discovery,
    options
  );

  // Analyzed submitted social profiles count as usable earned-media intelligence.
  const socialSummary = buildEarnedMediaSocialSummary(source);
  const socialNarrative = buildEarnedMediaSocialNarrative(socialSummary);
  const socialFallback = buildEarnedMediaSocialFallbackRows(socialSummary, socialNarrative);
  const hasSocialData = socialSummary.hasSocialData;
  // Prefer the section's own rows; only speak from social metrics when a module
  // would otherwise be empty and analyzed social data exists.
  const withSocialFallback = (rows, fallbackRows) =>
    rows.length ? rows : hasSocialData ? fallbackRows : rows;

  return {
    summary: formatMicrositeTextValue(
      source.summary,
      "Earned media creates the signal. Paid media scales the signal. Buy Tickets traffic validates demand."
    ),
    primarySuccessMetric: formatMicrositeTextValue(
      source.primarySuccessMetric,
      "Primary Success Signal: Buy Tickets Page Traffic"
    ),
    strategicThesis: formatMicrositeTextValue(
      source.strategicThesis || source.strategic_thesis,
      "Cast and director social footprint creates an opportunity map for earned reach. Cphere uses engagement quality, audience fit, and Buy Tickets traffic signals to prioritize paid amplification."
    ),
    dataStatus: formatMicrositeTextValue(
      source.dataStatus || source.data_status,
      hasSourceSignals
        ? "Talent Analysis Ready"
        : hasSocialData
        ? "Submitted Social Footprint Analyzed"
        : hasNamedPeople
        ? "Main Cast Identified · Social Footprint Pending"
        : "Earned Media Opportunity Inputs"
    ),
    dataInputTitle: formatMicrositeTextValue(
      source.dataInputTitle || source.data_input_title,
      hasSourceSignals ? "" : hasSocialData ? "Social Footprint Active" : "Earned Media Opportunity Inputs"
    ),
    dataNeededCallout: formatMicrositeTextValue(
      source.dataNeededCallout || source.data_needed_callout,
      hasSourceSignals
        ? ""
        : hasSocialData
        ? socialNarrative.callout
        : hasNamedPeople
        ? EARNED_MEDIA_SOCIAL_NEEDED_CALLOUT
        : EARNED_MEDIA_DATA_NEEDED_CALLOUT
    ),
    castDirectorDiscovery,
    castTalentAnalysis,
    talentRanking,
    talentSignals: withSocialFallback(
      normalizeRows(source.talentSignals, "Talent Signal"),
      socialFallback.talentSignals
    ),
    influencerOpportunities: withSocialFallback(
      normalizeRows(source.influencerOpportunities, "Advocate Opportunity"),
      socialFallback.influencerOpportunities
    ),
    organicConversationSignals: withSocialFallback(
      normalizeRows(source.organicConversationSignals, "Conversation Signal"),
      socialFallback.organicConversationSignals
    ),
    amplificationRecommendations: withSocialFallback(
      normalizeRows(source.amplificationRecommendations, "Amplification Recommendation"),
      socialFallback.amplificationRecommendations
    ),
    measurementPlan: withSocialFallback(
      normalizeRows(source.measurementPlan, "Measurement Signal"),
      socialFallback.measurementPlan
    ),
    buyTicketsTrafficPlan: normalizeBuyTicketsTrafficPlan(
      source.buyTicketsTrafficPlan || source.buy_tickets_traffic_plan
    ),
    submittedProfiles: normalizeEarnedMediaSubmittedProfiles(source),
    socialSummary,
    socialNarrative,
  };
};

export const normalizeReleaseMilestoneSchedule = (rows = []) =>
  (Array.isArray(rows) ? rows : [])
    .map((row, index) => {
      if (row && typeof row === "object" && !Array.isArray(row)) {
        return {
          key: row.key || row.phase || row.title || `milestone-${index + 1}`,
          phase: formatMicrositeTextValue(row.phase || row.title, `Milestone ${index + 1}`),
          timing: formatMicrositeTextValue(row.timing || row.window, "Timing pending"),
          objective: formatMicrositeTextValue(row.objective || row.copy || row.description, "Milestone objective pending."),
          actions: Array.isArray(row.actions)
            ? row.actions.map((item) => formatMicrositeTextValue(item, "")).filter(Boolean)
            : formatMicrositeTextValue(row.actions, "")
              ? [formatMicrositeTextValue(row.actions, "")]
              : [],
          successSignal: formatMicrositeTextValue(row.successSignal || row.metric, "Buy Tickets Page Traffic"),
        };
      }
      const text = formatMicrositeTextValue(row, "");
      if (!text) return null;
      return {
        key: `milestone-${index + 1}`,
        phase: text,
        timing: "Timing pending",
        objective: text,
        actions: [],
        successSignal: "Buy Tickets Page Traffic",
      };
    })
    .filter(Boolean);

// Per-dimension value rows with an honest state model. A missing score is
// never rendered as an empty zero-length track, "Research Priority" is never
// used as the metric value, and the research recommendation renders as an
// independent badge. See utils/earnedMediaDimensionState.js (identical in
// client and superadmin) for the deterministic resolver.
export function EarnedMediaScoreBars({ talent, socialActive = false, analysisPending = false }) {
  const brandPresentation = useMicrositeBrandPresentation();
  const displayState = normalizeEarnedMediaDisplayState(talent.displayState || talent.display_state, talent, { brandPresentation });
  const [detailOpen, setDetailOpen] = useState(false);
  const dimensions = useMemo(
    () =>
      resolveEarnedMediaDimensionStates(talent, {
        analysisPending,
        researchPriorityLevel: getEarnedMediaResearchPriorityLevel(talent),
      }),
    [talent, analysisPending]
  );

  if (!displayState.hasScoreData && !analysisPending) {
    return (
      <div className="campaign-strategy-microsite-earned-opportunity-state">
        <strong>{displayState.valueChartTitle}</strong>
        <span>
          {socialActive ? EARNED_MEDIA_VALUE_SOCIAL_ACTIVE_COPY : displayState.valueChartCopy}
        </span>
      </div>
    );
  }

  return (
    <div className="campaign-strategy-microsite-earned-score-bars" aria-label={`${talent.name} earned media value chart`}>
      {dimensions.map((dimension) => (
        <div key={`${talent.key}-${dimension.key}`} className="campaign-strategy-microsite-earned-score-row">
          <span>{dimension.label}</span>
          {dimension.status === EARNED_MEDIA_DIMENSION_STATUSES.SCORED ? (
            <>
              <div className="campaign-strategy-microsite-earned-score-track">
                <i style={{ width: `${dimension.value}%` }} />
              </div>
              <strong>
                {dimension.value}
                {dimension.confidence ? (
                  <em className="campaign-strategy-microsite-earned-score-confidence">
                    {dimension.confidence} confidence
                  </em>
                ) : null}
              </strong>
            </>
          ) : (
            <div className="campaign-strategy-microsite-earned-score-state">
              <span
                className={`campaign-strategy-microsite-earned-score-state-chip campaign-strategy-microsite-earned-score-state-chip--${dimension.status}`}
              >
                {dimension.statusLabel}
              </span>
              {dimension.researchPriority ? (
                <span className="campaign-strategy-microsite-earned-score-research-badge">
                  Research Priority
                </span>
              ) : null}
              <span className="campaign-strategy-microsite-visually-hidden">
                {dimension.reason}
              </span>
            </div>
          )}
        </div>
      ))}
      <button
        type="button"
        className="campaign-strategy-microsite-earned-score-detail-toggle"
        onClick={() => setDetailOpen((prev) => !prev)}
        aria-expanded={detailOpen}
        aria-label={`View full earned media intelligence for ${talent.name}`}
      >
        {detailOpen ? "Hide Full Intelligence" : "View Full Intelligence"}
      </button>
      {detailOpen ? (
        <dl className="campaign-strategy-microsite-earned-score-detail">
          {dimensions.map((dimension) => (
            <div
              key={`${talent.key}-${dimension.key}-detail`}
              className="campaign-strategy-microsite-earned-score-detail-row"
            >
              <dt>{dimension.label}</dt>
              <dd>
                {dimension.status === EARNED_MEDIA_DIMENSION_STATUSES.SCORED ? (
                  <>
                    <span>Score {dimension.value} / 100</span>
                    {dimension.confidence ? (
                      <span className="campaign-strategy-microsite-earned-score-detail-confidence">
                        {dimension.confidence} confidence
                      </span>
                    ) : null}
                    {dimension.reason ? <span>{dimension.reason}</span> : null}
                  </>
                ) : (
                  <>
                    <span className="campaign-strategy-microsite-earned-score-detail-state">
                      {dimension.statusLabel}
                    </span>
                    <span>{dimension.reason}</span>
                    {dimension.missingSignals.length ? (
                      <span>Missing signals: {dimension.missingSignals.join(", ")}.</span>
                    ) : null}
                    {dimension.status === EARNED_MEDIA_DIMENSION_STATUSES.ANALYSIS_PENDING ? (
                      <span>The score will populate after profile analysis completes.</span>
                    ) : null}
                    {dimension.researchPriority ? (
                      <span>Additional research is recommended for this dimension.</span>
                    ) : null}
                  </>
                )}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
    </div>
  );
}

function EarnedMediaTalentCardHeader({ talent, renderCopy = (item) => item }) {
  const name = formatMicrositeTextValue(talent?.name, "Primary Cast / Director Opportunity");
  const denseName = isDenseEarnedMediaRankingNarrative(name);

  return (
    <>
      <div className="campaign-strategy-microsite-card-header">
        <span className="campaign-strategy-microsite-card-title">
          {denseName ? EARNED_MEDIA_RANKING_STRATEGY_TITLE : name}
        </span>
        <span className="campaign-strategy-microsite-card-meta">{talent?.role}</span>
      </div>
      {denseName ? <EarnedMediaRankingCopy value={name} renderCopy={renderCopy} /> : null}
    </>
  );
}

function EarnedMediaMiniList({ items = [] }) {
  if (!items.length) return null;
  return (
    <ul className="campaign-strategy-microsite-bullet-list">
      {items.map((item, index) => (
        <li key={`${item}-${index}`}>{item}</li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Cast / Talent Earned Media Ranking — dense narrative readability
// ---------------------------------------------------------------------------
// Some ranking rows arrive with an entire generated media brief pasted into a
// single field (the talent name or recommendedUse). Rendered raw, that produces
// one oversized, all-caps wall of text. The helpers below are PRESENTATION ONLY:
// they never mutate, summarize, truncate, or drop content. They re-express the
// dense narrative as an intro + numbered sections (+ strategic notes), normalize
// casing for readability, and degrade gracefully to a single paragraph when the
// text is not structured.
export const EARNED_MEDIA_RANKING_STRATEGY_TITLE = "Cast / Talent Earned Media Strategy";

const EARNED_MEDIA_RANKING_ACRONYMS = new Map([
  ["ai", "AI"], ["api", "API"], ["cpa", "CPA"], ["cpc", "CPC"], ["cpm", "CPM"],
  ["cphere", "Cphere"], ["ctr", "CTR"], ["ctv", "CTV"], ["dooh", "DOOH"],
  ["fast", "FAST"], ["kpi", "KPI"], ["kpis", "KPIs"], ["olv", "OLV"],
  ["ooh", "OOH"], ["ott", "OTT"], ["roas", "ROAS"], ["tmdb", "TMDb"],
  ["ugc", "UGC"], ["url", "URL"], ["us", "U.S."], ["usa", "USA"],
  ["youtube", "YouTube"], ["tiktok", "TikTok"], ["instagram", "Instagram"],
  ["cbn", "CBN"], ["ewtn", "EWTN"], ["x", "X"],
]);

const isEarnedMediaRankingDomainToken = (token = "") =>
  /^https?:\/\//i.test(token) ||
  /^[a-z0-9](?:[\w-]*\.)+[a-z]{2,}(?:\/[^\s]*)?$/i.test(token);

const applyEarnedMediaRankingTokenCasing = (token = "") => {
  const match = String(token).match(/^([^0-9A-Za-z]*)([0-9A-Za-z][^\s]*?)([^0-9A-Za-z]*)$/);
  if (!match) return token;
  const [, prefix, core, suffix] = match;
  if (!core) return token;
  if (isEarnedMediaRankingDomainToken(core)) return `${prefix}${core.toLowerCase()}${suffix}`;
  const key = core.toLowerCase();
  if (EARNED_MEDIA_RANKING_ACRONYMS.has(key)) {
    return `${prefix}${EARNED_MEDIA_RANKING_ACRONYMS.get(key)}${suffix}`;
  }
  return `${prefix}${key}${suffix}`;
};

const shouldNormalizeEarnedMediaRankingCasing = (value = "") => {
  const letters = String(value).match(/[A-Za-z]/g) || [];
  if (letters.length < 16) return false;
  const uppercase = letters.filter((char) => char === char.toUpperCase()).length;
  return uppercase / letters.length > 0.68;
};

// Sentence-case a mostly-uppercase string for readable body copy while keeping
// domains lowercase and known acronyms intact. Already-readable (mixed-case)
// text is returned unchanged so nothing is ever mangled. Pass { force: true }
// to normalize a fragment (heading/body) once the parent narrative has already
// been judged mostly-uppercase, so casing stays consistent across sections.
export const normalizeEarnedMediaRankingCasing = (value = "", { force = false } = {}) => {
  const clean = String(value || "").replace(/\s+/g, " ").trim();
  if (!clean) return "";
  if (!force && !shouldNormalizeEarnedMediaRankingCasing(clean)) return clean;
  const lowered = clean.split(" ").map(applyEarnedMediaRankingTokenCasing).join(" ");
  return lowered.replace(
    /(^|[.!?:]["”)\]]?\s+|["“]\s*)([a-z])/g,
    (_, lead, char) => `${lead}${char.toUpperCase()}`
  );
};

const EARNED_MEDIA_RANKING_NOTE_RE =
  /(strategic note|critical|caution|warning|watch\s?out|risk|expect|polari[sz])/i;

// A short bracketed or parenthetical qualifier that opens a section —
// "BEHAVIORAL & DATA SIGNALS (AI OPTIMIZATION INPUTS) ..." — is secondary
// heading content, not body copy. Splitting on word count alone strands
// fragments like "INPUTS)" at the start of the body paragraph.
const EARNED_MEDIA_RANKING_QUALIFIER_RE =
  /^([^()[\]]{2,90}?)\s*[([]\s*([^()[\]]{1,60}?)\s*[)\]]\s*(.*)$/;

const buildEarnedMediaRankingSection = (num, rawBody = "", position = 0, force = false) => {
  const body = String(rawBody || "").trim();
  let headingRaw = body;
  let subheadingRaw = "";
  let restRaw = "";
  const qualifier = body.match(EARNED_MEDIA_RANKING_QUALIFIER_RE);
  const bracketIdx = body.indexOf("[");
  const words = body.split(" ");
  if (
    qualifier &&
    qualifier[1].trim().split(/\s+/).length <= 8 &&
    qualifier[2].trim().split(/\s+/).length <= 7
  ) {
    headingRaw = qualifier[1].trim();
    subheadingRaw = qualifier[2].trim();
    restRaw = qualifier[3].trim();
  } else if (bracketIdx > 0 && body.slice(0, bracketIdx).trim().split(" ").length <= 8) {
    headingRaw = body.slice(0, bracketIdx).trim();
    restRaw = body.slice(bracketIdx).trim();
  } else if (words.length > 7) {
    headingRaw = words.slice(0, 6).join(" ");
    restRaw = words.slice(6).join(" ");
  }
  return {
    key: `earned-ranking-section-${num}-${position}`,
    index: String(num).padStart(2, "0"),
    heading: normalizeEarnedMediaRankingCasing(headingRaw, { force }) || `Section ${num}`,
    subheading: normalizeEarnedMediaRankingCasing(subheadingRaw, { force }),
    body: normalizeEarnedMediaRankingCasing(restRaw, { force }),
    isNote: EARNED_MEDIA_RANKING_NOTE_RE.test(`${headingRaw} ${subheadingRaw} ${restRaw}`),
  };
};

// Parse a dense ranking narrative into { intro, sections }. Requires two or more
// numbered "N." markers to treat the text as structured; otherwise the entire
// (casing-normalized) narrative is returned as a single intro paragraph.
export const parseEarnedMediaRankingNarrative = (rawValue = "") => {
  const text = String(rawValue || "").replace(/\s+/g, " ").trim();
  if (!text) return { intro: "", sections: [] };
  const markerRe = /(?:^|\s)(\d{1,2})[.)]\s+/g;
  const markers = [];
  let match;
  while ((match = markerRe.exec(text))) {
    const headStart = match.index + (/^\s/.test(match[0]) ? 1 : 0);
    markers.push({ num: match[1], headStart, bodyStart: markerRe.lastIndex });
  }
  // Decide casing normalization once for the whole narrative so every section
  // (including domain-heavy ones) stays consistent instead of re-judging each
  // fragment in isolation.
  const normalizeCasing = shouldNormalizeEarnedMediaRankingCasing(text);
  if (markers.length < 2) {
    return {
      intro: normalizeEarnedMediaRankingCasing(text, { force: normalizeCasing }),
      sections: [],
    };
  }
  const intro = normalizeEarnedMediaRankingCasing(text.slice(0, markers[0].headStart).trim(), {
    force: normalizeCasing,
  });
  const sections = markers.map((marker, i) => {
    const end = i + 1 < markers.length ? markers[i + 1].headStart : text.length;
    return buildEarnedMediaRankingSection(
      marker.num,
      text.slice(marker.bodyStart, end),
      i,
      normalizeCasing
    );
  });
  return { intro, sections };
};

// True when a ranking field is a dense narrative that should render through the
// structured reader rather than as a plain title/paragraph.
export const isDenseEarnedMediaRankingNarrative = (value = "") => {
  const text = String(value || "").trim();
  if (!text) return false;
  const wordCount = text.split(/\s+/).length;
  const numberedSections = (text.match(/(?:^|\s)\d{1,2}[.)]\s+/g) || []).length;
  return wordCount >= 45 || numberedSections >= 2;
};

// Structured, collapsible renderer for a dense ranking narrative. The full
// content is always in the DOM — the collapsed state only clips it visually
// (max-height + gradient fade), so nothing is ever lost and the toggle stays
// visible below the clipped body.
export function EarnedMediaRankingCopy({ value, renderCopy = (item) => item }) {
  const parsed = useMemo(() => parseEarnedMediaRankingNarrative(value), [value]);
  const [expanded, setExpanded] = useState(false);
  const toggle = useCallback(() => setExpanded((prev) => !prev), []);
  if (!parsed || (!parsed.intro && parsed.sections.length === 0)) return null;
  const collapsible =
    parsed.sections.length > 2 || (!parsed.sections.length && parsed.intro.length > 320);
  const isCollapsed = collapsible && !expanded;
  return (
    <div
      className={`campaign-strategy-microsite-earned-ranking-copy${
        isCollapsed ? " campaign-strategy-microsite-earned-ranking-copy--collapsed" : ""
      }`}
    >
      <div className="campaign-strategy-microsite-earned-ranking-body">
        {parsed.intro ? (
          <p className="campaign-strategy-microsite-earned-ranking-intro">
            {renderCopy(parsed.intro)}
          </p>
        ) : null}
        {parsed.sections.map((section) => (
          <div
            key={section.key}
            className={`campaign-strategy-microsite-earned-ranking-section${
              section.isNote ? " campaign-strategy-microsite-earned-ranking-section--note" : ""
            }`}
          >
            <div className="campaign-strategy-microsite-earned-ranking-section-heading">
              <span className="campaign-strategy-microsite-earned-ranking-section-index">
                {section.isNote ? "!" : section.index}
              </span>
              <span>{renderCopy(section.heading)}</span>
            </div>
            {section.subheading ? (
              <div className="campaign-strategy-microsite-earned-ranking-section-subheading">
                {renderCopy(section.subheading)}
              </div>
            ) : null}
            {section.body ? (
              <p className="campaign-strategy-microsite-earned-ranking-section-body">
                {renderCopy(section.body)}
              </p>
            ) : null}
          </div>
        ))}
        {isCollapsed ? (
          <div className="campaign-strategy-microsite-earned-ranking-fade" aria-hidden="true" />
        ) : null}
      </div>
      {collapsible ? (
        <button
          type="button"
          className="campaign-strategy-microsite-earned-ranking-toggle"
          onClick={toggle}
          aria-expanded={expanded}
        >
          {expanded ? "Collapse" : "View Full Intelligence"}
        </button>
      ) : null}
    </div>
  );
}

// Compact strip that keeps the cast/director-oriented modules honest once
// analyzed social profiles exist: it states the social footprint is in use so
// the module never reads as empty. Renders nothing without analyzed data.
function EarnedMediaSocialNote({ summary, narrative }) {
  if (!summary?.hasSocialData || !narrative) return null;
  return (
    <p
      className="campaign-strategy-microsite-earned-social-note"
      data-testid="microsite-earned-media-social-note"
    >
      {narrative.note}
    </p>
  );
}

export function EarnedMediaIntelligenceSection({ section, renderCopy }) {
  const brandPresentation = useMicrositeBrandPresentation();
  const copy = selectMicrositeCopy(brandPresentation);
  const earned = normalizeEarnedMediaIntelligence(section, { brandPresentation });
  // Submitted profile analysis that has not completed yet. Drives the honest
  // "Analysis Pending" dimension state instead of an empty zero-like track.
  const earnedAnalysisPending = Boolean(
    earned.submittedProfiles?.profiles?.some(
      (profile) =>
        profile.analysisStatus === "pending" ||
        profile.analysisStatus === "connector_required"
    )
  );
  const hasRows =
    earned.talentSignals.length ||
    earned.influencerOpportunities.length ||
    earned.organicConversationSignals.length ||
    earned.amplificationRecommendations.length ||
    earned.measurementPlan.length;

  return (
    <MicrositeSection
      eyebrow="Earned Media Intelligence"
      title="Earned Media Intelligence"
      meta={earned.primarySuccessMetric}
    >
      <p className="campaign-strategy-microsite-section-intro">
        {renderCopy(earned.summary)}
      </p>
      {earned.strategicThesis ? (
        <div className="campaign-strategy-microsite-note-block">
          {renderCopy(earned.strategicThesis)}
        </div>
      ) : null}
      {earned.dataNeededCallout ? (
        <div className="campaign-strategy-microsite-earned-data-callout">
          <strong>{earned.dataInputTitle || earned.dataStatus}</strong>
          <span>{renderCopy(earned.dataNeededCallout)}</span>
        </div>
      ) : null}

      {earned.submittedProfiles ? (
        <div
          className="campaign-strategy-microsite-earned-module campaign-strategy-microsite-earned-accent-cyan campaign-strategy-microsite-earned-submitted-profiles"
          data-testid="microsite-earned-media-submitted-profiles"
        >
          <div className="campaign-strategy-microsite-card-header">
            <span className="campaign-strategy-microsite-card-title campaign-strategy-microsite-earned-module-title">
              Submitted Social Profiles
            </span>
            <span className="campaign-strategy-microsite-card-meta campaign-strategy-microsite-earned-module-meta">
              {earned.submittedProfiles.statusLabel}
            </span>
          </div>
          {earned.submittedProfiles.note ? (
            <p className="campaign-strategy-microsite-earned-submitted-note">
              {renderCopy(earned.submittedProfiles.note)}
            </p>
          ) : null}
          <ul className="campaign-strategy-microsite-earned-submitted-list">
            {earned.submittedProfiles.profiles.map((profile) => {
              // Route the flat analyzed metrics into scannable tiles, wide
              // paragraph blocks, and a source footer (presentation only).
              const { tiles, notes, source } = partitionEarnedMediaProfileMetrics(
                profile.metrics
              );
              return (
                <li
                  key={profile.key}
                  className={`campaign-strategy-microsite-earned-submitted-item${
                    profile.isAnalyzed ? " campaign-strategy-microsite-earned-submitted-item--analyzed" : ""
                  }`}
                >
                  <div className="campaign-strategy-microsite-earned-submitted-header">
                    <a
                      href={profile.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={profile.url}
                      className="campaign-strategy-microsite-earned-submitted-url"
                    >
                      {profile.url}
                    </a>
                    <div className="campaign-strategy-microsite-earned-submitted-badges">
                      <span className="campaign-strategy-microsite-earned-submitted-platform">
                        {profile.platform}
                      </span>
                      <span className="campaign-strategy-microsite-earned-submitted-status">
                        {profile.analysisStatusLabel}
                      </span>
                    </div>
                  </div>
                  {profile.isAnalyzed && tiles.length ? (
                    <dl className="campaign-strategy-microsite-earned-submitted-metrics">
                      {tiles.map((metric) => (
                        <div
                          key={metric.key}
                          className="campaign-strategy-microsite-earned-submitted-metric"
                        >
                          <dt className="campaign-strategy-microsite-earned-submitted-metric-label">
                            {metric.label}
                          </dt>
                          <dd className="campaign-strategy-microsite-earned-submitted-metric-value">
                            {metric.value}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  ) : null}
                  {profile.isAnalyzed && notes.length ? (
                    <div className="campaign-strategy-microsite-earned-submitted-analysis">
                      {notes.map((note) => (
                        <div
                          key={note.key}
                          className="campaign-strategy-microsite-earned-submitted-analysis-block"
                        >
                          <span className="campaign-strategy-microsite-earned-submitted-analysis-label">
                            {note.label}
                          </span>
                          <p className="campaign-strategy-microsite-earned-submitted-analysis-copy">
                            {note.value}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {profile.isAnalyzed && source ? (
                    <div className="campaign-strategy-microsite-earned-submitted-footer">
                      <span className="campaign-strategy-microsite-earned-submitted-footer-label">
                        {source.label}
                      </span>
                      <span className="campaign-strategy-microsite-earned-submitted-footer-value">
                        {source.value}
                      </span>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {earned.socialSummary?.hasSocialData ? (
        <div
          className="campaign-strategy-microsite-earned-module campaign-strategy-microsite-earned-accent-cyan campaign-strategy-microsite-earned-social-summary"
          data-testid="microsite-earned-media-social-summary"
        >
          <div className="campaign-strategy-microsite-card-header">
            <span className="campaign-strategy-microsite-card-title campaign-strategy-microsite-earned-module-title">
              Submitted Social Footprint Intelligence
            </span>
            <span className="campaign-strategy-microsite-card-meta campaign-strategy-microsite-earned-module-meta">
              {earned.socialSummary.isManual ? "Directional · manual metrics" : "Verified social metrics"}
            </span>
          </div>
          <dl className="campaign-strategy-microsite-earned-submitted-metrics campaign-strategy-microsite-earned-social-summary-metrics">
            <div className="campaign-strategy-microsite-earned-submitted-metric">
              <dt className="campaign-strategy-microsite-earned-submitted-metric-label">Analyzed Profiles</dt>
              <dd className="campaign-strategy-microsite-earned-submitted-metric-value">
                {earned.socialSummary.analyzedCount}
              </dd>
            </div>
            {earned.socialSummary.totalFollowersLabel ? (
              <div className="campaign-strategy-microsite-earned-submitted-metric">
                <dt className="campaign-strategy-microsite-earned-submitted-metric-label">Combined Audience</dt>
                <dd className="campaign-strategy-microsite-earned-submitted-metric-value">
                  {earned.socialSummary.totalFollowersLabel}
                </dd>
              </div>
            ) : null}
            {earned.socialSummary.platforms.length ? (
              <div className="campaign-strategy-microsite-earned-submitted-metric">
                <dt className="campaign-strategy-microsite-earned-submitted-metric-label">Platforms</dt>
                <dd className="campaign-strategy-microsite-earned-submitted-metric-value">
                  {earned.socialSummary.platforms.join(" · ")}
                </dd>
              </div>
            ) : null}
            {earned.socialSummary.strongestEngagement ? (
              <div className="campaign-strategy-microsite-earned-submitted-metric">
                <dt className="campaign-strategy-microsite-earned-submitted-metric-label">Top Engagement</dt>
                <dd className="campaign-strategy-microsite-earned-submitted-metric-value">
                  {earned.socialSummary.strongestEngagement.platform} ·{" "}
                  {earned.socialSummary.strongestEngagement.rateLabel}
                </dd>
              </div>
            ) : null}
          </dl>
          <div className="campaign-strategy-microsite-earned-social-summary-copy">
            <p>{renderCopy(earned.socialNarrative.combinedAudience)}</p>
            <p>{renderCopy(earned.socialNarrative.platformRoles)}</p>
            <p>{renderCopy(earned.socialNarrative.amplificationGuidance)}</p>
            <p>{renderCopy(earned.socialNarrative.buyTicketsValidator)}</p>
            <p className="campaign-strategy-microsite-earned-social-summary-qualifier">
              {renderCopy(earned.socialNarrative.sourceQualifier)}
            </p>
          </div>
        </div>
      ) : null}

      <div className="campaign-strategy-microsite-earned-module campaign-strategy-microsite-earned-accent-cyan">
        <div className="campaign-strategy-microsite-card-header">
          <span className="campaign-strategy-microsite-card-title campaign-strategy-microsite-earned-module-title">Cast / Talent Earned Media Ranking</span>
          <span className="campaign-strategy-microsite-card-meta campaign-strategy-microsite-earned-module-meta">Ranked by earned-media value</span>
        </div>
        <EarnedMediaSocialNote summary={earned.socialSummary} narrative={earned.socialNarrative} />
        <div className="campaign-strategy-microsite-earned-ranking-grid campaign-strategy-microsite-centered-card-grid campaign-strategy-microsite-centered-card-grid--2">
	          {earned.talentRanking.map((row) => {
	            const talent =
	              earned.castTalentAnalysis.find((item) => item.name === row.name) ||
	              earned.castTalentAnalysis[row.rank - 1] ||
	              {};
	            const displayState = normalizeEarnedMediaDisplayState(talent.displayState || talent.display_state, talent, { brandPresentation });
	            const hasPlatformSignal = Boolean(displayState.hasPlatformSignal);
	            const rankingNameIsDense = isDenseEarnedMediaRankingNarrative(row.name);
	            const recommendedUseIsDense = isDenseEarnedMediaRankingNarrative(talent.recommendedUse);
	            return (
	              <article key={row.key} className="campaign-strategy-microsite-earned-ranking-card">
                <span className="campaign-strategy-microsite-earned-rank">#{row.rank}</span>
                <div>
                  <strong>{rankingNameIsDense ? EARNED_MEDIA_RANKING_STRATEGY_TITLE : row.name}</strong>
                  <span>
                    {formatMicrositeTextValue(talent.role, "Talent / Partner Voice")}
                    {talent.characterName ? ` • ${talent.characterName}` : ""}
                  </span>
                </div>
                {rankingNameIsDense ? (
                  <EarnedMediaRankingCopy value={row.name} renderCopy={renderCopy} />
                ) : null}
	                {hasPlatformSignal ? (
	                  <dl className="campaign-strategy-microsite-earned-stat-grid">
	                    <div>
	                      <dt>Source</dt>
	                      <dd>{formatMicrositeTextValue(talent.source, copy.earnedSourceFallback)}</dd>
	                    </div>
	                    <div>
	                      <dt>Organic Distribution Potential</dt>
	                      <dd>{formatMicrositeTextValue(talent.totalEstimatedReach, "Earned reach opportunity")}</dd>
	                    </div>
	                    <div>
	                      <dt>Audience Fit</dt>
	                      <dd>{formatMicrositeTextValue(talent.audienceFit, EARNED_MEDIA_DISPLAY_RESEARCH_PRIORITY)}</dd>
	                    </div>
	                    <div>
	                      <dt>Engagement</dt>
	                      <dd>{formatMicrositeTextValue(talent.engagementQuality, "To Be Verified")}</dd>
	                    </div>
	                    <div>
	                      <dt>Amplification</dt>
	                      <dd>{formatMicrositeTextValue(talent.amplificationPriority, EARNED_MEDIA_DISPLAY_RESEARCH_PRIORITY)}</dd>
	                    </div>
	                    <div>
	                      <dt>Score</dt>
	                      <dd>{row.score}</dd>
	                    </div>
	                  </dl>
	                ) : (
	                  <dl className="campaign-strategy-microsite-earned-stat-grid campaign-strategy-microsite-earned-stat-grid--compact">
	                    <div>
	                      <dt>Source</dt>
	                      <dd>{formatMicrositeTextValue(talent.source, copy.earnedSourceFallback)}</dd>
	                    </div>
	                    <div>
	                      <dt>Research Priority</dt>
	                      <dd>{displayState.researchPriorityLabel}</dd>
	                    </div>
	                    <div>
	                      <dt>Earned Reach Opportunity</dt>
	                      <dd>{displayState.reachLabel}</dd>
	                    </div>
	                  </dl>
	                )}
	                {recommendedUseIsDense ? (
	                  <div className="campaign-strategy-microsite-earned-ranking-recommended">
	                    <span className="campaign-strategy-microsite-earned-ranking-recommended-label">
	                      Recommended Use
	                    </span>
	                    <EarnedMediaRankingCopy value={talent.recommendedUse} renderCopy={renderCopy} />
	                  </div>
	                ) : (
	                  <p>
	                    <strong>Recommended Use: </strong>
	                    {renderCopy(formatMicrositeTextValue(talent.recommendedUse, "Recommended use pending."))}
	                  </p>
	                )}
	              </article>
	            );
	          })}
        </div>
      </div>

      <div className="campaign-strategy-microsite-earned-module campaign-strategy-microsite-earned-accent-violet">
        <div className="campaign-strategy-microsite-card-header">
          <span className="campaign-strategy-microsite-card-title campaign-strategy-microsite-earned-module-title">Earned Media Value Chart</span>
          <span className="campaign-strategy-microsite-card-meta campaign-strategy-microsite-earned-module-meta">Audience, engagement, conversion</span>
        </div>
        <EarnedMediaSocialNote summary={earned.socialSummary} narrative={earned.socialNarrative} />
        <div className="campaign-strategy-microsite-earned-value-grid campaign-strategy-microsite-earned-stacked-grid">
          {earned.castTalentAnalysis.map((talent, index) => (
            <article
              key={`${talent.key}-score`}
              className={`campaign-strategy-microsite-earned-value-card campaign-strategy-microsite-earned-value-card--${
                index === 0 ? "primary" : "support"
              }`}
            >
              <EarnedMediaTalentCardHeader talent={talent} renderCopy={renderCopy} />
              <EarnedMediaScoreBars
                talent={talent}
                socialActive={earned.socialSummary?.hasSocialData}
                analysisPending={earnedAnalysisPending}
              />
            </article>
          ))}
        </div>
      </div>

      <div className="campaign-strategy-microsite-earned-module campaign-strategy-microsite-earned-accent-gold">
        <div className="campaign-strategy-microsite-card-header">
          <span className="campaign-strategy-microsite-card-title campaign-strategy-microsite-earned-module-title">Social Footprint Cards</span>
          <span className="campaign-strategy-microsite-card-meta campaign-strategy-microsite-earned-module-meta">Platform-level stats</span>
        </div>
        <EarnedMediaSocialNote summary={earned.socialSummary} narrative={earned.socialNarrative} />
        <div className="campaign-strategy-microsite-earned-footprint-grid campaign-strategy-microsite-earned-stacked-grid">
          {earned.castTalentAnalysis.map((talent, index) => {
            const displayState = normalizeEarnedMediaDisplayState(talent.displayState || talent.display_state, talent, { brandPresentation });
            const platformRows = (Array.isArray(talent.platforms) ? talent.platforms : []).filter(
              hasEarnedMediaPlatformSignal
            );
            return (
              <article
                key={`${talent.key}-footprint`}
                className={`campaign-strategy-microsite-earned-footprint-card campaign-strategy-microsite-earned-footprint-card--${
                  index === 0 ? "primary" : "support"
                }`}
              >
                <EarnedMediaTalentCardHeader
                  talent={{
                    ...talent,
                    role: displayState.hasPlatformSignal
                      ? talent.totalEstimatedReach
                      : EARNED_MEDIA_DISPLAY_OPPORTUNITY_MAPPING,
                  }}
                  renderCopy={renderCopy}
                />
                {platformRows.length ? (
                  platformRows.map((platform) => {
                    const followerLabel = hasEarnedMediaFollowerSignal(platform)
                      ? platform.followers
                      : "To Be Verified";
                    const engagementLabel = isEarnedMediaPendingValue(platform.engagementRate)
                      ? "To Be Verified"
                      : platform.engagementRate;
                    return (
                      <div key={platform.key} className="campaign-strategy-microsite-earned-platform-row">
                        <strong>{platform.platform}</strong>
                        {!isEarnedMediaPendingValue(platform.handle) ? <span>{platform.handle}</span> : null}
                        <span>Follower Reach: {followerLabel}</span>
                        <span>Engagement Benchmark: {engagementLabel}</span>
                        {!isEarnedMediaPendingValue(platform.engagementQuality) ? (
                          <small>{platform.engagementQuality}</small>
                        ) : null}
                        {platform.notes ? <p>{renderCopy(platform.notes)}</p> : null}
                      </div>
                    );
                  })
                ) : (
                  <div className="campaign-strategy-microsite-earned-opportunity-state">
                    <strong>{EARNED_MEDIA_DISPLAY_OPPORTUNITY_MAPPING}</strong>
                    <span>
                      {earned.socialSummary?.hasSocialData
                        ? EARNED_MEDIA_SOCIAL_FOOTPRINT_ACTIVE_COPY
                        : displayState.socialChecklist}
                    </span>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </div>

      <div className="campaign-strategy-microsite-earned-module campaign-strategy-microsite-earned-accent-cyan">
        <div className="campaign-strategy-microsite-card-header">
          <span className="campaign-strategy-microsite-card-title campaign-strategy-microsite-earned-module-title">Talent Activation Recommendations</span>
          <span className="campaign-strategy-microsite-card-meta campaign-strategy-microsite-earned-module-meta">Organic first, paid after proof</span>
        </div>
        <EarnedMediaSocialNote summary={earned.socialSummary} narrative={earned.socialNarrative} />
        <div className="campaign-strategy-microsite-earned-activation-grid campaign-strategy-microsite-earned-stacked-grid">
          {earned.castTalentAnalysis.map((talent, index) => {
            const recommendedUseIsDense = isDenseEarnedMediaRankingNarrative(talent.recommendedUse);
            return (
              <article
                key={`${talent.key}-activation`}
                className={`campaign-strategy-microsite-earned-activation-card campaign-strategy-microsite-earned-activation-card--${
                  index === 0 ? "primary" : "support"
                }`}
              >
                <EarnedMediaTalentCardHeader talent={talent} renderCopy={renderCopy} />
                {recommendedUseIsDense ? (
                  <EarnedMediaRankingCopy value={talent.recommendedUse} renderCopy={renderCopy} />
                ) : (
                  <p className="campaign-strategy-microsite-earned-activation-copy">
                    {renderCopy(talent.recommendedUse)}
                  </p>
                )}
                <div className="campaign-strategy-microsite-earned-activation-groups">
                  <div className="campaign-strategy-microsite-earned-activation-group">
                    <span className="campaign-strategy-microsite-kv-label">Content Formats</span>
                    <EarnedMediaMiniList items={talent.contentFormats} />
                  </div>
                  <div className="campaign-strategy-microsite-earned-activation-group">
                    <span className="campaign-strategy-microsite-kv-label">Outreach / Podcast / Press</span>
                    <EarnedMediaMiniList items={talent.outreachTargets} />
                  </div>
                </div>
                <div className="campaign-strategy-microsite-earned-activation-signal-grid">
                  <div className="campaign-strategy-microsite-release-signal">
                    <span className="campaign-strategy-microsite-kv-label">Paid Amplification Plan</span>
                    <strong>{renderCopy(talent.paidAmplificationPlan)}</strong>
                  </div>
                  <div className="campaign-strategy-microsite-release-signal">
                    <span className="campaign-strategy-microsite-kv-label">Buy Tickets Traffic Role</span>
                    <strong>{renderCopy(talent.buyTicketsRole)}</strong>
                  </div>
                </div>
                <p className="campaign-strategy-microsite-earned-activation-copy">
                  {renderCopy(talent.risksOrWatchouts)}
                </p>
              </article>
            );
          })}
        </div>
      </div>

      <div className="campaign-strategy-microsite-earned-module campaign-strategy-microsite-earned-module--traffic campaign-strategy-microsite-earned-accent-violet">
        <div className="campaign-strategy-microsite-card-header">
          <span className="campaign-strategy-microsite-card-title campaign-strategy-microsite-earned-module-title">Buy Tickets Traffic Plan</span>
          <span className="campaign-strategy-microsite-card-meta campaign-strategy-microsite-earned-module-meta">Measurement Plan</span>
        </div>
        <EarnedMediaSocialNote summary={earned.socialSummary} narrative={earned.socialNarrative} />
        <p>{renderCopy(earned.buyTicketsTrafficPlan.role)}</p>
        <div className="campaign-strategy-microsite-earned-traffic-grid">
          <div>
            <span className="campaign-strategy-microsite-kv-label">Signals To Watch</span>
            <EarnedMediaMiniList items={earned.buyTicketsTrafficPlan.signalsToWatch} />
          </div>
          <div>
            <span className="campaign-strategy-microsite-kv-label">Optimization Actions</span>
            <EarnedMediaMiniList items={earned.buyTicketsTrafficPlan.optimizationActions} />
          </div>
        </div>
      </div>

      {hasRows ? (
        <div className="campaign-strategy-microsite-earned-grid campaign-strategy-microsite-centered-card-grid campaign-strategy-microsite-centered-card-grid--2">
          <article className="campaign-strategy-microsite-earned-card campaign-strategy-microsite-earned-card--talent campaign-strategy-microsite-earned-accent-cyan">
            <div className="campaign-strategy-microsite-card-header">
              <span className="campaign-strategy-microsite-card-title campaign-strategy-microsite-earned-card-title">Talent & Cast Signal</span>
              <span className="campaign-strategy-microsite-card-meta">Signal Quality</span>
            </div>
            <div className="campaign-strategy-microsite-earned-metric-grid">
              {earned.talentSignals.map((item) => (
                <div key={item.key} className="campaign-strategy-microsite-earned-metric">
                  <span>{formatMicrositeTextValue(item.metricLabel || item.title, "Audience Relevance")}</span>
                  <p>{renderCopy(item.copy || item.insight || item.title)}</p>
                </div>
              ))}
            </div>
          </article>

          <article className="campaign-strategy-microsite-earned-card campaign-strategy-microsite-earned-accent-violet">
            <div className="campaign-strategy-microsite-card-header">
              <span className="campaign-strategy-microsite-card-title campaign-strategy-microsite-earned-card-title">Influencer / Advocate Opportunity</span>
              <span className="campaign-strategy-microsite-card-meta">Authentic Voices</span>
            </div>
            {earned.influencerOpportunities.map((item) => (
              <p key={item.key}>{renderCopy(item.copy || item.rationale || item.title)}</p>
            ))}
          </article>

          <article className="campaign-strategy-microsite-earned-card campaign-strategy-microsite-earned-accent-gold">
            <div className="campaign-strategy-microsite-card-header">
              <span className="campaign-strategy-microsite-card-title campaign-strategy-microsite-earned-card-title">Organic Conversation Tracking</span>
              <span className="campaign-strategy-microsite-card-meta">Traction First</span>
            </div>
            {earned.organicConversationSignals.map((item) => (
              <p key={item.key}>{renderCopy(item.copy || item.trackingLogic || item.title)}</p>
            ))}
          </article>

          <article className="campaign-strategy-microsite-earned-card campaign-strategy-microsite-earned-accent-cyan">
            <div className="campaign-strategy-microsite-card-header">
              <span className="campaign-strategy-microsite-card-title campaign-strategy-microsite-earned-card-title">Earned-to-Paid Amplification Strategy</span>
              <span className="campaign-strategy-microsite-card-meta">Scale Proven Demand</span>
            </div>
            {earned.amplificationRecommendations.map((item) => (
              <p key={item.key}>{renderCopy(item.copy || item.title)}</p>
            ))}
          </article>

          <article className="campaign-strategy-microsite-earned-card campaign-strategy-microsite-earned-card--intent campaign-strategy-microsite-earned-accent-violet">
            <div className="campaign-strategy-microsite-card-header">
              <span className="campaign-strategy-microsite-card-title campaign-strategy-microsite-earned-card-title">Ticket Intent Signal</span>
              <span className="campaign-strategy-microsite-card-meta">Leading Indicator</span>
            </div>
            <strong>{earned.primarySuccessMetric}</strong>
            {earned.measurementPlan.map((item) => (
              <p key={item.key}>{renderCopy(item.copy || item.value || item.title)}</p>
            ))}
          </article>
        </div>
      ) : (
        <EmptyPanel>Earned media recommendations will appear once title and audience signals are available.</EmptyPanel>
      )}
    </MicrositeSection>
  );
}

function ReleaseMilestoneScheduleSection({ rows, renderCopy, advertiserVertical = null }) {
  const milestones = normalizeReleaseMilestoneSchedule(rows);
  return (
    <MicrositeSection
      eyebrow="Release Milestone Schedule"
      title="Release Milestone Schedule"
      meta="Awareness to conversion"
    >
      <p className="campaign-strategy-microsite-section-intro">
        {normalizeAdvertiserVertical(advertiserVertical) === "film"
          ? "Critical planning windows from awareness building through opening-weekend conversion."
          : "Critical planning windows from awareness building through late-flight conversion."}
      </p>
      {milestones.length ? (
        <div className="campaign-strategy-microsite-release-grid campaign-strategy-microsite-centered-card-grid campaign-strategy-microsite-centered-card-grid--2">
          {milestones.map((milestone, index) => (
            <article
              key={milestone.key}
              className="campaign-strategy-microsite-release-card"
            >
              <span className="campaign-strategy-microsite-release-index">
                {`${index + 1}`.padStart(2, "0")}
              </span>
              <span className="campaign-strategy-microsite-card-meta campaign-strategy-microsite-release-card-eyebrow">
                {milestone.timing}
              </span>
              <strong className="campaign-strategy-microsite-card-title">
                {milestone.phase}
              </strong>
              <p>{renderCopy(milestone.objective)}</p>
              {milestone.actions.length ? (
                <ul className="campaign-strategy-microsite-bullet-list">
                  {milestone.actions.map((action, actionIndex) => (
                    <li key={`${milestone.key}-action-${actionIndex}`}>
                      {renderCopy(action)}
                    </li>
                  ))}
                </ul>
              ) : null}
              <div className="campaign-strategy-microsite-release-signal">
                <span className="campaign-strategy-microsite-kv-label">Success Signal</span>
                <strong>{milestone.successSignal}</strong>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <EmptyPanel>Release milestones will appear once the campaign schedule is available.</EmptyPanel>
      )}
    </MicrositeSection>
  );
}

// Phase 1 execution-workflow-inspired intelligence sections. All three are additive and
// render only when the backend supplies the corresponding payload section, so
// existing microsites are unaffected when the fields are absent.
export function ReleasePhaseIntelligenceSection({ intel, renderCopy, title = "Release Phase Intelligence" }) {
  if (!intel || typeof intel !== "object") return null;
  const phases = Array.isArray(intel.phases) ? intel.phases : [];
  if (!phases.length) return null;

  const metaParts = [];
  if (intel.releaseLabel) metaParts.push(`Release ${intel.releaseLabel}`);
  else if (intel.flightWindow?.label) metaParts.push(intel.flightWindow.label);

  const currentPhase = phases.find((phase) => phase.status === "current") || null;
  const statCards = [
    intel.currentPhaseLabel
      ? { key: "current_phase", label: "Current Phase", value: intel.currentPhaseLabel }
      : null,
    intel.releaseLabel ? { key: "release_date", label: "Release Date", value: intel.releaseLabel } : null,
    intel.flightWindow?.label
      ? { key: "flight_window", label: "Flight Window", value: intel.flightWindow.label }
      : null,
    intel.urgency ? { key: "urgency", label: "Launch Urgency", value: intel.urgency } : null,
  ].filter(Boolean);

  return (
    <MicrositeSection
      eyebrow="Campaign Timing"
      title={title}
      meta={metaParts.join(" · ")}
    >
      {intel.rationale ? (
        <p className="campaign-strategy-microsite-section-intro">{renderCopy(intel.rationale)}</p>
      ) : null}
      <div className="campaign-strategy-microsite-phase-strip" role="list">
        {phases.map((phase) => (
          <div
            key={phase.key}
            role="listitem"
            className={`campaign-strategy-microsite-phase-node campaign-strategy-microsite-phase-node--${
              phase.status || "upcoming"
            }`}
          >
            <span className="campaign-strategy-microsite-phase-node-label">{phase.label}</span>
            {phase.status === "current" ? (
              <span className="campaign-strategy-microsite-phase-node-now">Now</span>
            ) : null}
          </div>
        ))}
      </div>
      {statCards.length ? (
        <div className="campaign-strategy-microsite-stat-grid campaign-strategy-microsite-centered-card-grid campaign-strategy-microsite-centered-card-grid--3">
          {statCards.map((card) => (
            <article key={card.key} className="campaign-strategy-microsite-stat-card">
              <strong>{card.value}</strong>
              <span>{card.label}</span>
            </article>
          ))}
        </div>
      ) : null}
      {currentPhase?.description ? (
        <div className="campaign-strategy-microsite-note-block">
          <p>{renderCopy(currentPhase.description)}</p>
        </div>
      ) : null}
    </MicrositeSection>
  );
}

export function AntiWasteGuardrailsSection({ guardrails, renderCopy }) {
  if (!guardrails || typeof guardrails !== "object") return null;
  const items = Array.isArray(guardrails.items) ? guardrails.items : [];
  if (!items.length) return null;

  return (
    <MicrositeSection
      eyebrow="Anti-Waste Strategy"
      title="Guardrails — What This Plan Avoids"
      meta={`${items.length} guardrail${items.length === 1 ? "" : "s"}`}
    >
      {guardrails.intro ? (
        <p className="campaign-strategy-microsite-section-intro">{renderCopy(guardrails.intro)}</p>
      ) : null}
      <div className="campaign-strategy-microsite-insight-grid campaign-strategy-microsite-centered-card-grid campaign-strategy-microsite-centered-card-grid--2">
        {items.map((item, index) => (
          <article
            key={item.key || `guardrail-${index}`}
            className="campaign-strategy-microsite-insight-card"
          >
            <div className="campaign-strategy-microsite-card-header">
              <span className="campaign-strategy-microsite-card-title">{item.title}</span>
              {item.tag ? (
                <span className="campaign-strategy-microsite-card-meta">{item.tag}</span>
              ) : null}
            </div>
            <p>{renderCopy(item.copy)}</p>
          </article>
        ))}
      </div>
    </MicrositeSection>
  );
}

export function ScenarioExpansionTriggersSection({ triggers, renderCopy }) {
  if (!triggers || typeof triggers !== "object") return null;
  const scenarios = Array.isArray(triggers.scenarios) ? triggers.scenarios : [];
  if (!scenarios.length) return null;

  return (
    <MicrositeSection
      eyebrow="Scenario Planning"
      title="Scale, Hold, or Shift"
      meta="Decision triggers"
    >
      {triggers.intro ? (
        <p className="campaign-strategy-microsite-section-intro">{renderCopy(triggers.intro)}</p>
      ) : null}
      <div className="campaign-strategy-microsite-flow-grid campaign-strategy-microsite-centered-card-grid campaign-strategy-microsite-centered-card-grid--2">
        {scenarios.map((scenario, index) => (
          <article
            key={scenario.key || `scenario-${index}`}
            className="campaign-strategy-microsite-flow-card"
          >
            <div className="campaign-strategy-microsite-card-header">
              <span className="campaign-strategy-microsite-card-title">{scenario.label}</span>
              {scenario.tag ? (
                <span className="campaign-strategy-microsite-card-meta">{scenario.tag}</span>
              ) : null}
            </div>
            <div className="campaign-strategy-microsite-scenario-row">
              <span className="campaign-strategy-microsite-kv-label">Trigger</span>
              <p>{renderCopy(scenario.trigger)}</p>
            </div>
            <div className="campaign-strategy-microsite-scenario-row">
              <span className="campaign-strategy-microsite-kv-label">Action</span>
              <p>{renderCopy(scenario.action)}</p>
            </div>
            {scenario.kpi ? (
              <div className="campaign-strategy-microsite-release-signal">
                <span className="campaign-strategy-microsite-kv-label">KPI</span>
                <MicrositeKpiValue as="strong" value={scenario.kpi} />
              </div>
            ) : null}
          </article>
        ))}
      </div>
    </MicrositeSection>
  );
}

export function SuccessMetricsSection({ metrics, renderCopy, allocationRows = [], defaultFiguresApply = false }) {
  const semanticTypography = useMicrositeSemanticTypography();
  if (!metrics || typeof metrics !== "object") return null;
  const rows = orderMicrositePlatformRows(
    Array.isArray(metrics.rows) ? metrics.rows : [],
    allocationRows
  );
  if (!rows.length) return null;
  // The summary cards and the KPI targets are default figures
  // (micrositeShowsDefaultFigures): shown only when the page says they apply.
  const summaryStats =
    defaultFiguresApply && Array.isArray(metrics.summaryStats)
      ? metrics.summaryStats.filter((stat) => stat && stat.value && stat.label)
      : [];
  const hasTarget = defaultFiguresApply && rows.some((row) => row.target);

  return (
    <MicrositeSection
      eyebrow="Success Metrics"
      title="Every Platform Tied to a Measurable KPI"
      meta={metrics.objectiveLabel || "KPI intelligence"}
    >
      {metrics.intro ? (
        <p className="campaign-strategy-microsite-section-intro">{renderCopy(metrics.intro)}</p>
      ) : null}
      <div className="campaign-strategy-microsite-metrics-table-wrap">
        <table className="campaign-strategy-microsite-metrics-table" data-testid="success-metrics-table">
          <thead>
            <tr>
              <th scope="col">Platform</th>
              <th scope="col">Primary KPI</th>
              {hasTarget ? <th scope="col">Target</th> : null}
              <th scope="col">Secondary KPI</th>
              <th scope="col">Bidding</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.key || `success-metric-${index}`}>
                <td data-label="Platform">
                  <span className="campaign-strategy-microsite-platform-role-title">
                    <PlatformLogoFrame
                      platform={row}
                      className="campaign-strategy-microsite-platform-logo-frame--mini"
                    />
                    <span className="campaign-strategy-microsite-card-title">
                      {displayMicrositePlatformIdentityLabel(
                        formatMicrositeTextValue(row.platform, "Platform"),
                        row.key
                      )}
                    </span>
                  </span>
                </td>
                <td data-label="Primary KPI">
                  <strong>{semanticTypography ? formatMicrositePrimaryKpiDisplay(row.primaryKpi) : row.primaryKpi}</strong>
                </td>
                {hasTarget ? (
                  <td data-label="Target">
                    <strong className="campaign-strategy-microsite-metrics-target">
                      {row.target || "—"}
                    </strong>
                  </td>
                ) : null}
                <td data-label="Secondary KPI">{row.secondaryKpi}</td>
                <td data-label="Bidding">{renderCopy(row.bidding)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {summaryStats.length ? (
        <div className="campaign-strategy-microsite-metrics-summary-grid">
          {summaryStats.map((stat, index) => (
            <article
              key={stat.key || `summary-stat-${index}`}
              className="campaign-strategy-microsite-metrics-summary-card"
              style={stat.accent ? { "--proof-accent": stat.accent } : undefined}
            >
              <span className="campaign-strategy-microsite-metrics-summary-label">{stat.label}</span>
              <strong className="campaign-strategy-microsite-metrics-summary-value">{stat.value}</strong>
              {stat.caption ? (
                <p className="campaign-strategy-microsite-metrics-summary-caption">{stat.caption}</p>
              ) : null}
            </article>
          ))}
        </div>
      ) : null}
    </MicrositeSection>
  );
}

// Derive a readable dollar-value Y-axis (nice-number tick scale) for the spend
// ramp chart. Bars AND gridlines both scale to the returned axisMax so the
// stacked columns line up with the tick labels. Returns null for non-positive
// budgets so the chart can fail closed. Pure math — no plan data is mutated.
export const buildSpendRampAxis = (maxTotal) => {
  const max = Number(maxTotal);
  if (!(max > 0)) return null;
  const targetIntervals = 7;
  const rawStep = max / targetIntervals;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const normalized = rawStep / magnitude;
  let niceMultiple;
  if (normalized <= 1) niceMultiple = 1;
  else if (normalized <= 2) niceMultiple = 2;
  else if (normalized <= 2.5) niceMultiple = 2.5;
  else if (normalized <= 5) niceMultiple = 5;
  else niceMultiple = 10;
  const step = niceMultiple * magnitude;
  const axisMax = Math.ceil(max / step) * step;
  const ticks = [];
  for (let value = 0; value <= axisMax + step / 2; value += step) {
    const rounded = Math.round(value);
    ticks.push({
      value: rounded,
      label: `$${rounded.toLocaleString("en-US")}`,
      pct: axisMax > 0 ? (rounded / axisMax) * 100 : 0,
    });
  }
  return { axisMax, step, ticks };
};

const formatGovernedSpendRampCurrency = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: Number.isInteger(numeric) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(numeric);
};

export const SPEND_RAMP_PLATFORM_COLORS = Object.freeze({
  meta: "#3d63f5",
  youtube: "#e3a43a",
  ctv: "#28c7f7",
  tiktok: "#f05f88",
  x: "#aeb7c8",
  google_search: "#38c985",
  chatgpt_search: "#00d6a3",
  rumble: "#9bdc3c",
  truth_social: "#4f8cff",
  reddit: "#ff7043",
  amazon: "#f2c14e",
  shopify: "#95bf47",
  linkedin: "#5aa7ff",
  video: "#ff7a45",
  display: "#9a73ff",
  mobile: "#25d0b2",
  audio: "#d35cff",
  ingame: "#ffda57",
  dooh: "#ffb3a7",
  email: "#f78acb",
  reserve_test: "#b7becd",
});

const getSpendRampPlatformCandidates = (platform) => {
  if (platform && typeof platform === "object") {
    return [
      platform.key,
      platform.platformKey,
      platform.platform_key,
      platform.label,
      platform.platformLabel,
      platform.platform,
      platform.name,
      platform.title,
    ].filter(Boolean);
  }
  return [platform].filter(Boolean);
};

export const normalizeSpendRampPlatformColorKey = (platform) => {
  const candidates = getSpendRampPlatformCandidates(platform);
  for (const candidate of candidates) {
    const known = normalizePlatformAllocationKey(candidate, { allowUnknown: false });
    if (known) return known;
  }
  for (const candidate of candidates) {
    const fallback = normalizePlatformAllocationKey(candidate, { allowUnknown: true });
    if (fallback) return fallback;
  }
  return "unknown_platform";
};

const hashSpendRampPlatformKey = (key) =>
  String(key || "")
    .split("")
    .reduce((hash, char) => (hash * 31 + char.charCodeAt(0)) >>> 0, 2166136261);

const resolveUnknownSpendRampPlatformColor = (key) => {
  const hash = hashSpendRampPlatformKey(key);
  const hue = hash % 360;
  const saturation = 68 + ((hash >>> 4) % 16);
  const lightness = 50 + ((hash >>> 9) % 10);
  return `hsl(${hue} ${saturation}% ${lightness}%)`;
};

export const resolveSpendRampPlatformColor = (platform) => {
  const key = normalizeSpendRampPlatformColorKey(platform);
  return SPEND_RAMP_PLATFORM_COLORS[key] || resolveUnknownSpendRampPlatformColor(key);
};

const getSpendRampPlatformColorStyle = (platform) => ({
  "--spendramp-platform-color": resolveSpendRampPlatformColor(platform),
});

export const resolveLaunchActivationCalendar = (section = {}) => {
  const calendar =
    section && typeof section === "object" && section.launchActivationCalendar &&
    typeof section.launchActivationCalendar === "object"
      ? section.launchActivationCalendar
      : section && typeof section === "object" && Array.isArray(section.milestones)
      ? section
      : null;
  if (!calendar) return null;
  const weeks = Array.isArray(calendar.spendRamp?.weeks) ? calendar.spendRamp.weeks : [];
  const milestones = Array.isArray(calendar.milestones) ? calendar.milestones : [];
  if (!weeks.length && !milestones.length) return null;
  return calendar;
};

// Client-facing withheld copy comes from ONE presenter that reads the
// canonical reason codes the backend already published, so the badge, the
// title and the message cannot drift apart or contradict the evidence.
// The local chooser below is retained only for states the presenter maps to
// UNAVAILABLE, so no state loses its wording.
const resolveGovernanceBlockedCopy = (status, code, releaseDisposition, governance = null) => {
  const canonical = resolvePublicWithholdingCopy(governance);
  if (canonical.reason !== "UNAVAILABLE") {
    return {
      statusLabel: canonical.statusLabel,
      title: canonical.title,
      message: canonical.message,
    };
  }
  return resolveLegacyGovernanceBlockedCopy(status, code, releaseDisposition);
};

const resolveLegacyGovernanceBlockedCopy = (status, code, releaseDisposition) => {
  // Planner propagation lifecycle (planner_propagation_state_v1): a confirmed
  // governed Planner change is being applied downstream, or the application
  // needs internal attention. Client-safe by construction — no mechanics, no
  // failure internals; the sanitized payload only carries the state itself.
  if (code === "strategy_update_in_progress") {
    return {
      title: "Strategy update in progress",
      message:
        "This strategy is being updated to match the latest confirmed campaign details. The previous version has been withheld and will be replaced once the update is complete and approved.",
    };
  }
  if (code === "strategy_update_attention") {
    return {
      title: "Strategy update pending",
      message:
        "The latest confirmed campaign updates have not been applied to this strategy yet. It remains withheld while your Cphere team completes the update.",
    };
  }
  // The canonical release disposition outranks the legacy status for copy
  // selection: approval-only withholding must never wear regeneration copy
  // (production conflation: a fresh AWAITING_APPROVAL strategy rendered the
  // stale banner because one boolean collapsed every cause).
  if (releaseDisposition === "WITHHELD_APPROVAL_REQUIRED" || status === "AWAITING_APPROVAL") {
    return {
      title: "Updated strategy awaiting approval",
      message:
        "The campaign changed and its regenerated strategy is awaiting governed approval. The prior strategy has been withheld.",
    };
  }
  // The client surface must not assert WHY a strategy is withheld: a
  // REGENERATION_REQUIRED state does not imply the campaign changed (an
  // unbound pre-governance strategy reports it with inputs provably
  // unchanged), and regeneration/approval mechanics are internal remediation
  // that no client-facing page should instruct.
  if (status === "REGENERATION_REQUIRED" && code === "strategy_not_current") {
    return {
      title: "Strategy regeneration required",
      message:
        "This strategy is being finalized and has been withheld. It will appear here once it is current and approved.",
    };
  }
  // A declared allocation-approval-required state gets the client-safe
  // pending-allocation copy: it names the real blocker and never mentions
  // regeneration or internal review mechanics.
  if (status === "ALLOCATION_APPROVAL_REQUIRED") {
    return {
      title: CAMPAIGN_ALLOCATION_PENDING_BLOCKED_COPY.title,
      message: CAMPAIGN_ALLOCATION_PENDING_BLOCKED_COPY.message,
    };
  }
  // A governance response this build cannot interpret is an availability
  // problem, never proof the strategy is stale — the client surface stays
  // safely unavailable with no internal detail and no regeneration advice.
  if (GOVERNANCE_INTERPRETATION_FAILURE_CODES.includes(code)) {
    return {
      title: "Strategy temporarily unavailable",
      message:
        "The strategy-governance status could not be verified. The strategy remains withheld until the governance status is restored — reload this page to retry.",
    };
  }
  return {
    title: "Strategy verification required",
    message:
      "This strategy could not be verified against the current campaign and has been withheld.",
  };
};

// Neutral copy for the wait before the backend answers. It claims nothing
// about the strategy: not current, not withheld, not approved.
export const CAMPAIGN_STRATEGY_MICROSITE_LOADING_COPY = "Loading campaign strategy…";

export const CAMPAIGN_STRATEGY_REVIEW_ACCESS_HEADER_DESCRIPTION =
  "Secure review access is required to view this strategy.";

export const resolveCampaignStrategyMicrositeHeaderDescription = (
  governedRenderState = null,
  {
    answered = true,
    reviewAccessOffered = false,
    sessionAccessRefused = false,
    brandPresentation = false,
  } = {}
) => {
  /*
   * Nothing to describe until the backend has answered.
   *
   * Before the strategy response resolves, governedRenderState is derived from
   * an empty payload and reads as blocked, so this used to print "Campaign
   * strategy content remains withheld…" for the second or two every visitor
   * waited, campaign 53's client included (2026-09-26), before the strategy
   * replaced it. Withholding is the backend's answer, never the loading
   * state's: until there is one (still loading, or the request failed) the
   * header describes nothing, and the page shows its neutral loading card or
   * the error instead.
   */
  if (!answered) return null;
  if (governedRenderState?.clientSafeToRender) {
    return selectMicrositeCopy(brandPresentation).headerDescription;
  }
  /*
   * Nothing saved yet is not the same as something withheld.
   *
   * "Campaign strategy content remains withheld" read directly above this
   * page's own "No strategy is available yet" card, so the header described a
   * strategy being held back from the client while the body said none had ever
   * been written. NO_STRATEGY is the backend's own answer to that question —
   * both render-state paths carry it — so the header can state it.
   */
  if (
    governedRenderState?.nextActionState === STRATEGY_NEXT_ACTION_STATES.NO_STRATEGY
  ) {
    return "No strategy has been saved for this campaign yet. Your Cphere team will prepare it when the campaign is ready.";
  }
  /*
   * A reviewable strategy withheld only from this reader.
   *
   * The backend offers review of this governed state and withheld the body
   * because the read carried no review access, so what stands between the
   * reader and the strategy is access, not governance. "Remains withheld
   * until its governed dependencies are current and approved" was untrue of
   * it, and was the first of four statements of one status above the one
   * action the page offers. The page passes reviewAccessOffered only while it
   * shows the review-access panel, and sessionAccessRefused only while its
   * card names a refused sign-in (resolveStrategySessionAccessReadState).
   */
  if (reviewAccessOffered || sessionAccessRefused) {
    return CAMPAIGN_STRATEGY_REVIEW_ACCESS_HEADER_DESCRIPTION;
  }
  return "Campaign strategy content remains withheld until its governed dependencies are current and approved for client viewing.";
};

const buildBlockedGovernanceState = ({
  governance = null,
  code,
  detail = "",
  preserveGovernanceStatus = false,
  statusOverride = null,
  // The canonical client-safe next-action envelope, when the backend served
  // one. Its notice REPLACES the locally-selected copy so the client and the
  // superadmin are reading one backend answer about one strategy rather than
  // two independently-composed descriptions of it. The withholding DECISION is
  // untouched — it stays governance evidence, and stays fail-closed.
  nextAction = null,
} = {}) => {
  // A response this build cannot interpret is a technical verification
  // failure, never proof that regeneration is required — it gets its own
  // fail-closed status/badge instead of borrowing REGENERATION_REQUIRED.
  const interpretationFailure = GOVERNANCE_INTERPRETATION_FAILURE_CODES.includes(code);
  const status = interpretationFailure
    ? "GOVERNANCE_STATUS_UNAVAILABLE"
    : statusOverride
      ? statusOverride
      : preserveGovernanceStatus
        ? String(governance?.status || "REGENERATION_REQUIRED").trim().toUpperCase()
        : "REGENERATION_REQUIRED";
  // The BADGE must name the blocker, not the internal status token.
  //
  // `status` collapses every blocked cause to REGENERATION_REQUIRED, so an
  // approval-only withholding wore a regeneration badge directly above copy
  // that said "awaiting approval" — the card contradicted itself and told the
  // reader a current strategy needed regenerating. The backend distinguishes
  // the two causes, so the badge can too.
  //
  // This is presentation only. `status` is deliberately left alone: it drives
  // data-governance-status and every downstream consumer, and
  // clientSafeToRender stays false either way. Relabelling withheld content
  // never unwithholds it.
  const approvalOnlyWithholding =
    !interpretationFailure &&
    governance?.requiresRegeneration !== true &&
    (String(governance?.releaseDisposition || "").trim().toUpperCase() ===
      "WITHHELD_APPROVAL_REQUIRED" ||
      String(governance?.status || "").trim().toUpperCase() === "AWAITING_APPROVAL");
  const canonicalNotice = resolveStrategyNextActionNotice(nextAction);
  const blockedCopy = resolveGovernanceBlockedCopy(
    status,
    code,
    governance?.releaseDisposition || null,
    governance
  );
  // The canonical STATE is a machine token. De-underscoring it produced badges
  // reading "GENERATION RUNNING" and "GENERATION BLOCKED SYSTEM DEFECT" — our
  // internal vocabulary, on an advertiser's own page. The backend now sends the
  // client-safe NAME of the state alongside its notice; without one, the legacy
  // copy stands rather than the token.
  const statusLabel = canonicalNotice?.stateLabel
    ? canonicalNotice.stateLabel.toUpperCase()
    : blockedCopy.statusLabel ||
      (interpretationFailure
        ? "GOVERNANCE VERIFICATION TEMPORARILY UNAVAILABLE"
        : approvalOnlyWithholding
          ? "AWAITING APPROVAL"
          : status.replace(/_/g, " "));
  return {
    clientSafeToRender: false,
    status,
    code,
    detail,
    governance,
    freshnessStatus: governance?.freshnessStatus || null,
    previewReadiness: governance?.previewReadiness || null,
    approvalReadiness: governance?.approvalReadiness || null,
    changedDependencies: Array.isArray(governance?.changedDependencies)
      ? governance.changedDependencies
      : [],
    blockers: Array.isArray(governance?.blockers) ? governance.blockers : [],
    releaseDisposition: governance?.releaseDisposition || null,
    releaseReasons: Array.isArray(governance?.releaseReasons)
      ? governance.releaseReasons
      : [],
    ...blockedCopy,
    // Apply the canonical state label after the legacy copy object. Some
    // legacy blocked-copy variants also carry a statusLabel, but they are
    // subordinate diagnostics whenever the backend supplied nextAction.
    statusLabel,
    // Same state token the superadmin envelope carries, so portal parity is a
    // value that can be compared rather than prose that has to be matched.
    nextActionState: canonicalNotice?.state || null,
    ...(canonicalNotice
      ? {
          title: canonicalNotice.headline,
          message: [canonicalNotice.detail, canonicalNotice.nextAction]
            .filter(Boolean)
            .join(" "),
        }
      : {}),
  };
};

export const validateGovernedLaunchActivationCalendar =
  validateCampaignMicrositeCalendar;

export const resolveCampaignStrategyMicrositeGovernance = (payload) => {
  // The backend publishes an AUDIENCE-SPECIFIC verdict. When it is present it
  // is the authority — this surface does not re-derive who may read what, and
  // it never treats a superadmin projection as a client-review one: the
  // audience is stamped server-side from the authenticated identity, so a
  // client-supplied hint cannot widen it.
  const canonical = resolveStrategyVisibility(payload);
  if (canonical.projectionValid && canonical.displayArtifactAudience !== "CLIENT_REVIEW") {
    // A projection addressed to another audience authorises nothing here.
    return buildBlockedGovernanceState({
      governance: payload?.governance,
      code: "strategy_not_current",
      preserveGovernanceStatus: true,
      nextAction: payload?.nextAction,
    });
  }
  if (canonical.projectionValid && canonical.canViewStrategy !== true) {
    return buildBlockedGovernanceState({
      governance: payload?.governance,
      code: "strategy_not_current",
      preserveGovernanceStatus: true,
      nextAction: payload?.nextAction,
    });
  }

  const governance = payload?.governance;
  const campaign = payload?.campaign;
  const calendar =
    resolveLaunchActivationCalendar(campaign?.sections || {}) ||
    resolveLaunchActivationCalendar(campaign || {});
  const evidence = validateCampaignMicrositeGovernanceEvidence({
    governance,
    campaign,
    calendar,
  });
  if (!evidence.valid) {
    return buildBlockedGovernanceState({
      governance,
      code: evidence.code,
      detail: evidence.reason,
      nextAction: payload?.nextAction,
    });
  }
  // Review comes BEFORE approval. A strategy the backend reports as current,
  // governed and integrity-valid is readable by the reviewer who has to
  // approve it — otherwise the workflow is circular: nobody can approve what
  // nobody may read. The backend answers the two questions separately
  // (clientReviewSafeToRender vs clientSafeToRender/safeToPublish) and only
  // grants review to an authenticated reviewer scoped to the advertiser, so
  // an unapproved strategy still never reaches the anonymous public surface.
  //
  // Staleness is unaffected: clientReviewSafeToRender is false for anything
  // REGENERATION_REQUIRED, so a stale strategy stays withheld here exactly as
  // before, and a prior approval cannot authorize one.
  //
  // clientReviewSafeToRender is a property of the GOVERNED STATE — "this
  // artifact is reviewable" — not of the caller. The backend decides
  // separately whether THIS caller gets the artifact, and withholds the body
  // when they do not, declaring it with strategyUnavailable.
  //
  // So the two must be read together. Reading the review flag alone made an
  // anonymous response for an approval-pending campaign look renderable, and
  // the surface would have claimed to present a strategy the response did not
  // contain. This is not a second opinion about governance: it is the client
  // declining to render content it was not given.
  const artifactWithheldByBackend =
    payload?.strategyUnavailable === true ||
    payload?.strategy_unavailable === true ||
    campaign?.strategyUnavailable === true;
  const reviewSafe =
    governance?.clientReviewSafeToRender === true && !artifactWithheldByBackend;
  // Only an authorized reviewer receives this flag together with the body.
  // Pending allocation is readable as a proposal; it grants no approval.
  const awaitingAllocationApproval =
    evidence.status === "ALLOCATION_APPROVAL_REQUIRED" && reviewSafe;
  const awaitingApproval =
    (evidence.status === CAMPAIGN_MICROSITE_AWAITING_APPROVAL_STATUS && reviewSafe) ||
    awaitingAllocationApproval;

  // Planner propagation lifecycle. UPDATE_IN_PROGRESS withholds
  // unconditionally: the backend has declared the prior artifact not current
  // for approval while a confirmed change propagates, and that transient
  // window must never present old content as current. UPDATE_FAILED only
  // recolors an ALREADY-blocked state (below) — it never overrides a strategy
  // the governance evidence itself declares current and renderable, so a
  // lingering settled obligation cannot withhold a repaired strategy.
  const propagationState = governance?.propagation?.state;
  if (propagationState === "UPDATE_IN_PROGRESS") {
    return buildBlockedGovernanceState({
      governance,
      code: "strategy_update_in_progress",
      statusOverride: "STRATEGY_UPDATE_IN_PROGRESS",
      nextAction: payload?.nextAction,
    });
  }

  if (
    (evidence.status !== CAMPAIGN_MICROSITE_CURRENT_STATUS ||
      evidence.clientSafeToRender !== true) &&
    !awaitingApproval
  ) {
    if (propagationState === "UPDATE_FAILED") {
      return buildBlockedGovernanceState({
        governance,
        code: "strategy_update_attention",
        statusOverride: "STRATEGY_UPDATE_PENDING",
        nextAction: payload?.nextAction,
      });
    }
    return buildBlockedGovernanceState({
      governance,
      code: "strategy_not_current",
      preserveGovernanceStatus: true,
      nextAction: payload?.nextAction,
    });
  }

  return {
    // Renders the governed body in both states. `approved` below is what
    // distinguishes "reviewing" from "approved" for badges and copy.
    clientSafeToRender: true,
    approved: !awaitingApproval,
    awaitingApproval,
    status: evidence.status,
    code: awaitingApproval ? "awaiting_approval" : "current",
    title: awaitingApproval
      ? "Strategy ready for review"
      : "Current governed strategy",
    message: awaitingAllocationApproval
      ? "This is the current proposed strategy and media allocation for your campaign. Allocation and strategy approval are still pending."
      : awaitingApproval
        ? "This is the current governed strategy for your campaign. Your approval is still pending."
      : "",
    governance,
    current: evidence.current,
    strategy: evidence.strategy,
    publication: evidence.publication,
    calendarValidation: evidence.calendarValidation,
    nextActionState: normalizeStrategyNextAction(payload?.nextAction)?.state || null,
    freshnessStatus: governance?.freshnessStatus || "CURRENT",
    previewReadiness: governance?.previewReadiness || "PREVIEWABLE",
    approvalReadiness: governance?.approvalReadiness || "APPROVED",
    changedDependencies: Array.isArray(governance?.changedDependencies)
      ? governance.changedDependencies
      : [],
    blockers: Array.isArray(governance?.blockers) ? governance.blockers : [],
    releaseDisposition: governance?.releaseDisposition || "CURRENT_AND_APPROVED",
    releaseReasons: Array.isArray(governance?.releaseReasons)
      ? governance.releaseReasons
      : [],
  };
};

// ---------------------------------------------------------------------------
// SHERLOC governed channel-allocation recommendation (Cortex client-safe
// states). The client surface only ever names the real blocker and shows the
// client-safe recommendation shape — no net, no margin, no provider or
// constraint internals, and no approval controls.
// ---------------------------------------------------------------------------

export const CAMPAIGN_ALLOCATION_REQUIRED_FINDING =
  "GOVERNED_CHANNEL_ALLOCATION_REQUIRED";
export const CAMPAIGN_ALLOCATION_APPROVAL_FINDING = "ALLOCATION_APPROVAL_REQUIRED";

export const resolveCampaignAllocationFindingCodes = (payload) =>
  normalizeCampaignMicrositeGovernanceFindings(payload?.governance?.findings).map(
    (finding) => finding.code
  );

export const resolveCampaignAllocationRecommendation = (campaign) => {
  const recommendation = campaign?.allocationRecommendation;
  return recommendation && typeof recommendation === "object" ? recommendation : null;
};

export const resolveCampaignAllocationApprovalStatus = (recommendation) =>
  String(recommendation?.allocationApprovalStatus || "")
    .trim()
    .toUpperCase();

// Exact-dollar client-safe gross amounts (never the compact $287K style).
export const formatAllocationAmount = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "$0";
  const sign = numeric < 0 ? "-" : "";
  return `${sign}$${Math.abs(numeric).toLocaleString("en-US", {
    minimumFractionDigits: Number.isInteger(numeric) ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
};

// The blocked notice must name the real blocker when the only thing standing
// between the client and the strategy is a pending governed channel
// allocation — it must never instruct anyone to regenerate the plan.
export const CAMPAIGN_ALLOCATION_PENDING_BLOCKED_COPY = Object.freeze({
  statusLabel: "CHANNEL ALLOCATION PENDING",
  title: "Channel allocation pending",
  message:
    "This strategy's channel allocation requires review. The strategy preview remains withheld until allocation approval and all strategy governance requirements are satisfied.",
});

/*
 * The status card above the review-access panel.
 *
 * The panel is offered only to an anonymous reader of a reviewable strategy
 * the backend withheld from them. The backend's notice serves every audience
 * of the state and explains the campaign's authorization in full. Above the
 * panel it repeated the badge and the approval strip beneath it, and buried
 * the one action on the page. Here the card says only that the strategy is
 * ready for review and what stays pending, and leaves the next step to the
 * panel.
 *
 * Presentation only. The badge is still the backend's name for the state,
 * data-governance-status and data-next-action-state are unchanged, nothing
 * withheld is rendered, and a state without an entry here keeps the
 * backend's own words.
 */
export const CAMPAIGN_STRATEGY_REVIEW_GATEWAY_NOTICE_COPY = Object.freeze({
  [STRATEGY_NEXT_ACTION_STATES.STRATEGY_APPROVED_AWAITING_CAMPAIGN_APPROVAL]: Object.freeze({
    title: "Strategy ready for review",
    message:
      "The strategy is ready to review. Campaign approval will remain pending until the client review process is complete.",
  }),
});

/*
 * A signed-in reader whose session the backend refused for a strategy that a
 * reviewer it authorizes could read (resolveStrategySessionAccessReadState).
 *
 * The public payload the page then holds explains the campaign's state to the
 * public ("withheld until allocation approval"). For this reader that is the
 * wrong explanation: what stands between them and the strategy is the
 * session. So the card names the access problem and the one thing the reader
 * can do about it, and claims nothing about what the strategy needs.
 * Presentation only: the card still renders nothing withheld, and
 * data-governance-status is unchanged.
 */
export const CAMPAIGN_STRATEGY_SESSION_ACCESS_NOTICE_COPY = Object.freeze({
  [STRATEGY_REVIEW_READ.NOT_IN_SESSION_SCOPE]: Object.freeze({
    statusLabel: "REVIEW ACCESS REQUIRED",
    title: "This session cannot open this strategy",
    message:
      "You are signed in, but this session does not include review access to this advertiser's strategy. If access was added to your account after you signed in, sign out and sign in again. Otherwise ask your Cphere team for review access.",
  }),
  [STRATEGY_REVIEW_READ.SESSION_REFUSED]: Object.freeze({
    statusLabel: "SESSION ENDED",
    title: "Your session has ended",
    message: "Sign in again to review this strategy.",
  }),
  [STRATEGY_REVIEW_READ.ROLE_REFUSED]: Object.freeze({
    statusLabel: "REVIEW ACCESS REQUIRED",
    title: "This account cannot review this strategy",
    message:
      "Strategy review is available to client accounts with access to this advertiser. Ask your Cphere team for review access.",
  }),
});

export const resolveStrategySessionAccessNotice = (reviewReadState) =>
  Object.prototype.hasOwnProperty.call(CAMPAIGN_STRATEGY_SESSION_ACCESS_NOTICE_COPY, reviewReadState || "")
    ? CAMPAIGN_STRATEGY_SESSION_ACCESS_NOTICE_COPY[reviewReadState]
    : null;

/*
 * The refusal the withheld page explains, or null. ONE decision: the hero
 * line and the status card both follow it.
 *
 * A refusal explains a withheld page only when access is what stands between
 * this reader and the strategy. All of:
 *   - the read is THIS campaign's (one kept from the campaign the page showed
 *     before says nothing about this one) and was refused: 401, 403 or 404.
 *     Only a request that carried a sign-in can be refused, so an anonymous
 *     read never qualifies;
 *   - the page is withholding a strategy that exists. A strategy the reader
 *     can see needs no explanation; one that was never saved, a response the
 *     page could not interpret, and a payload without a campaign each have
 *     their own;
 *   - a reviewer the backend authorizes may read it as it stands: the
 *     backend calls the governed state reviewable
 *     (governance.clientReviewSafeToRender), or it is the current proposal
 *     awaiting allocation approval, which an authorized reviewer may read
 *     before any approval. The backend's rule for that needs both the
 *     governance status ALLOCATION_APPROVAL_REQUIRED and the next action
 *     ALLOCATION_PENDING_APPROVAL, so both are required here. (It also needs
 *     things a public payload does not carry; where those fail, a new sign-in
 *     is answered with the backend's notice.)
 * In every other withheld state (inputs changed, an update in progress)
 * signing in again opens nothing, and the backend's own notice stands.
 */
export const resolveStrategySessionAccessReadState = (
  strategyReviewRead,
  { campaignRequestKey, microsite = null, governedRenderState = null } = {}
) => {
  if (strategyReviewRead?.requestKey !== campaignRequestKey) return null;
  if (!resolveStrategySessionAccessNotice(strategyReviewRead?.governanceReadState)) return null;
  if (!microsite?.campaign || !governedRenderState || governedRenderState.clientSafeToRender) return null;
  if (governedRenderState.status === "GOVERNANCE_STATUS_UNAVAILABLE") return null;
  const nextActionState = governedRenderState.nextActionState;
  if (nextActionState === STRATEGY_NEXT_ACTION_STATES.NO_STRATEGY) return null;
  return microsite.governance?.clientReviewSafeToRender === true ||
    (nextActionState === STRATEGY_NEXT_ACTION_STATES.ALLOCATION_PENDING_APPROVAL &&
      microsite.governance?.status === "ALLOCATION_APPROVAL_REQUIRED")
    ? strategyReviewRead.governanceReadState
    : null;
};

export function CampaignStrategyMicrositeGovernanceNotice({
  state,
  findings = [],
  reviewGateway = false,
  reviewReadState = null,
}) {
  const resolved = state || buildBlockedGovernanceState({ code: "missing_governance" });
  const findingCodes = (Array.isArray(findings) ? findings : [])
    .map((finding) =>
      typeof finding === "string" ? finding : String(finding?.code || "")
    )
    .filter(Boolean);
  // An uninterpretable governance response keeps its dedicated fail-closed
  // copy — findings from a response we could not verify are never trusted to
  // soften the notice.
  const allocationPending =
    resolved.status !== "GOVERNANCE_STATUS_UNAVAILABLE" &&
    (resolved.status === "ALLOCATION_APPROVAL_REQUIRED" ||
      findingCodes.includes(CAMPAIGN_ALLOCATION_REQUIRED_FINDING) ||
      findingCodes.includes(CAMPAIGN_ALLOCATION_APPROVAL_FINDING));
  // A named blocker and a failed verification keep their own copy.
  const gatewayCopy =
    reviewGateway &&
    !allocationPending &&
    resolved.status !== "GOVERNANCE_STATUS_UNAVAILABLE" &&
    Object.prototype.hasOwnProperty.call(
      CAMPAIGN_STRATEGY_REVIEW_GATEWAY_NOTICE_COPY,
      resolved.nextActionState || ""
    )
      ? CAMPAIGN_STRATEGY_REVIEW_GATEWAY_NOTICE_COPY[resolved.nextActionState]
      : null;
  // An access problem is never explained as a governance requirement. A
  // response that could not be interpreted keeps its fail-closed copy.
  const sessionAccessCopy =
    resolved.status !== "GOVERNANCE_STATUS_UNAVAILABLE"
      ? resolveStrategySessionAccessNotice(reviewReadState)
      : null;
  const display = sessionAccessCopy
    ? { ...resolved, ...sessionAccessCopy }
    : allocationPending
    ? { ...resolved, ...CAMPAIGN_ALLOCATION_PENDING_BLOCKED_COPY }
    : gatewayCopy
      ? { ...resolved, ...gatewayCopy }
      : resolved;
  return (
    <div
      className={`campaign-strategy-microsite-state-card campaign-strategy-microsite-governance-state${
        reviewGateway ? " campaign-strategy-microsite-governance-state--review-gateway" : ""
      }`}
      data-testid="campaign-strategy-governance-state"
      data-governance-status={resolved.status}
      data-review-read={sessionAccessCopy ? reviewReadState : undefined}
      data-next-action-state={resolved.nextActionState || undefined}
      role="status"
      aria-live="polite"
    >
      <span className="campaign-strategy-microsite-governance-status">
        {display.statusLabel || "REGENERATION REQUIRED"}
      </span>
      <strong>{display.title}</strong>
      <p>{display.message}</p>
    </div>
  );
}

/**
 * The approval authorities, stated separately — in words the backend chose.
 *
 * Campaign 13 (Dark Horse) read as "APPROVED" on this page when only a
 * strategy version had been internally approved: the lifecycle chip was the
 * page's single voice about approval, and the lifecycle had been overloaded.
 * This strip renders the backend's client-safe presentation of each authority
 * — the strategy's own status, the campaign's authorization, and any plan
 * revision approval as explicitly historical evidence. It derives nothing:
 * a payload without the presentation renders nothing.
 *
 * `compact` is for the review-access gateway, beneath the access panel: only
 * the campaign's authorization, in the backend's words, as one secondary line.
 * The status card above it already states the strategy's own status.
 */
export function CampaignApprovalAuthorityStrip({ approvalAuthority = null, compact = false }) {
  const source =
    approvalAuthority && typeof approvalAuthority === "object" ? approvalAuthority : null;
  const strategy =
    source?.strategyApproval && typeof source.strategyApproval === "object"
      ? source.strategyApproval
      : null;
  const campaignApproval =
    source?.campaignApproval && typeof source.campaignApproval === "object"
      ? source.campaignApproval
      : null;
  const revision =
    source?.revisionApproval && typeof source.revisionApproval === "object"
      ? source.revisionApproval
      : null;
  if (compact ? !campaignApproval?.label : !strategy?.label && !campaignApproval?.label) {
    return null;
  }
  return (
    <section
      className={
        compact
          ? "campaign-approval-authority-strip campaign-approval-authority-strip--compact"
          : "campaign-strategy-microsite-state-card campaign-approval-authority-strip"
      }
      data-testid="campaign-approval-authority-strip"
      data-presentation={compact ? "compact" : undefined}
      role="status"
      aria-live="polite"
    >
      {!compact && strategy?.label ? (
        <div
          className="campaign-approval-authority-row"
          data-authority="strategy"
          data-state={strategy.state || undefined}
        >
          <span className="campaign-approval-authority-label">Strategy</span>
          <strong>{strategy.label}</strong>
        </div>
      ) : null}
      {campaignApproval?.label ? (
        <div
          className="campaign-approval-authority-row"
          data-authority="campaign"
          data-state={campaignApproval.state || undefined}
        >
          <span className="campaign-approval-authority-label">Campaign</span>
          <strong>{campaignApproval.label}</strong>
        </div>
      ) : null}
      {!compact && revision?.label ? (
        <div
          className="campaign-approval-authority-row campaign-approval-authority-row-historical"
          data-authority="revision"
          data-state={revision.state || undefined}
        >
          <span className="campaign-approval-authority-label">Revision history</span>
          <span>{revision.label}</span>
        </div>
      ) : null}
    </section>
  );
}

export function CampaignAllocationPendingSection({ recommendation }) {
  const rows = Array.isArray(recommendation?.rows) ? recommendation.rows : [];
  if (!rows.length) return null;
  return (
    <section
      className="campaign-strategy-microsite-state-card campaign-allocation-pending"
      data-testid="campaign-allocation-pending"
    >
      <strong className="campaign-allocation-pending-title">
        SHERLOC RECOMMENDED ALLOCATION — AWAITING APPROVAL
      </strong>
      <div className="campaign-allocation-pending-rows">
        {rows.map((row) => {
          const percent = Number(row?.percent);
          return (
            <div
              className="campaign-allocation-pending-row"
              key={row.platformKey || row.displayName}
              data-testid={`campaign-allocation-pending-row-${row.platformKey}`}
            >
              <div className="campaign-allocation-pending-row-head">
                <span className="campaign-allocation-platform-name">
                  {normalizeSherlocPlatformDisplayLabel(row.platformKey, row.displayName) || row.platformKey}
                </span>
                <span className="campaign-allocation-pending-gross">
                  {formatAllocationAmount(row.grossAmount)}
                </span>
                <span className="campaign-allocation-pending-percent">
                  {Number.isFinite(percent) ? `${percent.toFixed(1)}%` : "—"}
                </span>
              </div>
              {row.rationale ? (
                <p className="campaign-allocation-pending-rationale">{row.rationale}</p>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* The planner's proposed phases carry percentages and no dates. An even-daily
   weekly dollar chart beside them states a schedule nothing governs (campaign
   56, 2026-10-04: $35,000 a week beside 40% / 60%), so the page presents the
   activation rows without it, at any campaign status: the contradiction does
   not depend on approval. The note speaks for this calendar only: the page
   cannot know whether a schedule is governed elsewhere (a flight rhythm, for
   one). Presentation only: the served calendar, which the governance check
   reads, is untouched, and a page without planner phases keeps its chart. */
export const presentLaunchActivationCalendar = (calendar, timingPhases = []) => {
  if (!calendar || typeof calendar !== "object") return calendar;
  const plannerPhases = (Array.isArray(timingPhases) ? timingPhases : []).some(
    (phase) => phase?.planningStatus === "PROPOSED"
  );
  if (!plannerPhases) return calendar;
  return {
    ...calendar,
    title: "Activation Calendar",
    disclaimer:
      "Directional activation guidance by flight week, not guaranteed outcomes. This calendar shows no weekly spend schedule: the planner's proposed phases carry percentages, not dates.",
    spendRamp: null,
  };
};

export function LaunchActivationCalendar({ calendar, allocationRows = [] }) {
  const [activeSegment, setActiveSegment] = useState(null);
  const resolved = resolveLaunchActivationCalendar(
    calendar && (calendar.spendRamp || calendar.milestones)
      ? { launchActivationCalendar: calendar }
      : calendar || {}
  );
  if (!resolved) return null;
  const weeks = Array.isArray(resolved.spendRamp?.weeks)
    ? resolved.spendRamp.weeks.map((week) => ({
        ...week,
        segments: orderMicrositePlatformRows(week?.segments, allocationRows),
      }))
    : [];
  const rampPlatforms = Array.isArray(resolved.spendRamp?.platforms)
    ? orderMicrositePlatformRows(resolved.spendRamp.platforms, allocationRows)
    : [];
  const maxTotal =
    Number(resolved.spendRamp?.maxTotal) > 0
      ? Number(resolved.spendRamp.maxTotal)
      : weeks.reduce((max, week) => Math.max(max, Number(week.total) || 0), 0);
  const axis = buildSpendRampAxis(maxTotal);
  const axisMax = axis?.axisMax || maxTotal;
  const milestones = Array.isArray(resolved.milestones) ? resolved.milestones : [];

  return (
    <MicrositeSection
      eyebrow="Launch Timeline"
      title={resolved.title || "Weekly Spend Ramp + Activation Calendar"}
      meta={weeks.length ? `${weeks.length}-week ramp` : "Activation sequence"}
    >
      {resolved.disclaimer ? (
        <p className="campaign-strategy-microsite-section-intro">{resolved.disclaimer}</p>
      ) : null}
      {weeks.length && maxTotal > 0 ? (
        <div className="campaign-strategy-microsite-spendramp-chart" data-testid="spend-ramp-chart">
          {rampPlatforms.length ? (
            <div className="campaign-strategy-microsite-spendramp-legend">
              {rampPlatforms.map((platform) => (
                <span
                  key={platform.key || platform.label}
                  className="campaign-strategy-microsite-spendramp-legend-item"
                >
                  <span
                    className="campaign-strategy-microsite-spendramp-swatch"
                    data-platform={normalizeSpendRampPlatformColorKey(platform)}
                    style={getSpendRampPlatformColorStyle(platform)}
                  />
                  {formatMicrositePlatformDisplayLabel(platform.label)}
                </span>
              ))}
            </div>
          ) : null}
          <div className="campaign-strategy-microsite-spendramp-plot">
            {axis ? (
              <div
                className="campaign-strategy-microsite-spendramp-yaxis"
                data-testid="spend-ramp-yaxis"
                aria-hidden="true"
              >
                {axis.ticks.map((tick) => (
                  <span
                    key={tick.value}
                    className="campaign-strategy-microsite-spendramp-yaxis-tick"
                    style={{ bottom: `${tick.pct}%` }}
                  >
                    {tick.label}
                  </span>
                ))}
              </div>
            ) : null}
            <div className="campaign-strategy-microsite-spendramp-bars-frame">
              {/* Scroll wrapper: on mobile the frame scrolls horizontally while
                  the gridlines + bars keep their min-width so nothing squishes. */}
              <div className="campaign-strategy-microsite-spendramp-scroll" data-testid="spend-ramp-scroll">
              {axis ? (
                <div
                  className="campaign-strategy-microsite-spendramp-gridlines"
                  data-testid="spend-ramp-gridlines"
                  aria-hidden="true"
                >
                  {axis.ticks.map((tick) => (
                    <span
                      key={tick.value}
                      className="campaign-strategy-microsite-spendramp-gridline"
                      data-baseline={tick.value === 0 ? "true" : undefined}
                      style={{ bottom: `${tick.pct}%` }}
                    />
                  ))}
                </div>
              ) : null}
              <div className="campaign-strategy-microsite-spendramp-bars">
                {weeks.map((week) => {
                  const segments = Array.isArray(week.segments) ? week.segments : [];
                  const governedWeekTotalLabel = formatGovernedSpendRampCurrency(
                    week.total
                  );
                  const tipForWeek =
                    activeSegment && activeSegment.weekKey === week.key ? activeSegment : null;
                  return (
                    <div key={week.key} className="campaign-strategy-microsite-spendramp-column">
                      <div className="campaign-strategy-microsite-spendramp-stack-wrap">
                        <div
                          className="campaign-strategy-microsite-spendramp-stack"
                          style={{
                            height: `${Math.max(6, ((Number(week.total) || 0) / axisMax) * 100)}%`,
                          }}
                          title={`${week.label}: ${governedWeekTotalLabel}`}
                        >
                          {segments.map((segment) => {
                            const valueLabel = `$${Number(segment.value || 0).toLocaleString("en-US")}`;
                            const segmentColorKey = normalizeSpendRampPlatformColorKey(segment);
                            const segmentColor = resolveSpendRampPlatformColor(segment);
                            const rolloverLabel = `${week.label} — ${segment.platformLabel}: ${valueLabel}${
                              governedWeekTotalLabel
                                ? ` of ${governedWeekTotalLabel} weekly total`
                                : ""
                            }`;
                            const activate = () =>
                              setActiveSegment({
                                weekKey: week.key,
                                segmentKey: segment.key,
                                colorKey: segmentColorKey,
                                color: segmentColor,
                                weekLabel: week.label,
                                platformLabel: segment.platformLabel,
                                valueLabel,
                                totalLabel: governedWeekTotalLabel,
                              });
                            const deactivate = () =>
                              setActiveSegment((current) =>
                                current &&
                                current.weekKey === week.key &&
                                current.segmentKey === segment.key
                                  ? null
                                  : current
                              );
                            const isActive =
                              tipForWeek && tipForWeek.segmentKey === segment.key;
                            return (
                              <span
                                key={segment.key}
                                className="campaign-strategy-microsite-spendramp-segment"
                                data-platform={segmentColorKey}
                                data-active={isActive ? "true" : undefined}
                                style={{
                                  ...getSpendRampPlatformColorStyle(segment),
                                  flexGrow: Math.max(0, Number(segment.value) || 0),
                                }}
                                title={`${segment.platformLabel}: ${valueLabel}`}
                                role="img"
                                tabIndex={0}
                                aria-label={rolloverLabel}
                                onMouseEnter={activate}
                                onMouseLeave={deactivate}
                                onFocus={activate}
                                onBlur={deactivate}
                              />
                            );
                          })}
                          {tipForWeek ? (
                          <div
                            className="campaign-strategy-microsite-spendramp-tooltip"
                            role="tooltip"
                            data-testid="spend-ramp-tooltip"
                          >
                            <span className="campaign-strategy-microsite-spendramp-tooltip-week">
                              {tipForWeek.weekLabel}
                            </span>
                            <span className="campaign-strategy-microsite-spendramp-tooltip-metric">
                              <span
                                className="campaign-strategy-microsite-spendramp-tooltip-swatch"
                                data-platform={tipForWeek.colorKey}
                                style={{ "--spendramp-platform-color": tipForWeek.color }}
                              />
                              <span className="campaign-strategy-microsite-spendramp-tooltip-platform">
                                {tipForWeek.platformLabel}
                              </span>
                              <span className="campaign-strategy-microsite-spendramp-tooltip-value">
                                {tipForWeek.valueLabel}
                              </span>
                            </span>
                            {tipForWeek.totalLabel ? (
                              <span className="campaign-strategy-microsite-spendramp-tooltip-total">
                                Weekly total {tipForWeek.totalLabel}
                              </span>
                            ) : null}
                          </div>
                          ) : null}
                        </div>
                      </div>
                      <span className="campaign-strategy-microsite-spendramp-total">
                        {governedWeekTotalLabel}
                      </span>
                      <span className="campaign-strategy-microsite-spendramp-week">{week.label}</span>
                      {week.dateRange ? (
                        <span className="campaign-strategy-microsite-spendramp-date-range">
                          {week.dateRange}
                        </span>
                      ) : null}
                    </div>
                  );
                })}
              </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {milestones.length ? (
        <div className="campaign-strategy-microsite-calendar-grid">
          {milestones.map((milestone, index) => (
            <article
              key={milestone.key || `milestone-${index}`}
              className="campaign-strategy-microsite-calendar-card"
            >
              <div className="campaign-strategy-microsite-calendar-head">
                <span className="campaign-strategy-microsite-calendar-eyebrow">
                  {[milestone.weekLabel, milestone.dateRange].filter(Boolean).join(" · ")}
                </span>
                {milestone.urgency ? (
                  <span
                    className={`campaign-strategy-microsite-due-badge campaign-strategy-microsite-due-badge--${milestone.urgencyTone || "soon"}`}
                  >
                    {milestone.urgency}
                  </span>
                ) : null}
              </div>
              <strong className="campaign-strategy-microsite-card-title">{milestone.title}</strong>
              <p>{milestone.copy}</p>
            </article>
          ))}
        </div>
      ) : null}
    </MicrositeSection>
  );
}

export const resolveActionItems = (section = {}) => {
  const actionItems =
    section && typeof section === "object" && section.actionItems && typeof section.actionItems === "object"
      ? section.actionItems
      : section && typeof section === "object" && Array.isArray(section.items)
      ? section
      : null;
  if (!actionItems) return null;
  const items = Array.isArray(actionItems.items)
    ? actionItems.items.filter((item) => item && item.title)
    : [];
  if (!items.length) return null;
  return { ...actionItems, items };
};

export function ActionItemsSection({ actionItems, advertiserVertical = null }) {
  const resolved = resolveActionItems(
    actionItems && Array.isArray(actionItems.items) ? { actionItems } : actionItems || {}
  );
  if (!resolved) return null;
  const desktopFinalRowCount = resolved.items.length % 3 || 3;

  return (
    <MicrositeSection
      eyebrow="Action Items"
      title={`Steps to Immediate ${formatAdvertiserVerticalImpactLabel(
        advertiserVertical
      )} Impact`}
      meta={`${resolved.items.length} action${resolved.items.length === 1 ? "" : "s"}`}
    >
      {resolved.disclaimer ? (
        <p className="campaign-strategy-microsite-section-intro">{resolved.disclaimer}</p>
      ) : null}
      <div
        className={`campaign-strategy-microsite-action-grid campaign-strategy-microsite-action-grid--desktop-final-row-${desktopFinalRowCount}`}
        data-desktop-final-row-count={desktopFinalRowCount}
      >
        {resolved.items.map((item, index) => (
          <article
            key={item.key || `action-${index}`}
            className="campaign-strategy-microsite-action-card"
          >
            <span className="campaign-strategy-microsite-action-index">
              {`${item.index || index + 1}`.padStart(2, "0")}
            </span>
            <strong className="campaign-strategy-microsite-card-title">{item.title}</strong>
            <p>{item.copy}</p>
            {item.dueLabel ? (
              <span
                className={`campaign-strategy-microsite-due-badge campaign-strategy-microsite-due-badge--${item.dueTone || "soon"}`}
              >
                {item.dueLabel}
              </span>
            ) : null}
          </article>
        ))}
      </div>
    </MicrositeSection>
  );
}

// Governed execution readiness chips. No chip may claim launch readiness —
// activation is not certified, so a launch-ready label does not exist in
// this map. The terminal states mirror the backend's authoritative
// execution-preparation readiness contract; a legacy backend "ready" key is
// mapped to the governed review label rather than trusted.
export const EXECUTION_STATUS_CHIPS = {
  ready: { key: "ready", label: "Ready for Review", tone: "ready-review" },
  needs_creative: { key: "needs_creative", label: "Needs Creative", tone: "needs-creative" },
  awaiting_tracking: { key: "awaiting_tracking", label: "Awaiting Tracking", tone: "awaiting-tracking" },
  scheduled: { key: "scheduled", label: "Scheduled", tone: "scheduled" },
  setup_incomplete: { key: "setup_incomplete", label: "Setup Incomplete", tone: "setup-incomplete" },
  awaiting_execution_preparation: {
    key: "awaiting_execution_preparation",
    label: "Awaiting Execution Preparation",
    tone: "awaiting-preparation",
  },
  awaiting_audience_readiness: {
    key: "awaiting_audience_readiness",
    label: "Awaiting Audience Readiness",
    tone: "awaiting-audience",
  },
  awaiting_account_verification: {
    key: "awaiting_account_verification",
    label: "Awaiting Account Verification",
    tone: "awaiting-account",
  },
  ready_for_review: { key: "ready_for_review", label: "Ready for Review", tone: "ready-review" },
  ready_for_supervised_draft: {
    key: "ready_for_supervised_draft",
    label: "Ready for Supervised Draft",
    tone: "ready-supervised",
  },
  activation_not_available: {
    key: "activation_not_available",
    label: "Activation Not Available",
    tone: "activation-unavailable",
  },
};

// Resolve a per-platform status chip from the backend readinessStatus, falling
// back to legacy activationStatus text as a neutral (never launch-ready) chip.
// The CANONICAL label wins over a payload label: readiness wording is a trust
// state, so a stale or divergent backend label can never resurface
// launch-ready copy on a known key. Unknown keys fail closed to neutral.
export const resolveExecutionStatusChip = (card = {}, { planningReview = false } = {}) => {
  if (planningReview) return { key: "planned", label: "Awaiting approval", tone: "planned" };
  const status =
    card && typeof card.readinessStatus === "object" ? card.readinessStatus : null;
  const canonical = status && status.key ? EXECUTION_STATUS_CHIPS[status.key] : null;
  if (canonical) {
    return {
      key: canonical.key,
      label: canonical.label,
      tone: canonical.tone,
    };
  }
  const legacyLabel =
    (card && typeof card.activationStatus === "string" && card.activationStatus.trim()) || "Planned";
  return { key: "planned", label: legacyLabel, tone: "planned" };
};

// Normalize + guard the backend readiness object. Fails closed (null) so no
// fabricated score/checklist is ever rendered when the data is absent.
export const deriveCampaignReadiness = (section = {}) => {
  const readiness =
    section && typeof section === "object" && section.readiness && typeof section.readiness === "object"
      ? section.readiness
      : null;
  if (!readiness) return null;
  // A dimension the backend marked inapplicable is excluded from the list as
  // well as from the score, so the ticks a reader counts and the percentage
  // they are shown always describe the same set.
  const checklist = Array.isArray(readiness.checklist)
    ? readiness.checklist.filter((item) => item && item.label && item.applicable !== false)
    : [];
  const hasScore = Number.isFinite(readiness.score);
  if (!hasScore && !checklist.length) return null;
  return {
    score: hasScore ? readiness.score : null,
    checklist,
    readyCount: Number.isFinite(readiness.readyCount) ? readiness.readyCount : null,
    totalCount: Number.isFinite(readiness.totalCount) ? readiness.totalCount : null,
    scoreBasis: readiness.scoreBasis && typeof readiness.scoreBasis === "object" ? readiness.scoreBasis : null,
  };
};

// A KPI list in its display case, item by item (formatMicrositePrimaryKpiDisplay).
const formatMicrositeKpiItems = (items) =>
  Array.isArray(items) ? items.map(formatMicrositePrimaryKpiDisplay) : formatMicrositePrimaryKpiDisplay(items);

function ExecutionGroup({ label, items }) {
  const values = Array.isArray(items) ? items.filter(Boolean) : items ? [items] : [];
  if (!values.length) return null;
  return (
    <div className="campaign-strategy-microsite-execution-group">
      <span className="campaign-strategy-microsite-kv-label">{label}</span>
      {values.map((value, index) => (
        <p key={`${label}-${index}`}>{value}</p>
      ))}
    </div>
  );
}

export function ExecutionReadinessSummary({ readiness }) {
  const resolved =
    readiness && typeof readiness === "object" && !readiness.section
      ? readiness
      : deriveCampaignReadiness(readiness || {});
  if (!resolved) return null;
  const checklist = Array.isArray(resolved.checklist)
    ? resolved.checklist.filter((item) => item && item.label)
    : [];
  const hasScore = Number.isFinite(resolved.score);
  if (!checklist.length && !hasScore) return null;

  return (
    <article
      className="campaign-strategy-microsite-readiness-card"
      data-testid="campaign-readiness-summary"
    >
      <div className="campaign-strategy-microsite-readiness-head">
        <span className="campaign-strategy-microsite-section-kicker">Campaign Readiness</span>
        {hasScore ? (
          <div className="campaign-strategy-microsite-readiness-score">
            <strong>{resolved.score}%</strong>
            <span>Estimated launch readiness</span>
          </div>
        ) : null}
      </div>
      {checklist.length ? (
        <ul className="campaign-strategy-microsite-readiness-checklist">
          {checklist.map((item) => (
            <li
              key={item.key || item.label}
              className={[
                "campaign-strategy-microsite-readiness-item",
                item.met ? "campaign-strategy-microsite-readiness-item--met" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <span className="campaign-strategy-microsite-readiness-mark" aria-hidden="true">
                {item.met ? "✓" : "○"}
              </span>
              <span>{item.label}</span>
              {item.detail ? (
                <span className="campaign-strategy-microsite-readiness-detail">{item.detail}</span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </article>
  );
}

// Reference-plan-style segment helpers — targeting/keyword/contextual chips collapse into
// one de-duplicated Targeting segment; KPI copy renders as readable lines.
const collectCampaignTargetingChips = (campaign) => {
  const chips = [];
  const seen = new Set();
  ["targeting", "keywords", "contextualPlacements"].forEach((field) => {
    const values = Array.isArray(campaign?.[field]) ? campaign[field] : [];
    values.forEach((value) => {
      const label = formatMicrositeTextValue(value, "");
      if (!label) return;
      const dedupeKey = label.toLowerCase();
      if (seen.has(dedupeKey)) return;
      seen.add(dedupeKey);
      chips.push(label);
    });
  });
  return chips;
};

const collectCampaignKpiLines = (campaign) => {
  const lines = [];
  const seen = new Set();
  (Array.isArray(campaign?.kpis) ? campaign.kpis : []).forEach((value) => {
    const label = formatMicrositeTextValue(value, "");
    if (!label) return;
    const dedupeKey = label.toLowerCase();
    if (seen.has(dedupeKey)) return;
    seen.add(dedupeKey);
    lines.push(label);
  });
  return lines;
};

// Structured Ad Sets / Targeting / KPIs segment panel mirroring the internal reference
// campaign-detail lower grid. Fail closed: renders nothing when a card carries
// no segment-worthy data, and shows an explicit pending note (never a raw text
// block) when a card has targeting/KPIs but no published ad sets.
export function CampaignAdSetSegments({ campaign }) {
  const adSets = Array.isArray(campaign?.adSets)
    ? campaign.adSets.filter((adSet) => adSet && (adSet.adSetName || adSet.name))
    : [];
  const targetingChips = collectCampaignTargetingChips(campaign);
  const kpiLines = collectCampaignKpiLines(campaign);

  if (!adSets.length && !targetingChips.length && !kpiLines.length) return null;

  return (
    <div
      className="campaign-strategy-microsite-adset-segments"
      data-testid="campaign-adset-segments"
    >
      <section className="campaign-strategy-microsite-adset-segment campaign-strategy-microsite-adset-segment--sets">
        <span className="campaign-strategy-microsite-kv-label">Ad Sets</span>
        {adSets.length ? (
          <div className="campaign-strategy-microsite-adset-segment-items">
            {adSets.map((adSet, index) => {
              const name = adSet.adSetName || adSet.name;
              return (
                <div
                  key={adSet.unifiedLabel || name || `adset-${index}`}
                  className="campaign-strategy-microsite-adset-item"
                  data-testid="campaign-adset-item"
                >
                  <span className="campaign-strategy-microsite-adset-item-name">{name}</span>
                  {adSet.budget ? (
                    <span className="campaign-strategy-microsite-adset-item-budget">
                      {adSet.budget}
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : (
          <p
            className="campaign-strategy-microsite-adset-segment-empty"
            data-testid="campaign-adset-empty"
          >
            Ad sets pending publication.
          </p>
        )}
      </section>
      {targetingChips.length ? (
        <section className="campaign-strategy-microsite-adset-segment campaign-strategy-microsite-adset-segment--targeting">
          <span className="campaign-strategy-microsite-kv-label">Targeting</span>
          <div className="campaign-strategy-microsite-chip-row">
            {targetingChips.map((chip) => (
              <span key={chip} className="campaign-strategy-microsite-chip">
                {chip}
              </span>
            ))}
          </div>
        </section>
      ) : null}
      {kpiLines.length ? (
        <section className="campaign-strategy-microsite-adset-segment campaign-strategy-microsite-adset-segment--kpis">
          <span className="campaign-strategy-microsite-kv-label">KPIs &amp; Expected Results</span>
          <div className="campaign-strategy-microsite-adset-segment-lines">
            {kpiLines.map((line, index) => (
              <p key={`kpi-${index}`}>{line}</p>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

export function CampaignDetailCards({ campaigns = [], planningReview = false }) {
  const brandPresentation = useMicrositeBrandPresentation();
  const semanticTypography = useMicrositeSemanticTypography();
  const copy = selectMicrositeCopy(brandPresentation);
  if (!Array.isArray(campaigns) || !campaigns.length) {
    return <EmptyPanel>{copy.executionEmpty}</EmptyPanel>;
  }

  return (
    <div className="campaign-strategy-microsite-campaign-detail-grid campaign-strategy-microsite-centered-card-grid campaign-strategy-microsite-centered-card-grid--2">
      {campaigns.map((campaign) => {
        const chip = resolveExecutionStatusChip(campaign, { planningReview });
        const groups =
          campaign.executionGroups && typeof campaign.executionGroups === "object"
            ? campaign.executionGroups
            : null;
        const objectiveLabel = campaign.objective || campaign.campaignObjective;
        const budgetLabel = campaign.budget || campaign.budgetLabel;
        return (
          <article
            key={campaign.key || campaign.campaignTitle}
            className="campaign-strategy-microsite-campaign-detail-card"
          >
            <div className="campaign-strategy-microsite-card-header">
              <span className="campaign-strategy-microsite-execution-platform-title">
                <PlatformLogoFrame
                  platform={campaign}
                  className="campaign-strategy-microsite-platform-logo-frame--mini"
                />
                <span className="campaign-strategy-microsite-card-title">
                  {campaign.platform}
                  {objectiveLabel ? ` · ${objectiveLabel}` : ""}
                </span>
              </span>
              <span
                className={`campaign-strategy-microsite-status-chip campaign-strategy-microsite-status-chip--${chip.tone}`}
              >
                {chip.label}
              </span>
            </div>
            {campaign.campaignTitle ? <h4>{campaign.campaignTitle}</h4> : null}
            {campaign.narrative ? <p>{campaign.narrative}</p> : null}
            {budgetLabel ? (
              <div className="campaign-strategy-microsite-detail-stack">
                <div className="campaign-strategy-microsite-kv-row">
                  <span className="campaign-strategy-microsite-kv-label">Budget</span>
                  <span className="campaign-strategy-microsite-kv-value">{budgetLabel}</span>
                </div>
              </div>
            ) : null}
            {groups ? (
              <div className="campaign-strategy-microsite-execution-groups">
                <ExecutionGroup label="Objective" items={groups.objective} />
                <ExecutionGroup label="Audience" items={groups.audience} />
                <ExecutionGroup label="Creative" items={groups.creative} />
                <ExecutionGroup label="Optimization" items={groups.optimization} />
                <ExecutionGroup
                  label="Expected KPI"
                  items={semanticTypography ? formatMicrositeKpiItems(groups.expectedKpi) : groups.expectedKpi}
                />
                <ExecutionGroup label="Activation" items={planningReview ? "Approval and execution authority are separate; this strategy view does not start setup or activation." : groups.activation} />
              </div>
            ) : null}
            {campaign.biddingStrategy ? (
              <div className="campaign-strategy-microsite-detail-stack">
                <div className="campaign-strategy-microsite-kv-row">
                  <span className="campaign-strategy-microsite-kv-label">Bidding Strategy</span>
                  <span className={semanticTypography ? "campaign-strategy-microsite-kv-value campaign-strategy-microsite-kv-value--sentence" : "campaign-strategy-microsite-kv-value"}>
                    {campaign.biddingStrategy}
                  </span>
                </div>
              </div>
            ) : null}
            {!planningReview ? <CampaignAdSetSegments campaign={campaign} /> : null}
            {campaign.creativeDirection ? (
              <p className="campaign-strategy-microsite-flow-note">
                Creative direction: {campaign.creativeDirection}
              </p>
            ) : null}
          </article>
        );
      })}
    </div>
  );
}

export const MICROSITE_PROOF_STATS = [
  {
    key: "cinema_retargeting_roi",
    label: "Cinema Retargeting ROI",
    value: "14×",
    copy: "vs. 3.67× for cold prospecting — Gruvi cinema data",
    accent: "#3fe0a0",
  },
  {
    key: "tiktok_ticket_purchase_lift",
    label: "TikTok Ticket Purchase Lift",
    value: "+168%",
    copy: "Median lift across theatrical campaigns",
    accent: "#ff5b7f",
  },
  {
    key: "retarget_vs_cold_ctr",
    label: "Retarget vs. Cold CTR",
    value: "10×",
    copy: "Retargeted users click 10× more than cold traffic",
    accent: "#4c8dff",
  },
];

// What each proof benchmark presupposes about the plan it sits beside: a
// platform the campaign selected, or retargeting its audience evidence
// authorizes.
const MICROSITE_PROOF_STAT_REQUIREMENTS = Object.freeze({
  cinema_retargeting_roi: { retargeting: true },
  tiktok_ticket_purchase_lift: { platform: "tiktok" },
  retarget_vs_cold_ctr: { retargeting: true },
});

export const getMicrositeProofStats = (
  advertiserVertical,
  { campaign = null, selectedPlatformScope = null } = {}
) => {
  const vertical = normalizeAdvertiserVertical(advertiserVertical);
  // Only film (the locked Arts & Entertainment control) has benchmarks from
  // its own evidence: Gruvi cinema data and theatrical campaigns. Every other
  // vertical, declared or not, has none that applies. The set once shown to
  // declared verticals reused the same cinema and ticket-purchase figures
  // under general labels ("observed across Cphere media programs"), and a
  // missing, unknown or "general" vertical says nothing about the campaign.
  // So the strip is omitted (DJ, 2026-09-29).
  if (vertical !== "film") return [];
  // Presentation contract v2 (campaign 56, 2026-10-04: a TikTok benchmark on a
  // plan with no TikTok, retargeting benchmarks on a plan that activates
  // none): a stamped artifact carries the backend's retargeting verdict, and
  // a card is shown only when the plan has what the card presupposes. A
  // legacy payload carries no verdict and keeps every card.
  if (!presentationFeature(campaign, "retargetingSuppressedWithoutAuthority")) {
    return MICROSITE_PROOF_STATS;
  }
  const retargetingAuthorized = campaign?.audienceActivation?.retargetingActive === true;
  return MICROSITE_PROOF_STATS.filter((stat) => {
    const requirement = MICROSITE_PROOF_STAT_REQUIREMENTS[stat.key] || {};
    if (requirement.retargeting && !retargetingAuthorized) return false;
    if (
      requirement.platform &&
      !isLineItemInSelectedPlatformScope(requirement.platform, selectedPlatformScope)
    ) {
      return false;
    }
    return true;
  });
};

// Proof benchmarks accompany a saved strategy: one in a state the backend
// gives a saved strategy (strategyApprovalAuthorityService: awaiting approval,
// approved, needs reapproval, revoked; the client-safe projection's update
// pending / in progress). With none saved ("No strategy saved"), its status
// unknown (UNAVAILABLE, or no approval authority in the response) or a state
// this page does not know, the page presents no strategy it can vouch for,
// and template benchmarks must not read as its evidence.
const STRATEGY_STATES_WITH_A_SAVED_STRATEGY = new Set([
  "AWAITING_APPROVAL",
  "APPROVED",
  "NEEDS_REAPPROVAL",
  "REVOKED",
  "UPDATE_PENDING",
  "UPDATE_IN_PROGRESS",
]);
export const micrositePresentsSavedStrategy = (approvalAuthority) =>
  STRATEGY_STATES_WITH_A_SAVED_STRATEGY.has(
    String(approvalAuthority?.strategyApproval?.state || "").trim().toUpperCase()
  );

// The page's DEFAULT FIGURES: numbers that come from templates, not from this
// campaign or its governed plan (DJ, 2026-09-29: "a saved strategy does not
// substantiate default performance claims"):
//   - the proof strip (Gruvi cinema and ticket-purchase figures);
//   - the Success Metrics summary cards ($18 ticket CPA, per-screen target, 14×
//     ROAS, a ticket forecast: the backend's buildSuccessMetricSummaryStats) and
//     every KPI target (the backend's resolveSuccessMetricTarget defaults:
//     "≤ $18 CPA", "90%+ VCR", "50%+ VTR", "2%+ VTC", "3%+ engagement", "1.5%+
//     CTR", "14×"; none is set for the campaign);
//   - the Cphere Intelligence panel ("Observed patterns from prior entertainment
//     campaigns", "ticket-purchase lift can exceed 168%");
//   - the retargeting CTR curves, and the retargeting flow's stage shares
//     (usually the backend's deriveFunnelSplit template: defaults, not
//     evidence for this campaign);
//   - benchmark chart bars the backend does not mark substantiated (directional
//     rates with no source, sample or window);
//   - the brand-awareness figures (77%, 3.2×, 48 hrs).
// Film with a saved strategy keeps its presentation unchanged (the protected
// control in this task). That is an eligibility rule, not a source: none of
// these figures is substantiated by it. Every other page omits them, and keeps
// the text, the campaign's own numbers and the layout around them.
export const micrositeShowsDefaultFigures = (advertiserVertical, approvalAuthority) =>
  normalizeAdvertiserVertical(advertiserVertical) === "film" && micrositePresentsSavedStrategy(approvalAuthority);

export function ProofStatStrip({ stats = [] }) {
  const cards = Array.isArray(stats)
    ? stats.filter((stat) => stat && stat.value && stat.label)
    : [];
  if (!cards.length) return null;

  return (
    <section
      className="campaign-strategy-microsite-proof-strip"
      aria-label="Performance proof benchmarks"
    >
      {cards.map((card, index) => (
        <article
          key={card.key || `proof-${index}`}
          className="campaign-strategy-microsite-proof-card"
          style={card.accent ? { "--proof-accent": card.accent } : undefined}
        >
          <span className="campaign-strategy-microsite-proof-label">{card.label}</span>
          <strong className="campaign-strategy-microsite-proof-value">{card.value}</strong>
          {card.copy ? (
            <p className="campaign-strategy-microsite-proof-copy">{card.copy}</p>
          ) : null}
        </article>
      ))}
    </section>
  );
}

export const normalizeAdSetActivationBlueprint = (...groups) => {
  const seen = new Set();
  return groups
    .flat()
    .filter((item) => item && typeof item === "object")
    .filter((item, index) => {
      const key = item.unifiedLabel || item.adSetName || item.key || `blueprint-${index}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

// ---------------------------------------------------------------------------
// Selected-platform scope
//
// Invariant: every rendered line item (execution card, ad-set activation
// blueprint row) must belong to the campaign's explicitly selected platform
// set. Rows for any other platform — whatever source injected them — are
// dropped, so an unselected platform can never present budget, targeting,
// unified labels, or readiness language. Membership is resolved in two key
// spaces: allocation planning keys (which fold the Meta family into one
// platform) and platform brand keys (which cover platforms like cinema that
// planning keys do not resolve). Fail closed: with no resolvable selected
// set, no line items render.
// ---------------------------------------------------------------------------

const collectLineItemPlatformScopeKeys = (value) => {
  const keys = new Set();
  const raw =
    value && typeof value === "object"
      ? value.platformKey ||
        value.platform_key ||
        value.platform ||
        value.title ||
        value.label ||
        value.key ||
        value.name ||
        value.channel
      : value;
  const planningKey = normalizePlatformAllocationKey(raw, { allowUnknown: false });
  if (planningKey) keys.add(planningKey);
  const brandKey = resolvePlatformBrandKey(raw);
  if (brandKey) keys.add(`brand:${brandKey}`);
  return keys;
};

export const buildSelectedPlatformScopeKeySet = (platforms = []) => {
  const scope = new Set();
  (Array.isArray(platforms) ? platforms : []).forEach((platform) => {
    collectLineItemPlatformScopeKeys(platform).forEach((key) => scope.add(key));
  });
  return scope;
};

export const isLineItemInSelectedPlatformScope = (row, scope) => {
  if (!(scope instanceof Set) || !scope.size) return false;
  for (const key of collectLineItemPlatformScopeKeys(row)) {
    if (scope.has(key)) return true;
  }
  return false;
};

export const filterLineItemsToSelectedPlatformScope = (rows = [], scope) => {
  const list = Array.isArray(rows) ? rows : [];
  if (!(scope instanceof Set) || !scope.size) return [];
  return list.filter((row) => isLineItemInSelectedPlatformScope(row, scope));
};

const AD_SET_PLATFORM_LABEL_OVERRIDES = Object.freeze({
  audio: "Audio",
  ctv: "CTV",
  display: "Display",
  dooh: "DOOH",
  google_search: "Google Search",
  meta: "Meta",
  mobile: "Mobile",
  tiktok: "TikTok",
  video: "Video",
  youtube: "YouTube",
});

const AD_SET_LABEL_WORD_OVERRIDES = Object.freeze({
  cpa: "CPA",
  cpc: "CPC",
  cpm: "CPM",
  ctv: "CTV",
  dc: "DC",
  dma: "DMA",
  dmas: "DMAs",
  dooh: "DOOH",
  imdb: "IMDb",
  kpi: "KPI",
  la: "LA",
  nyc: "NYC",
  ott: "OTT",
  tiktok: "TikTok",
  youtube: "YouTube",
});

const AD_SET_AUDIENCE_PREFIX_ALIASES = [
  "audio",
  "ctv",
  "display",
  "dooh",
  "facebook",
  "google",
  "google search",
  "instagram",
  "meta",
  "mobile",
  "programmatic display",
  "tiktok",
  "video",
  "youtube",
];

const AD_SET_CHIP_MAX_LENGTH = 42;

const normalizeAdSetDisplayLabelText = (value) =>
  formatMicrositeTextValue(value, "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const formatAdSetClientLabel = (value) =>
  normalizeAdSetDisplayLabelText(value)
    .split(" ")
    .filter(Boolean)
    .map((word) => {
      const lookupKey = word.replace(/[^a-z0-9]/gi, "").toLowerCase();
      if (AD_SET_LABEL_WORD_OVERRIDES[lookupKey]) return AD_SET_LABEL_WORD_OVERRIDES[lookupKey];
      if (!/[a-z]/i.test(word) || /^\d/.test(word)) return word;
      return word
        .split("-")
        .map((part) => {
          if (!part || !/[a-z]/i.test(part)) return part;
          return `${part.charAt(0).toUpperCase()}${part.slice(1).toLowerCase()}`;
        })
        .join("-");
    })
    .join(" ");

export const formatAdSetPlatformLabel = (value) => {
  const key = normalizePlatformAllocationKey(value, { allowUnknown: false });
  if (AD_SET_PLATFORM_LABEL_OVERRIDES[key]) return AD_SET_PLATFORM_LABEL_OVERRIDES[key];
  return formatAdSetClientLabel(value);
};

const buildAdSetUnifiedLabelToken = (value) =>
  formatMicrositeTextValue(value, "")
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .replace(/[^A-Za-z0-9]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toUpperCase();

const AD_SET_UNIFIED_LABEL_PLATFORM_TOKEN_OVERRIDES = Object.freeze({
  GOOGLE: "GOOGLE_SEARCH",
  GOOGLESEARCH: "GOOGLE_SEARCH",
  GOOGLE_SEARCH: "GOOGLE_SEARCH",
  TIKTOK: "TIKTOK",
  TIK_TOK: "TIKTOK",
  YOUTUBE: "YOUTUBE",
  YOU_TUBE: "YOUTUBE",
});

const AD_SET_UNIFIED_LABEL_PLATFORM_TOKENS = Object.freeze([
  "GOOGLE_SEARCH",
  "PROGRAMMATIC_DISPLAY",
  "FACEBOOK",
  "INSTAGRAM",
  "YOUTUBE",
  "DISPLAY",
  "TIKTOK",
  "MOBILE",
  "AUDIO",
  "VIDEO",
  "META",
  "DOOH",
  "CTV",
]);

const normalizeAdSetUnifiedPlatformToken = (value) => {
  const token = buildAdSetUnifiedLabelToken(value);
  if (!token) return "";
  const compactToken = token.replace(/_/g, "");
  return AD_SET_UNIFIED_LABEL_PLATFORM_TOKEN_OVERRIDES[token] ||
    AD_SET_UNIFIED_LABEL_PLATFORM_TOKEN_OVERRIDES[compactToken] ||
    token;
};

const getAdSetUnifiedLabelTokenSequence = (value) =>
  String(value || "").split("_").filter(Boolean);

const collapseAdjacentAdSetLabelTokenSequence = (label, sequence) => {
  const labelTokens = getAdSetUnifiedLabelTokenSequence(label);
  const sequenceTokens = getAdSetUnifiedLabelTokenSequence(sequence);
  if (!labelTokens.length || !sequenceTokens.length) return label;

  const matchesAt = (index) =>
    sequenceTokens.every((token, tokenIndex) => labelTokens[index + tokenIndex] === token);

  const collapsed = [];
  for (let index = 0; index < labelTokens.length;) {
    if (matchesAt(index) && matchesAt(index + sequenceTokens.length)) {
      collapsed.push(...sequenceTokens);
      index += sequenceTokens.length * 2;
      while (matchesAt(index)) {
        index += sequenceTokens.length;
      }
      continue;
    }

    collapsed.push(labelTokens[index]);
    index += 1;
  }

  return collapsed.join("_") || label;
};

const buildAdSetUnifiedLabelPlatformTokenCandidates = (platform) => {
  const platformCandidates = platform
    ? [
        platform,
        normalizePlatformAllocationKey(platform, { allowUnknown: false }),
        formatAdSetPlatformLabel(platform),
      ]
    : [];

  return Array.from(
    new Set(
      [
        ...platformCandidates,
        ...Object.keys(AD_SET_PLATFORM_LABEL_OVERRIDES),
        ...Object.values(AD_SET_PLATFORM_LABEL_OVERRIDES),
        ...AD_SET_UNIFIED_LABEL_PLATFORM_TOKENS,
      ]
        .map(normalizeAdSetUnifiedPlatformToken)
        .filter(Boolean)
    )
  ).sort((a, b) =>
    getAdSetUnifiedLabelTokenSequence(b).length - getAdSetUnifiedLabelTokenSequence(a).length
  );
};

const findAdSetUnifiedPlatformMatch = (tokens, platform = "") => {
  const platformTokens = buildAdSetUnifiedLabelPlatformTokenCandidates(platform);

  for (const platformToken of platformTokens) {
    const sequence = getAdSetUnifiedLabelTokenSequence(platformToken);
    for (let index = 0; index <= tokens.length - sequence.length; index += 1) {
      if (sequence.every((token, tokenIndex) => tokens[index + tokenIndex] === token)) {
        return { index, sequence, platformToken };
      }
    }
  }

  return {
    index: -1,
    sequence: [],
    platformToken: normalizeAdSetUnifiedPlatformToken(platform),
  };
};

// The release slot of a unified ad-set name, derived from the canonical
// advertiser category. Film resolves to THEATRICAL, so film ad sets are
// unchanged.
export const buildAdSetReleaseToken = (advertiserVertical = null) =>
  formatAdvertiserVerticalImpactLabel(advertiserVertical)
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") || "CAMPAIGN";

export const formatAdSetUnifiedLabel = (value, platform = "", advertiserVertical = null) => {
  const rawLabel = buildAdSetUnifiedLabelToken(value);
  if (!rawLabel) return "";

  const rawTokens = getAdSetUnifiedLabelTokenSequence(rawLabel);
  const rawPlatformMatch = findAdSetUnifiedPlatformMatch(rawTokens, platform);
  const platformToken = rawPlatformMatch.platformToken || "PLATFORM";
  const collapsedLabel = collapseAdjacentAdSetLabelTokenSequence(rawLabel, platformToken);
  const collapsedTokens = getAdSetUnifiedLabelTokenSequence(collapsedLabel);
  const platformMatch = findAdSetUnifiedPlatformMatch(collapsedTokens, platformToken);
  const yearToken =
    [...collapsedTokens].reverse().find((token) => /^(?:19|20)\d{2}$/.test(token)) ||
    "YEAR_TBD";
  // A source label that already declares the theatrical release token keeps
  // it: that is the ad-set name as it exists on the platform, and the microsite
  // must not rename a live ad set. Every other label takes the campaign's own
  // category token, where this used to hard-code "THEATRICAL" onto the ad sets
  // of every vertical.
  const categoryReleaseToken = buildAdSetReleaseToken(advertiserVertical);
  const releaseToken = collapsedTokens.includes("THEATRICAL")
    ? "THEATRICAL"
    : categoryReleaseToken;
  const audienceStartIndex = platformMatch.index >= 0
    ? platformMatch.index + platformMatch.sequence.length
    : 0;
  const audienceToken = collapsedTokens
    .slice(audienceStartIndex)
    .filter((token) =>
      token !== releaseToken &&
      token !== categoryReleaseToken &&
      !/^(?:19|20)\d{2}$/.test(token) &&
      token !== "YEAR" &&
      token !== "TBD"
    )
    .join("_") || "CORE_AUDIENCE";

  return [platformToken, audienceToken, yearToken, releaseToken].filter(Boolean).join("_");
};

export const formatAdSetTargetingType = (value) => formatAdSetClientLabel(value);

const buildAdSetAudiencePrefixList = (platform) => {
  const platformLabel = formatAdSetPlatformLabel(platform);
  const platformKey = normalizePlatformAllocationKey(platform, { allowUnknown: false });
  return Array.from(
    new Set(
      [platformLabel, platformKey, ...AD_SET_AUDIENCE_PREFIX_ALIASES]
        .map((item) => normalizeAdSetDisplayLabelText(item).toLowerCase())
        .filter(Boolean)
    )
  ).sort((a, b) => b.length - a.length);
};

export const formatAdSetAudienceLabel = (value, platform = "") => {
  let text = normalizeAdSetDisplayLabelText(value);
  const prefixes = buildAdSetAudiencePrefixList(platform);
  let strippedPrefix = true;

  while (text && strippedPrefix) {
    strippedPrefix = false;
    const lowerText = text.toLowerCase();
    const prefix = prefixes.find(
      (candidate) => lowerText === candidate || lowerText.startsWith(`${candidate} `)
    );
    if (prefix) {
      text = text.slice(prefix.length).trim();
      strippedPrefix = true;
    }
  }

  return formatAdSetClientLabel(text || value);
};

export const buildAdSetActivationHeading = (row = {}) => {
  const platformCandidate = pickMicrositeTextValue(
    "",
    row.platform,
    row.platformLabel,
    row.platformName,
    row.channel
  );
  const sourceParts = [
    formatAdSetPlatformLabel(platformCandidate),
    formatAdSetTargetingType(
      pickMicrositeTextValue("", row.targetingType, row.type, row.objective, row.targetingMode)
    ),
  ].filter(Boolean);
  const audienceLabel =
    formatAdSetAudienceLabel(
      pickMicrositeTextValue(
        "",
        row.displayAudienceLabel,
        row.audienceLabel,
        row.adSetLabel,
        row.adSetName,
        row.name
      ),
      platformCandidate
    ) || "Ad Set";

  return {
    sourceLabel: sourceParts.join(" · "),
    audienceLabel,
  };
};

export const normalizeAdSetChipLabel = (value) => {
  const raw = formatMicrositeTextValue(value, "")
    .replace(/\s+/g, " ")
    .replace(/[.!?]+$/g, "")
    .trim();
  if (!raw) return "";

  const rawKey = raw.toLowerCase();
  if (rawKey.includes("retargeting lanes") && rawKey.includes("audience intent")) {
    return "Audience Intent Retargeting";
  }

  const text = raw.replace(/^(?:and|or|then|also|plus)\b[\s,:;-]*/i, "").trim();
  const key = text.toLowerCase();
  if (key.includes("retargeting lanes") && key.includes("audience intent")) {
    return "Audience Intent Retargeting";
  }
  if (key.length > AD_SET_CHIP_MAX_LENGTH) {
    if (key.includes("retarget")) return "Retargeting Strategy";
    if (key.includes("audience")) return "Audience Strategy";
    if (key.includes("contextual")) return "Contextual Targeting";
    return "Activation Tactic";
  }

  return formatAdSetClientLabel(text || raw);
};

export function CampaignStrategyPlatformPills({
  platformHeroLogos = [],
  precedingPlatformGridItemCount = 0,
}) {
  const renderedPlatforms = Array.isArray(platformHeroLogos) ? platformHeroLogos : [];

  return (
    <>
      {renderedPlatforms.map((platform, platformIndex) => {
        const isFinalPlatformOrphan = isFinalPlatformGridOrphan(
          platformIndex,
          renderedPlatforms.length,
          precedingPlatformGridItemCount
        );

        return (
          <span
            key={platform.key || platform.label}
            className="campaign-strategy-microsite-platform-chip"
            data-platform-grid-orphan={isFinalPlatformOrphan ? "true" : undefined}
          >
            {formatMicrositePlatformDisplayLabel(platform.label)}
          </span>
        );
      })}
    </>
  );
}

export default function CampaignStrategyMicrosite() {
  const { campaignId, advertiserSlug, campaignSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  /*
   * The readable address (/campaign-strategy/{advertiser}/{campaign}) is
   * resolved to its campaign by the backend before anything loads; from then
   * on the campaign id keys every read, exactly as a legacy
   * /campaign-strategy/{identifier} link does.
   */
  const readableAddress =
    advertiserSlug && campaignSlug ? `/campaign-strategy/${advertiserSlug}/${campaignSlug}` : "";
  const [addressResolution, setAddressResolution] = useState({ address: "", campaignId: "" });
  const campaignRequestKey = readableAddress
    ? addressResolution.address === readableAddress
      ? addressResolution.campaignId
      : ""
    : String(campaignId || "").trim();
  const [micrositeResponse, setMicrositeResponse] = useState({
    requestKey: "",
    payload: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  /*
   * Strategy review access code (utils/strategyReviewAccess). The read
   * records whether it was anonymous and the backend's verdict on any review
   * grant it presented; a verified code asks for the read again, naming the
   * campaign id it was verified against.
   */
  const [strategyReviewRead, setStrategyReviewRead] = useState({
    requestKey: "",
    anonymous: false,
    grantStatus: "",
    // How the authenticated governance read went (STRATEGY_REVIEW_READ).
    governanceReadState: null,
  });
  const [strategyReviewReload, setStrategyReviewReload] = useState({
    requestKey: "",
    campaignId: "",
  });
  const { registerPageAwareness, clearPageAwareness } = usePageAwarenessState();
  // Re-created on every pathname change. The strategy loads per campaign, not
  // per pathname: replacing a legacy link with the readable address must not
  // load it again.
  const clearPageAwarenessRef = useRef(clearPageAwareness);
  useEffect(() => {
    clearPageAwarenessRef.current = clearPageAwareness;
  }, [clearPageAwareness]);
  const microsite = resolveCampaignMicrositePayloadForRequest(
    micrositeResponse,
    campaignRequestKey
  );
  const loadedCampaignId = String(microsite?.campaign?.id ?? "").trim();
  const canonicalMicrositePath =
    readCampaignStrategyMicrositeAddress(microsite?.campaign) ||
    (isReadableCampaignStrategyMicrositePath(microsite?.strategyMicrositeAddress)
      ? microsite.strategyMicrositeAddress
      : "");

  useEffect(() => {
    document.body.classList.add(CAMPAIGN_STRATEGY_MICROSITE_ROUTE_CLASS);
    document.documentElement.classList.add(CAMPAIGN_STRATEGY_MICROSITE_ROUTE_CLASS);
    return () => {
      document.body.classList.remove(CAMPAIGN_STRATEGY_MICROSITE_ROUTE_CLASS);
      document.documentElement.classList.remove(CAMPAIGN_STRATEGY_MICROSITE_ROUTE_CLASS);
    };
  }, []);

  useEffect(() => markCampaignStrategyNoindex(loadedCampaignId), [loadedCampaignId]);

  /*
   * On a public proposal (DJ, 2026-10-05) a visitor with no sign-in stored
   * gets no Media Planner control, the top button or the footer link: they
   * point at a tool such a visitor cannot use. Signed-in viewers keep both,
   * and every other strategy page is unchanged. Before its strategy loads,
   * the campaign is known from its readable address or its numeric key.
   */
  const showMediaPlannerControls =
    Boolean(getAuthHeader().Authorization) ||
    !CAMPAIGN_STRATEGY_PUBLIC_PROPOSAL_CAMPAIGN_IDS.includes(
      resolveCampaignStrategyPageCampaignKey({
        loadedCampaignId,
        readableAddress,
        requestKey: campaignRequestKey,
      })
    );

  useEffect(() => {
    if (!readableAddress || addressResolution.address === readableAddress) return undefined;
    const controller = new AbortController();
    let active = true;

    const resolveAddress = async () => {
      try {
        setLoading(true);
        setError("");
        if (!API_BASE_URL) {
          throw new Error("Campaign strategy service is unavailable.");
        }
        const response = await fetch(
          `${safeBase(API_BASE_URL)}/api/media-campaigns/strategy-microsite-path/${encodeURIComponent(
            advertiserSlug
          )}/${encodeURIComponent(campaignSlug)}`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
            cache: "no-store",
            signal: controller.signal,
          }
        );
        const resolved = await response.json().catch(() => ({}));
        if (!response.ok || resolved?.campaignId == null) {
          throw new Error(resolved?.error || "Failed to load campaign strategy page.");
        }
        if (!active) return;
        const resolvedPath = isReadableCampaignStrategyMicrositePath(resolved.path)
          ? resolved.path
          : readableAddress;
        if (resolvedPath !== readableAddress) {
          // An earlier address of a renamed campaign: show its current one.
          navigate(`${resolvedPath}${location.search}${location.hash}`, {
            replace: true,
            state: location.state,
          });
          return;
        }
        setAddressResolution({ address: readableAddress, campaignId: String(resolved.campaignId) });
      } catch (err) {
        if (!active || err?.name === "AbortError") return;
        setError(err?.message || "Unable to load the Cphere strategy microsite.");
        setLoading(false);
      }
    };

    resolveAddress();

    return () => {
      active = false;
      controller.abort();
    };
  }, [
    addressResolution.address,
    advertiserSlug,
    campaignSlug,
    location.hash,
    location.search,
    location.state,
    navigate,
    readableAddress,
  ]);

  useEffect(() => {
    // A readable address loads once the backend has named its campaign.
    if (!campaignRequestKey) return undefined;
    const controller = new AbortController();
    let active = true;

    const loadMicrosite = async () => {
      try {
        setLoading(true);
        setError("");
        setMicrositeResponse({ requestKey: campaignRequestKey, payload: null });
        clearPageAwarenessRef.current();

        if (!API_BASE_URL) {
          throw new Error("Campaign strategy service is unavailable.");
        }

        const authHeader = getAuthHeader();
        /*
         * A viewer without a Cortex session may hold a review grant: an
         * HttpOnly cookie on the review API origin, one per campaign, which
         * the backend reads for the campaign this link resolves to (a legacy
         * slug link included). Their read is made there, with credentials;
         * if that origin cannot be reached, the plain public read comes from
         * the default backend. A signed-in viewer reads exactly as before and
         * never sends one.
         */
        const anonymousRead = !authHeader.Authorization;
        const micrositePath = `/api/media-campaigns/${encodeURIComponent(
          campaignRequestKey
        )}/strategy-microsite`;
        const { response, grantStatus } = await fetchStrategyMicrositeWithReviewGrant(
          `${safeBase(anonymousRead ? STRATEGY_REVIEW_API_BASE : API_BASE_URL)}${micrositePath}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              ...authHeader,
            },
            // Always fetch the latest saved campaign strategy — never a stale
            // cached microsite payload after planner edits/refresh.
            cache: "no-store",
            signal: controller.signal,
          },
          { withReviewCookie: anonymousRead, fallbackUrl: `${safeBase(API_BASE_URL)}${micrositePath}` }
        );

        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload?.error || "Failed to load campaign strategy page.");
        }

        if (!active) return;

        /*
         * INTERNAL GOVERNANCE comes from a second, AUTHENTICATED read.
         *
         * BE #485 removed governance evidence and the canonical visibility
         * projection from the public microsite response, because that response
         * is reachable anonymously and was serving hashes, fingerprints and
         * operation identifiers to anyone with a campaign id. displayArtifact
         * and approvalRevisionKey went with it, which is what took the
         * approval action out.
         *
         * They are served by GET /:campaignId/strategy-governance, behind
         * authenticate + roleCheck with campaign-level authorization. This
         * read is ADDITIVE: an anonymous viewer never issues it, and any
         * failure leaves the public contract exactly as received, so the page
         * degrades to public rendering rather than breaking.
         */
        const { payload: merged, reviewRead } = await readAuthenticatedStrategyGovernance(
          payload || null,
          // Governance is read by campaign id, which a legacy name link lacks.
          payload?.campaign?.id ?? campaignRequestKey,
          controller.signal,
          authHeader
        );

        if (!active) return;
        setStrategyReviewRead({
          requestKey: campaignRequestKey,
          anonymous: !authHeader.Authorization,
          grantStatus,
          governanceReadState: reviewRead?.state || null,
        });
        setMicrositeResponse({
          requestKey: campaignRequestKey,
          payload: merged,
        });
      } catch (err) {
        if (!active || err?.name === "AbortError") return;
        setError(err?.message || "Unable to load the Cphere strategy microsite.");
      } finally {
        if (active) setLoading(false);
      }
    };

    loadMicrosite();

    return () => {
      active = false;
      controller.abort();
    };
  }, [campaignRequestKey, strategyReviewReload]);

  /*
   * A legacy link (/campaign-strategy/{id or name}) gives way to the
   * campaign's readable address as soon as its strategy loads: replaced in
   * place, keeping the route state, so nothing reloads and Back still leaves.
   */
  useEffect(() => {
    if (!canonicalMicrositePath || !campaignRequestKey || !loadedCampaignId) return;
    if (location.pathname === canonicalMicrositePath) return;
    rememberMicrositeReturnAddress(CAMPAIGN_STRATEGY_MICROSITE_RETURN_STATE_KEY, canonicalMicrositePath, {
      pathname: location.pathname,
      campaignId: loadedCampaignId,
    });
    setAddressResolution({ address: canonicalMicrositePath, campaignId: campaignRequestKey });
    navigate(`${canonicalMicrositePath}${location.search}${location.hash}`, {
      replace: true,
      state: location.state,
    });
  }, [
    campaignRequestKey,
    canonicalMicrositePath,
    loadedCampaignId,
    location.hash,
    location.pathname,
    location.search,
    location.state,
    navigate,
  ]);

  const handleMicrositeBack = useCallback(() => {
    const routeState = location.state || {};
    const storedReturnState = readMicrositeReturnState({
      pathname: location.pathname,
      campaignId: loadedCampaignId,
    });
    const params = new URLSearchParams(location.search || window.location.search || "");
    const fallbackRoute = sanitizeMicrositeReturnPath(
      routeState.returnTo || storedReturnState.returnTo || params.get("from")
    );
    const returnSource = String(
      routeState.returnSource ||
        routeState.source ||
        storedReturnState.returnSource ||
        storedReturnState.source ||
        params.get("returnSource") ||
        ""
    ).trim();
    const returnCampaignId = String(
      routeState.campaignId ||
        storedReturnState.campaignId ||
        params.get("campaignId") ||
        loadedCampaignId ||
        campaignId ||
        ""
    ).trim();
    const returnMicrositeSlug = String(
      routeState.micrositeSlug ||
        routeState.publicSlug ||
        storedReturnState.micrositeSlug ||
        storedReturnState.publicSlug ||
        ""
    ).trim();
    const returnAdvertiserId = String(
      routeState.advertiserId || storedReturnState.advertiserId || params.get("advertiserId") || ""
    ).trim();
    const returnAdvertiserName = String(
      routeState.advertiserName ||
        storedReturnState.advertiserName ||
        params.get("advertiserName") ||
        ""
    ).trim();
    const returnCampaignName = String(
      routeState.campaignName ||
        storedReturnState.campaignName ||
        params.get("campaignName") ||
        ""
    ).trim();
    const shouldPushPlannerReturn =
      returnSource === "sherloc-media-planner" ||
      Boolean(
        returnCampaignId &&
          fallbackRoute.startsWith(CAMPAIGN_STRATEGY_MICROSITE_FALLBACK_ROUTE)
      );

    if (shouldPushPlannerReturn) {
      navigate(fallbackRoute, {
        replace: false,
        state: {
          source: "campaign-strategy-microsite",
          returnSource,
          restoredFromMicrosite: true,
          campaignId: returnCampaignId || null,
          micrositeSlug: returnMicrositeSlug || null,
          publicSlug: returnMicrositeSlug || null,
          advertiserId: returnAdvertiserId || null,
          advertiserName: returnAdvertiserName,
          campaignName: returnCampaignName,
        },
      });
      return;
    }

    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(fallbackRoute, { replace: false });
  }, [campaignId, loadedCampaignId, location.pathname, location.search, location.state, navigate]);

  /*
   * Strategy review access ("Request access code") is offered beneath the
   * withheld notice (the render branch decides "withheld") only to a viewer
   * this page read anonymously — never to a signed-in client or a superadmin
   * preview — and only while the backend says the governed state itself is
   * reviewable (governance.clientReviewSafeToRender): a code cannot open a
   * strategy that no reviewer may read yet, so none is offered. It is
   * withdrawn once the backend ACCEPTS this browser's grant: a strategy still
   * withheld then is withheld for another reason, which the notice names. A
   * grant the backend could not check ("unavailable") is kept, the public
   * body it came back with renders, and the request stays offered. Access is
   * keyed by the numeric campaign id: the resolved id, or for a legacy slug
   * link the id the backend reported for it.
   */
  const strategyReviewAccessCampaignId =
    strategyReviewRead.requestKey === campaignRequestKey &&
    strategyReviewRead.anonymous &&
    strategyReviewRead.grantStatus !== "accepted" &&
    microsite?.governance?.clientReviewSafeToRender === true
      ? normalizeStrategyReviewCampaignId(campaignRequestKey) ||
        normalizeStrategyReviewCampaignId(loadedCampaignId)
      : "";
  const handleStrategyReviewUnlocked = useCallback(
    (unlockedCampaignId) => {
      // Read the strategy again, now presenting the grant the verify kept.
      setStrategyReviewReload({
        requestKey: campaignRequestKey,
        campaignId: normalizeStrategyReviewCampaignId(unlockedCampaignId),
      });
    },
    [campaignRequestKey]
  );

  // A General campaign renders without the theatrical-only template
  // (utils/micrositeGeneralTemplate). Film and every declared vertical render
  // the campaign as served, and governance always reads the served payload.
  const loadedCampaign = microsite?.campaign || null;
  const loadedAdvertiserVertical = resolveCampaignStrategyAdvertiserVertical(loadedCampaign || {});
  const generalTemplate = campaignReceivesGeneralTemplate(loadedAdvertiserVertical);
  const campaign = useMemo(
    () => omitTheatricalTemplate(loadedCampaign, loadedAdvertiserVertical),
    [loadedCampaign, loadedAdvertiserVertical]
  );
  const brandPresentation = isMicrositeBrandPresentation(campaign);
  const semanticTypography = resolveMicrositeSemanticTypography(campaign);
  const copy = selectMicrositeCopy(brandPresentation);
  const governedRenderState = useMemo(
    () => resolveCampaignStrategyMicrositeGovernance(microsite),
    [microsite]
  );
  // The withheld page with the review-access panel on it: the hero line, the
  // status card and the approval strip all defer to the panel's one action.
  const reviewGateway =
    Boolean(strategyReviewAccessCampaignId) && !governedRenderState.clientSafeToRender;
  // A signed-in reader whose session the backend refused for a strategy an
  // authorized reviewer could read: the hero line and the card both name the
  // access problem, not a governance requirement.
  const sessionAccessReadState = resolveStrategySessionAccessReadState(strategyReviewRead, {
    campaignRequestKey,
    microsite,
    governedRenderState,
  });
  const headerDescription = resolveCampaignStrategyMicrositeHeaderDescription(
    governedRenderState,
    {
      answered: !loading && !error,
      reviewAccessOffered: reviewGateway,
      sessionAccessRefused: Boolean(sessionAccessReadState),
      brandPresentation,
    }
  );
  const allocationFindingCodes = useMemo(
    () => resolveCampaignAllocationFindingCodes(microsite),
    [microsite]
  );
  const allocationRecommendation = useMemo(
    () => resolveCampaignAllocationRecommendation(campaign),
    [campaign]
  );
  const allocationPendingRecommendation =
    resolveCampaignAllocationApprovalStatus(allocationRecommendation) ===
    "PENDING_APPROVAL"
      ? allocationRecommendation
      : null;
  const sections = useMemo(() => campaign?.sections || {}, [campaign]);
  // A bound proposal record (sections.proposalPlanning) switches the page to
  // proposal mode: one story in the owner's order, with the template sections
  // it would contradict withheld (components/CampaignProposalPlanning).
  // With semantic typography its Primary KPIs read in their display case
  // (formatMicrositePrimaryKpiDisplay) in the story, the charts' captions and
  // tooltips, and the proposal as Eden reads it.
  const proposalPlanning = useMemo(() => {
    const planning = resolveCampaignProposalPlanning(campaign);
    if (!planning || !semanticTypography || !Array.isArray(planning.kpis?.primary)) return planning;
    return {
      ...planning,
      kpis: {
        ...planning.kpis,
        primary: planning.kpis.primary.map((kpi) =>
          typeof kpi === "string" ? formatMicrositePrimaryKpiDisplay(kpi) : kpi
        ),
      },
    };
  }, [campaign, semanticTypography]);
  // A reviewed data-layers record (sections.recommendedDataLayers) renders as
  // its own section after Audience Intelligence. A proposal owns its data
  // signals, so proposal mode never renders the standalone section as well.
  const recommendedDataLayers = useMemo(
    () => (proposalPlanning ? null : resolveRecommendedDataLayers(campaign)),
    [campaign, proposalPlanning]
  );
  const showSection = (sectionKey) =>
    !isSectionWithheldInProposalMode(proposalPlanning, sectionKey) &&
    !(generalTemplate && GENERAL_CAMPAIGN_WITHHELD_SECTIONS.includes(sectionKey));
  const heroSection = useMemo(() => sections.hero || {}, [sections]);
  const filmSection = useMemo(
    () => sections.filmIntelligence || campaign?.filmIntelligence || {},
    [sections, campaign]
  );
  const resolvedFilmHero = useMemo(
    () => resolveMicrositeFilmPosterHero(filmSection, campaign),
    [filmSection, campaign]
  );
  // A film section with no hero title of its own names the film the backend
  // served (servedFilmName), never the campaign's flight label, when it can.
  const filmHeroTitle =
    resolvedFilmHero?.title || servedFilmName(filmSection) || unnamedFilmTitle(filmSection, campaign);
  const strategyOverviewSection = useMemo(
    () => sections.strategyOverview || campaign?.strategyOverview || {},
    [sections, campaign]
  );
  const audienceSection = useMemo(
    () => sections.audienceIntelligence || campaign?.audienceIntelligence || {},
    [sections, campaign]
  );
  const budgetSection = useMemo(
    () => sections.budgetArchitecture || campaign?.budgetArchitecture || {},
    [sections, campaign]
  );
  const benchmarkSection = useMemo(
    () => sections.benchmarkJustification || campaign?.benchmarkJustification || {},
    [sections, campaign]
  );
  const cphereIntelligence = useMemo(
    () => resolveCphereIntelligence(benchmarkSection),
    [benchmarkSection]
  );
  const retargetingSection = useMemo(
    () => sections.retargetingArchitecture || campaign?.retargetingArchitecture || {},
    [sections, campaign]
  );
  // Presentation contract v2 (P2): only a stamped artifact's verdict changes
  // the stage-sequencing section; a payload marker alone never does.
  const retargetingSuppressedBySection =
    presentationFeature(campaign, "retargetingSuppressedWithoutAuthority") &&
    retargetingSection?.retargetingActive === false;
  const earnedMediaSection = useMemo(
    () => sections.earnedMediaIntelligence || campaign?.earnedMediaIntelligence || {},
    [sections, campaign]
  );
  const releaseMilestoneSchedule = useMemo(
    () => sections.releaseMilestoneSchedule || campaign?.releaseMilestoneSchedule || [],
    [sections, campaign]
  );
  const campaignDetailSection = useMemo(
    () => sections.campaignDetailSection || campaign?.campaignDetails || {},
    [sections, campaign]
  );
  const creativeMessagingSection = useMemo(
    () => sections.creativeMessaging || campaign?.creativeMessaging || {},
    [sections, campaign]
  );
  const optimizationSection = useMemo(
    () => sections.optimizationMeasurement || campaign?.optimizationMeasurement || {},
    [sections, campaign]
  );
  const executionModulesSection = useMemo(
    () => sections.executionModules || campaign?.executionModules || {},
    [sections, campaign]
  );
  const releasePhaseIntelligence = useMemo(
    () => sections.releasePhaseIntelligence || campaign?.releasePhaseIntelligence || null,
    [sections, campaign]
  );
  const antiWasteGuardrails = useMemo(
    () => sections.antiWasteGuardrails || campaign?.antiWasteGuardrails || null,
    [sections, campaign]
  );
  const scenarioExpansionTriggers = useMemo(
    () => sections.scenarioExpansionTriggers || campaign?.scenarioExpansionTriggers || null,
    [sections, campaign]
  );
  const benchmarkChart = useMemo(
    () => sections.benchmarkChart || campaign?.benchmarkChart || null,
    [sections, campaign]
  );
  const successMetrics = useMemo(
    () => sections.successMetrics || campaign?.successMetrics || null,
    [sections, campaign]
  );
  const launchActivationCalendar = useMemo(
    () => resolveLaunchActivationCalendar(sections) || resolveLaunchActivationCalendar(campaign || {}),
    [sections, campaign]
  );
  const actionItems = useMemo(
    () => resolveActionItems(sections) || resolveActionItems(campaign || {}),
    [sections, campaign]
  );

  const summaryCards = useMemo(() => buildSummaryCards(campaign), [campaign]);
  const requestedPlatformChips = useMemo(
    () => expandCampaignPlatformScope(normalizeCampaignPlatformScope(campaign, campaignDetailSection?.platformCards)),
    [campaign, campaignDetailSection]
  );
  // The campaign's explicitly selected platform set — the only platforms a
  // line item is ever allowed to render for.
  const selectedPlatformScopeKeys = useMemo(
    () => buildSelectedPlatformScopeKeySet(requestedPlatformChips),
    [requestedPlatformChips]
  );
  const narrativeAdvertiserName = useMemo(
    () => resolveMicrositeNarrativeAdvertiserName(campaign),
    [campaign]
  );
  const resolvedAdvertiserVertical = useMemo(
    () => resolveCampaignStrategyAdvertiserVertical(campaign),
    [campaign]
  );
  const defaultFiguresApply = micrositeShowsDefaultFigures(
    resolvedAdvertiserVertical,
    microsite?.approvalAuthority
  );
  /*
   * A loaded campaign that declares no category still renders — fail-closed, on
   * the neutral General taxonomy with no entertainment intelligence. But it is
   * worth surfacing, because "General" then means "nobody said" rather than
   * "somebody chose General". Diagnostic only: no authority, no output change.
   */
  useEffect(() => {
    if (!campaign || typeof campaign !== "object") return;
    if (campaignStrategyAdvertiserVerticalIsDeclared(campaign)) return;
    console.warn(
      "[CampaignStrategyMicrosite] Campaign declares no advertiserVertical; rendering the neutral General taxonomy",
      {
        campaignId: campaign?.id ?? campaign?.campaignId ?? null,
        resolvedAdvertiserVertical,
      }
    );
  }, [campaign, resolvedAdvertiserVertical]);
  // Entertainment-only facts-panel row; empty label means the row never renders.
  const releaseDateDisplay = useMemo(
    () =>
      resolvedAdvertiserVertical === "film"
        ? formatCampaignReleaseDateLabel(campaign)
        : "",
    [campaign, resolvedAdvertiserVertical]
  );
  const advertiserVerticalContext = useMemo(
    () => serializeVerticalIntelligence(resolvedAdvertiserVertical),
    [resolvedAdvertiserVertical]
  );
  const verticalMicrositeFallbacks = useMemo(
    () => buildVerticalMicrositeFallbacks(resolvedAdvertiserVertical, {
      brandPresentation,
      allocationApprovalStatus: campaign?.allocationAuthority?.allocationApprovalStatus || campaign?.allocationRecommendation?.allocationApprovalStatus,
    }),
    [resolvedAdvertiserVertical, brandPresentation, campaign?.allocationAuthority?.allocationApprovalStatus, campaign?.allocationRecommendation?.allocationApprovalStatus]
  );
  const narrativeTitleCandidates = useMemo(
    () => [
      campaign?.campaignName,
      campaign?.filmTitle,
      campaign?.title,
      filmSection?.hero?.title,
      strategyOverviewSection?.title,
      creativeMessagingSection?.title,
    ],
    [campaign, filmSection, strategyOverviewSection, creativeMessagingSection]
  );
  // The campaign's own saved values that the vertical wording would alter.
  const ownWordGuard = useMemo(
    () =>
      campaignOwnWordGuard(campaign, (text) =>
        sanitizeVerticalIntelligenceCopy(text, resolvedAdvertiserVertical)
      ),
    [campaign, resolvedAdvertiserVertical]
  );
  const renderAdvertiserNarrativeCopy = useCallback(
    (value, fallback = "") =>
      sanitizeCampaignStrategyCopy(
        formatMicrositeAdvertiserNarrativeCopy(
          value,
          narrativeAdvertiserName,
          narrativeTitleCandidates,
          fallback
        ),
        resolvedAdvertiserVertical,
        ownWordGuard
      ),
    [narrativeAdvertiserName, narrativeTitleCandidates, resolvedAdvertiserVertical, ownWordGuard]
  );
  const strategyFunnel = useMemo(
    () => governedRenderState.clientSafeToRender
      ? buildStrategyFunnel(campaign, { proposalPlanning, formatText: renderAdvertiserNarrativeCopy })
      : null,
    [campaign, governedRenderState.clientSafeToRender, proposalPlanning, renderAdvertiserNarrativeCopy]
  );
  const heroChips = useMemo(
    () => (Array.isArray(heroSection?.chips) ? heroSection.chips : []),
    [heroSection]
  );
  const precedingPlatformGridItemCount = useMemo(
    () => heroChips.filter(isMicrositePlatformGridCompanionChip).length,
    [heroChips]
  );
  const advertiserLogoMeta = useMemo(
    () => resolveMicrositeAdvertiserLogoMeta(campaign),
    [campaign]
  );
  const advertiserLogoCandidates = useMemo(
    () =>
      buildAdvertiserLogoCandidates(
        API_BASE_URL,
        ...(Array.isArray(advertiserLogoMeta.candidates)
          ? advertiserLogoMeta.candidates
          : [])
      ),
    [advertiserLogoMeta]
  );
  const { resolvedSrc: advertiserLogoSrc, handleError: handleAdvertiserLogoError } =
    useResolvedImageCandidates(advertiserLogoCandidates, { apiBase: API_BASE_URL });
  const advertiserLogoAlt = advertiserLogoMeta.logoAltText || "Advertiser logo";
  const filmCards = useMemo(
    () => (Array.isArray(filmSection?.cards) ? filmSection.cards : []),
    [filmSection]
  );
  const sourceCardIdentity = useMemo(
    () => ({ advertiserHeroIdentity: presentationFeature(campaign, "advertiserHeroIdentity") }),
    [campaign]
  );
  const filmScoreItems = useMemo(
    () => buildFilmScoreItems(filmSection, resolvedAdvertiserVertical),
    [filmSection, resolvedAdvertiserVertical]
  );
  const hasExplicitFilmReference = Boolean(
    filmCards.length ||
      filmScoreItems.length ||
      filmSection?.combinedTakeaway ||
      filmSection?.hero?.synopsis ||
      filmSection?.hero?.image ||
      filmSection?.hero?.sourceLabel ||
      filmSection?.hero?.releaseLabel
  );
  const showFilmIntelligenceSection =
    resolvedAdvertiserVertical === "film" || hasExplicitFilmReference;
  const audienceCohorts = useMemo(
    () => (Array.isArray(audienceSection?.cohorts) ? audienceSection.cohorts : []),
    [audienceSection]
  );
  // Each saved entry, presented (utils/micrositeAudienceGroups): what it is,
  // its heading, its own words. The section is framed by what the entries
  // are, and the weight chart names its rows the way the cards are headed.
  const audienceGroups = useMemo(
    () => audienceCohorts.map((cohort) => ({ cohort, group: describeAudienceGroup(cohort) })),
    [audienceCohorts]
  );
  // Presentation contract v2: the section's planning weights as the backend
  // normalized them. A section without the model renders its saved entries
  // as before.
  const planningWeights = useMemo(
    () => readPlanningWeights(audienceSection, { audience: PLANNING_WEIGHTS_READER }),
    [audienceSection]
  );
  const audienceFraming = useMemo(
    () =>
      planningWeights
        ? framePlanningWeightsSection(planningWeights)
        : frameAudienceSection(audienceGroups.map(({ group }) => group)),
    [planningWeights, audienceGroups]
  );
  const audienceHasEntries = planningWeights ? planningWeights.rows.length > 0 : audienceCohorts.length > 0;
  const audienceWeightChartItems = useMemo(() => {
    const byKey = new Map(
      audienceGroups.map(({ cohort, group }, index) => [cohort?.key, { group, rank: index + 1 }])
    );
    const chart = Array.isArray(audienceSection?.chart) ? audienceSection.chart : [];
    return chart.map((item) => {
      const match = byKey.get(item?.key) || null;
      return {
        ...item,
        label: match
          ? formatAudienceGroupChartLabel(match.group, match.rank)
          : formatMicrositeTextValue(item?.label, "Planner note"),
      };
    });
  }, [audienceGroups, audienceSection]);
  const overviewCards = useMemo(
    () => (Array.isArray(strategyOverviewSection?.cards) ? strategyOverviewSection.cards : []),
    [strategyOverviewSection]
  );
  const strategyAllocationRows = useMemo(
    () => parseSherlocBudgetAllocationItems(campaign?.strategy?.text || ""),
    [campaign]
  );
  const structuredChannelSplit = useMemo(
    () =>
      normalizePlatformAllocationItems(
        Array.isArray(budgetSection?.channelSplit) ? budgetSection.channelSplit : []
      ),
    [budgetSection]
  );
  /*
   * ONE ALLOCATION IDENTITY (microsite_allocation_identity_v1).
   *
   * Campaign 53 held a canonical `allocation_authority_v2` CTV 100% row. Three
   * surfaces rendered it and the Budget by Platform chart said no structured
   * data had been published — because each surface decided "is there an
   * allocation?" on its own. They now all answer to this one published claim.
   */
  const allocationIdentity = useMemo(
    () =>
      budgetSection?.allocationIdentity && typeof budgetSection.allocationIdentity === "object"
        ? budgetSection.allocationIdentity
        : null,
    [budgetSection]
  );
  /*
   * A recommendation awaiting a human decision must never read as an approved
   * allocation. The governance layer already raises ALLOCATION_APPROVAL_REQUIRED;
   * this states it on the chart itself, where the numbers are.
   */
  const allocationApprovalNotice = useMemo(() => {
    if (allocationIdentity?.approvalStatus !== "PENDING_APPROVAL") return null;
    return "Sherloc recommendation — awaiting allocation approval";
  }, [allocationIdentity]);
  const totalBudgetNumeric = useMemo(
    () =>
      resolveMicrositeCampaignBudget(campaign, budgetSection),
    [budgetSection, campaign]
  );
  const totalBudgetDisplay = useMemo(
    () => formatMicrositeBudgetDisplay(campaign, budgetSection),
    [budgetSection, campaign]
  );
  /*
   * THE ALLOCATION DOLLAR BASIS IS THE ONE THE BACKEND DECLARED.
   *
   * This preferred the NET working-media budget whenever the campaign block
   * carried one. The budget section states its own basis
   * (`totalBudgetMonetaryBasis: GROSS_CLIENT_BILLED`) and the allocation
   * display contract in mediaPlanAllocations says a platform row surfaces its
   * share of the campaign's gross budget, "never net media" — so on Campaign 53
   * a resolved 100% CTV row would have rendered $11,200, the internal
   * working-media figure, where the client agreed $16,000.
   *
   * Rows now also carry an explicit `grossAmount`, so this basis is only a
   * fallback for rows that predate it; where the backend names its basis, that
   * name decides.
   */
  const allocationBudgetBasisNumeric = useMemo(() => {
    if (budgetSection?.totalBudgetMonetaryBasis === "GROSS_CLIENT_BILLED") {
      return totalBudgetNumeric;
    }
    return resolveMicrositeNetMediaBudget(campaign) || totalBudgetNumeric;
  }, [budgetSection, campaign, totalBudgetNumeric]);
  const mediaPlanChartData = useMemo(() => normalizeMediaPlanChartData(campaign || {}, {
    monetaryBasis: budgetSection?.totalBudgetMonetaryBasis,
  }), [campaign, budgetSection]);
  const requestedAllocationRows = useMemo(
    () => normalizeRequestedChannelAllocationRows(campaign || {}, allocationBudgetBasisNumeric),
    [allocationBudgetBasisNumeric, campaign]
  );
  // Follow the same source decision as the platform plan, but retain raw
  // evidence: derived budget percentages and parsed prose cannot add weight.
  const campaignMediaAllocationRows = useMemo(
    () => requestedAllocationRows.length
      ? selectCampaignRequestedAllocationRows(campaign)
      : Array.isArray(budgetSection?.channelSplit) ? budgetSection.channelSplit : [],
    [campaign, requestedAllocationRows, budgetSection]
  );
  const channelSplit = useMemo(
    () =>
      resolveMicrositeChannelSplit({
        requestedAllocationRows,
        structuredChannelSplit,
        strategyAllocationRows,
        totalBudgetNumeric: allocationBudgetBasisNumeric,
        selectedPlatformChips: requestedPlatformChips,
        allocationIdentity,
      }),
    [
      allocationIdentity,
      requestedAllocationRows,
      requestedPlatformChips,
      strategyAllocationRows,
      structuredChannelSplit,
      allocationBudgetBasisNumeric,
    ]
  );
  // Allocation presence never selects or removes a saved strategy destination.
  const platformChips = requestedPlatformChips;
  const displayPlatformChips = useMemo(
    () => orderMicrositePlatformRows(dedupeMicrositePlatformChips(platformChips), channelSplit),
    [channelSplit, platformChips]
  );
  const platformDisplayCount = displayPlatformChips.length || 0;
  const platformCaveats = useMemo(
    () => buildMicrositeSelectedPlatformCaveats(platformChips, channelSplit),
    [channelSplit, platformChips]
  );
  /*
   * PROPOSAL MODE RE-SOURCES, IT DOES NOT HIDE. The planner charts and the
   * Impression Outlook draw from the proposal and the governed allocation
   * (CampaignProposalPlanning PROPOSAL_MODE_PROJECTED_SECTIONS) instead of the
   * directional benchmark math. All of them pass one gate: when the governed
   * allocation disagrees with the proposal they render nothing — never the
   * directional figures, and never a proposal the allocation contradicts.
   */
  const proposalChartProjection = useMemo(
    () =>
      buildProposalMediaPlanChartProjection(proposalPlanning, {
        allocationRows: channelSplit,
        objective: campaign?.objective,
      }),
    [campaign, channelSplit, proposalPlanning]
  );
  const proposalImpressionOutlook = useMemo(
    () => buildProposalImpressionOutlook(proposalPlanning, { allocationRows: channelSplit }),
    [channelSplit, proposalPlanning]
  );
  // Proposal mode keeps the governed Weekly Spend Ramp and withholds only the
  // template activation milestones (CampaignProposalPlanning).
  const proposalSpendRampCalendar = useMemo(
    () =>
      buildProposalSpendRampCalendar(proposalPlanning, launchActivationCalendar, {
        allocationRows: channelSplit,
      }),
    [channelSplit, launchActivationCalendar, proposalPlanning]
  );
  const objectiveSplit = useMemo(
    () =>
      buildCanonicalFunnelSplit(
        proposalChartProjection?.funnelStages?.length
          ? proposalChartProjection.funnelStages
          : mediaPlanChartData?.funnelStages,
        Array.isArray(budgetSection?.objectiveSplit) ? budgetSection.objectiveSplit : []
      ),
    [budgetSection, mediaPlanChartData, proposalChartProjection]
  );
  const timingPhases = useMemo(
    () => (Array.isArray(budgetSection?.timingPhases) ? budgetSection.timingPhases : []),
    [budgetSection]
  );
  const benchmarkCards = useMemo(
    () => orderMicrositePlatformRows(
      Array.isArray(benchmarkSection?.cards) ? benchmarkSection.cards : [],
      channelSplit
    ),
    [benchmarkSection, channelSplit]
  );
  const estimatedDelivery = useMemo(() => {
    const rawDelivery = Array.isArray(budgetSection?.estimatedDelivery)
      ? budgetSection.estimatedDelivery
      : [];
    return buildEstimatedDeliveryRows({
      rawDelivery,
      channelSplit,
      benchmarkCards,
    });
  }, [benchmarkCards, budgetSection, channelSplit]);
  const flowStages = useMemo(
    () => (Array.isArray(retargetingSection?.stages) ? retargetingSection.stages : []),
    [retargetingSection]
  );
  const platformCards = useMemo(
    () =>
      Array.isArray(campaignDetailSection?.platformCards)
        ? campaignDetailSection.platformCards
        : [],
    [campaignDetailSection]
  );
  const trackingRequirements = useMemo(
    () =>
      Array.isArray(optimizationSection?.trackingRequirements)
        ? optimizationSection.trackingRequirements
        : [],
    [optimizationSection]
  );
  const reportingLogic = useMemo(
    () =>
      Array.isArray(optimizationSection?.reportingLogic)
        ? optimizationSection.reportingLogic
        : [],
    [optimizationSection]
  );
  const cadence = useMemo(
    () => (Array.isArray(optimizationSection?.cadence) ? optimizationSection.cadence : []),
    [optimizationSection]
  );
  const kpis = useMemo(
    () => (Array.isArray(optimizationSection?.kpis) ? optimizationSection.kpis : []),
    [optimizationSection]
  );
  const creativeThemes = useMemo(
    () =>
      Array.isArray(creativeMessagingSection?.themes)
        ? creativeMessagingSection.themes
        : [],
    [creativeMessagingSection]
  );
  const executionModules = useMemo(
    () =>
      Array.isArray(executionModulesSection?.modules)
        ? executionModulesSection.modules
        : [],
    [executionModulesSection]
  );
  const verticalPlatformCards = useMemo(
    () => buildVerticalPlatformStrategyCards(
      platformChips,
      resolvedAdvertiserVertical,
      channelSplit
    ),
    [channelSplit, platformChips, resolvedAdvertiserVertical]
  );
  const whyThisWorksCards = overviewCards;
  const objectiveCards = [];
  const objectiveJourney = [];
  const campaignObjectiveLabel = formatCampaignObjectiveLabel(campaign?.objective, "");
  // Campaign positioning / executive summary shown in the hero paragraph.
  const rawHeroSummary =
    strategyOverviewSection?.positioningStatement ||
    filmSection?.hero?.synopsis ||
    (campaignObjectiveLabel
      ? `Objective: ${campaignObjectiveLabel}`
      : "Full-funnel Cphere strategy overview");
  const heroSummary = resolvePlatformAwareHeroNarrative({
    narrative: rawHeroSummary,
    title: filmHeroTitle,
    platforms: displayPlatformChips,
    advertiserVertical: resolvedAdvertiserVertical,
  });
  // Cross-Platform Strategy Logic intro: distinct strategy rationale, never the hero summary.
  const strategyLogicIntro = resolveMicrositeStrategyLogicIntro({
    heroSummary,
    fallback: copy.strategyLogicFallback,
    candidates: [
      strategyOverviewSection?.strategyLogic,
      strategyOverviewSection?.crossPlatformStrategy,
      strategyOverviewSection?.channelStrategyRationale,
      strategyOverviewSection?.distributionStrategy,
      strategyOverviewSection?.mediaMixRationale,
      strategyOverviewSection?.overview,
      verticalMicrositeFallbacks.whyIntro,
    ],
  });
  const audienceInsightCards = [];
  const scaleChannels = [];
  const platformHeroLogos = useMemo(() => {
    if (!governedRenderState.clientSafeToRender) return [];
    const seen = new Set();
    return displayPlatformChips
      .map((platform) => resolveMicrositePlatformBranding(platform))
      .filter(Boolean)
      .filter((platform) => {
        const key =
          normalizeMicrositeKey(formatMicrositePlatformDisplayLabel(platform.label)) ||
          platform.key;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [displayPlatformChips, governedRenderState.clientSafeToRender]);
  const rankedEstimatedDelivery = useMemo(
    () => orderMicrositePlatformRows(estimatedDelivery, channelSplit),
    [channelSplit, estimatedDelivery]
  );
  const estimatedDeliveryCards = useMemo(
    () => buildImpressionOutlookEntries(rankedEstimatedDelivery, mediaPlanChartData?.platforms),
    [mediaPlanChartData, rankedEstimatedDelivery]
  );
  const estimatedImpressionTotal = useMemo(
    () => buildEstimatedImpressionTotal(estimatedDelivery),
    [estimatedDelivery]
  );
  const estimatedImpressionTotalLabel = estimatedImpressionTotal
    ? formatMetricCount(estimatedImpressionTotal)
    : "";
  const benchmarkEstimateCards = useMemo(
    () =>
      orderMicrositePlatformRows(benchmarkCards, channelSplit)
        .filter((card) => card?.primaryValue)
        .map((card) => ({
          key: card.key || card.label,
          label: displayMicrositePlatformIdentityLabel(card.label, card.key),
          value: card.primaryValue,
          meta: resolveBenchmarkDisplayLabel(card),
        })),
    [benchmarkCards, channelSplit]
  );
  /*
   * THE SECTION STATES ITS OWN EVIDENCE.
   *
   * Campaign 53's CPM card was headed "Pricing truth". The number under it was
   * the route's unsourced directional CTV constant divided by (1 - margin), on
   * a campaign with no provider forecast, no approved governing forecast and no
   * contracted rate. These labels come from the tier the backend published, so
   * the page can only ever claim the evidence it actually has.
   */
  const pricingEvidenceSummary = useMemo(
    () =>
      benchmarkSection?.evidenceSummary && typeof benchmarkSection.evidenceSummary === "object"
        ? benchmarkSection.evidenceSummary
        : null,
    [benchmarkSection]
  );
  const pricingEvidenceTier = useMemo(() => {
    if (pricingEvidenceSummary?.strongestEvidenceTier) {
      return pricingEvidenceSummary.strongestEvidenceTier;
    }
    const tiers = (Array.isArray(benchmarkCards) ? benchmarkCards : [])
      .map((card) => resolveMicrositeEvidenceTier(card))
      .filter(Boolean);
    return tiers.length ? tiers[0] : null;
  }, [benchmarkCards, pricingEvidenceSummary]);
  const pricingEvidenceIsForecast = useMemo(
    () =>
      pricingEvidenceSummary?.isForecastEvidence === true ||
      FORECAST_EVIDENCE_TIERS.has(pricingEvidenceTier),
    [pricingEvidenceSummary, pricingEvidenceTier]
  );
  const pricingEvidenceMetaLabel = useMemo(
    () =>
      (pricingEvidenceTier && MICROSITE_PRICING_EVIDENCE_TIER_LABEL[pricingEvidenceTier]) ||
      CLIENT_SAFE_BENCHMARK_DISPLAY_LABEL,
    [pricingEvidenceTier]
  );
  /* An estimate envelope is not a forecast, and a forecast is not an envelope. */
  const pricingEnvelopeTitle = pricingEvidenceIsForecast
    ? "CPM Forecast"
    : "CPM Estimate Envelope";
  const impressionOutlookTitle = "Impression Outlook";
  const impressionOutlookMetaLabel = useMemo(() => {
    const fromRows = (Array.isArray(rankedEstimatedDelivery) ? rankedEstimatedDelivery : [])
      .map((row) => resolveMicrositeDeliveryBasisLabel(row))
      .find(Boolean);
    return fromRows || pricingEvidenceMetaLabel;
  }, [pricingEvidenceMetaLabel, rankedEstimatedDelivery]);
  /*
   * Absence is stated, not merely omitted. "No executed provider forecast" is
   * information a client is entitled to; silence reads as a forecast existing.
   */
  const pricingEvidenceAbsences = useMemo(
    () =>
      Array.isArray(pricingEvidenceSummary?.absences) && !pricingEvidenceIsForecast
        ? pricingEvidenceSummary.absences
        : [],
    [pricingEvidenceIsForecast, pricingEvidenceSummary]
  );
  const resolvedPlatformCards = useMemo(() => {
    const scopedCards = filterCampaignPlatformCards(
      platformCards.length ? platformCards : verticalPlatformCards || [],
      platformChips
    );
    // The legacy helper can divide a parent allocation across children. Child
    // details instead fill missing labels only from exact raw saved child rows,
    // retaining existing values and never splitting a grouped Meta allocation.
    const canEnrich = (card) => {
      const identity = resolveCampaignPlatformIdentity(card);
      return identity?.known && !["facebook", "instagram"].includes(identity.key);
    };
    const cards = scopedCards.filter(canEnrich);
    const enrichedCards = [
      ...enrichPlatformStrategyCardsWithAllocations(cards, channelSplit),
      ...scopedCards.filter((card) => !canEnrich(card)).map((card) => {
        const percent = resolveCampaignMetaChildAllocationPercent(card, campaignMediaAllocationRows);
        return percent === null ? card : { ...card, canonicalAllocationLabel: formatAllocationPercentLabel(percent) };
      }),
    ];
    return orderMicrositePlatformRows(completeCampaignPlatformCards(
      ensureMicrositeSelectedPlatformCards({
        cards: enrichedCards,
        selectedPlatforms: platformChips,
        allocations: channelSplit,
        advertiserVertical: resolvedAdvertiserVertical,
      }),
      platformChips
    ), channelSplit);
  }, [campaignMediaAllocationRows, channelSplit, platformCards, platformChips, resolvedAdvertiserVertical, verticalPlatformCards]);
  const resolvedCampaignDetailCards = useMemo(
    () => orderMicrositePlatformRows(
      // Selected-platform scope: whatever source supplies the cards (backend
      // section, curated layer, or fallback platform cards), a card may only
      // render for a platform the campaign explicitly selected.
      filterLineItemsToSelectedPlatformScope(
        Array.isArray(campaignDetailSection?.campaigns) && campaignDetailSection.campaigns.length
          ? campaignDetailSection.campaigns
          // Generic fallback: the enriched per-platform cards (executionGroups +
          // readinessStatus) so every campaign renders an execution blueprint.
          : platformCards,
        selectedPlatformScopeKeys
      ),
      channelSplit
    ),
    [campaignDetailSection, channelSplit, platformCards, selectedPlatformScopeKeys]
  );
  const campaignReadiness = useMemo(
    () => deriveCampaignReadiness(campaignDetailSection),
    [campaignDetailSection]
  );
  const planningReview = isMicrositePlanningReview({
    approvalAuthority: microsite?.approvalAuthority,
    presentationCurrent: presentationFeature(campaign, "canonicalDeliveryFigures"),
    governance: microsite?.governance,
    allocationStatus: resolveCampaignAllocationApprovalStatus(allocationRecommendation),
  });
  const reviewNetMediaBudget = planningReview ? resolveMicrositeNetMediaBudget(campaign) : null;
  const plannerPhaseWorkingMedia = useMemo(
    () => resolvePlannerPhaseWorkingMedia(timingPhases, reviewNetMediaBudget),
    [timingPhases, reviewNetMediaBudget]
  );
  const groupedMetaPlanning = planningReview && channelSplit.some((row) =>
    normalizePlatformAllocationKey(row?.key || row?.label, { allowUnknown: true }) === "meta"
  );
  const creativeVariationChips = [];
  // Richest-available creative source wins, in priority order:
  //   1. explicit/backend creativeMessaging.themes
  //   2. generated creativeRecommendations
  //   3. generated creativeVariations
  // The placeholder is only reached when none of these carry real content.
  const resolvedCreativeThemes = useMemo(() => {
    const candidates = [
      creativeThemes,
      deriveMicrositeCreativeThemesFromList(campaign?.creativeRecommendations),
      deriveMicrositeCreativeThemesFromList(campaign?.creativeVariations),
    ];
    return (
      candidates.find((candidate) =>
        resolveMicrositeCreativeThemeCards(candidate).some((card) => !card.isGuidance)
      ) || []
    );
  }, [creativeThemes, campaign]);
  // Creative pillars de-duplicated: distinct content renders distinct cards; missing
  // content collapses to a single guidance card instead of repeating the placeholder.
  const creativeThemeCards = useMemo(
    () => resolveMicrositeCreativeThemeCards(resolvedCreativeThemes),
    [resolvedCreativeThemes]
  );
  const creativeVariationCards = useMemo(
    () => normalizeMicrositeCreativeVariationCards(campaign),
    [campaign]
  );
  const realCreativeThemeCount = useMemo(
    () => creativeThemeCards.filter((card) => !card.isGuidance).length,
    [creativeThemeCards]
  );
  const launchTimelinePhases = [];
  const resolvedExecutionModules = useMemo(
    () => orderMicrositePlatformRows(executionModules, channelSplit),
    [channelSplit, executionModules]
  );
  const resolvedKpis = useMemo(() => mergeUniqueTextItems(kpis, []), [kpis]);
  const resolvedReportingLogic = useMemo(
    () => mergeUniqueTextItems(reportingLogic, []),
    [reportingLogic]
  );
  const resolvedTrackingRequirements = useMemo(
    () => mergeUniqueTextItems(trackingRequirements, []),
    [trackingRequirements]
  );
  const resolvedCadence = useMemo(() => mergeUniqueTextItems(cadence, []), [cadence]);
  const didYouKnowStats = [];
  const brandAwarenessStrategy = useMemo(
    () =>
      buildBrandAwarenessStrategy({
        campaign,
        strategyOverviewSection,
        audienceSection,
        earnedMediaSection,
        releaseMilestoneSchedule,
        creativeMessagingSection,
        platformCards: resolvedPlatformCards,
        platformChips,
        filmSection,
        advertiserVertical: resolvedAdvertiserVertical,
      }),
    [
      audienceSection,
      campaign,
      creativeMessagingSection,
      earnedMediaSection,
      filmSection,
      platformChips,
      releaseMilestoneSchedule,
      resolvedAdvertiserVertical,
      resolvedPlatformCards,
      strategyOverviewSection,
    ]
  );
  const micrositeContext = useMemo(
    () =>
      buildCampaignStrategyMicrositeContext({
        campaign: governedRenderState.clientSafeToRender ? campaign : null,
        advertiserVertical: resolvedAdvertiserVertical,
        advertiserVerticalContext,
        filmSection,
        budgetSection,
        strategyOverviewSection,
        audienceSection,
        benchmarkSection,
        retargetingSection,
        earnedMediaSection,
        releaseMilestoneSchedule,
        campaignDetailSection,
        creativeMessagingSection,
        optimizationSection,
        executionModulesSection,
        summaryCards,
        platformChips,
        channelSplit,
        objectiveSplit,
        timingPhases,
        estimatedDelivery,
        benchmarkCards,
        platformCards,
        trackingRequirements: resolvedTrackingRequirements,
        reportingLogic: resolvedReportingLogic,
        cadence: resolvedCadence,
        kpis: resolvedKpis,
        creativeThemes: resolvedCreativeThemes,
        executionModules: resolvedExecutionModules,
          filmCards,
        filmScoreItems,
        proposalPlanning,
      }),
    [
      audienceSection,
      advertiserVerticalContext,
      benchmarkCards,
      benchmarkSection,
      budgetSection,
      campaign,
      campaignDetailSection,
      channelSplit,
      creativeMessagingSection,
      estimatedDelivery,
      earnedMediaSection,
      executionModulesSection,
      filmCards,
      filmScoreItems,
      filmSection,
      objectiveSplit,
      optimizationSection,
      platformCards,
      platformChips,
      proposalPlanning,
      resolvedAdvertiserVertical,
      resolvedCadence,
      resolvedCreativeThemes,
      resolvedExecutionModules,
      resolvedKpis,
      resolvedReportingLogic,
      resolvedTrackingRequirements,
      retargetingSection,
      releaseMilestoneSchedule,
      strategyOverviewSection,
      summaryCards,
      timingPhases,
      governedRenderState.clientSafeToRender,
    ]
  );

  useEffect(() => {
    if (
      !canRegisterCampaignMicrositePageAwareness({
        loading,
        error,
        micrositeContext,
        canRenderStrategy: governedRenderState.clientSafeToRender,
      })
    ) {
      clearPageAwareness();
      return undefined;
    }

    registerPageAwareness({
      routeKey: "campaign_strategy_microsite",
      pageTitle: "Campaign Strategy Microsite",
      pageType: "campaign_strategy_microsite",
      advertiserId: micrositeContext.advertiser?.id || null,
      advertiserName: micrositeContext.advertiser?.name || "",
      advertiserVertical: micrositeContext.advertiserVertical || "general",
      advertiserVerticalContext: micrositeContext.advertiserVerticalContext || null,
      campaignId: micrositeContext.campaign?.id || campaignRequestKey || null,
      campaignName: micrositeContext.campaign?.title || "",
      timeframe: micrositeContext.flightWindow?.start && micrositeContext.flightWindow?.end
        ? {
            start: micrositeContext.flightWindow.start,
            end: micrositeContext.flightWindow.end,
            label: micrositeContext.flightWindow.label,
          }
        : null,
      toplineScope: "page",
      pageCapabilities: [
        "campaign_strategy_microsite",
        "media_strategy",
        "platform_allocation",
        "film_positioning",
        "client_safe_strategy_answers",
      ],
      pageToplineMetrics: [
        {
          label: "Total Budget",
          value: micrositeContext.totalBudget,
          /* campaign_financial_basis_v1: the budget names its basis. The
             backend emits `budgetBasisLabel` ("Client-billed"); it is never
             inferred here. */
          meta: micrositeContext.budgetBasisLabel
            ? `${micrositeContext.budgetBasisLabel} · Published media plan`
            : "Published media plan",
        },
        {
          label: "Platforms",
          value: String(micrositeContext.platforms?.length || 0),
          meta: "Approved platform mix",
        },
        {
          label: "Flight",
          value: micrositeContext.flightWindow?.label || "Flight window pending",
          meta: "Campaign Strategy Microsite",
        },
      ],
      metadata: {
        contextType: "campaign_strategy_microsite",
        micrositeContext,
      },
    });

    return () => {
      clearPageAwareness();
    };
  }, [
    campaignRequestKey,
    clearPageAwareness,
    error,
    governedRenderState.clientSafeToRender,
    loading,
    micrositeContext,
    registerPageAwareness,
  ]);

  return (
    <MicrositeBrandPresentationContext.Provider value={brandPresentation}>
    <MicrositeSemanticTypographyContext.Provider value={semanticTypography}>
    <div className={micrositePageClassName(resolveMicrositeRendererPresentation(campaign))}>
      <div className="campaign-strategy-microsite-shell">
        <header className="campaign-strategy-microsite-header">
          <div className="campaign-strategy-microsite-brand">
            <CampaignStrategyMicrositeHeaderLogoLink />
            <div
              className="campaign-strategy-microsite-pill"
              aria-label="Sherloc Media Campaign Strategy"
            >
              <span className="campaign-strategy-microsite-pill-label">
                MEDIA CAMPAIGN
              </span>
              <span className="campaign-strategy-microsite-pill-badge">STRATEGY</span>
            </div>
          </div>
          <div className="campaign-strategy-microsite-header-copy">
            <h1 className="campaign-strategy-microsite-title">
              <span className="campaign-strategy-microsite-title-line">
                Digital Media Plan
              </span>
              <span className="campaign-strategy-microsite-title-plus">+</span>
              <span className="campaign-strategy-microsite-title-line">
                {formatAdvertiserVerticalStrategyLabel(resolvedAdvertiserVertical)}
              </span>
            </h1>
            {headerDescription ? <p>{headerDescription}</p> : null}
            {platformHeroLogos.length ? (
              <div className="campaign-strategy-microsite-platform-hero-row">
                {platformHeroLogos.map((platform) => (
                  <div
                    key={platform.key}
                    className="campaign-strategy-microsite-platform-hero-chip"
                  >
                    <span className="campaign-strategy-microsite-platform-hero-logo">
                      <img
                        src={platform.image}
                        alt={formatMicrositePlatformDisplayLabel(platform.label)}
                      />
                    </span>
                    <span className="campaign-strategy-microsite-platform-hero-label">
                      {formatMicrositePlatformDisplayLabel(platform.label)}
                    </span>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </header>

        {/* Microsite action area: a single centered Back to Media Planner control (Ask/Eden removed). */}
        {showMediaPlannerControls ? (
          <div
            className="campaign-strategy-microsite-mobile-controls"
            data-campaign-strategy-mobile-controls="true"
            role="group"
            aria-label="Campaign strategy controls"
          >
            <button
              type="button"
              className="campaign-strategy-microsite-mobile-control campaign-strategy-microsite-mobile-back"
              onClick={handleMicrositeBack}
              aria-label="Back to media planner"
            >
              Back to Media Planner
            </button>
          </div>
        ) : null}

        {loading ? (
          <div className="campaign-strategy-microsite-state-card" role="status">
            {CAMPAIGN_STRATEGY_MICROSITE_LOADING_COPY}
          </div>
        ) : error ? (
          <div className="campaign-strategy-microsite-state-card campaign-strategy-microsite-state-card-error">
            {error}
          </div>
        ) : !campaign ? (
          <div className="campaign-strategy-microsite-state-card">
            No campaign strategy is available for this microsite yet.
          </div>
        ) : !governedRenderState.clientSafeToRender ? (
          <>
            <CampaignStrategyMicrositeGovernanceNotice
              state={governedRenderState}
              findings={allocationFindingCodes}
              reviewGateway={reviewGateway}
              reviewReadState={sessionAccessReadState}
            />
            {strategyReviewAccessCampaignId ? (
              <StrategyReviewAccessPanel
                key={strategyReviewAccessCampaignId}
                apiBaseUrl={STRATEGY_REVIEW_API_BASE}
                campaignId={strategyReviewAccessCampaignId}
                onUnlocked={handleStrategyReviewUnlocked}
              />
            ) : null}
            <CampaignApprovalAuthorityStrip
              approvalAuthority={microsite?.approvalAuthority}
              compact={reviewGateway}
            />
            {allocationPendingRecommendation ? (
              <CampaignAllocationPendingSection
                recommendation={allocationPendingRecommendation}
              />
            ) : null}
          </>
        ) : (
          <>
            <section className="campaign-strategy-microsite-hero-card">
              <div className="campaign-strategy-microsite-hero-grid">
                <div className="campaign-strategy-microsite-hero-copy">
                  <span className="campaign-strategy-microsite-section-kicker">
                    {campaign.advertiserName || "Advertiser / Studio"}
                  </span>
                  <h2>{cleanMicrositeNarrativeTitle(campaign.campaignName) || "Campaign Strategy"}</h2>
                  <p>
                    {appendMicrositeDisplayTerminalPunctuation(
                      renderAdvertiserNarrativeCopy(heroSummary)
                    )}{" "}
                    {formatMicrositePrimaryKpiNarrative(campaign.primaryKpi, { displayCase: semanticTypography })}
                  </p>

                  <div className="campaign-strategy-microsite-chip-row campaign-strategy-microsite-hero-kpi-row">
                    {/* Every hero pill is a label in the UI face whatever its
                        length (CampaignStrategyMicrosite.css, "Hero pills").
                        With semantic typography the Primary KPI pill shows the
                        campaign's own KPI in its display case; the saved chip
                        only says that the slot exists. */}
                    {(heroChips.length ? heroChips : []).map((chip, chipIndex) => {
                      const chipKey = String(chip.key || "");
                      const chipLabel = String(chip.label || "");
                      const primaryKpiLabel = normalizeMicrositeDisplayTerminalPunctuation(
                        campaign.primaryKpi || ""
                      ).trim();
                      const normalizedChipLabel = normalizeMicrositeDisplayTerminalPunctuation(
                        chipLabel
                      ).trim();
                      const isPrimaryKpiChip =
                        /primary[_-]?kpi/i.test(chipKey) ||
                        /^primary\s+kpi\b/i.test(chipLabel) ||
                        Boolean(
                          primaryKpiLabel &&
                            (normalizedChipLabel === primaryKpiLabel ||
                              normalizedChipLabel.includes(primaryKpiLabel))
                        );
                      const displayChipLabel =
                        semanticTypography && isPrimaryKpiChip
                          ? formatMicrositePrimaryKpiDisplay(campaign.primaryKpi) ||
                            formatMicrositePrimaryKpiDisplay(chipLabel)
                          : formatMicrositeHeroChipLabel(chip, platformDisplayCount);
                      const isChannelCountChip = /(?:channels?|platforms?)\s+planned/i.test(
                        chipLabel
                      );
                      const isLeadKpiChip = chipIndex === 0 && !isPrimaryKpiChip;

                      return (
                        <span
                          key={chip.key || chip.label}
                          className={[
                            "campaign-strategy-microsite-chip",
                            isLeadKpiChip
                              ? "campaign-strategy-microsite-lead-kpi-chip-mobile"
                              : "",
                            isChannelCountChip
                              ? "campaign-strategy-microsite-channel-count-chip-mobile"
                              : "",
                            isPrimaryKpiChip
                              ? "campaign-strategy-microsite-kpi-pill-full-mobile campaign-strategy-microsite-primary-kpi-card-full-mobile"
                              : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                        >
                          {displayChipLabel}
                        </span>
                      );
                    })}
                    <CampaignStrategyPlatformPills
                      platformHeroLogos={platformHeroLogos}
                      precedingPlatformGridItemCount={precedingPlatformGridItemCount}
                    />
                  </div>

                  {advertiserLogoSrc ? (
                    <div
                      className="campaign-strategy-microsite-advertiser-logo-frame"
                      aria-label="Advertiser logo"
                    >
                      <img
                        src={advertiserLogoSrc}
                        alt={advertiserLogoAlt}
                        onError={handleAdvertiserLogoError}
                      />
                    </div>
                  ) : null}
                </div>

                <div className="campaign-strategy-microsite-hero-aside">
                  <MicrositeMediaArtwork
                    className="campaign-strategy-microsite-hero-art"
                    image={resolvedFilmHero?.image}
                    alt={filmHeroTitle || "Campaign hero"}
                    fallbackLabel="Cphere Strategy"
                    fallbackImage={dashboardBrainFallbackImage}
                    fallbackImageAlt="Cphere strategy fallback artwork"
                    hero={resolvedFilmHero}
                    variant="poster"
                    preferPosterFrame
                  />
                  <div className="campaign-strategy-microsite-status-panel">
                    <div className="campaign-strategy-microsite-status-row">
                      <span>Status</span>
                      <strong>{sentenceCase(campaign.status) || "Planned"}</strong>
                    </div>
                    <div className="campaign-strategy-microsite-status-row">
                      <span>Flight</span>
                      <strong>{formatFlightWindow(campaign)}</strong>
                    </div>
                    {releaseDateDisplay ? (
                      <div className="campaign-strategy-microsite-status-row">
                        <span>Release Date</span>
                        <strong>{releaseDateDisplay}</strong>
                      </div>
                    ) : null}
                    <div className="campaign-strategy-microsite-status-row">
                      <span>{planningReview ? "Recommended Scenario" : "Budget"}</span>
                      <strong>{totalBudgetDisplay}</strong>
                    </div>
                    {reviewNetMediaBudget != null ? (
                      <div className="campaign-strategy-microsite-status-row" data-testid="microsite-working-media">
                        <span>Working media</span>
                        <strong>{formatCurrency(reviewNetMediaBudget)}</strong>
                      </div>
                    ) : null}
                    <div className="campaign-strategy-microsite-status-row">
                      <span>{groupedMetaPlanning ? "Allocation groups" : "Platforms"}</span>
                      <strong>{platformDisplayCount || 0}</strong>
                    </div>
                    {groupedMetaPlanning ? (
                      <p className="campaign-strategy-microsite-flow-note" data-testid="microsite-meta-group">
                        Meta (Facebook + Instagram) shares one allocation group.
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>
            </section>

            <CampaignApprovalAuthorityStrip
              approvalAuthority={microsite?.approvalAuthority}
            />

            <section className="campaign-strategy-microsite-summary-grid">
              {summaryCards.map((card) => (
                <article
                  key={card.key || card.label}
                  className="campaign-strategy-microsite-summary-card"
                >
                  <span className="campaign-strategy-microsite-summary-label">{card.label}</span>
                  {/* The summary card decides its own face (CampaignStrategyMicrosite.css,
                      "Hero summary cards"). Length decides only the compact
                      size, never the face; isSentenceLikeKpi does not apply. */}
                  <span
                    className={
                      semanticTypography && isCompactSummaryValue(card.displayValue)
                        ? "campaign-strategy-microsite-summary-value campaign-strategy-microsite-summary-value--compact"
                        : "campaign-strategy-microsite-summary-value"
                    }
                  >
                    {card.displayValue}
                  </span>
                </article>
              ))}
            </section>

            <StrategyFunnel data={strategyFunnel} />

            {showSection("whyThisStrategyWorks") ? (
              <MicrositeSection
                eyebrow="Why This Strategy Works"
                title={verticalMicrositeFallbacks.whyTitle}
                meta={
                  platformDisplayCount
                    ? planningReview
                      ? // In planning-review mode the page counts what is
                        // planned, as the hero chip and group labels do.
                        `${platformDisplayCount} ${micrositeCountNoun(
                          platformDisplayCount,
                          "channel"
                        )} planned`
                      : `${platformDisplayCount} active ${micrositeCountNoun(
                          platformDisplayCount,
                          "platform"
                        )}`
                    : "Planning in progress"
                }
              >
                <p className="campaign-strategy-microsite-section-intro">
                  {renderAdvertiserNarrativeCopy(strategyLogicIntro)}
                </p>
                {whyThisWorksCards.length ? (
                  <div className="campaign-strategy-microsite-overview-grid">
                    {whyThisWorksCards.map((card) => (
                      <article
                        key={card.key || card.title}
                        className="campaign-strategy-microsite-insight-card campaign-strategy-microsite-strategy-card"
                      >
                        <div className="campaign-strategy-microsite-strategy-card-header">
                          {card.value ? (
                            <strong className="campaign-strategy-microsite-insight-stat">
                              {card.value}
                            </strong>
                          ) : null}
                          {card.valueLabel ? (
                            <span className="campaign-strategy-microsite-insight-caption">
                              {card.valueLabel}
                            </span>
                          ) : null}
                          <span className="campaign-strategy-microsite-overview-kicker campaign-strategy-microsite-strategy-card-pill">
                            {card.title}
                          </span>
                        </div>
                        <div className="campaign-strategy-microsite-strategy-card-body">
                          <p className="campaign-strategy-microsite-strategy-card-copy">
                            {renderAdvertiserNarrativeCopy(card.copy)}
                          </p>
                        </div>
                      </article>
                    ))}
                  </div>
                ) : (
                  <EmptyPanel>
                    {verticalMicrositeFallbacks.emptyStrategy}
                  </EmptyPanel>
                  )}
              </MicrositeSection>
            ) : null}

            {showSection("proofStatStrip") && defaultFiguresApply ? (
              <ProofStatStrip
                stats={getMicrositeProofStats(resolvedAdvertiserVertical, {
                  campaign,
                  selectedPlatformScope: selectedPlatformScopeKeys,
                })}
              />
            ) : null}

            <CampaignProposalPlanningStory planning={proposalPlanning} />

            {!proposalPlanning || proposalChartProjection ? (
              <SherlocMediaPlanCharts plan={campaign} projection={proposalChartProjection} monetaryBasis={budgetSection?.totalBudgetMonetaryBasis} />
            ) : null}

            {objectiveCards.length ? (
              <MicrositeSection
                eyebrow="Campaign Objectives"
                title={verticalMicrositeFallbacks.objectiveTitle}
                meta={copy.objectivesMeta}
              >
                <div className="campaign-strategy-microsite-objective-grid">
                  {objectiveCards.map((card) => (
                    <article
                      key={card.key}
                      className="campaign-strategy-microsite-objective-card"
                    >
                      <span className="campaign-strategy-microsite-card-meta">
                        {card.label}
                      </span>
                      <p>{renderAdvertiserNarrativeCopy(card.copy)}</p>
                    </article>
                  ))}
                </div>
                <div className="campaign-strategy-microsite-journey-row">
                  {objectiveJourney.map((item) => (
                    <div
                      key={item.key}
                      className="campaign-strategy-microsite-journey-chip"
                    >
                      <strong>{item.label}</strong>
                      <span>{item.detail}</span>
                    </div>
                  ))}
                </div>
              </MicrositeSection>
            ) : null}

            {showFilmIntelligenceSection ? (
              <MicrositeSection
                eyebrow={
                  resolvedAdvertiserVertical === "film"
                    ? "Situation & Film Intelligence"
                    : "Situation & Market Intelligence"
                }
                title={
                  resolvedAdvertiserVertical === "film"
                    ? "Film Reference + Positioning Context"
                    : "Source Reference + Positioning Context"
                }
                meta={
                  filmCards.length
                    ? `${filmCards.length} connected ${micrositeCountNoun(
                        filmCards.length,
                        "source"
                      )}`
                    : resolvedAdvertiserVertical === "film"
                      ? "Awaiting title sources"
                      : "Awaiting source references"
                }
              >
              <div className="campaign-strategy-microsite-film-hero">
                <div className="campaign-strategy-microsite-film-copy">
                  <h4>
                    {cleanMicrositeNarrativeTitle(filmHeroTitle)}
                  </h4>
                  <p>
                    {filmSection?.combinedTakeaway ||
                      resolvedFilmHero?.synopsis ||
                      "Attach the official website, IMDb URL, and Rotten Tomatoes URL to enrich this title module further."}
                  </p>
                  <div className="campaign-strategy-microsite-chip-row">
                    {resolvedFilmHero?.sourceLabel ? (
                      <span className="campaign-strategy-microsite-chip">
                        {resolvedFilmHero.sourceLabel}
                      </span>
                    ) : null}
                    {resolvedFilmHero?.releaseLabel ? (
                      <span className="campaign-strategy-microsite-chip">
                        {resolvedFilmHero.releaseLabel}
                      </span>
                    ) : null}
                    {resolvedFilmHero?.genreLabel ? (
                      <span className="campaign-strategy-microsite-chip">
                        {resolvedFilmHero.genreLabel}
                      </span>
                    ) : null}
                    {resolvedFilmHero?.contentSourceLabel ? (
                      <span className="campaign-strategy-microsite-chip">
                        Positioning • {resolvedFilmHero.contentSourceLabel}
                      </span>
                    ) : null}
                    {resolvedFilmHero?.imageSourceLabel ? (
                      <span className="campaign-strategy-microsite-chip">
                        Hero Art • {resolvedFilmHero.imageSourceLabel}
                      </span>
                    ) : null}
                  </div>
                  {filmScoreItems.length ? (
                    <div className="campaign-strategy-microsite-score-grid">
                      {filmScoreItems.map((item) => (
                        <article
                          key={item.key}
                          className="campaign-strategy-microsite-score-card"
                        >
                          <span className="campaign-strategy-microsite-score-label">
                            {item.label}
                          </span>
                          <strong className="campaign-strategy-microsite-score-value">
                            {item.value}
                          </strong>
                        </article>
                      ))}
                    </div>
                  ) : null}
                </div>
                {resolvedFilmHero?.image ? (
                  <MicrositeMediaArtwork
                    className="campaign-strategy-microsite-film-poster"
                    image={resolvedFilmHero.image}
                    alt={filmHeroTitle}
                    fallbackLabel={resolvedFilmHero?.imageSourceLabel || "Film Reference"}
                    hero={resolvedFilmHero}
                    variant="poster"
                    preferPosterFrame
                  />
                ) : null}
              </div>
              {strategyOverviewSection?.positioningStatement ? (
                <div className="campaign-strategy-microsite-mandate-card" data-testid="strategic-mandate-card">
                  <span className="campaign-strategy-microsite-card-meta">
                    Strategic Mandate
                  </span>
                  <p>
                    {renderAdvertiserNarrativeCopy(
                      strategyOverviewSection?.positioningStatement
                    )}
                  </p>
                </div>
              ) : null}

                {filmCards.length ? (
                  <div
                    className={`campaign-strategy-microsite-source-grid${
                      filmCards.length === 1
                        ? " campaign-strategy-microsite-source-grid--single"
                        : " campaign-strategy-microsite-centered-card-grid campaign-strategy-microsite-centered-card-grid--3"
                    }`}
                  >
                    {filmCards.map((card) => (
                      <article
                        key={card.key || card.title}
                        className={`campaign-strategy-microsite-source-card${
                          resolveFilmSourceRoleLabel(card, filmSection?.hero)
                            ? " campaign-strategy-microsite-source-card--active"
                            : ""
                        }`}
                      >
                        <div className="campaign-strategy-microsite-source-topline">
                          <div
                            className={`campaign-strategy-microsite-source-logo${
                              buildSourceCardArtwork(card, sourceCardIdentity).isBrandAsset
                                ? " campaign-strategy-microsite-source-logo--brand"
                                : ""
                            }${
                              buildSourceCardArtwork(card, sourceCardIdentity).sourceClassName
                                ? ` ${buildSourceCardArtwork(card, sourceCardIdentity).sourceClassName}`
                                : ""
                            }`}
                          >
                            {buildSourceCardArtwork(card, sourceCardIdentity).image ? (
                              <img
                                src={buildSourceCardArtwork(card, sourceCardIdentity).image}
                                alt={card.title}
                              />
                            ) : null}
                          </div>
                          <div className="campaign-strategy-microsite-source-copy">
                            <span className="campaign-strategy-microsite-card-title">
                              {card.title}
                            </span>
                            <span className="campaign-strategy-microsite-source-value">
                              {formatFilmSourceCardValue(card, filmSection)}
                            </span>
                            <span className="campaign-strategy-microsite-source-badge">
                              {card.badgeLabel}
                            </span>
                          </div>
                        </div>
                        <p>{card.copy}</p>
                        {card.url ? (
                          <MicrositeSourceButton
                            card={card}
                            hero={filmSection?.hero}
                            compact
                          />
                        ) : null}
                      </article>
                  ))}
                </div>
              ) : (
                <EmptyPanel>
                  Attach the official site, IMDb, or Rotten Tomatoes URLs to populate this section.
                </EmptyPanel>
              )}
              </MicrositeSection>
            ) : null}

            <section className="campaign-strategy-microsite-audience-platform-stack">
              {showSection("audienceCohorts") ? (
                <MicrositeSection
                  eyebrow="Audience Intelligence"
                  title={audienceFraming.title}
                  subtitle={audienceFraming.subtitle}
                  meta={audienceHasEntries ? audienceFraming.badge : copy.audienceAwaitingMeta}
                  className="campaign-strategy-microsite-section-card--audience"
                >
                  {planningWeights ? (
                    planningWeights.rows.length ? (
                      <div className="campaign-strategy-microsite-audience-grid campaign-strategy-microsite-audience-grid--planning-weights">
                        <div className="campaign-strategy-microsite-cohort-list campaign-strategy-microsite-centered-card-grid campaign-strategy-microsite-centered-card-grid--3">
                          {planningWeights.rows.map((row) => (
                            <article
                              key={row.key}
                              className="campaign-strategy-microsite-cohort-card"
                              data-planning-category={row.category}
                            >
                              <div className="campaign-strategy-microsite-cohort-topline">
                                <span className="campaign-strategy-microsite-cohort-rank">
                                  <span>{row.priorityLabel}</span>
                                  <span className="campaign-strategy-microsite-cohort-source">
                                    {row.categoryLabel}
                                  </span>
                                </span>
                                <span className="campaign-strategy-microsite-cohort-share">
                                  <span className="campaign-strategy-microsite-cohort-share-label">
                                    {AUDIENCE_PLANNING_WEIGHT_LABEL}
                                  </span>
                                  <span className="campaign-strategy-microsite-cohort-share-value">
                                    {formatPlanningWeight(row.weight)}
                                  </span>
                                </span>
                              </div>
                              <h4>{row.displayLabel}</h4>
                              {row.members.length ? <p>{row.members.join(", ")}</p> : null}
                              {row.note ? (
                                <span className="campaign-strategy-microsite-cohort-note">{row.note}</span>
                              ) : null}
                            </article>
                          ))}
                        </div>
                        <div className="campaign-strategy-microsite-chart-card campaign-strategy-microsite-cohort-priority-card">
                          <div className="campaign-strategy-microsite-card-header">
                            <span className="campaign-strategy-microsite-card-title">
                              Planning Weights
                            </span>
                            <span className="campaign-strategy-microsite-card-meta">
                              {PLANNING_WEIGHTS_CHART_META}
                            </span>
                          </div>
                          <PlanningWeightBars planningWeights={planningWeights} />
                          {planningWeights.notices.map((notice) => (
                            <p key={notice} className="campaign-strategy-microsite-planning-weight-notice">
                              {notice}
                            </p>
                          ))}
                          <p className="campaign-strategy-microsite-audience-chart-note">
                            {PLANNING_WEIGHTS_DISCLOSURE}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <EmptyPanel>{planningWeights.emptyCopy}</EmptyPanel>
                        {planningWeights.notices.map((notice) => (
                          <p key={notice} className="campaign-strategy-microsite-planning-weight-notice">
                            {notice}
                          </p>
                        ))}
                      </>
                    )
                  ) : audienceCohorts.length ? (
                    <div className="campaign-strategy-microsite-audience-grid">
                      <p className="campaign-strategy-microsite-audience-disclosure">
                        {AUDIENCE_PLANNING_WEIGHT_DISCLOSURE}
                      </p>
                      <div className="campaign-strategy-microsite-cohort-list campaign-strategy-microsite-centered-card-grid campaign-strategy-microsite-centered-card-grid--3">
                        {audienceGroups.map(({ cohort, group }) => {
                          // The campaign's own saved entry stays as saved; only
                          // backend-written copy is worded for the advertiser's
                          // category.
                          const copyFor = (text, origin) =>
                            origin === "served"
                              ? renderAdvertiserNarrativeCopy(text)
                              : formatMicrositeTextValue(text, "");
                          return (
                            <article
                              key={cohort.key}
                              className="campaign-strategy-microsite-cohort-card"
                            >
                              <div className="campaign-strategy-microsite-cohort-topline">
                                <span className="campaign-strategy-microsite-cohort-rank">
                                  <span>{formatMicrositeTextValue(cohort.priorityLabel, "Priority")}</span>
                                  {group.labels.map((label) => (
                                    <span key={label} className="campaign-strategy-microsite-cohort-source">
                                      {label}
                                    </span>
                                  ))}
                                </span>
                                <span className="campaign-strategy-microsite-cohort-share">
                                  <span className="campaign-strategy-microsite-cohort-share-label">
                                    {AUDIENCE_PLANNING_WEIGHT_LABEL}
                                  </span>
                                  <span className="campaign-strategy-microsite-cohort-share-value">
                                    {formatPlanningWeight(cohort.share) || "Planned"}
                                  </span>
                                </span>
                              </div>
                              <h4>
                                {normalizeAudienceLabel(
                                  formatMicrositeTextValue(group.name, "Planner note")
                                )}
                              </h4>
                              {group.description ? (
                                <p>{copyFor(group.description, group.descriptionOrigin)}</p>
                              ) : null}
                              {group.note ? (
                                <span className="campaign-strategy-microsite-cohort-note">
                                  {copyFor(group.note, group.noteOrigin)}
                                </span>
                              ) : null}
                            </article>
                          );
                        })}
                      </div>
                      <div className="campaign-strategy-microsite-chart-card campaign-strategy-microsite-cohort-priority-card">
                        <div className="campaign-strategy-microsite-card-header">
                          <span className="campaign-strategy-microsite-card-title">
                            Planning Weights
                          </span>
                          <span className="campaign-strategy-microsite-card-meta">
                            Assigned from planner order
                          </span>
                        </div>
                        <MetricBarList
                          items={audienceWeightChartItems}
                          valueFormatter={formatAudienceWeightChartValue}
                        />
                        <p className="campaign-strategy-microsite-audience-chart-note">
                          {AUDIENCE_PLANNING_WEIGHT_CHART_NOTE}
                        </p>
                      </div>
                    </div>
                  ) : !audienceInsightCards.length ? (
                    <EmptyPanel>
                      Audience planning inputs will appear once the planner's targeting is saved.
                    </EmptyPanel>
                  ) : null}
                  {audienceInsightCards.length ? (
                    <div className="campaign-strategy-microsite-insight-grid campaign-strategy-microsite-centered-card-grid campaign-strategy-microsite-centered-card-grid--2">
                      {audienceInsightCards.map((card) => (
                        <article
                          key={card.key}
                          className="campaign-strategy-microsite-insight-card"
                        >
                          <div className="campaign-strategy-microsite-card-header">
                            <span className="campaign-strategy-microsite-card-title">
                              {card.title}
                            </span>
                            <span className="campaign-strategy-microsite-card-meta">
                              {card.meta}
                            </span>
                          </div>
                          <p>{renderAdvertiserNarrativeCopy(card.copy)}</p>
                        </article>
                      ))}
                    </div>
                  ) : null}
                </MicrositeSection>
              ) : null}

              {/* Mounted only with a record: the section's heading id comes from
                  useId, and an unconditional mount would renumber every later id. */}
              {recommendedDataLayers ? (
                <CampaignRecommendedDataLayers dataLayers={recommendedDataLayers} />
              ) : null}

              <MicrositeSection
                eyebrow="Platform Architecture"
                title="Platform Mix + Funnel Allocation"
                meta={totalBudgetDisplay || "Budget pending"}
              >
                {totalBudgetDisplay ? (
                  <p className="campaign-strategy-microsite-section-intro">
                    {renderAdvertiserNarrativeCopy(
                      "",
                      verticalMicrositeFallbacks.platformIntro(totalBudgetDisplay)
                    )}
                  </p>
                ) : null}
                {scaleChannels.length ? (
                  <div className="campaign-strategy-microsite-scale-row">
                    {orderMicrositePlatformRows(scaleChannels, channelSplit).map((label) => (
                      <span key={label} className="campaign-strategy-microsite-chip">
                        {label}
                      </span>
                    ))}
                  </div>
                ) : null}
                {platformCaveats.length ? (
                  <div className="campaign-strategy-microsite-note-block">
                    <ul className="campaign-strategy-microsite-bullet-list">
                      {platformCaveats.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {proposalPlanning ? (
                  <ProposalImpressionOutlook outlook={proposalImpressionOutlook} />
                ) : estimatedDeliveryCards.length || benchmarkEstimateCards.length ? (
                  <div className="campaign-strategy-microsite-visual-grid">
                    <article className="campaign-strategy-microsite-visual-card campaign-strategy-microsite-visual-card--impression-outlook">
                      <div className="campaign-strategy-microsite-card-header">
                        <span className="campaign-strategy-microsite-card-title">
                          {impressionOutlookTitle}
                        </span>
                        {/*
                          * 311.1K was $16,000 divided by a planning benchmark.
                          * The meta names that basis so it cannot read as
                          * forecast delivery.
                          */}
                        <span className="campaign-strategy-microsite-card-meta">
                          {impressionOutlookMetaLabel}
                        </span>
                      </div>
                      {estimatedImpressionTotalLabel ? (
                        <div className="campaign-strategy-microsite-impression-total-tile">
                          <span>Total Est. Impressions</span>
                          <strong>{estimatedImpressionTotalLabel}</strong>
                        </div>
                      ) : null}
                      <ImpressionOutlookBars entries={estimatedDeliveryCards} />
                      <div className="campaign-strategy-microsite-mini-stat-grid campaign-strategy-microsite-centered-card-grid campaign-strategy-microsite-centered-card-grid--3">
                        {estimatedDeliveryCards.map((entry) => (
                          <div
                            key={entry.key}
                            className="campaign-strategy-microsite-mini-stat-card"
                          >
                            <div className="campaign-strategy-microsite-mini-stat-platform-head">
                              <PlatformLogoFrame
                                platform={entry}
                                className="campaign-strategy-microsite-platform-logo-frame--mini"
                              />
                              <span>{entry.label}</span>
                            </div>
                            <strong>{entry.value}</strong>
                          </div>
                        ))}
                      </div>
                    </article>

                    <article className="campaign-strategy-microsite-visual-card campaign-strategy-microsite-visual-card--cpm-envelope">
                      <div className="campaign-strategy-microsite-card-header">
                        <span className="campaign-strategy-microsite-card-title">
                          {pricingEnvelopeTitle}
                        </span>
                        {/*
                          * "Pricing truth" sat over $51.43 — a Cphere directional
                          * constant moved onto the client-billed basis on a
                          * campaign with no provider forecast, no approved
                          * forecast and no contracted rate. The meta line now
                          * states the evidence tier the numbers actually hold.
                          */}
                        <span className="campaign-strategy-microsite-card-meta">
                          {pricingEvidenceMetaLabel}
                        </span>
                      </div>
                      <div className="campaign-strategy-microsite-cpm-chip-grid campaign-strategy-microsite-centered-card-grid campaign-strategy-microsite-centered-card-grid--3">
                        {benchmarkEstimateCards.map((entry) => (
                          <article
                            key={entry.key}
                            className="campaign-strategy-microsite-cpm-chip-card"
                          >
                            <div className="campaign-strategy-microsite-cpm-platform-head">
                              <PlatformLogoFrame
                                platform={entry}
                                className="campaign-strategy-microsite-platform-logo-frame--mini"
                              />
                              <span>{entry.label}</span>
                            </div>
                            <strong>{entry.value}</strong>
                            <small>{entry.meta}</small>
                          </article>
                        ))}
                      </div>
                      {pricingEvidenceAbsences.length ? (
                        <ul className="campaign-strategy-microsite-evidence-absence-list">
                          {pricingEvidenceAbsences.map((line) => (
                            <li key={line}>{line}</li>
                          ))}
                        </ul>
                      ) : null}
                    </article>
                  </div>
                ) : null}
                <div className="campaign-strategy-microsite-budget-grid">
                  <div className="campaign-strategy-microsite-chart-card">
                    <div className="campaign-strategy-microsite-card-header">
                      <span className="campaign-strategy-microsite-card-title">
                        Budget by Platform
                      </span>
                      <span className="campaign-strategy-microsite-card-meta">
                        {allocationApprovalNotice ? "Recommendation" : "Allocation"}
                      </span>
                    </div>
                    <MetricBarList
                      items={channelSplit}
                      showPlatformLogos
                      /*
                       * A chart fed by a canonical allocation may not report
                       * that allocation absent. If the rows are empty while the
                       * published identity says structured, that is a rendering
                       * fault in this page — say so, rather than telling the
                       * client their plan has no allocation.
                       */
                      emptyCopy={
                        allocationIdentity?.structured
                          ? "This plan's allocation could not be rendered on this device. It has not been withdrawn — reload the page, or ask your Cphere contact for the allocation summary."
                          : ""
                      }
                    />
                    {allocationApprovalNotice ? (
                      <p className="campaign-strategy-microsite-chart-note">
                        {allocationApprovalNotice}
                      </p>
                    ) : null}
                    {totalBudgetDisplay && channelSplit.length ? (
                      <p className="campaign-strategy-microsite-chart-note">
                        {`${totalBudgetDisplay} client-billed`}
                      </p>
                    ) : null}
                  </div>

                  <div className="campaign-strategy-microsite-chart-card">
                    <div className="campaign-strategy-microsite-card-header">
                      <span className="campaign-strategy-microsite-card-title">
                        Funnel Architecture
                      </span>
                      <span className="campaign-strategy-microsite-card-meta">
                        Recommended Split
                      </span>
                    </div>
                    <MetricBarList items={objectiveSplit} />
                    {proposalPlanning ? (
                      <ProposalFunnelEstimateRows outlook={proposalImpressionOutlook} />
                    ) : rankedEstimatedDelivery.length ? (
                      <div className="campaign-strategy-microsite-estimate-list">
                        {estimatedImpressionTotalLabel ? (
                          <div className="campaign-strategy-microsite-estimate-row campaign-strategy-microsite-estimate-row--total">
                            <span>Total Est. Impressions</span>
                            <strong>{estimatedImpressionTotalLabel}</strong>
                          </div>
                        ) : null}
                        {rankedEstimatedDelivery.map((entry) => (
                          <div
                            key={entry.key || entry.label}
                            className="campaign-strategy-microsite-estimate-row"
                          >
                            <span
                              className="campaign-strategy-microsite-estimate-platform"
                              title={displayMicrositePlatformIdentityLabel(entry.label, entry.key)}
                            >
                              <PlatformLogoFrame
                                platform={entry}
                                className="campaign-strategy-microsite-platform-logo-frame--mini"
                              />
                              <span className="campaign-strategy-microsite-estimate-platform-label">
                                {formatMicrositePlatformDisplayLabel(entry.label)}
                              </span>
                            </span>
                            <strong>{formatMetricCount(resolveCanonicalVolume(entry))}</strong>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="campaign-strategy-microsite-empty-inline">
                        Estimated delivery will populate when benchmark CPMs are available.
                      </div>
                    )}
                  </div>

                  {/*
                   * The card states the served phases. With none served (Campaign 55,
                   * 2026-10-05: no phase split was planned, and the objective default is
                   * no longer presented) it renders nothing: no placeholder, and no grid
                   * cell held open beside the other charts. The rhythm chart below
                   * decides for itself.
                   */}
                  {timingPhases.length ? (
                    <div className="campaign-strategy-microsite-chart-card">
                      <div className="campaign-strategy-microsite-card-header">
                        <span className="campaign-strategy-microsite-card-title">
                          Timing / Phasing
                        </span>
                        <span className="campaign-strategy-microsite-card-meta">
                          {plannerPhaseWorkingMedia.size ? "Share of working media" : "Flight Rhythm"}
                        </span>
                      </div>
                      <MetricBarList
                        items={timingPhases}
                        valueFormatter={(item) => {
                          const share = item?.allocationLabel || `${item?.value || 0}%`;
                          if (item?.rangeLabel) return `${item.rangeLabel} · ${share}`;
                          return plannerPhaseWorkingMedia.has(item?.key)
                            ? `${share} · ${formatCurrency(plannerPhaseWorkingMedia.get(item.key))}`
                            : share;
                        }}
                      />
                    </div>
                  ) : null}
                  <FlightRhythmMiniChart items={timingPhases} />
                </div>
                {resolvedPlatformCards.length ? (
                  <div className="campaign-strategy-microsite-objective-matrix campaign-strategy-microsite-platform-recommendation-grid campaign-strategy-microsite-centered-card-grid campaign-strategy-microsite-centered-card-grid--4-to-3">
                    {resolvedPlatformCards.map((card) => {
                      const brand =
                        resolveMicrositePlatformBranding(card) ||
                        PLATFORM_FALLBACK_BRANDING;
                      return (
                        <article
                          key={card.key || card.title}
                          className="campaign-strategy-microsite-objective-matrix-card"
                          {...platformStrategyCompletenessProps(card)}
                        >
                          <div className="campaign-strategy-microsite-objective-matrix-topline">
                            <div className="campaign-strategy-microsite-objective-matrix-brand">
                              {brand?.image ? (
                                <span className="campaign-strategy-microsite-objective-matrix-logo">
                                  <img
                                    src={brand.image}
                                    alt={formatMicrositePlatformDisplayLabel(brand.label)}
                                  />
                                </span>
                              ) : null}
                              <span>
                                {displayMicrositePlatformIdentityLabel(card.title, card.key)}
                              </span>
                            </div>
                            <span className="campaign-strategy-microsite-objective-pill">
                              {resolvePlatformRolePillLabel(card)}
                            </span>
                          </div>
                          <p>{resolvePlatformStrategyValue(card, "role") || copy.platformRoleFallback}</p>
                          <div className="campaign-strategy-microsite-objective-matrix-meta">
                            <MicrositeKpiValue value={card.primaryKpi} />
                            <strong>{card.budgetLabel || card.shareLabel}</strong>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                ) : null}
              </MicrositeSection>
            </section>

            {showSection("earnedMedia") ? (
              <EarnedMediaIntelligenceSection
                section={earnedMediaSection}
                renderCopy={renderAdvertiserNarrativeCopy}
              />
            ) : null}

            <ReleasePhaseIntelligenceSection
              intel={releasePhaseIntelligence}
              renderCopy={renderAdvertiserNarrativeCopy}
              /* Presentation contract v2 (P16 residue): a stamped artifact that
                 carries flightPhaseSectionTitle names the section by the flight,
                 not a release; a legacy payload keeps the title byte for byte. */
              title={
                presentationFeature(campaign, "flightPhaseSectionTitle")
                  ? "Flight Phase Intelligence"
                  : "Release Phase Intelligence"
              }
            />

            {showSection("releaseMilestones") ? (
              <ReleaseMilestoneScheduleSection
                rows={releaseMilestoneSchedule}
                renderCopy={renderAdvertiserNarrativeCopy}
                advertiserVertical={resolvedAdvertiserVertical}
              />
            ) : null}

            {showSection("antiWaste") ? (
              <AntiWasteGuardrailsSection
                guardrails={antiWasteGuardrails}
                renderCopy={renderAdvertiserNarrativeCopy}
              />
            ) : null}

            {showSection("scenarioTriggers") ? (
              <ScenarioExpansionTriggersSection
                triggers={scenarioExpansionTriggers}
                renderCopy={renderAdvertiserNarrativeCopy}
              />
            ) : null}

            {showSection("benchmarkJustification") || showSection("retargeting") ? (
              <section className="campaign-strategy-microsite-benchmark-sequencing-stack">
                {showSection("benchmarkJustification") ? (
                  <MicrositeSection
                    eyebrow="Benchmark Justification"
                    title="Commercial Logic + Planning Benchmarks"
                    meta={benchmarkCards.length ? copy.countBadge(benchmarkCards.length, "platform benchmark") : "Directional"}
                  >
                    <BenchmarkEfficiencyChart
                      chart={benchmarkChart}
                      allocationRows={channelSplit}
                      defaultFiguresApply={defaultFiguresApply}
                    />
                    {benchmarkCards.length ? (
                      <div className="campaign-strategy-microsite-benchmark-grid campaign-strategy-microsite-centered-card-grid campaign-strategy-microsite-centered-card-grid--4-to-3">
                        {benchmarkCards.map((card) => (
                          <article
                            key={card.key || card.label}
                            className="campaign-strategy-microsite-benchmark-card"
                          >
                            <div className="campaign-strategy-microsite-benchmark-topline">
                              <span className="campaign-strategy-microsite-benchmark-platform">
                                <PlatformLogoFrame
                                  platform={card}
                                  className="campaign-strategy-microsite-platform-logo-frame--mini"
                                />
                                <span className="campaign-strategy-microsite-benchmark-platform-text">
                                  {displayMicrositePlatformIdentityLabel(card.label, card.key)}
                                </span>
                              </span>
                              <span className="campaign-strategy-microsite-benchmark-primary">
                                {card.primaryMetricKey ? (
                                  <span
                                    className={`campaign-strategy-microsite-benchmark-metric-tag campaign-strategy-microsite-benchmark-metric-tag--${card.primaryMetricKey}`}
                                  >
                                    {card.primaryMetricLabel ||
                                      resolveBenchmarkMetricShortLabel(card.primaryMetricKey)}
                                  </span>
                                ) : null}
                                <strong>{card.primaryValue}</strong>
                              </span>
                            </div>
                            <span className="campaign-strategy-microsite-benchmark-secondary">
                              {resolveBenchmarkDisplayLabel(card)}
                            </span>
                            <p>{card.explanation}</p>
                          </article>
                        ))}
                      </div>
                    ) : (
                      <EmptyPanel>
                        Benchmark cards will expand as more platform pricing data is connected.
                      </EmptyPanel>
                    )}
                    {defaultFiguresApply ? (
                      <CphereIntelligencePanel
                        intelligence={cphereIntelligence}
                        allocationRows={channelSplit}
                      />
                    ) : null}
                    {benchmarkSection?.narrative ? (
                      <div className="campaign-strategy-microsite-note-block">
                        {renderAdvertiserNarrativeCopy(benchmarkSection.narrative)}
                      </div>
                    ) : null}
                  </MicrositeSection>
                ) : null}

                {showSection("retargeting") ? (
                  <MicrositeSection
                    eyebrow={
                      /* Presentation contract v2 (P2): a plan that activates no
                         retargeting sequences audiences without a retargeting
                         programme; the section says so and shows no CTR curve. */
                      retargetingSuppressedBySection
                        ? "Audience Sequencing"
                        : "Retargeting Architecture"
                    }
                    title="Stage Sequencing + Messaging Flow"
                    meta={flowStages.length ? copy.countBadge(flowStages.length, "stage") : "Pending"}
                  >
                    <FlowStages items={flowStages} defaultFiguresApply={defaultFiguresApply} />
                    {defaultFiguresApply && !retargetingSuppressedBySection ? (
                      <RetargetingPerformanceChart
                        chart={retargetingSection?.performanceChart}
                      />
                    ) : null}
                    {Array.isArray(retargetingSection?.customAudiencePriorities) &&
                    retargetingSection.customAudiencePriorities.length ? (
                      <div className="campaign-strategy-microsite-retargeting-lists">
                        <div className="campaign-strategy-microsite-retargeting-panel campaign-strategy-microsite-retargeting-panel--audiences">
                          <span className="campaign-strategy-microsite-kv-label">
                            Custom Audiences — Priority Order
                          </span>
                          {retargetingSection.customAudiencePriorities.map((item, index) => (
                            <article
                              key={item.key || item.title}
                              className="campaign-strategy-microsite-retargeting-list-card"
                            >
                              <div className="campaign-strategy-microsite-retargeting-title-row">
                                <span
                                  className={`campaign-strategy-microsite-retargeting-tier-label campaign-strategy-microsite-stage-tier-label campaign-strategy-microsite-stage-tier-label--tier-${index + 1}`}
                                >
                                  {item.tier}
                                </span>
                                <strong className="campaign-strategy-microsite-retargeting-list-title">
                                  {item.title}
                                </strong>
                              </div>
                              <p>{item.description}</p>
                            </article>
                          ))}
                        </div>
                        <div className="campaign-strategy-microsite-retargeting-panel campaign-strategy-microsite-retargeting-panel--messaging">
                          <span className="campaign-strategy-microsite-kv-label">
                            Creative Messaging by Retargeting Tier
                          </span>
                          {(retargetingSection.creativeMessagingByTier || []).map((item, index) => (
                            <article
                              key={item.key || item.title}
                              className="campaign-strategy-microsite-retargeting-list-card"
                            >
                              <strong
                                className={`campaign-strategy-microsite-retargeting-list-title campaign-strategy-microsite-retargeting-message-title campaign-strategy-microsite-stage-tier-label campaign-strategy-microsite-stage-tier-label--tier-${index + 1}`}
                              >
                                {item.tier} — {item.title}
                              </strong>
                              <p>{item.message}</p>
                            </article>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    {retargetingSection?.narrative ? (
                      <div className="campaign-strategy-microsite-note-block">
                        {renderAdvertiserNarrativeCopy(retargetingSection.narrative)}
                      </div>
                    ) : null}
                  </MicrositeSection>
                ) : null}
              </section>
            ) : null}

            {showSection("launchCalendar") ? (
              <LaunchActivationCalendar
                calendar={presentLaunchActivationCalendar(launchActivationCalendar, timingPhases)}
                allocationRows={channelSplit}
              />
            ) : proposalSpendRampCalendar ? (
              <LaunchActivationCalendar calendar={proposalSpendRampCalendar} allocationRows={channelSplit} />
            ) : null}

            {showSection("successMetrics") ? (
              <SuccessMetricsSection
                metrics={successMetrics}
                renderCopy={renderAdvertiserNarrativeCopy}
                allocationRows={channelSplit}
                defaultFiguresApply={defaultFiguresApply}
              />
            ) : null}

            {showSection("actionItems") ? (
              <ActionItemsSection
                actionItems={actionItems}
                advertiserVertical={resolvedAdvertiserVertical}
              />
            ) : null}

            {/* Mounted only with a selected scope: the network draws nothing
                for an empty plan, and an unmounted section allocates no useId,
                so a page without platforms keeps every later id unchanged. */}
            {resolvedPlatformCards.length ? (
              <CampaignMediaNetwork
                platforms={resolvedPlatformCards}
                selectedScope={platformChips}
                allocations={campaignMediaAllocationRows}
                renderCopy={renderAdvertiserNarrativeCopy}
              />
            ) : null}

            <MicrositeSection
              eyebrow="Platform Strategy"
              title="Digital Media Plan + Platform Roles"
              meta={
                resolvedPlatformCards.length
                  ? copy.countBadge(resolvedPlatformCards.length, "platform plan")
                  : "Pending"
              }
            >
              <p className="campaign-strategy-microsite-section-intro">
                {renderAdvertiserNarrativeCopy(
                  campaignDetailSection?.summary,
                  copy.platformStrategyIntro
                )}
              </p>
              {resolvedPlatformCards.length ? (
                <div className="campaign-strategy-microsite-platform-grid campaign-strategy-microsite-centered-card-grid campaign-strategy-microsite-centered-card-grid--2">
                  {resolvedPlatformCards.map((card) => (
                    <article
                      key={card.key || card.title}
                      className="campaign-strategy-microsite-platform-card"
                      {...platformStrategyCompletenessProps(card)}
                    >
                      <div className="campaign-strategy-microsite-card-header">
                        <span className="campaign-strategy-microsite-platform-role-title">
                          <PlatformLogoFrame
                            platform={card}
                            className="campaign-strategy-microsite-platform-logo-frame--role"
                          />
                          <span className="campaign-strategy-microsite-card-title">
                            {displayMicrositePlatformIdentityLabel(
                              formatMicrositeTextValue(card.title, "Platform"),
                              card.key
                            )}
                          </span>
                        </span>
                        <span className="campaign-strategy-microsite-card-meta">
                          {formatMicrositeTextValue(
                            card.canonicalAllocationLabel || card.budgetLabel || card.shareLabel,
                            "Planned"
                          )}
                        </span>
                      </div>

                      <p className="campaign-strategy-microsite-channel-role">
                        {renderAdvertiserNarrativeCopy(
                          resolvePlatformStrategyValue(card, "role"),
                          copy.platformRoleFallback
                        )}
                      </p>

                      <div className="campaign-strategy-microsite-channel-chip-row">
                        {formatMicrositeTextValue(resolvePlatformStrategyValue(card, "funnelPurpose"), "") ? (
                          <span className="campaign-strategy-microsite-status-chip">
                            {formatMicrositeTextValue(resolvePlatformStrategyValue(card, "funnelPurpose"), "")}
                          </span>
                        ) : null}
                        {card.primaryKpi ? (
                          semanticTypography && isSentenceLikeKpi(card.primaryKpi) ? (
                            <span className="campaign-strategy-microsite-platform-chip campaign-strategy-microsite-platform-chip--sentence">
                              <span className="campaign-strategy-microsite-kpi-sentence-label">KPI:</span>{" "}
                              {formatMicrositePrimaryKpiDisplay(formatMicrositeTextValue(card.primaryKpi, "Planned"))}
                            </span>
                          ) : (
                            <span className="campaign-strategy-microsite-platform-chip">
                              KPI: {semanticTypography
                                ? formatMicrositePrimaryKpiDisplay(formatMicrositeTextValue(card.primaryKpi, "Planned"))
                                : formatMicrositeTextValue(card.primaryKpi, "Planned")}
                            </span>
                          )
                        ) : null}
                      </div>

                      <div className="campaign-strategy-microsite-channel-stack">
                        <div className="campaign-strategy-microsite-channel-block">
                          <span className="campaign-strategy-microsite-kv-label">
                            Targeting Logic
                          </span>
                          <p className="campaign-strategy-microsite-channel-copy">
                            {renderAdvertiserNarrativeCopy(
                              resolvePlatformStrategyValue(card, "targetingLogic") || card.targetingSummary,
                              "Targeting logic will appear once the platform plan is finalized."
                            )}
                          </p>
                        </div>

                        <div className="campaign-strategy-microsite-channel-block">
                          <span className="campaign-strategy-microsite-kv-label">
                            Creative Approach
                          </span>
                          <p className="campaign-strategy-microsite-channel-copy">
                            {renderAdvertiserNarrativeCopy(
                              resolvePlatformStrategyValue(card, "creativeApproach") ||
                                card.creativeStrategy ||
                                card.creativeDirection ||
                                card.creative,
                              "Platform-ready creative direction pending final assets."
                            )}
                          </p>
                        </div>

                        <div className="campaign-strategy-microsite-channel-block">
                          <span className="campaign-strategy-microsite-kv-label">
                            Measurement Logic
                          </span>
                          <p className="campaign-strategy-microsite-channel-copy">
                            {renderAdvertiserNarrativeCopy(
                              resolvePlatformStrategyValue(card, "measurementLogic"),
                              "Measurement framing will publish once KPI instrumentation is attached."
                            )}
                          </p>
                        </div>
                      </div>

                      <div className="campaign-strategy-microsite-detail-stack">
                        <div className="campaign-strategy-microsite-kv-row">
                          <span className="campaign-strategy-microsite-kv-label">
                            {card.budgetBasisLabel ? `Budget · ${card.budgetBasisLabel}` : "Budget"}
                          </span>
                          <span className="campaign-strategy-microsite-kv-value">
                            {formatMicrositeTextValue(card.budgetLabel, "Planned")}
                          </span>
                        </div>
                        <div className="campaign-strategy-microsite-kv-row">
                          <span className="campaign-strategy-microsite-kv-label">
                            Funnel Purpose
                          </span>
                          <span className="campaign-strategy-microsite-kv-value">
                            {formatMicrositeTextValue(resolvePlatformStrategyValue(card, "funnelPurpose"), "Planned")}
                          </span>
                        </div>
                        <div className="campaign-strategy-microsite-kv-row">
                          <span className="campaign-strategy-microsite-kv-label">
                            Activation Notes
                          </span>
                          <span className={semanticTypography ? "campaign-strategy-microsite-kv-value campaign-strategy-microsite-kv-value--sentence" : "campaign-strategy-microsite-kv-value"}>
                            {renderAdvertiserNarrativeCopy(
                              card.launchReadiness || card.activationNotes,
                              copy.activationNotesFallback
                            )}
                          </span>
                        </div>
                        {card.caveat ? (
                          <div className="campaign-strategy-microsite-kv-row">
                            <span className="campaign-strategy-microsite-kv-label">
                              Caveat
                            </span>
                            <span className="campaign-strategy-microsite-kv-value">
                              {card.caveat}
                            </span>
                          </div>
                        ) : null}
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <EmptyPanel>
                  Platform-specific cards will populate once the saved platform mix is available.
                </EmptyPanel>
              )}
            </MicrositeSection>

            <MicrositeSection
              eyebrow="Execution Blueprint"
              title="Execution Blueprint"
              meta={
                planningReview
                  ? `${resolvedCampaignDetailCards.length} allocation group${resolvedCampaignDetailCards.length === 1 ? "" : "s"} planned`
                  : campaignReadiness && Number.isFinite(campaignReadiness.readyCount) &&
                Number.isFinite(campaignReadiness.totalCount) && campaignReadiness.totalCount
                  ? campaignReadiness.readyCount > 0
                    ? `${campaignReadiness.readyCount} of ${campaignReadiness.totalCount} ${micrositeCountNoun(
                        campaignReadiness.totalCount,
                        "platform"
                      )} launch-ready`
                    : `${campaignReadiness.totalCount} platform${campaignReadiness.totalCount === 1 ? "" : "s"} in setup`
                  : resolvedCampaignDetailCards.length
                  ? copy.countBadge(resolvedCampaignDetailCards.length, "platform setup")
                  : "Activation layer"
              }
            >
              <p className="campaign-strategy-microsite-section-intro">
                {renderAdvertiserNarrativeCopy(
                  planningReview
                    ? "Proposed platform roles for review. Approval and execution authority are separate."
                    : campaignDetailSection?.detailSummary,
                  "The execution blueprint translates the media plan into a per-platform operational playbook — objective, audience, creative, optimization, expected KPI, and activation readiness for each approved platform."
                )}
              </p>
              <CampaignDetailCards campaigns={resolvedCampaignDetailCards} planningReview={planningReview} />
              {!planningReview ? <ExecutionReadinessSummary readiness={campaignReadiness} /> : null}
            </MicrositeSection>

            <section className="campaign-strategy-microsite-creative-launch-stack">
              <MicrositeSection
                eyebrow="Creative System"
                title="Creative / Messaging Strategy"
                meta={
                  realCreativeThemeCount
                    ? copy.countBadge(realCreativeThemeCount, "message pillar")
                    : "Story in progress"
                }
              >
                <p className="campaign-strategy-microsite-section-intro">
                  {renderAdvertiserNarrativeCopy(
                    creativeMessagingSection?.summary,
                    // A General page names no film "title context" (utils/micrositeGeneralTemplate).
                    generalTemplate
                      ? ""
                      : "Creative strategy will render here once planner recommendations and title-context inputs are attached."
                  )}
                </p>
                {creativeVariationChips.length ? (
                  <div className="campaign-strategy-microsite-chip-row">
                    {creativeVariationChips.map((label) => (
                      <span key={label} className="campaign-strategy-microsite-chip">
                        {label}
                      </span>
                    ))}
                  </div>
                ) : null}

                {(creativeMessagingSection?.objectiveContext ||
                  creativeMessagingSection?.callToAction ||
                  creativeMessagingSection?.landingPageLogic) ? (
                  <div className="campaign-strategy-microsite-note-block">
                    {[creativeMessagingSection?.objectiveContext, creativeMessagingSection?.callToAction, creativeMessagingSection?.landingPageLogic]
                      .map((item) => renderAdvertiserNarrativeCopy(item, ""))
                      .filter(Boolean)
                      .join(" ")}
                  </div>
                ) : null}

                <div className="campaign-strategy-microsite-theme-grid campaign-strategy-microsite-creative-pillar-grid">
                  {creativeThemeCards.map((card) => (
                    <article
                      key={card.key}
                      className="campaign-strategy-microsite-theme-card"
                    >
                      <div className="campaign-strategy-microsite-card-header">
                        <span className="campaign-strategy-microsite-card-title">
                          {card.title}
                        </span>
                        <span className="campaign-strategy-microsite-card-meta">
                          {card.priorityLabel}
                        </span>
                      </div>
                      <p className="campaign-strategy-microsite-theme-copy">
                        {renderAdvertiserNarrativeCopy(card.copy)}
                      </p>
                    </article>
                  ))}
                </div>
                <CreativeVariationExamples
                  cards={creativeVariationCards}
                  renderCopy={renderAdvertiserNarrativeCopy}
                />
              </MicrositeSection>

              <MicrositeSection
                eyebrow="Launch Readiness"
                title="Launch + Optimization Timeline"
                meta={
                  resolvedExecutionModules.length
                    ? copy.countBadge(resolvedExecutionModules.length, "launch plan")
                    : "Awaiting activation"
                }
              >
                {launchTimelinePhases.length ? (
                  <div className="campaign-strategy-microsite-timeline-grid campaign-strategy-microsite-centered-card-grid campaign-strategy-microsite-centered-card-grid--4">
                    {launchTimelinePhases.map((phase) => (
                      <article
                        key={phase.key}
                        className="campaign-strategy-microsite-timeline-card"
                      >
                        <span className="campaign-strategy-microsite-card-meta">
                          {phase.phaseLabel}
                        </span>
                        <strong className="campaign-strategy-microsite-card-title">
                          {phase.title}
                        </strong>
                        <p>{renderAdvertiserNarrativeCopy(phase.copy)}</p>
                      </article>
                    ))}
                  </div>
                ) : null}
                {resolvedExecutionModules.length ? (
                  <div className="campaign-strategy-microsite-execution-grid campaign-strategy-microsite-launch-module-grid">
                    {resolvedExecutionModules.map((module) => (
                      <article
                        key={module.key || module.title}
                        className="campaign-strategy-microsite-execution-card"
                      >
                        <div className="campaign-strategy-microsite-card-header">
                          <span className="campaign-strategy-microsite-card-title campaign-strategy-microsite-execution-platform-title">
                            <PlatformLogoFrame
                              platform={module}
                              className="campaign-strategy-microsite-platform-logo-frame--mini"
                            />
                            <span className="campaign-strategy-microsite-execution-platform-text">
                              {displayMicrositePlatformIdentityLabel(module.title, module.key)}
                            </span>
                          </span>
                          <span className="campaign-strategy-microsite-status-chip">
                            {planningReview ? "Planned" : module.statusLabel || "Planned"}
                          </span>
                        </div>

                        {module.launchNotes ? (
                          <p className="campaign-strategy-microsite-channel-copy">
                            {renderAdvertiserNarrativeCopy(planningReview ? "Proposed channel plan; execution remains subject to approval." : module.launchNotes)}
                          </p>
                        ) : null}

                        {(Array.isArray(module.dependencies) && module.dependencies.length) ||
                        module.optimizationNotes ? (
                          <div className="campaign-strategy-microsite-launch-module-body">
                            {Array.isArray(module.dependencies) && module.dependencies.length ? (
                              <div className="campaign-strategy-microsite-execution-block campaign-strategy-microsite-launch-module-dependencies">
                                <span className="campaign-strategy-microsite-kv-label">
                                  Dependencies
                                </span>
                                <ul className="campaign-strategy-microsite-bullet-list">
                                  {module.dependencies.map((dependency, index) => (
                                    <li key={`${module.key || module.title}-dep-${index}`}>
                                      {dependency}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ) : null}

                            {module.optimizationNotes ? (
                              <div className="campaign-strategy-microsite-execution-block campaign-strategy-microsite-launch-module-optimization">
                                <span className="campaign-strategy-microsite-kv-label">
                                  Optimization Note
                                </span>
                                <p className="campaign-strategy-microsite-channel-copy">
                                  {renderAdvertiserNarrativeCopy(module.optimizationNotes)}
                                </p>
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                      </article>
                    ))}
                  </div>
                ) : (
                  <EmptyPanel>
                    Launch readiness will appear once each platform's execution plan is prepared.
                  </EmptyPanel>
                )}
              </MicrositeSection>
            </section>

            {showSection("brandAwareness") ? (
              <BrandAwarenessStrategySection
                strategy={brandAwarenessStrategy}
                renderCopy={renderAdvertiserNarrativeCopy}
                defaultFiguresApply={defaultFiguresApply}
              />
            ) : null}

              {showSection("kpiMeasurement") ? (
                <MicrositeSection
                  eyebrow="Optimization + Measurement"
                  title="KPI / Measurement"
                  meta={resolvedKpis.length ? copy.countBadge(resolvedKpis.length, "KPI") : "Always On"}
                >
                  {verticalMicrositeFallbacks.measurementSummary ? (
                    <p className="campaign-strategy-microsite-section-intro">
                      {renderAdvertiserNarrativeCopy(verticalMicrositeFallbacks.measurementSummary)}
                    </p>
                  ) : null}
                  {platformCaveats.length ? (
                    <div className="campaign-strategy-microsite-note-block">
                      <ul className="campaign-strategy-microsite-bullet-list">
                        {platformCaveats.map((item) => (
                          <li key={`measurement-${item}`}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  <div className="campaign-strategy-microsite-measurement-grid campaign-strategy-microsite-centered-card-grid campaign-strategy-microsite-centered-card-grid--2">
                    <article className="campaign-strategy-microsite-detail-card campaign-strategy-microsite-kpi-card">
                    <div className="campaign-strategy-microsite-card-header">
                      <span className="campaign-strategy-microsite-card-title campaign-strategy-microsite-kpi-card-title">KPIs</span>
                      <span className="campaign-strategy-microsite-card-meta">Measurement</span>
                    </div>
                    {resolvedKpis.length ? (
                      <ul className="campaign-strategy-microsite-bullet-list">
                        {resolvedKpis.map((item, index) => (
                          <li key={`${item}-${index}`}>
                            {semanticTypography
                              ? formatMicrositePrimaryKpiDisplay(renderAdvertiserNarrativeCopy(item))
                              : renderAdvertiserNarrativeCopy(item)}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <EmptyPanel>KPI expectations will appear here once assigned.</EmptyPanel>
                    )}
                  </article>

                  <article className="campaign-strategy-microsite-detail-card campaign-strategy-microsite-kpi-card">
                    <div className="campaign-strategy-microsite-card-header">
                      <span className="campaign-strategy-microsite-card-title campaign-strategy-microsite-kpi-card-title">
                        Reporting Logic
                      </span>
                      <span className="campaign-strategy-microsite-card-meta">Cadence</span>
                    </div>
                    {resolvedReportingLogic.length ? (
                      <ul className="campaign-strategy-microsite-bullet-list">
                        {resolvedReportingLogic.map((item, index) => (
                          <li key={`${item}-${index}`}>{renderAdvertiserNarrativeCopy(item)}</li>
                        ))}
                      </ul>
                    ) : (
                      <EmptyPanel>{copy.reportingEmpty}</EmptyPanel>
                    )}
                  </article>

                  <article className="campaign-strategy-microsite-detail-card campaign-strategy-microsite-kpi-card">
                    <div className="campaign-strategy-microsite-card-header">
                      <span className="campaign-strategy-microsite-card-title campaign-strategy-microsite-kpi-card-title">
                        Tracking Requirements
                      </span>
                      <span className="campaign-strategy-microsite-card-meta">Readiness</span>
                    </div>
                    {resolvedTrackingRequirements.length ? (
                      <ul className="campaign-strategy-microsite-bullet-list">
                        {resolvedTrackingRequirements.map((item, index) => (
                          <li key={`${item}-${index}`}>{renderAdvertiserNarrativeCopy(item)}</li>
                        ))}
                      </ul>
                    ) : (
                      <EmptyPanel>Tracking requirements will appear once links and KPIs are attached.</EmptyPanel>
                    )}
                  </article>

                  <article className="campaign-strategy-microsite-detail-card campaign-strategy-microsite-kpi-card">
                    <div className="campaign-strategy-microsite-card-header">
                      <span className="campaign-strategy-microsite-card-title campaign-strategy-microsite-kpi-card-title">
                        Optimization Cadence
                      </span>
                      <span className="campaign-strategy-microsite-card-meta">Operations</span>
                    </div>
                    {resolvedCadence.length ? (
                      <ul className="campaign-strategy-microsite-bullet-list">
                        {resolvedCadence.map((item, index) => (
                          <li key={`${item}-${index}`}>{renderAdvertiserNarrativeCopy(item)}</li>
                        ))}
                      </ul>
                    ) : (
                      <EmptyPanel>{copy.optimizationEmpty}</EmptyPanel>
                    )}
                  </article>
                  </div>
                </MicrositeSection>
              ) : null}

            {showSection("didYouKnow") && didYouKnowStats.length ? (
              <MicrositeSection
                eyebrow="Did You Know?"
                title="Cross-Platform Market Signals"
                meta={copy.benchmarksMeta}
              >
                <div className="campaign-strategy-microsite-stat-grid campaign-strategy-microsite-centered-card-grid campaign-strategy-microsite-centered-card-grid--3">
                  {didYouKnowStats.map((item) => (
                    <article
                      key={item.key}
                      className="campaign-strategy-microsite-stat-card"
                    >
                      <strong>{item.value}</strong>
                      <span>{item.label}</span>
                      <small>{item.footnote}</small>
                    </article>
                  ))}
                </div>
              </MicrositeSection>
            ) : null}

            <MicrositeSection
              eyebrow="Full Strategy"
              title={copy.narrativeTitle}
              meta={copy.countBadge(Number(campaign?.strategy?.blockCount || 0), "strategy section")}
            >
              <ProposalNarrativeNote planning={proposalPlanning} />
              {/*
                * The narrative expands with its content and scrolls with the
                * PAGE: the planner's scroll-box styling is released under this
                * wrapper (CampaignStrategyMicrosite.css).
                */}
              <div className="campaign-strategy-microsite-narrative">
                <MicrositeStrategyNarrative
                  strategy={campaign.strategy}
                  generalTemplate={generalTemplate}
                />
              </div>
            </MicrositeSection>

            {showMediaPlannerControls ? (
              <div className="campaign-strategy-microsite-footer">
                <Link
                  to="/sherloc-media-planner"
                  className="campaign-strategy-microsite-return-button"
                >
                  Return to Media Planner
                </Link>
              </div>
            ) : null}
            <CampaignStrategyMicrositeFooter />
          </>
        )}
      </div>
    </div>
    </MicrositeSemanticTypographyContext.Provider>
    </MicrositeBrandPresentationContext.Provider>
  );
}
